/**
 * GAME — owns the loop, stage lifecycle, and resize.
 * Keeps main.js as thin wiring/init.
 */

import Matter from "matter-js";
import * as C from "../config/constants.js";

import { GameStateMachine } from "./game-state-machine.js";
import { createRetentionManager } from "./retention-manager.js";
import { createRewardReveal } from "./reward-reveal.js";
import { EventBus } from "./event-bus.js";
import { FallPhase } from "../run/fall-phase.js";
import { getKeyboardRampSpeed } from "./input-manager.js";
import { buildFallRunConfig, getFallCountdownTickMs, getFallCountdownTickSec } from "../run/fall-run-config.js";
import { pickSpawnTierFromRunMax } from "../run/spawn-weights.js";
import { FlaskSystem } from "../flask/flask-system.js";
import { getDropSpawnY, getDropSpawnYOffsetPx, getFlaskPanelScale } from "../flask/system/geometry.js";
import { getMetaPhysicsSizeTier, isMinimizableMeta } from "../flask/system/minimize.js";
import { getScale } from "../layout/scale.js";
import { computeResponsiveBoard, resolveGridWidthPx } from "../layout/responsive-board.js";
import { computeVerticalBoard } from "../layout/vertical-board.js";
import {
  readLayoutModePreference,
  writeLayoutModePreference,
  normalizeLayoutPreference,
  resolveLayoutMode,
  resolveEffectiveLayoutMode,
} from "../layout/layout-mode.js";
import {
  computeMockupUiScale,
  MOCKUP_PLAY_BASE_WIDTH_PX,
  scaleMockupSidePx,
} from "../layout/mockup-ui-scale.js";
import {
  drawSkyScene,
  drawSkyBackdropScene,
  drawSkyForegroundScene,
  shouldDrawSkyStarsForHour,
} from "../render/sky-scene.js";
import { resetStarFieldCache } from "../render/sky/stars.js";
import {
  buildSkyShaderUniforms,
  skyShaderSolidUniforms,
} from "../render/sky/shader-uniforms.js";
import {
  beginFallSkyClock,
  beginTransitionToFall,
  beginTransitionToShake,
  completeTransitionToFall,
  completeTransitionToShake,
  computeFallBarShrink,
  createSkyClockController,
  cycleBaseHourFromCycleIndex,
  freezeSkyClock,
  getSkyHour,
  restoreShakeCrawlFromHour,
  resetSkyClockPauseMotion,
  SKY_MODE,
  unfreezeSkyClock,
} from "./sky-clock-controller.js";
import { drawHeartsBar } from "../render/hearts-renderer.js";
import { canUsePixi } from "../render/pixi/can-use-pixi.js";
import { skyRenderScale } from "../render/pixi/sky-quality.js";
import { collapseTiers } from "../render/queue-chips.js";
import { updateStashPanel } from "../render/stash-panel-dom.js";
import { buildEvolutionRingSignature, buildRingTiers, renderEvolutionRing } from "../render/evolution-ring-dom.js";
import { buildEvolutionStripSignature, renderEvolutionStrip } from "../render/evolution-strip-dom.js";
import {
  DISCOVERY_REVEAL_TIMING,
  getSecretRevealMode,
  runDiscoveryCeremony,
} from "../render/secret-reveal.js";
import {
  VICTORY_POPUP_TIMING,
  showVictoryOverlay,
} from "../render/victory-popup.js";
import { setFallMoveHintVisible } from "../render/fall-move-hint-dom.js";
import {
  positionShakeHintTooltip,
  setShakeHintVisible,
} from "../render/shake-hint-dom.js";
import { showStatusToast } from "../render/status-toast-dom.js";
import {
  flaskHasShakeHintPair,
  getShakeHintSettleMs,
  shouldShowShakeHint,
} from "../run/shake-hint.js";
import { shouldShowHealHint } from "../run/heal-hint.js";
import { renderHpHeartsHtml } from "../render/hp-hearts-dom.js";
import {
  getDropPreviewSpriteMetrics,
  getDropSpawnLiftPx,
  updateJarDropPreview,
  updateJarDropQueue,
} from "../render/shake-jar-chrome-dom.js";
import { layoutShakeJarChrome, syncShakeJarChrome, traceJarUPath } from "../render/shake-jar-visual.js";
import { drawOverflowOutlines } from "../render/overflow-outline.js";
import { getOverflowAlertY, getOverflowWarningY } from "../flask/system/overflow.js";
import {
  createOverflowState,
  isOverflowAlertFlashing,
  isOverflowBlockingContinue,
  resetOverflowPlayerAction,
  tickOverflowState,
} from "../flask/system/overflow-state.js";
import {
  computeScorePopupPeakFontPx,
  createScorePopup,
  drawScorePopups,
  pruneScorePopups,
} from "../run/score-popups.js";
import {
  captureFallBuoyFadeState,
  collectFallBuoyFadeSpriteSpecs,
  drawFallBuoyFadePair,
} from "../run/fall-movement-range.js";

import { computeStageElementAlpha } from "./stage-transition.js";
import { createDevFpsTracker } from "./dev-fps-overlay.js";
import { formatDevStatsOverlayText } from "./dev-stats-overlay.js";
import {
  relocateDevPerfOverlaysToShell,
  setDevPerfDialogOpen,
  syncDevPerfDialogUi,
} from "./dev-perf-dialog.js";
import {
  formatDialDiscTransform,
  formatDialRotCssVar,
  resolveFallProgressDial,
} from "./fall-progress-dial.js";
import { clamp01 } from "./day-night.js";
import { FlaskRenderer } from "../flask/flask-renderer.js";
import { MergeAssets } from "../assets/merge-assets.js";
import { preloadAllGameAssets } from "../assets/asset-preloader.js";
import { mergeTierImageUrl } from "../assets/merge-tier-urls.js";
import { gameImageUrl } from "../assets/game-image-urls.js";
import { TestFlaskMode } from "../flask/test-flask-mode.js";
import { runtimeFlags } from "./runtime-flags.js";
import { CrazyGamesBridge } from "./crazygames-bridge.js";
import { Y8Bridge } from "./y8-bridge.js";
import { YtBridge } from "./yt-bridge.js";
import { PokiBridge } from "./poki-bridge.js";
import {
  createPokiProgressState,
  stageProgressEvents,
  enterNightStageEvents,
  tierReachedEvent,
} from "./poki-progress.js";
import { GdBridge } from "./gd-bridge.js";
import { canOfferRewardedAds, requestRewardedAd, isRewardedAdGranted } from "./rewarded-ads-bridge.js";
import {
  INTERSTITIAL_AD_NAME_NEXT_DAY,
  REWARD_AD_NAME_REVIVE,
} from "../config/ad-placements.js";
import { PlaygamaBridge } from "./playgama-bridge.js";
import { FrvrBridge } from "./frvr-bridge.js";
import { resolveGameStorage } from "./persistence-backend.js";
import {
  StorageSlot,
  getStorageKey,
  readStorageEncoded,
  readStorageRecord,
  removeStorageRecord,
  writeStorageRecord,
  writeCheckpointStorage,
  extractEconomyFromProgress,
} from "./storage.js";
import { reportStorageBackendUnavailable } from "./storage-errors.js";
import { devInfo } from "./dev-log.js";

function isPokiPortalActive() {
  if (typeof import.meta !== "undefined" && import.meta.env && import.meta.env.VITE_WITH_POKI === "true") {
    return true;
  }
  return !!(PokiBridge && typeof PokiBridge.isEnabled === "function" && PokiBridge.isEnabled());
}

function platformGameplayStartPoki() {
  if (PokiBridge && typeof PokiBridge.gameplayStart === "function") {
    PokiBridge.gameplayStart();
  }
}

function platformGameplayStart(opts) {
  opts = opts || {};
  var skipPoki = !!opts.skipPoki;
  if (CrazyGamesBridge && typeof CrazyGamesBridge.gameplayStart === "function") {
    CrazyGamesBridge.gameplayStart();
  }
  if (PlaygamaBridge && typeof PlaygamaBridge.gameplayStart === "function") {
    PlaygamaBridge.gameplayStart();
  }
  if (!skipPoki) {
    platformGameplayStartPoki();
  }
}

function platformGameplayStop() {
  if (CrazyGamesBridge && typeof CrazyGamesBridge.gameplayStop === "function") {
    CrazyGamesBridge.gameplayStop();
  }
  if (PlaygamaBridge && typeof PlaygamaBridge.gameplayStop === "function") {
    PlaygamaBridge.gameplayStop();
  }
  if (PokiBridge && typeof PokiBridge.gameplayStop === "function") {
    PokiBridge.gameplayStop();
  }
}

function usesLocalBestScoreCard() {
  if (typeof import.meta !== "undefined" && import.meta.env && import.meta.env.VITE_WITH_POKI === "true") {
    return true;
  }
  if (PokiBridge && typeof PokiBridge.isEnabled === "function" && PokiBridge.isEnabled()) {
    return true;
  }
  return !!(YtBridge && typeof YtBridge.isEnabled === "function" && YtBridge.isEnabled());
}
import {
  loadPlayerStats,
  savePlayerStats,
  recordSessionStart,
  recordGameLoss,
  recordRunStart,
  markPendingRunIndexBump,
  clearPendingRunIndexBump,
  recordMaxTierEver,
  addPlayTimeMs,
  syncBestScore,
  recordVictoryFeedback,
  clearVictoryFeedback,
  toPlayerStatsSnapshot,
} from "./player-stats.js";
import {
  createAnalyticsTracker,
  createPerfMonitor,
  AnalyticsEvents,
  discoveredTierEventName,
  applyPerfAnalyticsUserProperties,
  getViewportBucket,
  detectIsTouch,
} from "../analytics/index.js";
import {
  ANALYTICS_USER_PROPERTY_SKY_BG_MODE,
  ANALYTICS_USER_PROPERTY_SKY_BG_PREF,
} from "../analytics/firebase-client.js";
import {
  clearSkyBackgroundPrefs,
  getSkyBackgroundAnalyticsMode,
  getSkyStaticSeedHints,
  getSkyStaticSeedReason,
  loadSkyBackgroundPrefs,
  normalizeSkyBackgroundPreference,
  prefersReducedMotion,
  resolveSkyBackgroundEffective,
  saveSkyBackgroundPrefs,
  shouldAttemptSkyCycleUpgrade,
  shouldRunSkyAutoProbe,
  shouldSeedStaticFromHints,
  shouldUpgradeSkyFromCycleResult,
  shouldUseFirstFallProbeCountdownArc,
  shouldUseFirstFallSplitHourArc,
  SKY_BG_RESOLVE_SOURCES,
} from "./sky-background-prefs.js";
import { createSkyAutoProbe } from "./sky-auto-probe.js";
import {
  renderPuzzleBoard,
  puzzleRevealHeadline,
  puzzleRevealCountLabel,
  PUZZLE_REVEAL_GLOW_MS,
} from "../render/puzzle-reveal.js";
import {
  PUZZLE_PIECE_COUNT,
  puzzlePieceForTierId,
  puzzlePiecesForMaxTier,
} from "../config/puzzle-reward.js";

/** True only in a Vite dev build. Gates dev-bar behaviour out of shipped bundles. */
function isDevBuildEnv() {
  return !!(typeof import.meta !== "undefined" && import.meta.env && import.meta.env.DEV);
}

/** Continue-button copy per job. Static strings — no user input reaches innerHTML. */
var CONTINUE_BTN_LABEL_HTML = {
  next_day: "NEXT<br>DAY ›",
  drop_all: "DROP<br>ALL",
  // No forced break — "DROPPING" has no clean 2-line split; CSS shrinks it to fit instead.
  dropping: "DROPPING…",
};

var CONTINUE_BTN_ARIA = {
  next_day: "Next day",
  drop_all: "Drop all queued creatures into the flask",
  dropping: "Dropping queued creatures",
};

function normalizeUseCount(value, fallback) {
    var n = value == null ? fallback : Number(value);
    return Math.max(0, Math.floor(isFinite(n) ? n : fallback));
  }

  function normalizeStashTierList(list) {
    if (!Array.isArray(list)) return [];
    var out = [];
    for (var i = 0; i < list.length; i++) {
      var t = Math.floor(Number(list[i]));
      if (isFinite(t) && t >= 0) out.push(t);
    }
    return out;
  }

  function captureDropModeSnapshot(game) {
    if (!game || !game.isDropMode) return null;
    var queue = normalizeStashTierList(game.dropQueue);
    var current = null;
    if (game.dropCurrent && game.dropCurrent.tier != null) {
      current = {
        tier: Math.max(0, Math.floor(Number(game.dropCurrent.tier)) || 0),
        panelX: Number(game.dropCurrent.panelX) || 0,
      };
    }
    if (!current && queue.length === 0) return null;
    return { queue: queue, current: current };
  }

  function parseDropModeSnapshot(raw) {
    if (!raw || typeof raw !== "object") return null;
    var queue = normalizeStashTierList(raw.queue);
    var current = null;
    if (raw.current && raw.current.tier != null) {
      current = {
        tier: Math.max(0, Math.floor(Number(raw.current.tier)) || 0),
        panelX: Number(raw.current.panelX) || 0,
      };
    }
    if (!current && queue.length === 0) return null;
    return { queue: queue, current: current };
  }

  /** Landscape Fall: shared vertical offset for labels, DOM tube, and `layout.flaskBounds` (`C.FALL_GAMEPLAY_ROW_NUDGE_DOWN_PX`). */
  function getFallGameplayRowNudgeDownPx() {
    var v = (typeof window !== "undefined" && C.FALL_GAMEPLAY_ROW_NUDGE_DOWN_PX != null)
      ? Math.floor(Number(C.FALL_GAMEPLAY_ROW_NUDGE_DOWN_PX))
      : 0;
    if (!isFinite(v) || v < 0) v = 0;
    return v;
  }

  function getMockupScaleFromGame(game, coreWidthPx) {
    if (coreWidthPx != null && coreWidthPx > 0) return computeMockupUiScale(coreWidthPx);
    var layout = game && game.layout;
    var coreW = layout && layout.coreRect && layout.coreRect.width;
    if (!coreW) return computeMockupUiScale(720);
    return computeMockupUiScale(coreW);
  }

  /** Fall top HUD + title band height (`C.FALL_TOP_HUD_HEIGHT_PX`, default 48), scaled like mockup `--s-btn`. */
  function getFallTopHudHeightPx(game, coreWidthPx) {
    var v = (typeof window !== "undefined" && C.FALL_TOP_HUD_HEIGHT_PX != null)
      ? Math.floor(Number(C.FALL_TOP_HUD_HEIGHT_PX))
      : 48;
    if (!isFinite(v) || v < 40) v = 48;
    var scale = getMockupScaleFromGame(game, coreWidthPx).scaleSide;
    return Math.max(32, scaleMockupSidePx(v, scale));
  }

  function getFallLogoToScoreGapPx(game, coreWidthPx) {
    var v = (typeof window !== "undefined" && C.FALL_LOGO_TO_SCORE_GAP_PX != null)
      ? Math.floor(Number(C.FALL_LOGO_TO_SCORE_GAP_PX))
      : 10;
    if (!isFinite(v) || v < 0) v = 10;
    return scaleMockupSidePx(v, getMockupScaleFromGame(game, coreWidthPx).scaleSide);
  }

  /** Fall score block height reserved in layout / card math (`C.FALL_SCORE_PANEL_MIN_HEIGHT_PX`, default 118). */
  function getFallScorePanelMinHeightPx(game, coreWidthPx) {
    var v = (typeof window !== "undefined" && C.FALL_SCORE_PANEL_MIN_HEIGHT_PX != null)
      ? Math.floor(Number(C.FALL_SCORE_PANEL_MIN_HEIGHT_PX))
      : 118;
    if (!isFinite(v) || v < 80) v = 118;
    return scaleMockupSidePx(v, getMockupScaleFromGame(game, coreWidthPx).scaleSide);
  }

  /** Fall-only left sidebar width (`C.FALL_SIDEBAR_WIDTH_PX`, default 148). */
  function getFallSidebarWidthPx() {
    var v = (typeof window !== "undefined" && C.FALL_SIDEBAR_WIDTH_PX != null)
      ? Math.floor(Number(C.FALL_SIDEBAR_WIDTH_PX))
      : 148;
    if (!isFinite(v) || v < 100) v = 148;
    return Math.min(v, 220);
  }

  /** Fall sidebar top reserve for controls + lives + gems. */
  function getFallSidebarHudReservePx(game, coreWidthPx) {
    var v = (typeof window !== "undefined" && C.FALL_SIDEBAR_HUD_RESERVE_PX != null)
      ? Math.floor(Number(C.FALL_SIDEBAR_HUD_RESERVE_PX))
      : 196;
    if (!isFinite(v) || v < 100) v = 128;
    return scaleMockupSidePx(v, getMockupScaleFromGame(game, coreWidthPx).scaleSide);
  }

  function Game(opts) {
    this.opts = opts || {};
    /** Used by menu-time asset warmup (`preloadAllGameAssets`). */
    this.soundManager = opts.soundManager || null;
    /** @type {Promise<void> | null} */
    this._menuAssetPreloadPromise = null;
    this.canvas = opts.canvas || document.getElementById("gameCanvas");
    this.usePixi = opts.usePixi === true;
    this.pixi = null;
    this.ctx = (!this.usePixi && this.canvas) ? this.canvas.getContext("2d") : null;

    this.stateMachine = new GameStateMachine("start");
    this.runPhase = null;
    this.gemsCollected = 0;
    this.currentScore = 0;
    this.bestScore = this.loadBestScore();
    /** True once the current run's score has beaten the lifetime best (loss screen "NEW BEST!"). */
    this.newBestThisRun = false;
    this.unlockedTiers = {};
    this.shakeUses = C.SHAKE_USES_DEFAULT || 3;
    this._pendingMaxShakeCheat = false;
    this._pendingNastyaDropQueueCheat = false;
    this._restoredSkyHour = null;
    this._skyBackgroundPrefs = loadSkyBackgroundPrefs(
      this._getProgressStorage()
    );
    this._skyAutoProbe = null;
    this._skyCycleUpgradeProbe = null;
    this._displayHzEst = null;
    this._skyPerfForcedStatic = false;
    this._devPerfSoundsMuted = false;

    this.bombUses = C.BOMB_USES_DEFAULT != null ? C.BOMB_USES_DEFAULT : 1;
    this.bombCellUses = normalizeUseCount(C.BOMB_CELL_USES_DEFAULT, 1);
    this.bombTripleUses = normalizeUseCount(C.BOMB_THREE_TIER_USES_DEFAULT, 1);
    this.minimizeUses = normalizeUseCount(C.MINIMIZE_USES_DEFAULT, 1);
    this.isMinimizeMode = false;
    this._minimizeHoverMeta = null;
    this.hasCompletedARun = false;
    this.flaskSystem = null;
    this.isRunFlaskShattered = false;
    this.revivesUsedThisRun = 0;
    this.isReviveOfferActive = false;
    this.reviveOfferStage = null;
    this.reviveOfferMode = null;
    this._lastReviveSource = null;
    this._reviveAdPending = false;
    this._continueToRunAdPending = false;
    this._pokiUnpauseAdPending = false;
    this._platformGameplayReported = false;
    /** Poki: first Fall input of this Fall; kept across pause/unpause. */
    this._platformGameplayInputLatch = false;
    this.layout = null;
    this._layoutDirty = true;
    this._shakeUiBoundsDirty = true;
    this.testFlaskMode = false;
    this.testRunwayMode = false;
    this.testRunway = null;

    this.constants = {
      SHAKE_USES_DEFAULT: C.SHAKE_USES_DEFAULT || 3,
      BOMB_USES_DEFAULT: C.BOMB_USES_DEFAULT != null ? C.BOMB_USES_DEFAULT : 1,
      BOMB_CELL_USES_DEFAULT: normalizeUseCount(C.BOMB_CELL_USES_DEFAULT, 1),
      BOMB_THREE_TIER_USES_DEFAULT: normalizeUseCount(C.BOMB_THREE_TIER_USES_DEFAULT, 1),
      SHAKE_USE_COST: C.SHAKE_USE_COST || 3,
      BOMB_USE_COST: C.BOMB_USE_COST || 3,
      BOMB_CELL_COST: C.BOMB_CELL_COST || 1,
      BOMB_THREE_TIER_COST: C.BOMB_THREE_TIER_COST || 3,
      MINIMIZE_USES_DEFAULT: normalizeUseCount(C.MINIMIZE_USES_DEFAULT, 1),
      MINIMIZE_COST: C.MINIMIZE_COST != null ? C.MINIMIZE_COST : 30,
      MINIMIZE_PHYSICS_SIZE_TIER: C.MINIMIZE_PHYSICS_SIZE_TIER != null ? C.MINIMIZE_PHYSICS_SIZE_TIER : 0,
      MINIMIZE_PHYSICS_TIER_STEP: C.MINIMIZE_PHYSICS_TIER_STEP != null ? C.MINIMIZE_PHYSICS_TIER_STEP : 4,
      FLASK_FIX_COST: C.FLASK_FIX_COST || 5,
      SHAKE_IMPULSE_STRENGTH: C.SHAKE_IMPULSE_STRENGTH || 0.04,
      BOMB_REMOVE_COUNT: C.BOMB_REMOVE_COUNT || 10,
      SCORE_MERGE_CATCH_BASE: C.SCORE_MERGE_CATCH_BASE || 50,
      SCORE_MERGE_BASE: C.SCORE_MERGE_BASE || 100,
    };

    this.isDropMode = false;
    this.dropQueue = [];
    this.dropCurrent = null;
    /** Drop all: true while the auto-pour is running (locks input and overflow). */
    this._dropAllActive = false;
    this._dropAllTimer = null;
    /** Drop all: panel X of the previously poured piece, for spacing the next one. */
    this._dropAllLastX = null;
    this._nextShakeReason = null;
    /** Latest pointer clientX from window pointermove/mousemove (for stage-start aim seeding). */
    this._lastPointerClientX = null;
    this._boundPointerClientTrack = null;
    this._dropMoveHandler = null;
    this._dropClickHandler = null;
    this._dropPointerDownHandler = null;
    this._dropPointerMoveHandler = null;
    this._dropPointerUpHandler = null;
    this._dropPointerCancelHandler = null;
    this._dropContextMenuHandler = null;
    this._dropTouchGestureActive = false;
    this._dropTouchStartedInFlask = false;
    this._dropTouchPointerId = null;
    this._dropTouchClickSuppressUntil = 0;
    this._dropAdvanceTimeout = null;
    this._shakeDropHintActive = false;
    this._shakeHintDismissed = false;
    this._shakeHintVisible = false;
    this._shakeHintReadyAt = 0;
    this._healHintDismissed = false;
    this._healHintVisible = false;
    this._lastDropPanelX = null;
    this._dropSettleInterval = null;
    this._continueWiggleInitialTimeout = null;
    this._continueWiggleInterval = null;
    this._continueWiggleAnimTimeout = null;
    this._dropKeyLeft = false;
    this._dropKeyRight = false;
    this._dropAimKeyHoldDir = 0;
    this._dropAimKeyHeldSinceMs = null;
    this._dropKeyDownHandler = null;
    this._dropKeyUpHandler = null;
    this.boostBar = [];
    this.overflow = createOverflowState();
    this.playerStats = null;
    this.currentGameMaxTier = 0;
    this.currentCycleIndex = 0;
    this._pokiProgress = createPokiProgressState();
    this._analyticsPlayTimeSince = null;
    this._runPlayTimeMs = 0;
    this.analyticsTracker = null;
    this.leaderboardClient = opts.leaderboard || null;
    this.layoutModePreference = opts.layoutModePreference != null
      ? resolveLayoutMode(opts.layoutModePreference)
      : null;
    this._analyticsRunStartSource = null;
    this._analyticsFallStartSource = null;
    this._pendingFallCountdownSource = null;
    this._analyticsMergeStartSource = null;
    this.analyticsStage = "fall";
    this._mergeStartedEmpty = false;
    this._stageKeysLeft = false;
    this._stageKeysRight = false;
    this._stageKeysDown = false;
    this._lastBgmSliderInt = null;
    this._lastSfxSliderInt = null;
    this._secretRevealActive = false;
    this._secretRevealHandle = null;
    this._secretRevealSettleTimer = null;
    this._discoveryRevealQueue = [];
    /** Puzzle piece index waiting to be shown once the discovery ceremony clears. */
    this._pendingPuzzlePiece = 0;
    this._puzzleRevealActive = false;
    this._puzzleRevealTimer = null;
    this._evoRevealHold = false;
    this._victoryPopupActive = false;
    this._victoryPopupHandle = null;
    this._pendingVictoryPopup = false;
    this._victoryFirstUnlockPending = false;
    this._victorySettleTimer = null;
    this.victoryShownThisRun = false;

    this.dom = {};
    this.boundResize = this.handleResize.bind(this);
    this.boundGlobalKeyDown = this.handleGlobalKeyDown.bind(this);
    this.boundPageVisibilityChange = this._handlePageVisibilityChange.bind(this);
    this.boundFallCountdownFinished = this._onFallCountdownFinished.bind(this);
    this._boundGameLoop = this.gameLoop.bind(this);
    this._gameLoopRafId = null;
    this._skyCacheCanvas = null;
    this._skyCacheCtx = null;
    this._skyCacheKey = null;
    /** Last sky cache paint geometry (canvas + layout); stale blits require a match. */
    this._skyCacheLayoutSignature = null;
    this._starOverlayCacheCanvas = null;
    this._starOverlayCacheCtx = null;
    this._starOverlayCacheKey = null;
    this._shakeChromeCacheCanvas = null;
    this._shakeChromeCacheCtx = null;
    this._shakeChromeCacheKey = null;
    this._boardViewportSig = null;
    this._lastSkyBgRepaintMs = null;
    this.runToShakeTimeoutId = null;
    this._bridgeShakeFadeInTimeoutId = null;
    this.lastRunGems = 0;
    this.flaskVisualShakeUntil = 0;
    this.flaskVisualShakeStrength = 0;
    this.flaskVisualShakeStart = 0;

    this.dropQueuePanelAlpha = 1;
    this.dropQueueEverShown = false;
    this.dropHandoffUntil = 0;
    this.dropHandoffFromY = 0;
    this.dropHandoffTier = null;
    this.dropAppearPulseStartedAt = 0;
    this.dropChargeUntil = 0;
    this._dropPreviewWasCharging = false;
    this.shakeHpBarAlpha = 1;
    this._shakeHpWasDamagedThisStage = false;
    this._shakeHpHideTimeout = null;
    this._shakeHpFadeTimeout = null;
    this._lastBombBtnPaid = {};
    this.flaskMaxHp = 3;
    this.savedFlaskHp = 3;
    this.savedFlaskHp = 3;
    this.pendingRestoreFromSave = false;
    this.hasSavedProgressSnapshot = false;
    this.resumeStage = "fall";
    this.lastFrameTime = 0;
    this.canvasCssWidth = 0;
    this.canvasCssHeight = 0;
    this.canvasDpr = 1;
    this.lastRunSummary = null;
    /** Player pressed pause (or platform pause during Fall) — one flag for all stages. */
    this.isPlayerPaused = false;
    /** Retention/volume/cheat dialogs — freezes sim without pausing BGM/SFX. */
    this.isDialogPaused = false;
    this._dialogPauseDepth = 0;
    /** True when pause was toggled via pause button / Space (shows play-column "paused" hint). */
    this._playerPauseHintEligible = false;
    /** Tab/window hidden — environment pause (not the pause button). */
    this.isEnvironmentPaused = false;
    /** Host/runtime pause (e.g. YouTube `onPause` / `onResume`). */
    this.isHostPaused = false;
    /** True when Fall rAF was cancelled by host pause and should restart on resume. */
    this._hostSuspendedFallLoop = false;
    /** Rewarded/midgame ad playback — separate from player and environment pause. */
    this.isAdPaused = false;
    this._fallSimFrozen = false;
    this._shakeSimFrozen = false;
    this._transitionSimFrozen = false;
    this._bridgeTransitionFrozenSceneTime = null;
    this._bridgeCelestialFrozenAlpha = null;
    this._bridgeCelestialPauseWallMs = null;
    this._bridgeDwellDeadlineMs = null;
    this._bridgeDwellRemainingMs = null;
    this._bridgeShakeFadeInDeadlineMs = null;
    this._bridgeShakeFadeInRemainingMs = null;
    this._bridgeThemeCrossfadeDeadlineMs = null;
    this._bridgeThemeCrossfadeRemainingMs = null;
    this.celestialTransition = null;
    this.stageContentAlpha = 1;
    this.scorePopups = [];
    this.frozenFallArcProgress = null;
    this.frozenFallSceneTime = null;
    this.frozenShakePreviewTime = null;
    this.skyTimeOffsetMs = 0;
    this.skyClock = createSkyClockController(C.RUN_CONFIG || {});
    this.frozenSkyHour = null;
    this.continueToFallTimeoutId = null;
    this._bridgeThemeCrossfadeTimeoutId = null;
    this._transitionRedrawId = null;
    this._runwayFlaskFadeSnapshot = null;
    this._runwayFlaskFadePauseWallMs = null;
    this._shakeFlaskIntroFadeStartedAt = null;
    this._fallCatcherFlaskIntroFadeStartedAt = null;
    this._fallCatcherFlaskIntroFadePauseWallMs = null;
    this.heartsRevealUntil = 0;
    this.heartsRevealStartedAt = 0;
  }

  Game.prototype.drawLiquidBackground = function (ctx, w, h) {
    var now = Date.now() / 1000;
    // Sunny lagoon water — inside the flask. Bright top light over a soft blue ramp
    // so the "water" reads as having depth instead of a single flat tone.
    var base = ctx.createLinearGradient(0, 0, 0, h);
    base.addColorStop(0, "#F4FCFF");
    base.addColorStop(0.55, "#CFEFFB");
    base.addColorStop(1, "#ADE5FA");
    ctx.fillStyle = base;
    ctx.fillRect(0, 0, w, h);
    // Central radial glow — sunlight filtering down through liquid.
    var gx = w * 0.5, gy = h * 0.18;
    var gr = ctx.createRadialGradient(gx, gy, 0, gx, gy, Math.max(w, h) * 0.85);
    gr.addColorStop(0, "rgba(255, 255, 255, 0.5)");
    gr.addColorStop(0.45, "rgba(255, 255, 255, 0.18)");
    gr.addColorStop(1, "rgba(255, 255, 255, 0)");
    ctx.fillStyle = gr;
    ctx.fillRect(0, 0, w, h);
    // Slow drifting caustic light bands.
    for (var i = 0; i < 5; i++) {
      var fy = ((now * 0.055 + i * 0.2) % 1.0) * h;
      var bandH = h * 0.18;
      var bg = ctx.createLinearGradient(0, fy - bandH, 0, fy + bandH);
      bg.addColorStop(0, "rgba(255, 255, 255, 0.0)");
      bg.addColorStop(0.5, "rgba(255, 255, 255, 0.28)");
      bg.addColorStop(1, "rgba(255, 255, 255, 0.0)");
      ctx.fillStyle = bg;
      ctx.fillRect(0, fy - bandH, w, bandH * 2);
    }
  };

  Game.prototype.drawLightBlueTiles = function (ctx, x, y, w, h) {
    var tileSize = 32;
    var light = "#b8dff0";
    var lighter = "#c8e8f5";
    for (var py = 0; py < h; py += tileSize) {
      for (var px = 0; px < w; px += tileSize) {
        ctx.fillStyle = ((px / tileSize) + (py / tileSize)) % 2 === 0 ? light : lighter;
        ctx.fillRect(x + px, y + py, Math.min(tileSize, w - px), Math.min(tileSize, h - py));
      }
    }
  };

  Game.prototype._computeFallArcProgress = function () {
    var rp = this.runPhase;
    if (!rp || !rp.runConfig) return 0;
    var cfg = rp.runConfig;
    var countdownTotal = Math.max(0, Number(cfg.RUN_COUNTDOWN_SECONDS) || 0);
    var tickSec = getFallCountdownTickSec(cfg);
    var countdownWall = countdownTotal * tickSec;
    var runDur = Math.max(0, Number(cfg.RUN_DURATION) || 30);
    var total = countdownWall + runDur;
    if (total <= 0) return 0;
    if (rp.state === "countdown") {
      var rem = Math.max(0, Number(rp.countdownRemaining) || 0);
      return clamp01(((countdownTotal - rem) * tickSec) / total);
    }
    var runElapsed = runDur - Math.max(0, Number(rp.timeRemaining) || 0);
    return clamp01((countdownWall + runElapsed) / total);
  };

  Game.prototype._getSkySeaDrawTime = function (currentTime) {
    if (this._isSimPaused() && this.frozenShakePreviewTime != null) {
      return this.frozenShakePreviewTime;
    }
    if (this._isSimPaused() && this._bridgeTransitionFrozenSceneTime != null) {
      return this._bridgeTransitionFrozenSceneTime;
    }
    if (this._isSimPaused() && this.frozenFallSceneTime != null) {
      return this.frozenFallSceneTime;
    }
    var now = currentTime != null && isFinite(currentTime)
      ? currentTime
      : (typeof performance !== "undefined" ? performance.now() : Date.now());
    var offset = this.skyTimeOffsetMs != null ? Number(this.skyTimeOffsetMs) : 0;
    if (!isFinite(offset) || offset < 0) offset = 0;
    return now - offset;
  };

  /** Wall clock for sky-clock-controller anchors — must match `_getSkyClockSnapshot().nowMs`. */
  Game.prototype._getSkyClockNowMs = function (currentTime) {
    return this._getSkySeaDrawTime(currentTime);
  };

  Game.prototype._clearBridgePauseState = function () {
    this._transitionSimFrozen = false;
    this._bridgeTransitionFrozenSceneTime = null;
    this._bridgeCelestialFrozenAlpha = null;
    this._bridgeCelestialPauseWallMs = null;
    this._bridgeDwellRemainingMs = null;
    this._bridgeShakeFadeInRemainingMs = null;
    this._bridgeThemeCrossfadeRemainingMs = null;
    this._bridgeDwellDeadlineMs = null;
    this._bridgeShakeFadeInDeadlineMs = null;
    this._bridgeThemeCrossfadeDeadlineMs = null;
  };

  Game.prototype._captureAndPauseBridgeTimers = function (now) {
    if (this._bridgeDwellDeadlineMs != null) {
      this._bridgeDwellRemainingMs = Math.max(0, this._bridgeDwellDeadlineMs - now);
    }
    this._stopTransitionRedrawLoop();
    if (this.runToShakeTimeoutId != null) {
      clearTimeout(this.runToShakeTimeoutId);
      this.runToShakeTimeoutId = null;
    }
    if (this.continueToFallTimeoutId != null) {
      clearTimeout(this.continueToFallTimeoutId);
      this.continueToFallTimeoutId = null;
    }
    if (this._bridgeShakeFadeInTimeoutId != null) {
      if (this._bridgeShakeFadeInDeadlineMs != null) {
        this._bridgeShakeFadeInRemainingMs = Math.max(0, this._bridgeShakeFadeInDeadlineMs - now);
      }
      clearTimeout(this._bridgeShakeFadeInTimeoutId);
      this._bridgeShakeFadeInTimeoutId = null;
    }
    if (this._bridgeThemeCrossfadeTimeoutId != null) {
      if (this._bridgeThemeCrossfadeDeadlineMs != null) {
        this._bridgeThemeCrossfadeRemainingMs = Math.max(0, this._bridgeThemeCrossfadeDeadlineMs - now);
      }
      clearTimeout(this._bridgeThemeCrossfadeTimeoutId);
      this._bridgeThemeCrossfadeTimeoutId = null;
    }
  };

  Game.prototype._resumeBridgeTimers = function (now) {
    var self = this;
    var sm = this.soundManager;
    var cfg = C.RUN_CONFIG || {};
    var toFall = this.celestialTransition && this.celestialTransition.direction === "to_fall";

    if (this._bridgeDwellRemainingMs != null && this._bridgeDwellRemainingMs > 0) {
      var dwellRemain = this._bridgeDwellRemainingMs;
      this._bridgeDwellRemainingMs = null;
      this._bridgeDwellDeadlineMs = now + dwellRemain;
      if (toFall) {
        this.continueToFallTimeoutId = setTimeout(function () {
          self.continueToFallTimeoutId = null;
          if (!self.stateMachine.is("transition")) return;
          if (self._isSimPaused()) return;
          self._finishContinueToFall();
        }, dwellRemain);
      } else {
        this.runToShakeTimeoutId = setTimeout(function () {
          self.runToShakeTimeoutId = null;
          if (!self.stateMachine.is("transition")) return;
          if (self._isSimPaused()) return;
          self.goToShake({ mergeStartSource: "fall" });
        }, dwellRemain);
      }
    }

    if (this._bridgeShakeFadeInRemainingMs != null && this._bridgeShakeFadeInRemainingMs > 0 && sm
      && typeof sm.fadeInBgmTo === "function") {
      var fadeDelay = this._bridgeShakeFadeInRemainingMs;
      this._bridgeShakeFadeInRemainingMs = null;
      this._bridgeShakeFadeInDeadlineMs = now + fadeDelay;
      var stopTrack = toFall ? "shake" : "fall";
      var fadeInScene = toFall ? "fall" : "shake";
      var dwellMs = cfg.DAY_NIGHT_TRANSITION_MS != null
        ? cfg.DAY_NIGHT_TRANSITION_MS
        : (cfg.RUN_TO_SHAKE_TRANSITION_MS || 3000);
      var bridgeFallFadeMs = cfg.RUN_BGM_FADE_OUT_ON_BRIDGE_MS != null ? cfg.RUN_BGM_FADE_OUT_ON_BRIDGE_MS : 1000;
      var fadeInMs = toFall
        ? (cfg.RUN_BGM_FADE_IN_ON_BRIDGE_MS != null ? cfg.RUN_BGM_FADE_IN_ON_BRIDGE_MS : 1000)
        : (cfg.RUN_BGM_FADE_IN_ON_BRIDGE_MS != null
          ? cfg.RUN_BGM_FADE_IN_ON_BRIDGE_MS
          : Math.max(400, dwellMs - bridgeFallFadeMs));
      this._bridgeShakeFadeInTimeoutId = setTimeout(function () {
        self._bridgeShakeFadeInTimeoutId = null;
        if (!self.stateMachine.is("transition")) return;
        if (typeof sm.stopBgmTrack === "function") sm.stopBgmTrack(stopTrack);
        sm.fadeInBgmTo(fadeInScene, fadeInMs);
      }, fadeDelay);
    }

    if (this._bridgeThemeCrossfadeRemainingMs != null && this._bridgeThemeCrossfadeRemainingMs > 0) {
      var themeRemain = this._bridgeThemeCrossfadeRemainingMs;
      this._bridgeThemeCrossfadeRemainingMs = null;
      this._bridgeThemeCrossfadeDeadlineMs = now + themeRemain;
      var appEl = document.getElementById("app");
      if (appEl) {
        this._bridgeThemeCrossfadeTimeoutId = setTimeout(function () {
          self._bridgeThemeCrossfadeTimeoutId = null;
          if (!self.stateMachine || (!self.stateMachine.is("transition") && !self.stateMachine.is("fall"))) {
            return;
          }
          appEl.classList.remove("stage-theme-night");
        }, themeRemain);
      }
    }
  };

  /** New random sky decor seed (cloud/star layout). Use on Play Again / new run only. */
  Game.prototype._rollSkyDecorSeed = function () {
    this.skyDecorSeed = Math.floor(Math.random() * 1000000000);
    return this.skyDecorSeed;
  };

  /** Keep cloud layout stable across Fall days in the same run (see `sky-scene.js`). */
  Game.prototype._ensureSkyDecorSeed = function () {
    var seed = this.skyDecorSeed;
    if (seed == null || !isFinite(Number(seed))) {
      return this._rollSkyDecorSeed();
    }
    return seed;
  };

  /** Clear pause offsets before stage-bridge timelapses (Fall↔Shake). */
  Game.prototype._resetSkyClockMotionForBridge = function (rawNow) {
    var now = rawNow != null && isFinite(rawNow)
      ? rawNow
      : (typeof performance !== "undefined" ? performance.now() : Date.now());
    this._clearBridgePauseState();
    this._stopTransitionRedrawLoop();
    this.skyTimeOffsetMs = 0;
    this.frozenFallSceneTime = null;
    this.frozenShakePreviewTime = null;
    this.frozenSkyHour = null;
    this._fallSimFrozen = false;
    this._transitionSimFrozen = false;
    this._clearPlayerPauseState();
    if (this.skyClock) resetSkyClockPauseMotion(this.skyClock, now);
    this._reconcileFallSimPause();
    this._reconcileTransitionSimPause();
    return now;
  };

  Game.prototype._shiftBridgeCelestialForPause = function (pauseWallMs, resumeWallMs) {
    if (!this.celestialTransition || pauseWallMs == null || !isFinite(pauseWallMs)) return;
    var resume = resumeWallMs != null && isFinite(resumeWallMs)
      ? resumeWallMs
      : (typeof performance !== "undefined" ? performance.now() : Date.now());
    var delta = Math.max(0, resume - pauseWallMs);
    if (delta > 0) this.celestialTransition.startedAt += delta;
  };

  Game.prototype._shiftRunwayFlaskFadeForPause = function (pauseWallMs, resumeWallMs) {
    var snap = this._runwayFlaskFadeSnapshot;
    if (!snap || pauseWallMs == null || !isFinite(pauseWallMs)) return;
    var resume = resumeWallMs != null && isFinite(resumeWallMs)
      ? resumeWallMs
      : (typeof performance !== "undefined" ? performance.now() : Date.now());
    var delta = Math.max(0, resume - pauseWallMs);
    if (delta > 0) snap.startedAt += delta;
  };

  Game.prototype._getRunwayFlaskFadeDurationMs = function () {
    var cfg = C.RUN_CONFIG || {};
    return cfg.STAGE_ELEMENT_FADE_MS != null ? cfg.STAGE_ELEMENT_FADE_MS : 300;
  };

  Game.prototype._captureRunwayFlaskFadeSnapshot = function (now) {
    var rp = this.runPhase;
    if (!rp) {
      this._runwayFlaskFadeSnapshot = null;
      return;
    }
    var flaskW;
    var flaskH;
    var flaskTopY;
    var fr = rp.fallRenderer;
    if (fr && typeof fr._getFlaskDims === "function") {
      var dims = fr._getFlaskDims(rp.runHeight);
      flaskW = dims.flaskW;
      flaskH = dims.flaskH;
      flaskTopY = dims.flaskTopY;
    } else {
      var scale = rp.scale || (this.layout && this.layout.scale);
      flaskW = scale && scale.runwayFlaskDisplayWidth ? scale.runwayFlaskDisplayWidth : 120;
      flaskH = scale && scale.runwayFlaskDisplayHeight ? scale.runwayFlaskDisplayHeight : 120;
      var topFromBottom = C.FLASK_TOP_FROM_BOTTOM_RATIO != null
        ? C.FLASK_TOP_FROM_BOTTOM_RATIO
        : 0.24;
      var desiredTopY = rp.runHeight * (1 - topFromBottom);
      var bottomGap = Math.max(12, rp.runHeight * 0.018);
      flaskTopY = Math.min(desiredTopY, rp.runHeight - flaskH - bottomGap);
      if (flaskTopY < 0) flaskTopY = 0;
    }
    var ox = rp.runOriginX || 0;
    var oy = rp.runOriginY || 0;
    var wallNow = now != null && isFinite(now)
      ? now
      : (typeof performance !== "undefined" ? performance.now() : Date.now());
    var simTime = typeof rp._getAnimTime === "function"
      ? rp._getAnimTime(now)
      : (typeof rp._getSimTime === "function" ? rp._getSimTime(now) : wallNow);
    var scaleUi = fr && typeof fr._getMockupScaleSide === "function"
      ? fr._getMockupScaleSide()
      : 1;
    var snap = {
      startedAt: wallNow,
      flask: null,
      buoys: null,
    };
    if (!this._isVerticalLayout() && rp.runWidth != null && rp.runHeight != null) {
      var buoyState = captureFallBuoyFadeState({
        runWidth: rp.runWidth,
        runHeight: rp.runHeight,
        scale: rp.scale || (this.layout && this.layout.scale),
        flaskW: flaskW,
        scaleUi: scaleUi,
        simTime: simTime,
      });
      snap.buoys = {
        leftX: ox + buoyState.leftX,
        rightX: ox + buoyState.rightX,
        y: oy + buoyState.y,
        scaleUi: buoyState.scaleUi,
        simTime: buoyState.simTime,
      };
    }
    if (rp.player && rp.player.hp > 0) {
      snap.flask = {
        flaskX: ox + rp.player.x - flaskW / 2,
        flaskY: oy + flaskTopY,
        flaskW: flaskW,
        flaskH: flaskH,
        hp: rp.player.hp,
        drawOpts: null,
      };
    }
    if (!snap.flask && !snap.buoys) {
      this._runwayFlaskFadeSnapshot = null;
      return;
    }
    this._runwayFlaskFadeSnapshot = snap;
  };

  Game.prototype._getRunwayFlaskFadeAlpha = function (now) {
    var snap = this._runwayFlaskFadeSnapshot;
    if (!snap) return 0;
    var fadeMs = this._getRunwayFlaskFadeDurationMs();
    var wallNow = now != null && isFinite(now)
      ? now
      : (typeof performance !== "undefined" ? performance.now() : Date.now());
    var elapsed = wallNow - snap.startedAt;
    if (elapsed >= fadeMs) {
      this._runwayFlaskFadeSnapshot = null;
      return 0;
    }
    return clamp01(1 - elapsed / fadeMs);
  };

  /**
   * Alpha for Fall catcher flask + buoys during Fall→Shake bridge.
   * Uses celestial stageContentAlpha (same clock as fade-in) on exit.
   */
  Game.prototype._getRunwayFadeOverlayAlpha = function (now) {
    var tr = this.celestialTransition;
    if (tr && tr.fadeStageContent && tr.direction === "to_shake") {
      var wallNow = now != null && isFinite(now)
        ? now
        : (typeof performance !== "undefined" ? performance.now() : Date.now());
      var fadeMs = this._getRunwayFlaskFadeDurationMs();
      var elapsed = wallNow - tr.startedAt;
      if (elapsed >= fadeMs) {
        this._runwayFlaskFadeSnapshot = null;
        return 0;
      }
      if (this.stageContentAlpha != null && isFinite(this.stageContentAlpha)) {
        return Math.max(0, Math.min(1, this.stageContentAlpha));
      }
    }
    return this._getRunwayFlaskFadeAlpha(now);
  };

  Game.prototype._syncRunwayFlaskFadeSprites = function (snap, alpha) {
    var playfield = this.pixi;
    if (!playfield || typeof playfield.sprite !== "function" || !snap || !(alpha > 0)) {
      return false;
    }
    var flaskGpu = false;
    var buoyGpu = false;
    if (snap.flask && FlaskRenderer && typeof FlaskRenderer.collectFlaskVisualSprites === "function") {
      var collected = FlaskRenderer.collectFlaskVisualSprites(
        snap.flask.flaskX,
        snap.flask.flaskY,
        snap.flask.flaskW,
        snap.flask.flaskH,
        snap.flask.hp,
        "fade-catcher"
      );
      var specs = collected && collected.specs ? collected.specs : collected;
      if (specs && specs.length) {
        for (var i = 0; i < specs.length; i++) {
          specs[i].alpha = (specs[i].alpha == null ? 1 : specs[i].alpha) * alpha;
          playfield.sprite(specs[i]);
        }
        flaskGpu = true;
      }
    }
    if (snap.buoys) {
      var buoys = collectFallBuoyFadeSpriteSpecs(snap.buoys, alpha);
      if (buoys && buoys.length) {
        for (var j = 0; j < buoys.length; j++) playfield.sprite(buoys[j]);
        buoyGpu = true;
      }
    }
    return { flaskGpu: flaskGpu, buoyGpu: buoyGpu, any: flaskGpu || buoyGpu };
  };

  Game.prototype._drawRunwayFlaskFadeOverlay = function (ctx, now) {
    if (!this._runwayFlaskFadeSnapshot) return;
    var alpha = this._getRunwayFadeOverlayAlpha(now);
    if (alpha <= 0) return;
    var snap = this._runwayFlaskFadeSnapshot;
    if (!snap) return;
    var gpu = this._syncRunwayFlaskFadeSprites(snap, alpha);
    var needFlask2d = snap.flask && !(gpu && gpu.flaskGpu);
    var needBuoy2d = snap.buoys && !(gpu && gpu.buoyGpu);
    if (!needFlask2d && !needBuoy2d) return;
    if (!ctx) return;
    ctx.save();
    ctx.globalAlpha = alpha;
    if (needFlask2d && FlaskRenderer && typeof FlaskRenderer.drawFlaskAtRect === "function") {
      FlaskRenderer.drawFlaskAtRect(
        ctx,
        snap.flask.flaskX,
        snap.flask.flaskY,
        snap.flask.flaskW,
        snap.flask.flaskH,
        snap.flask.hp,
        snap.flask.drawOpts || undefined
      );
    }
    if (needBuoy2d) {
      drawFallBuoyFadePair(ctx, snap.buoys, 1);
    }
    ctx.restore();
  };

  Game.prototype._updateShakeFlaskIntroAlpha = function (now) {
    if (this._shakeFlaskIntroFadeStartedAt == null) return;
    if (this._isSimPaused()) return;
    var fadeMs = this._getRunwayFlaskFadeDurationMs();
    var wallNow = now != null && isFinite(now)
      ? now
      : (typeof performance !== "undefined" ? performance.now() : Date.now());
    var elapsed = wallNow - this._shakeFlaskIntroFadeStartedAt;
    this.stageContentAlpha = clamp01(elapsed / fadeMs);
    if (elapsed >= fadeMs) {
      this._shakeFlaskIntroFadeStartedAt = null;
      this.stageContentAlpha = 1;
    }
  };

  Game.prototype._shiftFallCatcherFlaskIntroForPause = function (pauseWallMs, resumeWallMs) {
    if (this._fallCatcherFlaskIntroFadeStartedAt == null) return;
    if (pauseWallMs == null || !isFinite(pauseWallMs)) return;
    var resume = resumeWallMs != null && isFinite(resumeWallMs)
      ? resumeWallMs
      : (typeof performance !== "undefined" ? performance.now() : Date.now());
    var delta = Math.max(0, resume - pauseWallMs);
    if (delta > 0) this._fallCatcherFlaskIntroFadeStartedAt += delta;
  };

  Game.prototype._updateFallCatcherFlaskIntroAlpha = function (now) {
    if (this._fallCatcherFlaskIntroFadeStartedAt == null) return;
    if (this._isSimPaused()) return;
    var fadeMs = this._getRunwayFlaskFadeDurationMs();
    var wallNow = now != null && isFinite(now)
      ? now
      : (typeof performance !== "undefined" ? performance.now() : Date.now());
    var elapsed = wallNow - this._fallCatcherFlaskIntroFadeStartedAt;
    this.stageContentAlpha = clamp01(elapsed / fadeMs);
    if (elapsed >= fadeMs) {
      this._fallCatcherFlaskIntroFadeStartedAt = null;
      this.stageContentAlpha = 1;
    }
  };

  Game.prototype.getCatcherFlaskIntroAlpha = function () {
    return this.stageContentAlpha != null && isFinite(this.stageContentAlpha)
      ? Math.max(0, Math.min(1, this.stageContentAlpha))
      : 1;
  };

  Game.prototype.getFallArcProgress = function () {
    if (this._isSimPaused() && this.frozenFallArcProgress != null) {
      return this.frozenFallArcProgress;
    }
    return this._computeFallArcProgress();
  };

  Game.prototype._buildSkyFallSnapshot = function (currentTime) {
    var rp = this.runPhase;
    if (!rp) return null;
    var cfg = rp.runConfig || C.RUN_CONFIG || {};
    var countdownTotalSec = cfg.RUN_COUNTDOWN_SECONDS != null ? cfg.RUN_COUNTDOWN_SECONDS : 3;
    var settleTotalMs = cfg.FALL_SETTLE_MS != null ? cfg.FALL_SETTLE_MS : 2000;
    var snap = {
      state: rp.state,
      countdownRemaining: rp.countdownRemaining,
      countdownTotalSec: countdownTotalSec,
      countdownTickMs: getFallCountdownTickMs(cfg),
      timeRemainingExact: rp.timeRemaining,
      runDurationSec: cfg.RUN_DURATION != null ? cfg.RUN_DURATION : 20,
      settleTotalMs: settleTotalMs,
      isSettling: !!rp.isRunEndingAftermath,
    };
    if (rp.isRunEndingAftermath && rp.runEndAftermathEndsAt != null) {
      var snapTime = currentTime;
      if (typeof rp._getSimTime === "function") {
        snapTime = rp._getSimTime(currentTime);
      } else if (rp.isPaused && rp.pausedAt) {
        snapTime = rp.pausedAt;
      }
      snap.state = "settle";
      snap.settleRemainingMs = Math.max(0, rp.runEndAftermathEndsAt - snapTime);
    }
    return snap;
  };

  Game.prototype._getSkyClockSnapshot = function (currentTime) {
    var now = currentTime != null && isFinite(currentTime)
      ? currentTime
      : (typeof performance !== "undefined" ? performance.now() : Date.now());
    var frozen = null;
    if (this.frozenSkyHour != null && isFinite(this.frozenSkyHour)) {
      frozen = this.frozenSkyHour;
    } else if (this.skyClock && this.skyClock.frozenHour != null) {
      frozen = this.skyClock.frozenHour;
    }
    var fallSnap = null;
    if (this.stateMachine && this.stateMachine.is && this.stateMachine.is("fall") && this.runPhase) {
      fallSnap = this._buildSkyFallSnapshot(now);
    }
    var fallState = fallSnap && fallSnap.state;
    return {
      nowMs: this._getSkySeaDrawTime(now),
      frozenHour: frozen,
      fall: fallSnap,
      isFirstFallOfFirstRun: this._isFirstFallOfFirstRun(),
      skyAutoProbeCountdown: this._isSkyAutoProbeCountdown(fallState),
      skyUseFirstFallSplitHourArc: this._useFirstFallSplitHourArc(fallState),
    };
  };

  Game.prototype._getSkyBackgroundContext = function () {
    var hints = getSkyStaticSeedHints(typeof window !== "undefined" ? window : undefined);
    var forceReducedMotion = prefersReducedMotion();
    var pref = this._skyBackgroundPrefs
      ? normalizeSkyBackgroundPreference(this._skyBackgroundPrefs.preference)
      : C.SKY_BACKGROUND_MODES.AUTO;
    var seedStaticFromHints = !forceReducedMotion
      && pref === C.SKY_BACKGROUND_MODES.AUTO
      && this._skyBackgroundPrefs
      && !this._skyBackgroundPrefs.autoResolved
      && shouldSeedStaticFromHints(hints);
    return {
      forceReducedMotion: forceReducedMotion,
      seedStaticFromHints: seedStaticFromHints,
      perfForcedStatic: !!this._skyPerfForcedStatic,
    };
  };

  Game.prototype._isSkyAutoProbeCountdown = function (fallState) {
    if (!this._skyBackgroundPrefs) return false;
    var ctx = this._getSkyBackgroundContext();
    return shouldUseFirstFallProbeCountdownArc(this._skyBackgroundPrefs, {
      isFirstFallOfFirstRun: this._isFirstFallOfFirstRun(),
      fallState: fallState,
      forceReducedMotion: ctx.forceReducedMotion,
      seedStaticFromHints: ctx.seedStaticFromHints,
      perfForcedStatic: ctx.perfForcedStatic,
    });
  };

  Game.prototype._useFirstFallSplitHourArc = function (fallState) {
    if (!this._skyBackgroundPrefs) return false;
    return shouldUseFirstFallSplitHourArc(this._skyBackgroundPrefs, {
      isFirstFallOfFirstRun: this._isFirstFallOfFirstRun(),
      fallState: fallState,
    });
  };

  Game.prototype._initSkyBackgroundPrefs = function () {
    if (this.testFlaskMode) return;
    this._skyBackgroundPrefs = loadSkyBackgroundPrefs(
      this._getProgressStorage()
    );
    this._applySkyBackgroundPrefsToConfig();
    this._resolveSkyBackgroundBootstrap();
    this._syncSkyBackgroundEffective();
    this._syncSkyBackgroundAnalytics();
  };

  /**
   * Lock static from reduced-motion / weak-device hints before the FPS probe runs.
   * Each check gets its own `sky_bg_resolve_source` (probe is fail-only later).
   */
  Game.prototype._resolveSkyBackgroundBootstrap = function () {
    if (this.testFlaskMode || !this._skyBackgroundPrefs) return;
    if (this._skyBgBootstrapResolved) return;

    if (prefersReducedMotion()) {
      this._skyBgBootstrapResolved = true;
      this._trackSkyBgResolved(C.SKY_BACKGROUND_MODES.STATIC, SKY_BG_RESOLVE_SOURCES.REDUCED_MOTION);
      return;
    }

    var prefs = this._skyBackgroundPrefs;
    var pref = normalizeSkyBackgroundPreference(prefs.preference);
    if (pref !== C.SKY_BACKGROUND_MODES.AUTO || prefs.autoResolved) return;

    var reason = getSkyStaticSeedReason(getSkyStaticSeedHints(
      typeof window !== "undefined" ? window : undefined
    ));
    if (!reason) return;

    prefs.autoResolved = C.SKY_BACKGROUND_MODES.STATIC;
    saveSkyBackgroundPrefs(
      prefs,
      this._getProgressStorage()
    );
    this._skyBgBootstrapResolved = true;
    this._trackSkyBgResolved(C.SKY_BACKGROUND_MODES.STATIC, reason);
  };

  /**
   * @param {"dynamic"|"static"} mode
   * @param {string} source
   * @param {Record<string, unknown>} [extra]
   */
  Game.prototype._trackSkyBgResolved = function (mode, source, extra) {
    if (this.testFlaskMode || this.opts.disableAnalytics) return;
    var payload = {
      sky_bg_mode: mode,
      sky_bg_resolve_source: source,
    };
    if (extra) {
      var keys = Object.keys(extra);
      for (var i = 0; i < keys.length; i++) {
        payload[keys[i]] = extra[keys[i]];
      }
    }
    this._trackAnalytics(AnalyticsEvents.SKY_BG_RESOLVED, payload);
  };

  Game.prototype._applySkyBackgroundPrefsToConfig = function () {
    var cfg = C.RUN_CONFIG || {};
    var prefs = this._skyBackgroundPrefs || loadSkyBackgroundPrefs();
    cfg.SKY_BACKGROUND_MODE = normalizeSkyBackgroundPreference(prefs.preference);
    if (this.skyClock && this.skyClock.cfg) {
      this.skyClock.cfg.SKY_BACKGROUND_MODE = cfg.SKY_BACKGROUND_MODE;
    }
  };

  Game.prototype._syncSkyBackgroundEffective = function () {
    var prefs = this._skyBackgroundPrefs || loadSkyBackgroundPrefs();
    var effective = resolveSkyBackgroundEffective(prefs, this._getSkyBackgroundContext());
    var cfg = C.RUN_CONFIG || {};
    cfg.SKY_BACKGROUND_EFFECTIVE = effective;
    if (this.skyClock && this.skyClock.cfg) {
      this.skyClock.cfg.SKY_BACKGROUND_EFFECTIVE = effective;
    }
    this._skyCacheKey = null;
    this._starOverlayCacheKey = null;
    this._lastSkyBgRepaintMs = null;
  };

  Game.prototype._syncSkyBackgroundAnalytics = function () {
    if (this.testFlaskMode || this.opts.disableAnalytics) return;
    var prefs = this._skyBackgroundPrefs || loadSkyBackgroundPrefs();
    this._setPerfUserProperty(
      ANALYTICS_USER_PROPERTY_SKY_BG_PREF,
      normalizeSkyBackgroundPreference(prefs.preference)
    );
    this._setPerfUserProperty(
      ANALYTICS_USER_PROPERTY_SKY_BG_MODE,
      getSkyBackgroundAnalyticsMode(prefs, this._getSkyBackgroundContext())
    );
    this._flushPerfUserProperties();
  };

  Game.prototype._startSkyAutoProbeIfNeeded = function () {
    if (this.testFlaskMode || !this._skyBackgroundPrefs) return;
    var ctx = this._getSkyBackgroundContext();
    if (!shouldRunSkyAutoProbe(this._skyBackgroundPrefs, {
      isFirstFallOfFirstRun: this._isFirstFallOfFirstRun(),
      forceReducedMotion: ctx.forceReducedMotion,
    })) {
      this._skyAutoProbe = null;
      return;
    }
    var cfg = C.RUN_CONFIG || {};
    var threshold = cfg.SKY_AUTO_PROBE_FPS_THRESHOLD != null
      ? Number(cfg.SKY_AUTO_PROBE_FPS_THRESHOLD)
      : 60;
    this._skyAutoProbe = createSkyAutoProbe({
      fpsThreshold: threshold,
      displayHz: this._displayHzEst,
      warmupMs: cfg.SKY_AUTO_PROBE_WARMUP_MS,
      droppedFrameRatioMax: cfg.SKY_AUTO_PROBE_DROPPED_FRAME_RATIO_MAX,
      frameMsP95Max: cfg.SKY_AUTO_PROBE_FRAME_MS_P95_MAX,
    });
  };

  /**
   * @param {number} now
   * @param {number} [frameMs]
   */
  Game.prototype._tickSkyAutoProbe = function (now, frameMs) {
    if (!this._skyAutoProbe || !this.runPhase || this.runPhase.state !== "countdown") return;
    this._skyAutoProbe.tick(now, frameMs);
  };

  Game.prototype._finishSkyAutoProbe = function () {
    if (!this._skyAutoProbe || !this._skyBackgroundPrefs) return;
    var result = this._skyAutoProbe.finish();
    this._skyAutoProbe = null;
    var resolved = result.passed
      ? C.SKY_BACKGROUND_MODES.DYNAMIC
      : C.SKY_BACKGROUND_MODES.STATIC;
    this._skyBackgroundPrefs.autoResolved = resolved;
    saveSkyBackgroundPrefs(
      this._skyBackgroundPrefs,
      this._getProgressStorage()
    );
    this._syncSkyBackgroundEffective();
    this._syncSkyBackgroundAnalytics();
    // `"probe"` is fail-only (low FPS); pass uses `"probe_pass"`.
    this._trackSkyBgResolved(
      resolved,
      result.passed ? SKY_BG_RESOLVE_SOURCES.PROBE_PASS : SKY_BG_RESOLVE_SOURCES.PROBE,
      {
        sky_bg_probe_fps: result.medianFps,
        sky_bg_probe_fps_threshold: result.fpsThreshold,
        sky_bg_probe_frame_ms_p95: result.frameMsP95,
        sky_bg_probe_dropped_permille: Math.round(result.droppedFrameRatio * 1000),
        sky_bg_probe_display_hz: result.displayHz != null ? result.displayHz : 0,
      }
    );
  };

  Game.prototype._degradeSkyBackgroundFromPerfEpisode = function () {
    if (this.testFlaskMode || !this._skyBackgroundPrefs) return;
    var pref = normalizeSkyBackgroundPreference(this._skyBackgroundPrefs.preference);
    if (pref === C.SKY_BACKGROUND_MODES.STATIC) return;
    var ctx = this._getSkyBackgroundContext();
    if (ctx.forceReducedMotion) return;
    var effective = resolveSkyBackgroundEffective(this._skyBackgroundPrefs, ctx);
    if (effective === C.SKY_BACKGROUND_MODES.STATIC) return;

    if (pref === C.SKY_BACKGROUND_MODES.AUTO) {
      this._skyBackgroundPrefs.autoResolved = C.SKY_BACKGROUND_MODES.STATIC;
      saveSkyBackgroundPrefs(
        this._skyBackgroundPrefs,
        this._getProgressStorage()
      );
    } else {
      this._skyPerfForcedStatic = true;
    }
    this._syncSkyBackgroundEffective();
    this._syncSkyBackgroundAnalytics();
    this._trackSkyBgResolved(
      C.SKY_BACKGROUND_MODES.STATIC,
      SKY_BG_RESOLVE_SOURCES.PERF_EPISODE
    );
  };

  /**
   * Sample Fall+Merge frames while auto is locked static; upgrade after a strong cycle.
   * @param {number} now
   * @param {number} [frameMs]
   */
  Game.prototype._tickSkyCycleUpgradeProbe = function (now, frameMs) {
    if (this.testFlaskMode || !this._skyBackgroundPrefs) return;
    var ctx = this._getSkyBackgroundContext();
    if (!shouldAttemptSkyCycleUpgrade(this._skyBackgroundPrefs, {
      forceReducedMotion: ctx.forceReducedMotion,
    })) {
      this._skyCycleUpgradeProbe = null;
      return;
    }
    if (!this._skyCycleUpgradeProbe) {
      var cfg = C.RUN_CONFIG || {};
      var threshold = cfg.SKY_AUTO_PROBE_FPS_THRESHOLD != null
        ? Number(cfg.SKY_AUTO_PROBE_FPS_THRESHOLD)
        : 60;
      var dropMax = cfg.SKY_CYCLE_UPGRADE_DROPPED_FRAME_RATIO_MAX != null
        ? Number(cfg.SKY_CYCLE_UPGRADE_DROPPED_FRAME_RATIO_MAX)
        : 0.08;
      var warmupMs = cfg.SKY_CYCLE_UPGRADE_WARMUP_MS != null
        ? Number(cfg.SKY_CYCLE_UPGRADE_WARMUP_MS)
        : 1000;
      var p95Mult = cfg.SKY_CYCLE_UPGRADE_P95_BUDGET_MULT != null
        ? Number(cfg.SKY_CYCLE_UPGRADE_P95_BUDGET_MULT)
        : 1.1;
      var hz = this._displayHzEst != null && isFinite(this._displayHzEst) && this._displayHzEst > 0
        ? this._displayHzEst
        : null;
      this._skyCycleUpgradeProbe = createSkyAutoProbe({
        fpsThreshold: threshold,
        displayHz: hz,
        warmupMs: warmupMs,
        droppedFrameRatioMax: dropMax,
        p95BudgetMult: p95Mult,
      });
    } else if (
      this._displayHzEst != null
      && typeof this._skyCycleUpgradeProbe.setDisplayHz === "function"
    ) {
      this._skyCycleUpgradeProbe.setDisplayHz(this._displayHzEst);
    }
    this._skyCycleUpgradeProbe.tick(now, frameMs);
  };

  /**
   * After a completed Fall→Merge cycle, upgrade auto-static → dynamic when samples are strong.
   */
  Game.prototype._maybeUpgradeSkyBackgroundFromCycle = function () {
    if (this.testFlaskMode || !this._skyBackgroundPrefs || !this._skyCycleUpgradeProbe) {
      this._skyCycleUpgradeProbe = null;
      return;
    }
    var ctx = this._getSkyBackgroundContext();
    if (!shouldAttemptSkyCycleUpgrade(this._skyBackgroundPrefs, {
      forceReducedMotion: ctx.forceReducedMotion,
    })) {
      this._skyCycleUpgradeProbe = null;
      return;
    }
    var result = this._skyCycleUpgradeProbe.finish();
    this._skyCycleUpgradeProbe = null;
    var cfg = C.RUN_CONFIG || {};
    var minFrames = cfg.SKY_CYCLE_UPGRADE_MIN_FRAME_SAMPLES != null
      ? Number(cfg.SKY_CYCLE_UPGRADE_MIN_FRAME_SAMPLES)
      : 300;
    if (!shouldUpgradeSkyFromCycleResult(result, { minFrameSamples: minFrames })) return;

    this._skyBackgroundPrefs.autoResolved = C.SKY_BACKGROUND_MODES.DYNAMIC;
    this._skyPerfForcedStatic = false;
    saveSkyBackgroundPrefs(
      this._skyBackgroundPrefs,
      this._getProgressStorage()
    );
    this._syncSkyBackgroundEffective();
    this._syncSkyBackgroundAnalytics();
    this._trackSkyBgResolved(
      C.SKY_BACKGROUND_MODES.DYNAMIC,
      SKY_BG_RESOLVE_SOURCES.CYCLE_UPGRADE,
      {
        sky_bg_probe_fps: result.medianFps,
        sky_bg_probe_fps_threshold: result.fpsThreshold,
        sky_bg_probe_frame_ms_p95: result.frameMsP95,
        sky_bg_probe_dropped_permille: Math.round(result.droppedFrameRatio * 1000),
        sky_bg_probe_display_hz: result.displayHz != null ? result.displayHz : 0,
        sky_bg_probe_sample_count: result.sampleCount || 0,
      }
    );
  };

  Game.prototype.getSkyHour = function (currentTime) {
    if (!this.skyClock) return 0;
    return getSkyHour(this.skyClock, this._getSkyClockSnapshot(currentTime));
  };

  Game.prototype.startCelestialTransition = function (durationMs, opts) {
    opts = opts || {};
    var now = typeof performance !== "undefined" ? performance.now() : Date.now();
    this.celestialTransition = {
      durationMs: Math.max(1, durationMs || 2000),
      startedAt: now,
      fadeStageContent: opts.fadeStageContent === true,
      direction: opts.direction === "to_fall" ? "to_fall" : "to_shake",
    };
    if (opts.fadeStageContent) this.stageContentAlpha = 1;
  };

  Game.prototype.updateCelestialTransition = function (currentTime) {
    if (!this.celestialTransition || !this.celestialTransition.fadeStageContent) return;
    var now = currentTime != null ? currentTime : (typeof performance !== "undefined" ? performance.now() : Date.now());
    if (this._isSimPaused()) {
      if (this._bridgeCelestialFrozenAlpha != null) {
        this.stageContentAlpha = this._bridgeCelestialFrozenAlpha;
      }
      this._syncStageChromeAlpha();
      this._updateFallProgressDial(now);
      return;
    }
    var tr = this.celestialTransition;
    var elapsed = now - tr.startedAt;
    var cfg = C.RUN_CONFIG || {};
    var fadeMs = cfg.STAGE_ELEMENT_FADE_MS != null ? cfg.STAGE_ELEMENT_FADE_MS : 500;
    this.stageContentAlpha = computeStageElementAlpha(elapsed, tr.durationMs, fadeMs);
    if (elapsed >= tr.durationMs) {
      this.stageContentAlpha = 1;
      this.celestialTransition.fadeStageContent = false;
    }
    this._syncStageChromeAlpha();
    this._updateFallProgressDial(now);
  };

  Game.prototype._getFallStageChromeFadeNodes = function () {
    if (this._isVerticalLayout()) {
      return [
        this.dom && this.dom.verticalStashPanel,
        this.dom && this.dom.fallHpRow,
      ];
    }
    return [
      this.dom && this.dom.fallStashPanel,
      this.dom && this.dom.fallHpRow,
    ];
  };

  Game.prototype._bridgeFadeOwnsShakeHpOpacity = function () {
    var tr = this.celestialTransition;
    return !!(tr && tr.fadeStageContent);
  };

  Game.prototype._getShakeStageChromeFadeNodes = function () {
    var hpRow = this.dom && this.dom.shakeHpRow;
    if (this._isVerticalLayout()) {
      return [hpRow, this.dom && this.dom.verticalShakeActionsRow].filter(function (el) { return !!el; });
    }
    return [hpRow, this.dom && this.dom.shakeActionsCol].filter(function (el) { return !!el; });
  };

  Game.prototype._hideFallPlayChromeForBridge = function () {
    this._fallBridgeChromeLocked = true;
    var fallNodes = this._getFallStageChromeFadeNodes();
    for (var i = 0; i < fallNodes.length; i++) {
      var el = fallNodes[i];
      if (!el) continue;
      if (el.classList && el.classList.toggle) el.classList.toggle("hidden", true);
      else if (el.classList && el.classList.add) el.classList.add("hidden");
      if (el.style && el.style.removeProperty) {
        el.style.removeProperty("opacity");
      } else if (el.style) {
        el.style.opacity = "";
      }
    }
  };

  Game.prototype._getStageChromeFadeSets = function () {
    // Shake HP row participates in bridge crossfades (fade-in only if <max HP at Fall→Shake handoff;
    // fade-out on Shake→Fall). Internal full-HP auto-hide/fade still managed by _syncShakeHpBarVisibility.
    // Header, gems, and evolution strip use theme crossfade only (see mockup-chrome.css).
    return {
      fallNodes: this._getFallStageChromeFadeNodes(),
      shakeNodes: this._getShakeStageChromeFadeNodes(),
    };
  };

  Game.prototype._clearStageChromeOpacity = function () {
    this._fallBridgeChromeLocked = false;
    var sets = this._getStageChromeFadeSets();
    var all = sets.fallNodes.concat(sets.shakeNodes);
    for (var i = 0; i < all.length; i++) {
      var el = all[i];
      if (el && el.style && el.style.removeProperty) {
        el.style.removeProperty("opacity");
      } else if (el && el.style) {
        el.style.opacity = "";
      }
    }
  };

  Game.prototype._syncStageChromeAlpha = function () {
    var tr = this.celestialTransition;
    if (!tr || !tr.fadeStageContent) return;
    var now = typeof performance !== "undefined" ? performance.now() : Date.now();
    var elapsed = now - tr.startedAt;
    var cfg = C.RUN_CONFIG || {};
    var fadeMs = cfg.STAGE_ELEMENT_FADE_MS != null ? cfg.STAGE_ELEMENT_FADE_MS : 300;
    var totalMs = Math.max(1, tr.durationMs || 2000);
    var outgoing = elapsed <= fadeMs ? Math.max(0, 1 - elapsed / fadeMs) : 0;
    var incoming = elapsed >= totalMs - fadeMs
      ? Math.min(1, (elapsed - (totalMs - fadeMs)) / fadeMs)
      : 0;
    var sets = this._getStageChromeFadeSets();
    var toFall = tr.direction === "to_fall";
    var bridgeFallLocked = this._fallBridgeChromeLocked && !toFall;
    var outNodes = toFall ? sets.shakeNodes : sets.fallNodes;
    var inNodes = toFall ? sets.fallNodes : sets.shakeNodes;
    var i;
    if (bridgeFallLocked) {
      for (i = 0; i < sets.fallNodes.length; i++) {
        var lockedEl = sets.fallNodes[i];
        if (!lockedEl) continue;
        if (lockedEl.style) lockedEl.style.opacity = "0";
        if (lockedEl.classList) lockedEl.classList.toggle("hidden", true);
      }
    }
    for (i = 0; i < outNodes.length; i++) {
      var outEl = outNodes[i];
      if (!outEl) continue;
      if (bridgeFallLocked && sets.fallNodes.indexOf(outEl) >= 0) continue;
      if (outEl === (this.dom && this.dom.shakeHpRow)) {
        // Full HP: never flash the merge HP row during Shake→Fall bridge fade-out.
        var outMaxHp = Math.max(1, Number(this.flaskMaxHp) || 3);
        var outHp = Math.max(0, Math.min(outMaxHp, Math.floor(Number(this.savedFlaskHp) || 0)));
        if (outHp >= outMaxHp) {
          if (outEl.classList) outEl.classList.toggle("hidden", true);
          if (outEl.style) outEl.style.opacity = "0";
          continue;
        }
      }
      if (outEl.style) outEl.style.opacity = String(outgoing);
      if (outEl.classList) outEl.classList.toggle("hidden", outgoing <= 0);
    }
    for (i = 0; i < inNodes.length; i++) {
      var inEl = inNodes[i];
      if (!inEl) continue;
      if (inEl === (this.dom && this.dom.verticalStashPanel)) {
        var showVerticalStash = !!(this.boostBar && this.boostBar.length > 0);
        if (!showVerticalStash) {
          if (inEl.classList) inEl.classList.toggle("hidden", true);
          if (inEl.style) inEl.style.opacity = "0";
          continue;
        }
      }
      if (inEl === (this.dom && this.dom.shakeHpRow)) {
        // Only fade-in the merge HP panel if it should be visible (HP < max) at handoff.
        // Full HP case: keep hidden (the post-heal fade-out is handled separately on-stage).
        var maxHp = Math.max(1, Number(this.flaskMaxHp) || 3);
        var hp = Math.max(0, Math.min(maxHp, Math.floor(Number(this.savedFlaskHp) || 0)));
        if (hp >= maxHp) {
          if (inEl.classList) inEl.classList.toggle("hidden", true);
          if (inEl.style) inEl.style.opacity = "0";
          continue;
        }
        // Paint actual hearts before any opacity > 0 (stale full-HP markup from a prior Merge).
        this.updateLivesHud();
        // Keep internal alpha in sync so _syncShakeHpPillState does not zero bridge fade opacity.
        this._shakeHpWasDamagedThisStage = true;
        this.shakeHpBarAlpha = 1;
      }
      if (incoming > 0 && inEl.classList) {
        inEl.classList.remove("hidden");
        if (typeof inEl.setAttribute === "function") inEl.setAttribute("aria-hidden", "false");
      }
      if (inEl.style) inEl.style.opacity = String(incoming);

      if (inEl === (this.dom && this.dom.shakeHpRow)) {
        // Ensure the pill's disabled state, classes, and heartbeat var are up-to-date now
        // that the row may have become visible for fade-in. Restore the bridge opacity
        // afterwards so the visual ramp (incoming) is not overwritten by internal alpha.
        var preservedOp = inEl.style ? inEl.style.opacity : null;
        this._syncShakeHpPillState();
        if (preservedOp != null && inEl.style) inEl.style.opacity = preservedOp;
      }
    }

    // After bridge alpha sync, refresh pill disabled/heartbeat without clobbering fade opacity.
    // On shake (or Fall→Shake bridge with damage), visibility logic owns hidden state.
    var sm = this.stateMachine;
    if (sm && sm.is) {
      if (sm.is("shake")) {
        this._syncShakeHpBarVisibility();
      } else if (sm.is("transition") && tr.direction === "to_shake") {
        var bridgeMaxHp = Math.max(1, Number(this.flaskMaxHp) || 3);
        var bridgeHp = Math.max(0, Math.min(bridgeMaxHp, Math.floor(Number(this.savedFlaskHp) || 0)));
        if (bridgeHp < bridgeMaxHp) {
          var bridgeRow = this.dom && this.dom.shakeHpRow;
          var bridgeOp = bridgeRow && bridgeRow.style ? bridgeRow.style.opacity : null;
          this._syncShakeHpPillState();
          if (bridgeOp != null && bridgeRow && bridgeRow.style) bridgeRow.style.opacity = bridgeOp;
        }
      }
    }

    // Left column + gem pill crossfade theme only — never opacity-faded here.
  };

  Game.prototype.applyUiScaleVars = function () {
    var app = document.getElementById("app");
    if (!app || !this.layout) return;
    var core = this.layout.coreRect;
    var w = resolveGridWidthPx(this.layout);
    if (!(w > 0)) {
      w = core && core.width > 0
        ? core.width
        : (this.layout.canvasWidth || (this.layout.runBounds && this.layout.runBounds.width) || 720);
    }
    var h = core && core.height > 0
      ? core.height
      : (this.layout.canvasHeight || 540);
    var uiW = String(Math.round(w));
    var uiH = String(Math.round(h));
    var scale = this.layout.mockupScale || computeMockupUiScale(w);
    var sideStripW = 0;
    if (this.layout.layoutMode !== "vertical") {
      var leftStrip = this.layout.leftStrip;
      if (leftStrip && leftStrip.width > 0) {
        sideStripW = Math.round(leftStrip.width);
      }
    }
    var applyScaleVars = function (el) {
      if (!el || !el.style) return;
      el.style.setProperty("--ui-w", uiW);
      el.style.setProperty("--ui-h", uiH);
      el.style.setProperty("--col-side", String(scale.colSideWidth) + "px");
      el.style.setProperty("--col-play", String(scale.colPlayWidth) + "px");
      el.style.setProperty("--scale-side", String(scale.scaleSide));
      el.style.setProperty("--scale-play", String(scale.scalePlay));
      el.style.setProperty("--scale-ui", String(scale.scaleUi));
      if (sideStripW > 0) {
        el.style.setProperty("--col-side-strip", sideStripW + "px");
      } else if (typeof el.style.removeProperty === "function") {
        el.style.removeProperty("--col-side-strip");
      }
    };
    applyScaleVars(app);
    applyScaleVars(document.documentElement);
  };

  Game.prototype.applyStageChromeLeftBounds = function (clearOnly) {
    var panel = this.dom && this.dom.fallRightHudModule;
    if (!panel || !panel.style) return;
    if (clearOnly) {
      panel.style.left = "";
      panel.style.top = "";
      panel.style.width = "";
      panel.style.height = "";
      return;
    }
    this.layout = this.layout || this.getLayout();
    var ls = this.layout && this.layout.leftStrip;
    if (!ls || ls.width <= 0) return;
    panel.style.left = Math.round(ls.x) + "px";
    panel.style.top = Math.round(ls.y) + "px";
    panel.style.width = Math.round(ls.width) + "px";
    panel.style.height = Math.round(ls.height) + "px";
  };

  Game.prototype.applyStageChromeRightBounds = function (clearOnly) {
    var panel = this.dom && this.dom.stageChromeRight;
    if (!panel || !panel.style) return;
    if (clearOnly) {
      panel.classList.add("hidden");
      panel.style.left = "";
      panel.style.top = "";
      panel.style.width = "";
      panel.style.height = "";
      return;
    }
    this.layout = this.layout || this.getLayout();
    var rs = this.layout && this.layout.rightStrip;
    if (!rs || rs.width <= 0) return;
    panel.classList.remove("hidden");
    panel.style.left = Math.round(rs.x) + "px";
    panel.style.top = Math.round(rs.y) + "px";
    panel.style.width = Math.round(rs.width) + "px";
    panel.style.height = Math.round(rs.height) + "px";
  };

  Game.prototype.applyPlayChromeBounds = function (clearOnly) {
    var chrome = this.dom && this.dom.playChrome;
    if (!chrome || !chrome.style) return;
    if (clearOnly) {
      chrome.classList.add("hidden");
      chrome.style.left = "";
      chrome.style.top = "";
      chrome.style.width = "";
      chrome.style.height = "";
      return;
    }
    this.layout = this.layout || this.getLayout();
    var play = this.layout && this.layout.playArea;
    var playContent = this.layout && this.layout.playContentArea;
    var chromeBounds = this._isVerticalLayout() && playContent ? playContent : play;
    if (!chromeBounds || chromeBounds.width <= 0) return;
    chrome.classList.remove("hidden");
    chrome.style.left = Math.round(chromeBounds.x) + "px";
    chrome.style.top = Math.round(chromeBounds.y) + "px";
    chrome.style.width = Math.round(chromeBounds.width) + "px";
    chrome.style.height = Math.round(chromeBounds.height) + "px";
    if (chrome.style && typeof chrome.style.setProperty === "function") {
      var playScale = play.width / MOCKUP_PLAY_BASE_WIDTH_PX;
      if (isFinite(playScale) && playScale > 0) {
        chrome.style.setProperty("--scale-play", String(playScale));
      }
    }
    var isFall = this.stateMachine && this.stateMachine.is && this.stateMachine.is("fall");
    if (isFall && chrome.style && typeof chrome.style.setProperty === "function") {
      var progressBottom = C.FALL_PROGRESS_BOTTOM_PX != null
        ? Math.max(0, Math.floor(Number(C.FALL_PROGRESS_BOTTOM_PX)))
        : 3;
      var hpBottom = C.FALL_HP_BOTTOM_PX != null
        ? Math.max(0, Math.floor(Number(C.FALL_HP_BOTTOM_PX)))
        : progressBottom;
      chrome.style.setProperty("--fall-progress-bottom-px", progressBottom + "px");
      chrome.style.setProperty("--fall-hp-bottom-px", hpBottom + "px");
    } else if (chrome.style && typeof chrome.style.removeProperty === "function") {
      chrome.style.removeProperty("--fall-progress-bottom-px");
      chrome.style.removeProperty("--fall-hp-bottom-px");
    }
  };

  Game.prototype.applyShakeJarChromeBounds = function (clearOnly) {
    var el = this.dom && this.dom.shakeJarChrome;
    if (!el || !el.style) return;
    if (clearOnly) {
      el.classList.add("hidden");
      el.style.left = "";
      el.style.top = "";
      el.style.width = "";
      el.style.height = "";
      return;
    }
    this.layout = this.layout || this.getLayout();
    var origin = this.layout && this.layout.flaskPanelOrigin;
    var fb = this.layout && this.layout.flaskBounds;
    var play = this.layout && this.layout.playArea;
    var playContent = this.layout && this.layout.playContentArea;
    var coordPlay = this._isVerticalLayout() && playContent ? playContent : play;
    if (!origin || !fb || !coordPlay) return;
    el.style.left = Math.round(origin.x + fb.left - coordPlay.x) + "px";
    el.style.top = Math.round(origin.y + fb.top - coordPlay.y) + "px";
    el.style.width = Math.round(fb.right - fb.left) + "px";
    el.style.height = Math.round(fb.bottom - fb.top) + "px";
    this._syncJarDropSpawnAlignVars(el);
  };

  /**
   * Align DOM drop preview + queue with Matter spawn Y (`dropSpawnY` in panel space).
   * @param {HTMLElement | null | undefined} chrome `#shakeJarChrome`
   */
  Game.prototype._syncJarDropSpawnAlignVars = function (chrome) {
    if (!chrome || !chrome.style || typeof chrome.style.setProperty !== "function") return;
    if (!this.layout || !this.layout.flaskBounds) return;
    var fb = this.layout.flaskBounds;
    var sys = this.flaskSystem;
    var rimTop = fb.top != null ? Number(fb.top) : 0;
    var spawnPanelY = sys && sys.dropSpawnY != null && isFinite(sys.dropSpawnY)
      ? Number(sys.dropSpawnY)
      : getDropSpawnY(fb);
    var spawnLocalY = spawnPanelY - rimTop;
    if (!isFinite(spawnLocalY)) spawnLocalY = getDropSpawnYOffsetPx(fb);
    chrome.style.setProperty("--p-drop-spawn-y", spawnLocalY.toFixed(2) + "px");
  };

  Game.prototype._getDropSpawnPanelY = function () {
    var fb = this.layout && this.layout.flaskBounds;
    var sys = this.flaskSystem;
    if (sys && sys.dropSpawnY != null && isFinite(sys.dropSpawnY)) {
      return Number(sys.dropSpawnY);
    }
    return fb ? getDropSpawnY(fb) : 0;
  };

  /**
   * Panel Y where a dropped body should spawn to match the DOM drop preview aim.
   * Keeps the preview/queue on the rim spawn line; lifts physics spawn for tall sprites.
   * @param {number | null | undefined} tier
   */
  Game.prototype._getDropAimSpawnPanelY = function (tier) {
    var spawnLine = this._getDropSpawnPanelY();
    if (tier == null || tier < 0) return spawnLine;
    var sys = this.flaskSystem;
    var R = (sys && sys.bodyRadiusByTier && sys.bodyRadiusByTier[tier] != null)
      ? Number(sys.bodyRadiusByTier[tier]) : null;
    if (!R || !isFinite(R)) return spawnLine;
    var liftFrac = C.FLASK_DROP_SPAWN_AIM_LIFT_FRAC != null
      ? Number(C.FLASK_DROP_SPAWN_AIM_LIFT_FRAC) : 0.25;
    return spawnLine - getDropSpawnLiftPx(tier, R, liftFrac);
  };

  Game.prototype._getFallHpRevealTiming = function () {
    var fadeIn = C.FALL_HP_REVEAL_FADE_IN_MS != null ? Number(C.FALL_HP_REVEAL_FADE_IN_MS) : 150;
    var hold = C.FALL_HP_REVEAL_HOLD_MS != null ? Number(C.FALL_HP_REVEAL_HOLD_MS) : 1500;
    var fadeOut = C.FALL_HP_REVEAL_FADE_OUT_MS != null ? Number(C.FALL_HP_REVEAL_FADE_OUT_MS) : 300;
    if (!isFinite(fadeIn) || fadeIn < 0) fadeIn = 150;
    if (!isFinite(hold) || hold < 0) hold = 1500;
    if (!isFinite(fadeOut) || fadeOut < 0) fadeOut = 300;
    var total = fadeIn + hold + fadeOut;
    if (!isFinite(total) || total <= 0) {
      var hideMs = C.HEARTS_TRANSIENT_HIDE_MS != null ? Number(C.HEARTS_TRANSIENT_HIDE_MS) : 1950;
      total = isFinite(hideMs) && hideMs > 0 ? hideMs : 1950;
      fadeIn = Math.min(150, total);
      hold = Math.max(0, total - fadeIn - fadeOut);
    }
    return { fadeInMs: fadeIn, holdMs: hold, fadeOutMs: fadeOut, totalMs: total };
  };

  Game.prototype._getFallHpRevealStartedAt = function () {
    var startedAt = Number(this.heartsRevealStartedAt) || 0;
    if (startedAt > 0) return startedAt;
    var until = Number(this.heartsRevealUntil) || 0;
    if (until <= 0) return 0;
    return until - this._getFallHpRevealTiming().totalMs;
  };

  Game.prototype.getFallHpRowAlpha = function (currentTime) {
    var now = currentTime != null ? currentTime : (typeof performance !== "undefined" ? performance.now() : Date.now());
    var startedAt = this._getFallHpRevealStartedAt();
    if (startedAt <= 0 || now < startedAt) return 0;
    var timing = this._getFallHpRevealTiming();
    var elapsed = now - startedAt;
    if (elapsed >= timing.totalMs) return 0;
    if (elapsed < timing.fadeInMs) {
      return timing.fadeInMs > 0 ? elapsed / timing.fadeInMs : 1;
    }
    if (elapsed < timing.fadeInMs + timing.holdMs) return 1;
    var fadeOutElapsed = elapsed - timing.fadeInMs - timing.holdMs;
    return timing.fadeOutMs > 0
      ? Math.max(0, 1 - fadeOutElapsed / timing.fadeOutMs)
      : 0;
  };

  Game.prototype._getFallHpStageChromeAlpha = function (currentTime) {
    if (this._fallBridgeChromeLocked) return 0;
    var tr = this.celestialTransition;
    if (!tr || !tr.fadeStageContent) return 1;
    var now = currentTime != null ? currentTime : (typeof performance !== "undefined" ? performance.now() : Date.now());
    var elapsed = now - tr.startedAt;
    var cfg = C.RUN_CONFIG || {};
    var fadeMs = cfg.STAGE_ELEMENT_FADE_MS != null ? cfg.STAGE_ELEMENT_FADE_MS : 300;
    var totalMs = Math.max(1, tr.durationMs || 2000);
    if (tr.direction === "to_fall") {
      if (elapsed >= totalMs - fadeMs) {
        return Math.min(1, (elapsed - (totalMs - fadeMs)) / fadeMs);
      }
      return 0;
    }
    if (elapsed <= fadeMs) return Math.max(0, 1 - elapsed / fadeMs);
    return 0;
  };

  Game.prototype._getFallCatcherFlaskDims = function () {
    var runHeight = this.layout && this.layout.runHeight;
    if (!runHeight && this.layout && this.layout.playArea) {
      runHeight = this.layout.playArea.height;
    }
    var fr = this.runPhase && this.runPhase.fallRenderer;
    if (!fr || typeof fr._getFlaskDims !== "function" || !(runHeight > 0)) return null;
    return fr._getFlaskDims(runHeight);
  };

  Game.prototype._getFallHpBelowFlaskGapPx = function () {
    var gap = C.FALL_HP_BELOW_FLASK_GAP_PX != null
      ? Math.max(0, Math.floor(Number(C.FALL_HP_BELOW_FLASK_GAP_PX)))
      : 6;
    return isFinite(gap) ? gap : 6;
  };

  Game.prototype.syncFallHpRow = function (currentTime) {
    var row = this.dom && this.dom.fallHpRow;
    var livesEl = this.dom && this.dom.fallHudLives;
    var pill = this.dom && (this.dom.fallHpPill
      || (row && row.querySelector ? row.querySelector("#fallHpPill") : null));
    if (!row || !this.stateMachine || !this.stateMachine.is || !this.stateMachine.is("fall")) {
      if (row && row.classList) row.classList.add("hidden");
      if (livesEl) livesEl.innerHTML = "";
      this._fallHpHeartSig = null;
      this._fallHpPillPosSig = null;
      this._fallHpPillTransformSig = null;
      this._fallHpRowOpacitySig = null;
      return;
    }
    if (this._fallBridgeChromeLocked) return;
    var now = currentTime != null ? currentTime : (typeof performance !== "undefined" ? performance.now() : Date.now());
    var revealAlpha = this.getFallHpRowAlpha(now);
    var alpha = revealAlpha * this._getFallHpStageChromeAlpha(now);
    var show = alpha > 0;
    row.classList.toggle("hidden", !show);
    if (row.style) {
      var opacityStr = show ? alpha.toFixed(4) : "";
      if (this._fallHpRowOpacitySig !== opacityStr) {
        this._fallHpRowOpacitySig = opacityStr;
        if (show) row.style.opacity = String(alpha);
        else if (row.style.removeProperty) row.style.removeProperty("opacity");
        else row.style.opacity = "";
      }
    }
    if (typeof row.setAttribute === "function") {
      row.setAttribute("aria-hidden", show ? "false" : "true");
    }
    if (!show || !livesEl) {
      if (livesEl) livesEl.innerHTML = "";
      this._fallHpHeartSig = null;
      this._fallHpPillPosSig = null;
      this._fallHpPillTransformSig = null;
      if (pill && pill.style) {
        pill.style.transform = "";
        pill.style.top = "";
        pill.style.bottom = "";
      }
      return;
    }
    var maxHp = Math.max(1, Math.floor(Number(this.flaskMaxHp) || 3));
    var hp = maxHp;
    if (this.runPhase && this.runPhase.player) {
      hp = Math.max(0, Math.min(maxHp, Math.floor(Number(this.runPhase.player.hp) || 0)));
    }
    var heartSig = maxHp + "/" + hp;
    if (this._fallHpHeartSig !== heartSig) {
      this._fallHpHeartSig = heartSig;
      this._fallHpHeartUid = (this._fallHpHeartUid || 0) + 1;
      livesEl.innerHTML = renderHpHeartsHtml(maxHp, hp, { uid: this._fallHpHeartUid });
      livesEl.setAttribute("aria-label", hp + " of " + maxHp + " lives");
    }
    var play = this.layout && this.layout.playArea;
    var playContent = this.layout && this.layout.playContentArea;
    var chromePlay = this._isVerticalLayout() && playContent ? playContent : play;
    var run = this.layout && this.layout.runBounds;
    var player = this.runPhase && this.runPhase.player;
    if (pill && pill.style && chromePlay && player && Number(player.x) === player.x) {
      var runOffsetX = (run && run.x != null && chromePlay.x != null) ? (run.x - chromePlay.x) : 0;
      var runOffsetY = (run && run.y != null && chromePlay.y != null) ? (run.y - chromePlay.y) : 0;
      var localX = runOffsetX + player.x;
      var flaskDims = this._getFallCatcherFlaskDims();
      var localY = runOffsetY;
      if (flaskDims) {
        localY += flaskDims.flaskTopY + flaskDims.flaskH + this._getFallHpBelowFlaskGapPx();
      }
      var timing = this._getFallHpRevealTiming();
      var startedAt = this._getFallHpRevealStartedAt();
      var elapsed = startedAt > 0 ? Math.max(0, now - startedAt) : 0;
      var scale = 1;
      if (elapsed < timing.fadeInMs && timing.fadeInMs > 0) {
        var fadeT = elapsed / timing.fadeInMs;
        scale = 0.92 + 0.08 * fadeT;
      }
      var posSig = Math.round(localX) + "/" + Math.round(localY);
      if (this._fallHpPillPosSig !== posSig) {
        this._fallHpPillPosSig = posSig;
        pill.style.position = "absolute";
        pill.style.left = Math.round(localX) + "px";
        if (flaskDims) {
          pill.style.top = Math.round(localY) + "px";
          if (pill.style.removeProperty) pill.style.removeProperty("bottom");
          else pill.style.bottom = "";
        }
      }
      var transform = "translateX(-50%) scale(" + scale.toFixed(3) + ")";
      if (this._fallHpPillTransformSig !== transform) {
        this._fallHpPillTransformSig = transform;
        pill.style.transform = transform;
      }
    }
  };

  Game.prototype._hasDropAim = function () {
    var dc = this.dropCurrent;
    if (!dc || dc.tier == null) return false;
    var panelX = dc.panelX != null ? Number(dc.panelX) : null;
    if (panelX == null || !isFinite(panelX)) return false;
    var fb = this.layout && this.layout.flaskBounds;
    return !!(fb && fb.left != null && fb.right != null);
  };

  Game.prototype._isDropCurrentReady = function (now) {
    if (!this.dropCurrent) return false;
    var t = now != null && isFinite(Number(now)) ? Number(now) : Date.now();
    return !this.dropChargeUntil || t >= this.dropChargeUntil;
  };

  Game.prototype._getDropChargeMs = function () {
    var ms = C.FLASK_DROP_NEXT_DELAY_MS != null ? Number(C.FLASK_DROP_NEXT_DELAY_MS) : 300;
    if (!isFinite(ms) || ms < 0) ms = 300;
    return ms;
  };

  /**
   * @param {HTMLElement} preview
   * @param {HTMLElement | null | undefined} aim
   */
  Game.prototype._applyDropPreviewChargeVars = function (preview, aim) {
    if (!preview || !preview.style || typeof preview.style.setProperty !== "function") return;
    var chargeMs = this._getDropChargeMs();
    preview.style.setProperty("--drop-charge-ms", chargeMs + "ms");
    var queueIconPx = 34;
    if (typeof getComputedStyle === "function" && this.dom && this.dom.jarDropQueueStack) {
      var qSt = getComputedStyle(this.dom.jarDropQueueStack);
      var qVar = qSt.getPropertyValue("--p-queue-icon").trim();
      if (qVar) queueIconPx = parseFloat(qVar) || queueIconPx;
    }
    var previewPx = aim && aim.style && aim.style.width
      ? parseFloat(aim.style.width) : queueIconPx;
    if (!isFinite(previewPx) || previewPx <= 0) previewPx = queueIconPx;
    var startScale = Math.max(0.25, Math.min(1, queueIconPx / previewPx));
    preview.style.setProperty("--drop-charge-start-scale", startScale.toFixed(3));
    var previewH = previewPx;
    if (typeof preview.style.getPropertyValue === "function") {
      var previewHRaw = parseFloat(preview.style.getPropertyValue("--p-drop-preview-h"));
      if (isFinite(previewHRaw) && previewHRaw > 0) previewH = previewHRaw;
    }
    var startY = Math.max(12, Math.min(previewH * 0.35, 72));
    preview.style.setProperty("--drop-charge-start-y", (-startY).toFixed(1) + "px");
  };

  Game.prototype._hideJarDropGuideAim = function () {
    var chrome = this.dom && this.dom.shakeJarChrome;
    if (!chrome || !chrome.querySelector) return;
    var guide = chrome.querySelector(".jar-drop-guide");
    var aim = chrome.querySelector(".jar-drop-aim");
    if (guide && guide.classList) guide.classList.add("hidden");
    if (aim && aim.classList) aim.classList.add("hidden");
  };

  /**
   * Hide the whole NEXT queue UI and keep it hidden. Drop all takes the queue away from
   * the player, so leaving a stale, uncontrollable strip on screen would be a lie.
   * `drawDropMode` early-returns while pouring, so `updateShakeJarChromeDOM` cannot do it.
   */
  Game.prototype._hideDropQueueUI = function () {
    this._hideJarDropGuideAim();
    var chrome = this.dom && this.dom.shakeJarChrome;
    if (chrome && chrome.classList) chrome.classList.add("hidden");
    var preview = this.dom && this.dom.jarDropPreview;
    if (preview && preview.classList) preview.classList.add("hidden");
    updateJarDropQueue(this.dom && this.dom.jarDropQueueStack, [], 0);
    // Canvas NEXT column (portrait / no DOM chrome) reads this alpha.
    this.dropQueuePanelAlpha = 0;
    this._dropQueueFadeStart = null;
  };

  Game.prototype.updateShakeJarChromeDOM = function () {
    var chrome = this.dom && this.dom.shakeJarChrome;
    if (!chrome) return;
    var active = this.stateMachine && this.stateMachine.is && this.stateMachine.is("shake")
      && this._isDropInputActive();
    chrome.classList.toggle("hidden", !active);
    var guide = chrome.querySelector && chrome.querySelector(".jar-drop-guide");
    var aim = chrome.querySelector && chrome.querySelector(".jar-drop-aim");
    var hasAim = active && this._hasDropAim();
    if (guide) guide.classList.toggle("hidden", !hasAim);
    if (aim) aim.classList.toggle("hidden", !hasAim);
    if (!active) return;
    this._syncJarDropSpawnAlignVars(chrome);
    updateJarDropQueue(
      this.dom.jarDropQueueStack,
      this.dropQueue,
      this.dropQueuePanelAlpha
    );
    var previewTier = this.dropCurrent && this.dropCurrent.tier != null
      ? this.dropCurrent.tier
      : null;
    updateJarDropPreview(this.dom.jarDropPreview, previewTier);
    var pctStr = null;
    if (hasAim) {
      var fb = this.layout.flaskBounds;
      var panelX = Number(this.dropCurrent.panelX);
      var jarW = Math.max(1, fb.right - fb.left);
      var pct = ((panelX - fb.left) / jarW) * 100;
      pct = Math.max(0, Math.min(100, pct));
      pctStr = pct.toFixed(2) + "%";
      if (guide && guide.style) guide.style.left = pctStr;
      if (aim && aim.style) {
        aim.style.left = pctStr;
        this._layoutDropAimPreview(aim, this.dom.jarDropPreview, previewTier);
      }
    }
    var now = Date.now();
    if (this.dom.jarDropPreview && previewTier != null) {
      var preview = this.dom.jarDropPreview;
      var charging = !this._isDropCurrentReady(now);
      var tierChanged = previewTier !== this._lastDropPreviewTier;
      var wasCharging = !!this._dropPreviewWasCharging;
      var chargeStarted = charging && !wasCharging;
      if (preview.classList && typeof preview.classList.remove === "function") {
        if (tierChanged || chargeStarted) {
          preview.classList.remove("jar-drop-preview--pop", "jar-drop-preview--charging");
          void preview.offsetWidth;
          if (charging) {
            preview.classList.add("jar-drop-preview--charging");
            this._applyDropPreviewChargeVars(preview, aim);
          } else {
            preview.classList.add("jar-drop-preview--pop");
          }
        } else if (!charging && wasCharging) {
          preview.classList.remove("jar-drop-preview--charging");
        }
      }
      this._dropPreviewWasCharging = charging;
    } else if (this.dom.jarDropPreview && this.dom.jarDropPreview.classList) {
      this.dom.jarDropPreview.classList.remove("jar-drop-preview--charging");
      this._dropPreviewWasCharging = false;
    }
    this._lastDropPreviewTier = previewTier;
  };

  /**
   * Size/place the drop preview like `MergeAssets.draw`: assetCenter on spawn Y.
   * Queue chrome stays on the fixed spawn Y (aim height is collapsed).
   */
  Game.prototype._layoutDropAimPreview = function (aim, preview, tier) {
    if (!aim || !aim.style || tier == null) return;
    var sys = this.flaskSystem;
    var R = (sys && sys.bodyRadiusByTier && sys.bodyRadiusByTier[tier] != null)
      ? Number(sys.bodyRadiusByTier[tier]) : null;
    var metrics = getDropPreviewSpriteMetrics(tier, R);
    if (!metrics) return;
    var chain = (typeof C.getActiveEvolutionChain === "function")
      ? C.getActiveEvolutionChain()
      : (C.EVOLUTION_CHAIN || []);
    var cfg = chain[tier] || {};
    var center = (cfg && cfg.assetCenter) || [0.5, 0.5];
    var cxRatio = center[0] != null ? Number(center[0]) : 0.5;
    var cyRatio = center[1] != null ? Number(center[1]) : 0.5;
    if (!isFinite(cxRatio)) cxRatio = 0.5;
    if (!isFinite(cyRatio)) cyRatio = 0.5;
    aim.style.width = metrics.drawW.toFixed(1) + "px";
    aim.style.minWidth = "0px";
    if (preview && preview.style) {
      preview.style.top = (-metrics.anchorOffsetY).toFixed(2) + "px";
      preview.style.height = metrics.drawH.toFixed(2) + "px";
      preview.style.setProperty("--p-drop-preview-h", metrics.drawH.toFixed(2) + "px");
      preview.style.setProperty("--p-drop-anchor-x", (cxRatio * 100).toFixed(2) + "%");
      preview.style.setProperty("--p-drop-anchor-y", (cyRatio * 100).toFixed(2) + "%");
      var img = preview.querySelector && preview.querySelector("img.jar-body--drop");
      if (img && img.style) {
        img.style.width = metrics.drawW.toFixed(1) + "px";
        img.style.height = metrics.drawH.toFixed(1) + "px";
      }
    }
  };

  Game.prototype.applyShakeHpRowBounds = function (clearOnly) {
    var row = this.dom && this.dom.shakeHpRow;
    if (!row || !row.style) return;
    if (clearOnly) {
      row.classList.add("hidden");
      return;
    }
    var isShake = this.stateMachine && this.stateMachine.is && this.stateMachine.is("shake");
    if (!isShake) {
      row.classList.add("hidden");
      return;
    }
    this._syncShakeHpBarVisibility();
  };

  Game.prototype._invalidateSkyCache = function () {
    this._skyCacheKey = null;
    this._skyCacheLayoutSignature = null;
    this._starOverlayCacheKey = null;
    this._shakeChromeCacheKey = null;
    if (typeof resetStarFieldCache === "function") resetStarFieldCache();
  };

  /** Canvas CSS size, DPR, and responsive layout — must match for throttled sky blits. */
  Game.prototype._getSkySceneLayoutSignature = function (w, h) {
    var layout = this.layout || {};
    var layoutKey = "";
    if (layout.coreRect) {
      layoutKey = layout.coreRect.width + "x" + layout.coreRect.height;
    }
    if (layout.playArea) {
      layoutKey += "@" + layout.playArea.x + "," + layout.playArea.y
        + "," + layout.playArea.width + "," + layout.playArea.height;
    }
    if (layout.gridWidthPx != null) {
      layoutKey += "|gw" + layout.gridWidthPx;
    }
    if (layout.leftStrip) {
      layoutKey += "|ls" + layout.leftStrip.width;
    }
    if (layout.rightStrip) {
      layoutKey += "|rs" + layout.rightStrip.width;
    }
    return w + "x" + h + "@dpr" + this._getCanvasDpr() + "|" + layoutKey;
  };

  Game.prototype._getSkySceneCacheBucketMs = function () {
    var cfg = C.RUN_CONFIG || {};
    var bucket = cfg.SKY_SCENE_CACHE_CRAWL_BUCKET_MS != null
      ? Number(cfg.SKY_SCENE_CACHE_CRAWL_BUCKET_MS)
      : (cfg.SKY_SCENE_CACHE_BUCKET_MS != null ? Number(cfg.SKY_SCENE_CACHE_BUCKET_MS) : 132);
    if (!isFinite(bucket) || bucket <= 0) bucket = 132;
    return bucket;
  };

  Game.prototype._getSkyBgMaxRepaintIntervalMs = function () {
    var cfg = C.RUN_CONFIG || {};
    var fps = cfg.SKY_SCENE_CACHE_MAX_FPS != null
      ? Number(cfg.SKY_SCENE_CACHE_MAX_FPS)
      : (cfg.SKY_SCENE_CACHE_FALL_MAX_FPS != null
        ? Number(cfg.SKY_SCENE_CACHE_FALL_MAX_FPS)
        : 60);
    if (!isFinite(fps) || fps <= 0) fps = 60;
    return 1000 / fps;
  };

  Game.prototype._isSkyBgRepaintThrottled = function (currentTime) {
    if (this._lastSkyBgRepaintMs == null) return false;
    var t = Number(currentTime);
    if (!isFinite(t)) return false;
    return (t - this._lastSkyBgRepaintMs) < this._getSkyBgMaxRepaintIntervalMs();
  };

  Game.prototype._getSkySceneCacheKey = function (w, h, hour, currentTime) {
    var bucketMs = this._getSkySceneCacheBucketMs();
    if (!isFinite(bucketMs) || bucketMs < 0) bucketMs = 132;
    var bucket = bucketMs > 0 ? Math.floor((currentTime || 0) / bucketMs) : 0;
    var seed = this.skyDecorSeed || 0;
    return this._getSkySceneLayoutSignature(w, h)
      + "|" + seed + "|" + hour.toFixed(3) + "|" + bucket;
  };

  Game.prototype._canBlitSkySceneCache = function (cacheKey, layoutSig, currentTime) {
    if (this.celestialTransition && this.celestialTransition.fadeStageContent) {
      return false;
    }
    if (!this._skyCacheCanvas) return false;
    if (this._skyCacheKey === cacheKey) return true;
    if (this._skyCacheLayoutSignature !== layoutSig) return false;
    return this._isSkyBgRepaintThrottled(currentTime);
  };

  Game.prototype._ensureSkyCacheCanvas = function (w, h) {
    if (typeof document === "undefined") return null;
    var dpr = this._getCanvasDpr();
    var pixelW = Math.max(1, Math.round(w * dpr));
    var pixelH = Math.max(1, Math.round(h * dpr));
    if (!this._skyCacheCanvas) {
      this._skyCacheCanvas = document.createElement("canvas");
      this._skyCacheCtx = this._skyCacheCanvas.getContext("2d");
    }
    var cacheCanvas = this._skyCacheCanvas;
    if (cacheCanvas.width !== pixelW || cacheCanvas.height !== pixelH) {
      cacheCanvas.width = pixelW;
      cacheCanvas.height = pixelH;
      this._skyCacheKey = null;
    }
    return this._skyCacheCtx;
  };

  Game.prototype._ensureStarOverlayCacheCanvas = function (w, h) {
    if (typeof document === "undefined") return null;
    var dpr = this._getCanvasDpr();
    var pixelW = Math.max(1, Math.round(w * dpr));
    var pixelH = Math.max(1, Math.round(h * dpr));
    if (!this._starOverlayCacheCanvas) {
      this._starOverlayCacheCanvas = document.createElement("canvas");
      this._starOverlayCacheCtx = this._starOverlayCacheCanvas.getContext("2d");
    }
    var cacheCanvas = this._starOverlayCacheCanvas;
    if (cacheCanvas.width !== pixelW || cacheCanvas.height !== pixelH) {
      cacheCanvas.width = pixelW;
      cacheCanvas.height = pixelH;
      this._starOverlayCacheKey = null;
    }
    return this._starOverlayCacheCtx;
  };

  Game.prototype._blitStarOverlayCacheCanvas = function (ctx, cssW, cssH) {
    if (!ctx || !this._starOverlayCacheCanvas) return;
    var w = Math.max(1, Number(cssW) || 1);
    var h = Math.max(1, Number(cssH) || 1);
    var cacheCanvas = this._starOverlayCacheCanvas;
    var pixelW = cacheCanvas.width;
    var pixelH = cacheCanvas.height;
    if (pixelW <= 0 || pixelH <= 0) return;
    ctx.drawImage(cacheCanvas, 0, 0, pixelW, pixelH, 0, 0, w, h);
  };

  /** Map the DPR-sized sky cache bitmap to CSS board coords without a right-edge gap. */
  Game.prototype._blitSkyCacheCanvas = function (ctx, cssW, cssH) {
    if (!ctx || !this._skyCacheCanvas) return;
    var w = Math.max(1, Number(cssW) || 1);
    var h = Math.max(1, Number(cssH) || 1);
    var cacheCanvas = this._skyCacheCanvas;
    var pixelW = cacheCanvas.width;
    var pixelH = cacheCanvas.height;
    if (pixelW <= 0 || pixelH <= 0) return;
    ctx.drawImage(cacheCanvas, 0, 0, pixelW, pixelH, 0, 0, w, h);
  };

  Game.prototype._getShakeChromeCacheKey = function () {
    var layout = this.layout || {};
    var b = layout.flaskBounds || {};
    var geom = this.getFlaskSystem() && this.getFlaskSystem()._flaskGeometry;
    var geomKey = "";
    if (geom) {
      geomKey = geom.neckLeft + "," + geom.neckRight + "," + geom.T + "," + geom.bodyBottom;
    }
    return (layout.flaskPanelWidth || 0) + "x" + (layout.flaskPanelHeight || 0) + "|"
      + (layout.flaskPanelOrigin && layout.flaskPanelOrigin.x) + ","
      + (layout.flaskPanelOrigin && layout.flaskPanelOrigin.y) + "|"
      + b.left + "," + b.top + "," + b.right + "," + b.bottom + "|" + geomKey
      + "|jarR" + this._getSmallestFlaskBodyRadius();
  };

  Game.prototype._ensureShakeChromeCacheCanvas = function (w, h) {
    if (typeof document === "undefined") return null;
    var dpr = this._getCanvasDpr();
    var pixelW = Math.max(1, Math.round(w * dpr));
    var pixelH = Math.max(1, Math.round(h * dpr));
    if (!this._shakeChromeCacheCanvas) {
      this._shakeChromeCacheCanvas = document.createElement("canvas");
      this._shakeChromeCacheCtx = this._shakeChromeCacheCanvas.getContext("2d");
    }
    var cacheCanvas = this._shakeChromeCacheCanvas;
    if (cacheCanvas.width !== pixelW || cacheCanvas.height !== pixelH) {
      cacheCanvas.width = pixelW;
      cacheCanvas.height = pixelH;
      this._shakeChromeCacheKey = null;
    }
    return this._shakeChromeCacheCtx;
  };

  Game.prototype._rebuildShakeChromeCache = function () {
    if (!this.layout) return false;
    var layout = this.layout;
    var W = layout.flaskPanelWidth || 0;
    var H = layout.flaskPanelHeight || 0;
    var b = layout.flaskBounds;
    if (W <= 0 || H <= 0 || !b) return false;
    var cacheKey = this._getShakeChromeCacheKey();
    if (this._shakeChromeCacheKey === cacheKey && this._shakeChromeCacheCanvas) {
      return true;
    }
    var cacheCtx = this._ensureShakeChromeCacheCanvas(W, H);
    if (!cacheCtx || typeof cacheCtx.setTransform !== "function") return false;
    var dpr = this._getCanvasDpr();
    cacheCtx.setTransform(dpr, 0, 0, dpr, 0, 0);
    cacheCtx.clearRect(0, 0, W, H);
    cacheCtx.save();
    cacheCtx.beginPath();
    cacheCtx.rect(0, 0, W, H);
    cacheCtx.clip();
    this.drawShakeFlaskChrome(cacheCtx, W, H, b);
    cacheCtx.restore();
    this._shakeChromeCacheKey = cacheKey;
    return true;
  };

  Game.prototype._blitShakeChromeCache = function (ctx) {
    if (!this.layout || !this._shakeChromeCacheCanvas) return false;
    var layout = this.layout;
    var W = layout.flaskPanelWidth || 0;
    var H = layout.flaskPanelHeight || 0;
    var origin = layout.flaskPanelOrigin || { x: 0, y: 0 };
    if (W <= 0 || H <= 0) return false;
    var cacheCanvas = this._shakeChromeCacheCanvas;
    if (this.pixi) {
      this.pixi.setChrome(cacheCanvas, origin.x, origin.y, W, H, this._shakeChromeCacheKey);
      return true;
    }
    if (!ctx) return false;
    ctx.drawImage(
      cacheCanvas,
      0, 0, cacheCanvas.width, cacheCanvas.height,
      origin.x, origin.y, W, H
    );
    return true;
  };

  Game.prototype.drawSkySeaBackgroundLayer = function (ctx, currentTime) {
    if (!this.layout) return;
    var w = this.layout.canvasWidth || 0;
    var h = this.layout.canvasHeight || 0;
    if (w <= 0 || h <= 0) return;

    var bounds = { x: 0, y: 0, width: w, height: h };
    if (this.pixi && typeof this.pixi.setSkyShader === "function") {
      if (!runtimeFlags.drawSkySeaBackground) {
        if (this.pixi.setSkyShader(skyShaderSolidUniforms())) return;
      } else {
        var hourGpu = this.getSkyHour(currentTime);
        var skyOptsGpu = {
          layout: null,
          runSeed: this.skyDecorSeed || 0,
        };
        if (this.layout.coreRect && this.layout.playArea) {
          skyOptsGpu.layout = {
            coreRect: this.layout.coreRect,
            playArea: this.layout.playArea,
            leftStrip: this.layout.leftStrip,
            rightStrip: this.layout.rightStrip,
            gridUnitPx: this.layout.gridUnitPx,
            gridWidthPx: this.layout.gridWidthPx,
            layoutMode: this.layout.layoutMode,
          };
        }
        var starsEnabledGpu = runtimeFlags.drawSkyStars !== false
          && shouldDrawSkyStarsForHour(hourGpu, skyOptsGpu.layout, bounds);
        if (this.pixi.setSkyShader((function (u) {
          u.freeze = C.RUN_CONFIG && C.RUN_CONFIG.SKY_BACKGROUND_EFFECTIVE === C.SKY_BACKGROUND_MODES.STATIC;
          u.renderScale = skyRenderScale(getSkyStaticSeedHints(
            typeof window !== "undefined" ? window : undefined
          ));
          return u;
        })(buildSkyShaderUniforms(bounds, hourGpu, {
          layout: skyOptsGpu.layout,
          runSeed: skyOptsGpu.runSeed,
          drawStars: starsEnabledGpu,
        })))) {
          return;
        }
      }
    }

    if (!ctx || typeof ctx.createLinearGradient !== "function") return;
    if (!runtimeFlags.drawSkySeaBackground) {
      if (this.pixi) {
        this.pixi.skyBackdrop.clear();
        this.pixi.skyBackdrop.ctx.fillStyle = "#CFEFFB";
        this.pixi.skyBackdrop.ctx.fillRect(0, 0, w, h);
        this.pixi.skyBackdrop.publish();
        this.pixi.skyBackdrop.sprite.visible = true;
        this.pixi.skyForeground.sprite.visible = false;
        return;
      }
      ctx.save();
      ctx.fillStyle = "#CFEFFB";
      ctx.fillRect(0, 0, w, h);
      ctx.restore();
      return;
    }
    var hour = this.getSkyHour(currentTime);
    var skyOpts = {
      layout: null,
      runSeed: this.skyDecorSeed || 0,
    };
    if (this.layout.coreRect && this.layout.playArea) {
      skyOpts.layout = {
        coreRect: this.layout.coreRect,
        playArea: this.layout.playArea,
        leftStrip: this.layout.leftStrip,
        rightStrip: this.layout.rightStrip,
        gridUnitPx: this.layout.gridUnitPx,
        gridWidthPx: this.layout.gridWidthPx,
        layoutMode: this.layout.layoutMode,
      };
    }
    var dpr = this._getCanvasDpr();
    var layoutSig = this._getSkySceneLayoutSignature(w, h);
    var cacheKey = this._getSkySceneCacheKey(w, h, hour, currentTime);
    var canBlitSkyCache = this._canBlitSkySceneCache(cacheKey, layoutSig, currentTime);
    var starsEnabled = runtimeFlags.drawSkyStars !== false
      && shouldDrawSkyStarsForHour(hour, skyOpts.layout, bounds);
    var backdropOpts = Object.assign({}, skyOpts, {
      dpr: dpr,
      drawStars: starsEnabled,
      starLegacy: this._isDevMode() && runtimeFlags.drawSkyStarsLegacy === true,
    });

    if (canBlitSkyCache && this._starOverlayCacheCanvas) {
      this._publishSkyBackdrop(ctx, w, h);
    } else {
      var backdropCtx = this._ensureStarOverlayCacheCanvas(w, h);
      if (backdropCtx && typeof backdropCtx.setTransform === "function") {
        backdropCtx.setTransform(dpr, 0, 0, dpr, 0, 0);
        backdropCtx.clearRect(0, 0, w, h);
        drawSkyBackdropScene(backdropCtx, bounds, hour, backdropOpts);
        this._starOverlayCacheKey = cacheKey;
        this._skyCacheLayoutSignature = layoutSig;
        this._lastSkyBgRepaintMs = Number(currentTime) || 0;
        this._publishSkyBackdrop(ctx, w, h);
      } else if (!this.pixi) {
        drawSkyBackdropScene(ctx, bounds, hour, backdropOpts);
      }
    }

    if (canBlitSkyCache) {
      this._publishSkyForeground(ctx, w, h);
    } else {
      var cacheCtx = this._ensureSkyCacheCanvas(w, h);
      if (cacheCtx && typeof cacheCtx.setTransform === "function") {
        cacheCtx.setTransform(dpr, 0, 0, dpr, 0, 0);
        cacheCtx.clearRect(0, 0, w, h);
        drawSkyForegroundScene(cacheCtx, bounds, hour, skyOpts);
        this._skyCacheKey = cacheKey;
        this._skyCacheLayoutSignature = layoutSig;
        this._lastSkyBgRepaintMs = Number(currentTime) || 0;
        this._publishSkyForeground(ctx, w, h);
      } else if (!this.pixi) {
        drawSkyForegroundScene(ctx, bounds, hour, skyOpts);
      }
    }
  };

  Game.prototype._publishSkyBackdrop = function (ctx, w, h) {
    if (this.pixi && this._starOverlayCacheCanvas) {
      this.pixi.setSkyBackdrop(this._starOverlayCacheCanvas);
      return;
    }
    this._blitStarOverlayCacheCanvas(ctx, w, h);
  };

  Game.prototype._publishSkyForeground = function (ctx, w, h) {
    if (this.pixi && this._skyCacheCanvas) {
      this.pixi.setSkyForeground(this._skyCacheCanvas);
      return;
    }
    this._blitSkyCacheCanvas(ctx, w, h);
  };

  Game.prototype.revealHeartsTransient = function () {
    var timing = this._getFallHpRevealTiming();
    var hideMs = timing.totalMs;
    var now = typeof performance !== "undefined" ? performance.now() : Date.now();
    this.heartsRevealStartedAt = now;
    this.heartsRevealUntil = now + hideMs;
  };

  Game.prototype.shouldShowFallHearts = function (currentTime) {
    return this.getFallHpRowAlpha(currentTime) > 0;
  };

  Game.prototype.drawDarkNeonGrid = function (ctx, x, y, w, h) {
    this.drawSkySeaBackgroundLayer(ctx, this._getSkySeaDrawTime(
      typeof performance !== "undefined" ? performance.now() : Date.now()
    ));
  };

  /**
   * Paint a "sunlit lagoon" panel background — light water vertical gradient + optional faint
   * blue grid + 1px inner-top highlight, so canvas panels match the DOM white-glass cards.
   * Used by all canvas chrome (shake card, NEXT column, evolution-dock backdrop, playfield
   * base, test-tube column). Grid is **opt-in** (`opts.grid === true`).
   *
   * @param {CanvasRenderingContext2D} ctx
   * @param {number} x
   * @param {number} y
   * @param {number} w
   * @param {number} h
   * @param {Object} [opts]
   * @param {boolean} [opts.grid=false]   When true, overlay faint 36px blue grid lines.
   * @param {boolean} [opts.highlight=true] Stroke a 1px top-edge inner highlight.
   * @param {boolean} [opts.ambient=true] Shared top sunlight wash + bottom deep-water band (matches playfield).
   * @param {string} [opts.top]   Top stop color override (default: --surface-elev #EAF9FF).
   * @param {string} [opts.bot]   Bottom stop color override (default: --surface-deepest #92D8F2).
   */
  Game.prototype._paintLiftedSurface = function (ctx, x, y, w, h, opts) {
    if (!ctx || w <= 0 || h <= 0) return;
    opts = opts || {};
    var grid = opts.grid === true;
    var highlight = opts.highlight !== false;
    var ambient = opts.ambient !== false;
    var topColor = opts.top || "#EAF9FF";
    var botColor = opts.bot || "#92D8F2";

    ctx.save();
    var grad = ctx.createLinearGradient(0, y, 0, y + h);
    grad.addColorStop(0, topColor);
    grad.addColorStop(0.6, "#BFEAF9");
    grad.addColorStop(1, botColor);
    ctx.fillStyle = grad;
    ctx.fillRect(x, y, w, h);

    if (ambient) {
      var topLight = ctx.createRadialGradient(x + w * 0.5, y, 0, x + w * 0.5, y, h * 0.6);
      topLight.addColorStop(0, "rgba(255, 255, 255, 0.4)");
      topLight.addColorStop(1, "rgba(255, 255, 255, 0)");
      ctx.fillStyle = topLight;
      ctx.fillRect(x, y, w, h);
      var vign = ctx.createLinearGradient(0, y + h * 0.62, 0, y + h);
      vign.addColorStop(0, "rgba(38, 150, 199, 0)");
      vign.addColorStop(1, "rgba(38, 150, 199, 0.14)");
      ctx.fillStyle = vign;
      ctx.fillRect(x, y, w, h);
    }

    if (grid) {
      var tile = 36;
      ctx.strokeStyle = "rgba(31, 118, 160, 0.05)";
      ctx.lineWidth = 1;
      ctx.beginPath();
      for (var px = x + 0.5; px < x + w; px += tile) {
        ctx.moveTo(px, y);
        ctx.lineTo(px, y + h);
      }
      for (var py = y + 0.5; py < y + h; py += tile) {
        ctx.moveTo(x, py);
        ctx.lineTo(x + w, py);
      }
      ctx.stroke();
    }

    if (highlight) {
      ctx.strokeStyle = "rgba(255, 255, 255, 0.6)";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(x, y + 0.5);
      ctx.lineTo(x + w, y + 0.5);
      ctx.stroke();
    }
    ctx.restore();
  };

  Game.prototype._roundedRectPath = function (ctx, x, y, w, h, r) {
    r = Math.max(0, Math.min(r || 0, w / 2, h / 2));
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
  };

  /** Tier-0 body radius — smallest merge object; jar corners use the same value. */
  Game.prototype._getSmallestFlaskBodyRadius = function () {
    var sys = this.flaskSystem;
    if (!sys && typeof this.getFlaskSystem === "function") {
      sys = this.getFlaskSystem();
    }
    if (sys && sys.bodyRadiusByTier && sys.bodyRadiusByTier.length > 0) {
      var r = Number(sys.bodyRadiusByTier[0]);
      if (isFinite(r) && r > 0) return r;
    }
    return 6;
  };

  Game.prototype._getShakeJarCornerRadius = function (innerW, innerH) {
    var bodyR = this._getSmallestFlaskBodyRadius();
    var w = Math.max(0, Number(innerW) || 0);
    var h = Math.max(0, Number(innerH) || 0);
    return Math.max(0, Math.min(bodyR, w * 0.5, h));
  };

  Game.prototype.attachPixiRenderer = async function () {
    if (!this.canvas) return false;
    if (!this.usePixi || !canUsePixi()) {
      this.usePixi = false;
      if (!this.ctx) this.ctx = this.canvas.getContext("2d");
      return false;
    }
    try {
      var w = this.canvasCssWidth || (this.canvas.clientWidth) || 800;
      var h = this.canvasCssHeight || (this.canvas.clientHeight) || 600;
      var pixiMod = await import("../render/pixi/playfield.js");
      this.pixi = await pixiMod.createPixiPlayfield(this.canvas, {
        width: Math.max(100, w),
        height: Math.max(100, h),
        resolution: this._getCanvasDpr(),
      });
      this.ctx = this.pixi.ctx;
      return true;
    } catch (err) {
      console.warn("[pixi] init failed, using Canvas 2D:", err);
      this.usePixi = false;
      this.pixi = null;
      try {
        this.ctx = this.canvas.getContext("2d");
      } catch (e2) {
        this.ctx = null;
      }
      return false;
    }
  };

  Game.prototype._beginPlayfieldFrame = function () {
    if (this.pixi) this.pixi.beginFrame();
  };

  Game.prototype._presentPlayfield = function () {
    if (this.pixi) this.pixi.present();
  };

  /** Open-top jar (U): straight sides, rounded bottom corners, no top edge (night.html .jar). */
  Game.prototype._jarUPath = function (ctx, x, top, w, h, r) {
    ctx.beginPath();
    traceJarUPath(ctx, x, top, w, h, r, false);
  };

  /** Top-right corner only — matches `#app.stage-run .fall-right-hud-module__chassis` / `--panel-radius`. */
  Game.prototype._pathTopRightRoundedRect = function (ctx, x, y, w, h, r) {
    r = Math.max(0, Math.min(r || 0, w / 2, h / 2));
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h);
    ctx.lineTo(x, y + h);
    ctx.closePath();
  };

  Game.prototype.drawMutedBlueTiles = function (ctx, x, y, w, h) {
    var tileSize = 32;
    var base = "#8fa2b1";
    var alt = "#7f93a1";
    for (var py = 0; py < h; py += tileSize) {
      for (var px = 0; px < w; px += tileSize) {
        ctx.fillStyle = ((px / tileSize) + (py / tileSize)) % 2 === 0 ? base : alt;
        ctx.fillRect(x + px, y + py, Math.min(tileSize, w - px), Math.min(tileSize, h - py));
      }
    }
  };

  Game.prototype._getCanvasDpr = function () {
    if (typeof window === "undefined" || typeof window.devicePixelRatio !== "number") return 1;
    var dpr = window.devicePixelRatio;
    if (!isFinite(dpr) || dpr <= 0) return 1;
    var cap = C.CANVAS_MAX_DPR != null ? Number(C.CANVAS_MAX_DPR) : 2;
    if (!isFinite(cap) || cap <= 0) cap = 2;
    return Math.max(1, Math.min(cap, dpr));
  };

  Game.prototype.applyCanvasSize = function (cssWidth, cssHeight, opts) {
    if (!this.canvas || (!this.ctx && !this.pixi)) return false;
    opts = opts || {};
    var safeW = Math.max(100, Math.floor(cssWidth || 0));
    var safeH = Math.max(100, Math.floor(cssHeight || 0));
    var dpr = this._getCanvasDpr();
    if (this.pixi) {
      var pixiChanged = this.canvasCssWidth !== safeW
        || this.canvasCssHeight !== safeH
        || this.canvasDpr !== dpr;
      if (!opts.force && !pixiChanged) return false;
      this.pixi.resize(safeW, safeH, dpr);
      this.canvasCssWidth = safeW;
      this.canvasCssHeight = safeH;
      this.canvasDpr = dpr;
      this._invalidateSkyCache();
      if (opts.skipBoardViewport !== true) {
        this.applyBoardViewport(this.layout || { canvasWidth: safeW, canvasHeight: safeH });
      }
      return true;
    }
    var pixelW = Math.max(1, Math.round(safeW * dpr));
    var pixelH = Math.max(1, Math.round(safeH * dpr));
    var sizeChanged = this.canvas.width !== pixelW || this.canvas.height !== pixelH;
    if (!opts.force && !sizeChanged
        && this.canvasCssWidth === safeW && this.canvasCssHeight === safeH
        && this.canvasDpr === dpr) {
      return false;
    }
    if (sizeChanged || this.canvasDpr !== dpr) {
      if (sizeChanged) {
        this.canvas.width = pixelW;
        this.canvas.height = pixelH;
      }
      this._invalidateSkyCache();
    }
    if (this.canvas.style) {
      this.canvas.style.width = safeW + "px";
      this.canvas.style.height = safeH + "px";
    }
    this.canvasCssWidth = safeW;
    this.canvasCssHeight = safeH;
    this.canvasDpr = dpr;
    if (typeof this.ctx.setTransform === "function") {
      this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }
    if (opts.skipBoardViewport !== true) {
      this.applyBoardViewport(this.layout || { canvasWidth: safeW, canvasHeight: safeH });
    }
    return true;
  };

  Game.prototype._syncBoardViewportIfNeeded = function () {
    var layout = this.layout;
    if (!layout) return;
    var viewport = this.getViewportSize();
    var boardW = Math.max(1, layout.canvasWidth || 1);
    var boardH = Math.max(1, layout.canvasHeight || 1);
    var visualScale = 1;
    if (boardW > viewport.width || boardH > viewport.height) {
      visualScale = Math.min(viewport.width / boardW, viewport.height / boardH);
      if (!isFinite(visualScale) || visualScale <= 0) visualScale = 1;
      if (visualScale > 1) visualScale = 1;
    }
    var sig = boardW + "x" + boardH + "@" + viewport.width + "x" + viewport.height
      + "@" + visualScale.toFixed(4);
    if (this._boardViewportSig === sig) return;
    this._boardViewportSig = sig;
    this.applyBoardViewport(layout);
  };

  Game.prototype.getViewportSize = function () {
    var w = typeof window !== "undefined" ? window.innerWidth : 1200;
    var h = typeof window !== "undefined" ? window.innerHeight : 900;
    if (typeof window !== "undefined" && window.visualViewport) {
      var vv = window.visualViewport;
      if (vv.width > 0) w = vv.width;
      if (vv.height > 0) h = vv.height;
    }
    if (!w || w <= 0) w = 1200;
    if (!h || h <= 0) h = 900;
    return { width: w, height: h };
  };

  Game.prototype._ensureBoardShell = function () {
    var app = typeof document !== "undefined" ? document.getElementById("app") : null;
    if (!app || !app.parentNode) return null;
    var shell = document.getElementById("boardShell");
    if (!shell) {
      shell = document.createElement("div");
      shell.id = "boardShell";
      app.parentNode.insertBefore(shell, app);
      shell.appendChild(app);
    }
    return shell;
  };

  Game.prototype.getFixedBoardSize = function () {
    return this.getBoardSize();
  };

  /** Viewport-sized board (responsive layout). */
  Game.prototype.getBoardSize = function () {
    var vp = this.getViewportSize();
    return {
      width: Math.max(320, Math.floor(vp.width)),
      height: Math.max(240, Math.floor(vp.height)),
      isLandscape: true,
    };
  };

  Game.prototype.applyBoardViewport = function (layout) {
    var app = typeof document !== "undefined" ? document.getElementById("app") : null;
    if (!app || !layout) return;
    var shell = this._ensureBoardShell();
    var viewport = this.getViewportSize();
    var boardW = Math.max(1, layout.canvasWidth || 1);
    var boardH = Math.max(1, layout.canvasHeight || 1);
    var visualScale = 1;
    if (boardW > viewport.width || boardH > viewport.height) {
      visualScale = Math.min(viewport.width / boardW, viewport.height / boardH);
      if (!isFinite(visualScale) || visualScale <= 0) visualScale = 1;
      if (visualScale > 1) visualScale = 1;
    }
    var shellW = Math.max(1, Math.round(boardW * visualScale));
    var shellH = Math.max(1, Math.round(boardH * visualScale));
    app.style.width = boardW + "px";
    app.style.height = boardH + "px";
    app.style.transformOrigin = "0 0";
    app.style.transform = visualScale === 1 ? "" : "scale(" + visualScale + ")";
    if (shell && shell.style) {
      shell.style.width = shellW + "px";
      shell.style.height = shellH + "px";
      shell.style.overflow = "hidden";
      shell.style.margin = "0 auto";
    }
    this.viewportScale = visualScale;
    if (this._isDevMode() && this.dom) {
      relocateDevPerfOverlaysToShell(this.dom);
    }
  };

  Game.prototype.clientXToBoardX = function (clientX) {
    if (!this.canvas || !this.layout) return clientX;
    var rect = this.canvas.getBoundingClientRect ? this.canvas.getBoundingClientRect() : null;
    if (!rect || !rect.width) return clientX;
    var visualToBoard = this.layout.canvasWidth / rect.width;
    return (clientX - rect.left) * visualToBoard;
  };

  /** Latest known pointer clientX, or null if the cursor has never moved this session. */
  Game.prototype.getLastPointerClientX = function () {
    var x = this._lastPointerClientX;
    return x != null && isFinite(x) ? x : null;
  };

  Game.prototype._trackPointerClientPosition = function (e) {
    if (!e || e.clientX == null) return;
    var x = Number(e.clientX);
    if (!isFinite(x)) return;
    this._lastPointerClientX = x;
  };

  Game.prototype._bindPointerClientTracking = function () {
    if (typeof window === "undefined" || this._boundPointerClientTrack) return;
    this._boundPointerClientTrack = this._trackPointerClientPosition.bind(this);
    window.addEventListener("pointermove", this._boundPointerClientTrack, { passive: true });
    window.addEventListener("pointerdown", this._boundPointerClientTrack, { passive: true });
    window.addEventListener("mousemove", this._boundPointerClientTrack, { passive: true });
  };

  Game.prototype.clientYToBoardY = function (clientY) {
    if (!this.canvas || !this.layout) return clientY;
    var rect = this.canvas.getBoundingClientRect ? this.canvas.getBoundingClientRect() : null;
    if (!rect || !rect.height) return clientY;
    var visualToBoard = this.layout.canvasHeight / rect.height;
    return (clientY - rect.top) * visualToBoard;
  };

  Game.prototype._panelPointFromClient = function (clientX, clientY) {
    var layout = this.layout || this.getLayout();
    var boardX = this.clientXToBoardX(clientX);
    var boardY = this.clientYToBoardY(clientY);
    var origin = layout.flaskPanelOrigin || { x: 0, y: 0 };
    return {
      panelX: boardX - origin.x,
      panelY: boardY - origin.y,
    };
  };

  Game.prototype._isPanelPointInFlask = function (panelX, panelY) {
    var layout = this.layout;
    var b = layout && layout.flaskBounds;
    if (!b || b.left == null || b.right == null || b.top == null || b.bottom == null) {
      return false;
    }
    return panelX >= b.left && panelX <= b.right && panelY >= b.top && panelY <= b.bottom;
  };

  /** Upper panel-Y bound for Merge drop hits (preview + queue above the rim). */
  Game.prototype._getDropZoneTopPanelY = function () {
    var layout = this.layout;
    var b = layout && layout.flaskBounds;
    if (!b || b.top == null) return null;
    var abovePx = C.FLASK_DROP_ZONE_ABOVE_PX != null ? Number(C.FLASK_DROP_ZONE_ABOVE_PX) : 90;
    if (!isFinite(abovePx) || abovePx < 0) abovePx = 90;
    var zoneTop = b.top - abovePx;

    var row = this.dom && this.dom.shakeHpRow;
    if (row && row.classList && !row.classList.contains("hidden")
      && typeof row.getBoundingClientRect === "function") {
      var appEl = typeof document !== "undefined" ? document.getElementById("app") : null;
      if (appEl) {
        var rowBoard = this._clientRectToBoardSpace(appEl, row.getBoundingClientRect());
        if (rowBoard) {
          var origin = layout.flaskPanelOrigin || { x: 0, y: 0 };
          var hpBottom = rowBoard.top + rowBoard.height - origin.y;
          if (isFinite(hpBottom)) zoneTop = Math.max(zoneTop, hpBottom);
        }
      }
    }
    return zoneTop;
  };

  /** Merge drop hit zone: flask column horizontally, rim interior + band above for preview/queue. */
  Game.prototype._isPanelPointInDropZone = function (panelX, panelY) {
    var layout = this.layout;
    var b = layout && layout.flaskBounds;
    if (!b || b.left == null || b.right == null || b.bottom == null) return false;
    if (panelX < b.left || panelX > b.right) return false;
    if (panelY > b.bottom) return false;
    var zoneTop = this._getDropZoneTopPanelY();
    if (zoneTop == null) return false;
    return panelY >= zoneTop;
  };

  /** Map a viewport `DOMRect` into `#app` board coordinates (undo `#app` CSS scale). */
  Game.prototype._clientRectToBoardSpace = function (appEl, rect) {
    if (!appEl || !rect || typeof appEl.getBoundingClientRect !== "function") return null;
    var appR = appEl.getBoundingClientRect();
    var scale = this.viewportScale || 1;
    if (!isFinite(scale) || scale <= 0) scale = 1;
    return {
      left: (rect.left - appR.left) / scale,
      top: (rect.top - appR.top) / scale,
      width: rect.width / scale,
      height: rect.height / scale,
    };
  };

  /** Layout fallback for `.shake-controls` inner span in board px. */
  Game.prototype._getShakeControlsBoardRect = function () {
    this.layout = this.layout || this.getLayout();
    var rb = this.layout && this.layout.runBounds;
    if (!rb) return null;
    var padX = 17.6;
    var ui = this.dom && this.dom.shakeUI;
    if (ui && typeof getComputedStyle === "function") {
      var uiSt = getComputedStyle(ui);
      var pl = parseFloat(uiSt.paddingLeft);
      var pr = parseFloat(uiSt.paddingRight);
      if (isFinite(pl) && isFinite(pr)) padX = (pl + pr) / 2;
    }
    var innerW = Math.max(0, rb.width - padX * 2);
    return {
      left: rb.x + padX,
      width: innerW,
    };
  };

  function sameBounds(a, b) {
    if (!a || !b) return false;
    return a.left === b.left && a.right === b.right && a.top === b.top && a.bottom === b.bottom;
  }

  Game.prototype.getProgressStorageKey = function () {
    return getStorageKey(StorageSlot.PROGRESS);
  };

  Game.prototype.getEconomyStorageKey = function () {
    return getStorageKey(StorageSlot.ECONOMY);
  };

  Game.prototype.getBestScoreStorageKey = function () {
    return getStorageKey(StorageSlot.BEST_SCORE);
  };

  Game.prototype._savePlayerStats = function () {
    if (this.testFlaskMode) return;
    savePlayerStats(this.playerStats);
  };

  Game.prototype._refreshEvolutionRunProgressUI = function () {
    if (this._evoRevealHold || this._isEvolutionRevealPending()) return;
    this.populateEvolutionChrome();
  };

  Game.prototype._noteTierForAnalytics = function (tier) {
    if (this.testFlaskMode) return;
    var t = Math.max(0, Math.floor(Number(tier) || 0));
    var prevRunMax = this.currentGameMaxTier;
    if (t > this.currentGameMaxTier) this.currentGameMaxTier = t;
    if (this.currentGameMaxTier > prevRunMax) {
      this._refreshEvolutionRunProgressUI();
    }
    if (this.playerStats) {
      var tierResult = recordMaxTierEver(this.playerStats, t);
      this.playerStats = tierResult.stats;
      this._savePlayerStats();
      for (var i = 0; i < tierResult.newTiers.length; i++) {
        var chainIdx = this._tierIdToChainIndex(tierResult.newTiers[i]);
        this._trackAnalytics(discoveredTierEventName(chainIdx));
        this._notePuzzlePieceForTier(tierResult.newTiers[i]);
        this._pokiReachTier(chainIdx);
      }
    }
  };

  /**
   * Queue a puzzle reveal when a lifetime-new tier grants a piece. Merge stage only —
   * a piece earned by a Fall catch waits until the player is back in Merge.
   * @param {number} tierId
   */
  Game.prototype._notePuzzlePieceForTier = function (tierId) {
    var piece = puzzlePieceForTierId(tierId);
    if (!piece) return;
    if (!this.dom || !this.dom.puzzleRevealOverlay) return;
    // Highest pending piece wins if two land at once (e.g. a multi-tier jump).
    if (piece > this._pendingPuzzlePiece) this._pendingPuzzlePiece = piece;
    this._schedulePuzzleRevealShow();
  };

  /** Wait for the discovery ceremony to finish, then open the puzzle overlay. */
  Game.prototype._schedulePuzzleRevealShow = function () {
    var self = this;
    if (!this._pendingPuzzlePiece) return;
    if (this._puzzleRevealActive || this._puzzleRevealTimer) return;
    if (!(this.stateMachine && this.stateMachine.is && this.stateMachine.is("shake"))) return;
    // Discovery ceremony and victory popup both own the screen first.
    if (this._isEvolutionRevealPending()) return;
    if (this._victoryPopupActive || this._pendingVictoryPopup) return;

    this._puzzleRevealTimer = setTimeout(function () {
      self._puzzleRevealTimer = null;
      self._openPuzzleReveal();
    }, 300);
  };

  /** @param {{ force?: boolean }} [opts] force skips the Merge-stage/queue gates (dev preview). */
  Game.prototype._openPuzzleReveal = function (opts) {
    var force = !!(opts && opts.force);
    var piece = this._pendingPuzzlePiece;
    if (!piece) return;
    if (!force && !(this.stateMachine && this.stateMachine.is && this.stateMachine.is("shake"))) return;
    if (!force && (this._isEvolutionRevealPending() || this._victoryPopupActive)) {
      this._schedulePuzzleRevealShow();
      return;
    }
    var dom = this.dom || {};
    if (!dom.puzzleRevealOverlay) return;

    this._pendingPuzzlePiece = 0;
    this._puzzleRevealActive = true;
    if (dom.puzzleRevealHeadline) dom.puzzleRevealHeadline.textContent = puzzleRevealHeadline(piece);
    if (dom.puzzleRevealCount) dom.puzzleRevealCount.textContent = puzzleRevealCountLabel(piece);
    renderPuzzleBoard(dom.puzzleRevealBoard, piece);
    this._showOverlayWithFade(dom.puzzleRevealOverlay);
    this._pauseForDialog();
    this._trackAnalytics(AnalyticsEvents.PUZZLE_PIECE_REVEALED, { puzzle_piece: piece });
    var self = this;
    // Let the new piece glow, then settle so the finished picture reads cleanly.
    this._puzzleRevealTimer = setTimeout(function () {
      self._puzzleRevealTimer = null;
      var board = self.dom && self.dom.puzzleRevealBoard;
      var glow = board && board.querySelector(".puzzle-reveal-overlay__piece--new");
      if (glow && glow.classList) glow.classList.remove("puzzle-reveal-overlay__piece--new");
    }, PUZZLE_REVEAL_GLOW_MS);
  };

  /** OK button — close and hand the flask back. */
  Game.prototype.closePuzzleReveal = function () {
    if (!this._puzzleRevealActive) return;
    this._puzzleRevealActive = false;
    if (this._puzzleRevealTimer) {
      clearTimeout(this._puzzleRevealTimer);
      this._puzzleRevealTimer = null;
    }
    this._hideOverlayWithFade(this.dom && this.dom.puzzleRevealOverlay);
    this._resumeFromDialog();
  };

  /**
   * DEV ONLY — step through the four reveals using the real open path (pause,
   * glow, OK-close), so the overlay can be checked without grinding to T-rex.
   */
  Game.prototype.devPreviewPuzzleReveal = function () {
    if (this._puzzleRevealActive) {
      this.closePuzzleReveal();
      return;
    }
    var next = (Math.floor(Number(this._devPuzzlePreviewPiece) || 0) % PUZZLE_PIECE_COUNT) + 1;
    this._devPuzzlePreviewPiece = next;
    this._pendingPuzzlePiece = next;
    this._openPuzzleReveal({ force: true });
  };

  /** @returns {number} pieces owned, derived from lifetime max tier. */
  Game.prototype.getPuzzlePieceCount = function () {
    var stats = this.playerStats || {};
    return puzzlePiecesForMaxTier(stats.maxTierEver);
  };

  Game.prototype._getRunStartMaxTier = function () {
    var n = C.RUN_START_MAX_TIER != null ? Number(C.RUN_START_MAX_TIER) : 2;
    return Math.max(0, Math.floor(isFinite(n) ? n : 2));
  };

  Game.prototype._applyBaselineUnlockedTiers = function () {
    var ids = C.RUN_START_UNLOCKED_TIER_IDS;
    if (!ids || !ids.length) ids = [0, 1, 2];
    var changed = false;
    for (var i = 0; i < ids.length; i++) {
      var id = Math.floor(Number(ids[i]) || 0);
      if (!this.unlockedTiers[id]) {
        this.unlockedTiers[id] = true;
        changed = true;
      }
    }
    this.unlockedTiers[0] = true;
    if (changed) this.saveUnlockedTiers();
  };

  /** Seed first-Fall runway cap + lifetime unlocks for tiers 0..RUN_START_MAX_TIER. */
  Game.prototype._applyRunStartTierBaseline = function () {
    this._applyBaselineUnlockedTiers();
    this.currentGameMaxTier = Math.max(
      Math.max(0, Math.floor(Number(this.currentGameMaxTier) || 0)),
      this._getRunStartMaxTier()
    );
  };

  Game.prototype._resetCurrentGameTierForAnalytics = function () {
    this.currentGameMaxTier = this._getRunStartMaxTier();
    this.currentCycleIndex = 0;
    this._runPlayTimeMs = 0;
    this._pokiProgress = createPokiProgressState();
  };

  Game.prototype._beginAnalyticsPlayTime = function () {
    if (this.testFlaskMode || this._analyticsPlayTimeSince != null) return;
    this._analyticsPlayTimeSince = typeof performance !== "undefined" ? performance.now() : Date.now();
  };

  Game.prototype._endAnalyticsPlayTime = function () {
    if (this.testFlaskMode || !this.playerStats || this._analyticsPlayTimeSince == null) return;
    var now = typeof performance !== "undefined" ? performance.now() : Date.now();
    var delta = Math.max(0, Math.floor(now - this._analyticsPlayTimeSince));
    this._analyticsPlayTimeSince = null;
    if (delta > 0) {
      this.playerStats = addPlayTimeMs(this.playerStats, delta);
      this._runPlayTimeMs = Math.max(0, Math.floor(Number(this._runPlayTimeMs) || 0)) + delta;
      this._savePlayerStats();
    }
  };

  Game.prototype._getRunPlayTimeMs = function (now) {
    var total = Math.max(0, Math.floor(Number(this._runPlayTimeMs) || 0));
    if (this._analyticsPlayTimeSince == null) return total;
    var t = now != null && isFinite(now)
      ? now
      : (typeof performance !== "undefined" ? performance.now() : Date.now());
    return total + Math.max(0, Math.floor(t - this._analyticsPlayTimeSince));
  };

  Game.prototype._syncBestScoreToPlayerStats = function () {
    if (this.testFlaskMode || !this.playerStats) return;
    var before = this.playerStats.bestScore;
    this.playerStats = syncBestScore(this.playerStats, this.bestScore);
    if (this.playerStats.bestScore !== before) this._savePlayerStats();
  };

  Game.prototype._recomputeCurrentGameMaxTierFromFlask = function () {
    var sys = this.getFlaskSystem();
    if (!sys || !sys.bodies) return;
    for (var i = 0; i < sys.bodies.length; i++) {
      var m = sys.bodies[i];
      if (m && m.tier != null) this._noteTierForAnalytics(m.tier);
    }
  };

  Game.prototype._maybeRecordRunStartForNewRun = function () {
    if (this.testFlaskMode || !this.playerStats) return;
    this.playerStats = recordRunStart(this.playerStats);
    this._savePlayerStats();
  };

  Game.prototype._recordAnalyticsGameLoss = function (reason) {
    if (this.testFlaskMode || !this.playerStats) return;
    this._endAnalyticsPlayTime();
    this.playerStats = recordGameLoss(this.playerStats, {
      reason: reason === "overflow" ? "overflow" : "flask_broken",
      scoreAtLoss: Math.max(0, Math.floor(Number(this.currentScore) || 0)),
      bestScore: Math.max(0, Math.floor(Number(this.bestScore) || 0)),
    });
    this._savePlayerStats();
  };

  Game.prototype._initPlayerStats = function () {
    this.playerStats = recordSessionStart(loadPlayerStats());
    this.playerStats = syncBestScore(this.playerStats, this.bestScore);
    this._savePlayerStats();
  };

  Game.prototype._tierIdToChainIndex = function (tierId) {
    var chain = (typeof C.getActiveEvolutionChain === "function")
      ? C.getActiveEvolutionChain()
      : (C.EVOLUTION_CHAIN || []);
    var t = Math.max(0, Math.floor(Number(tierId) || 0));
    for (var i = 0; i < chain.length; i++) {
      var entry = chain[i];
      if (entry && entry.id != null && Math.floor(Number(entry.id)) === t) return i;
    }
    return t;
  };

  Game.prototype._getAnalyticsHp = function () {
    if (this.runPhase && this.runPhase.player && this.stateMachine
      && this.stateMachine.is && this.stateMachine.is("fall")) {
      return Math.max(0, Math.floor(Number(this.runPhase.player.hp) || 0));
    }
    return Math.max(0, Math.floor(Number(this.savedFlaskHp) || 0));
  };

  Game.prototype.getPlayerStatsSnapshot = function () {
    return toPlayerStatsSnapshot(this.playerStats || loadPlayerStats(), {
      currentGameMaxTier: this.currentGameMaxTier,
      cycleIndex: this.currentCycleIndex,
      stage: this.analyticsStage,
      score: Math.max(0, Math.floor(Number(this.currentScore) || 0)),
      hp: this._getAnalyticsHp(),
      gems: Math.max(0, Math.floor(Number(this.gemsCollected) || 0)),
    });
  };

  Game.prototype._initAnalyticsTracker = function () {
    if (this.testFlaskMode || this.opts.disableAnalytics) return;
    if (this.opts.analyticsTracker) {
      this.analyticsTracker = this.opts.analyticsTracker;
      if (typeof this.analyticsTracker.init === "function") {
        this.analyticsTracker.init();
      }
      return;
    }
    var self = this;
    this.analyticsTracker = createAnalyticsTracker({
      getStatsSnapshot: function () { return self.getPlayerStatsSnapshot(); },
      enabled: this.opts.enableAnalytics !== false,
    });
    this.analyticsTracker.init();
  };

  Game.prototype._initPerfMonitor = function () {
    if (this.testFlaskMode || this.opts.disableAnalytics) return;
    var self = this;
    this._perfUserProps = {};
    this._perfUserPropsDirty = false;
    this.perfMonitor = createPerfMonitor({
      onDisplayHzReady: function (hz) {
        self._displayHzEst = hz;
        self._setPerfUserProperty("display_hz_est", hz);
        if (self._skyAutoProbe && typeof self._skyAutoProbe.setDisplayHz === "function") {
          self._skyAutoProbe.setDisplayHz(hz);
        }
        if (
          self._skyCycleUpgradeProbe
          && typeof self._skyCycleUpgradeProbe.setDisplayHz === "function"
        ) {
          self._skyCycleUpgradeProbe.setDisplayHz(hz);
        }
      },
    });
    var cssW = this.canvasCssWidth || (this.layout && this.layout.canvasWidth) || 0;
    var cssH = this.canvasCssHeight || (this.layout && this.layout.canvasHeight) || 0;
    this._setPerfUserProperty("canvas_dpr", Math.floor(this._getCanvasDpr()));
    this._setPerfUserProperty("viewport_bucket", getViewportBucket(cssW, cssH));
    this._setPerfUserProperty("is_touch", detectIsTouch());

    this._setPerfUserProperty("layout_mode", this._getCurrentLayoutMode());
  };

  Game.prototype._setPerfUserProperty = function (key, value) {
    if (this.testFlaskMode || this.opts.disableAnalytics) return;
    if (!this._perfUserProps) this._perfUserProps = {};
    if (this._perfUserProps[key] === value) return;
    this._perfUserProps[key] = value;
    this._perfUserPropsDirty = true;
  };

  Game.prototype._flushPerfUserProperties = function () {
    if (this.testFlaskMode || this.opts.disableAnalytics) return;
    if (!this._perfUserPropsDirty) return;
    if (!this._perfUserProps || Object.keys(this._perfUserProps).length === 0) return;
    var analytics = this.analyticsTracker && this.analyticsTracker.analytics;
    if (!analytics) return;
    applyPerfAnalyticsUserProperties(analytics, this._perfUserProps);
    this._perfUserPropsDirty = false;
  };

  /**
   * Returns the currently effective layout mode ("horizontal" or "vertical").
   * Safe to call early (before this.layout is populated).
   */
  Game.prototype._getCurrentLayoutMode = function () {
    try {
      if (this.layout && this.layout.layoutMode) {
        return this.layout.layoutMode;
      }
      if (typeof this.getLayout === "function") {
        var l = this.getLayout();
        if (l && l.layoutMode) {
          return l.layoutMode;
        }
      }
      // Fallback using direct resolve (needs board size)
      if (typeof this.getBoardSize === "function" && typeof resolveEffectiveLayoutMode === "function") {
        var board = this.getBoardSize();
        var opts = (typeof this._getLayoutComputeOpts === "function") ? this._getLayoutComputeOpts() : {};
        var pref = (typeof this._getLayoutModePreference === "function") ? this._getLayoutModePreference() : "auto";
        var mode = resolveEffectiveLayoutMode(
          { width: board.width, height: board.height },
          pref,
          opts
        );
        if (mode === "vertical" || mode === "horizontal") {
          return mode;
        }
      }
    } catch (e) {
      // ignore
    }
    return "horizontal";
  };

  /** Sync (or update) the current layout_mode user property and flush it. */
  Game.prototype._syncLayoutAnalytics = function () {
    if (this.testFlaskMode || this.opts.disableAnalytics) return;
    this._setPerfUserProperty("layout_mode", this._getCurrentLayoutMode());
    this._flushPerfUserProperties();
  };

  /**
   * @param {number} now
   * @param {"fall" | "merge" | "transition"} perfLoop
   * @param {{ frameMs?: number, bodies?: number }} [opts]
   */
  Game.prototype._tickPerfMonitor = function (now, perfLoop, opts) {
    if (this.testFlaskMode || !this.perfMonitor || this._isSimPaused()) return;
    this._flushPerfUserProperties();
    var frameOpts = opts || {};
    var payload = this.perfMonitor.tick(now, perfLoop, frameOpts);
    if (payload) {
      this._trackAnalytics(AnalyticsEvents.PERF_LOW_FPS, payload);
      this._degradeSkyBackgroundFromPerfEpisode();
    }
    this._tickSkyCycleUpgradeProbe(now, frameOpts.frameMs);
  };

  /**
   * @param {string} eventName
   * @param {Record<string, unknown>} [extraParams]
   */
  Game.prototype._trackAnalytics = function (eventName, extraParams) {
    if (this.testFlaskMode || !this.analyticsTracker) return;
    this._flushPerfUserProperties();
    this.analyticsTracker.track(eventName, extraParams || {});
  };

  Game.prototype._trackRunStart = function (startSource) {
    this._trackAnalytics(AnalyticsEvents.RUN_START, {
      start_source: startSource || "menu",
    });
    if (FrvrBridge.isEnabled()) {
      var runIndex = this.playerStats && this.playerStats.runIndex != null
        ? Math.max(0, Math.floor(Number(this.playerStats.runIndex) || 0))
        : 0;
      this._frvrActiveLevelId = "run_" + runIndex;
      FrvrBridge.trackLevelStart(this._frvrActiveLevelId);
    }
  };

  Game.prototype._trackFallStart = function (startSource) {
    this._trackAnalytics(AnalyticsEvents.FALL_START, {
      fall_start_source: startSource || "menu",
    });
    this._pokiStage("start", "day");
  };

  Game.prototype._trackFallCountdownFinished = function (startSource) {
    this._trackAnalytics(AnalyticsEvents.FALL_COUNTDOWN_FINISHED, {
      fall_start_source: startSource || "menu",
    });
  };

  Game.prototype._onFallCountdownFinished = function () {
    if (this.testFlaskMode) return;
    this._finishSkyAutoProbe();
    this._trackFallCountdownFinished(this._pendingFallCountdownSource || "menu");
    this._pendingFallCountdownSource = null;
    this._syncPlatformGameplay();
  };

  Game.prototype._trackMergeStart = function (startSource) {
    this._trackAnalytics(AnalyticsEvents.MERGE_START, {
      merge_start_source: startSource || "fall",
    });
    this._pokiEnterNight();
  };

  Game.prototype._emitPokiProgress = function (events) {
    if (this.testFlaskMode || !events || !events.length) return;
    if (!PokiBridge || typeof PokiBridge.measure !== "function") return;
    for (var i = 0; i < events.length; i++) {
      var ev = events[i];
      if (!ev) continue;
      PokiBridge.measure(ev.category, ev.what, ev.action);
    }
  };

  /**
   * @param {"start" | "complete" | "fail"} action
   * @param {"day" | "night"} [half]
   */
  Game.prototype._pokiStage = function (action, half) {
    if (this.testFlaskMode) return;
    if (!this._pokiProgress) this._pokiProgress = createPokiProgressState();
    this._emitPokiProgress(stageProgressEvents(this._pokiProgress, action, this.currentCycleIndex, half));
  };

  Game.prototype._pokiEnterNight = function () {
    if (this.testFlaskMode) return;
    if (!this._pokiProgress) this._pokiProgress = createPokiProgressState();
    this._emitPokiProgress(enterNightStageEvents(this._pokiProgress, this.currentCycleIndex));
  };

  /** @param {number} chainIndex 0-based chain position just discovered */
  Game.prototype._pokiReachTier = function (chainIndex) {
    if (this.testFlaskMode) return;
    this._emitPokiProgress(tierReachedEvent(chainIndex));
  };

  Game.prototype._resetStageKeyboardAnalytics = function () {
    this._stageKeysLeft = false;
    this._stageKeysRight = false;
    this._stageKeysDown = false;
  };

  /**
   * @param {"left" | "right" | "down"} key
   */
  Game.prototype._recordStageKeyboardKey = function (key) {
    if (key === "left") this._stageKeysLeft = true;
    else if (key === "right") this._stageKeysRight = true;
    else if (key === "down") this._stageKeysDown = true;
  };

  /**
   * @param {"fall" | "merge"} stage
   * @returns {{ used_keyboard: 0 | 1 }}
   */
  Game.prototype._getStageUsedKeyboardParam = function (stage) {
    if (detectIsTouch() === 1) return { used_keyboard: 0 };
    if (stage === "fall") {
      return { used_keyboard: (this._stageKeysLeft && this._stageKeysRight) ? 1 : 0 };
    }
    if (stage === "merge") {
      return {
        used_keyboard: (this._stageKeysLeft && this._stageKeysRight && this._stageKeysDown) ? 1 : 0,
      };
    }
    return { used_keyboard: 0 };
  };

  Game.prototype.loadBestScore = function () {
    try {
      var raw = readStorageRecord(
        this._getProgressStorage(),
        StorageSlot.BEST_SCORE
      );
      return Math.max(0, Math.floor(Number(raw) || 0));
    } catch (e) {
      return 0;
    }
  };

  Game.prototype._notifyProgressSaved = function (key, value) {
    if (typeof this.opts.onProgressSaved !== "function") return;
    try {
      this.opts.onProgressSaved(key, value);
    } catch (e) {
      console.warn("[Game] onProgressSaved failed:", e);
    }
  };

  Game.prototype.saveBestScore = function () {
    var bestKey = this.getBestScoreStorageKey();
    var bestVal = Math.max(0, this.bestScore || 0);
    try {
      writeStorageRecord(
        this._getProgressStorage(),
        StorageSlot.BEST_SCORE,
        bestVal
      );
      var encoded = readStorageEncoded(
        this._getProgressStorage(),
        StorageSlot.BEST_SCORE
      );
      if (encoded) this._notifyProgressSaved(bestKey, encoded);
    } catch (e) { /* ignore */ }
    this._syncBestScoreToPlayerStats();
  };

  Game.prototype.getDefaultEconomyState = function () {
    return {
      gemsCollected: 0,
      shakeUses: this.constants.SHAKE_USES_DEFAULT,
      bombTripleUses: this.constants.BOMB_THREE_TIER_USES_DEFAULT,
      minimizeUses: this.constants.MINIMIZE_USES_DEFAULT,
      bombCellUses: this.constants.BOMB_CELL_USES_DEFAULT,
    };
  };

  Game.prototype.getDefaultProgressState = function () {
    return {
      currentScore: 0,
      flaskHp: this.flaskMaxHp,
      flaskBodies: [],
      hasCompletedARun: false,
      cycleIndex: 0,
      resumeStage: "fall",
      savedAt: Date.now(),
      victoryShownThisRun: false,
    };
  };

  Game.prototype._getProgressStorage = function () {
    return resolveGameStorage(null);
  };

  Game.prototype.applyEconomyState = function (state) {
    var s = state || this.getDefaultEconomyState();
    this.gemsCollected = Math.max(0, Number(s.gemsCollected) || 0);
    this.shakeUses = Math.max(0, normalizeUseCount(s.shakeUses, this.constants.SHAKE_USES_DEFAULT));
    this.bombTripleUses = Math.max(0, normalizeUseCount(s.bombTripleUses, this.constants.BOMB_THREE_TIER_USES_DEFAULT));
    this.minimizeUses = Math.max(0, normalizeUseCount(s.minimizeUses, this.constants.MINIMIZE_USES_DEFAULT));
    this.bombCellUses = Math.max(0, normalizeUseCount(s.bombCellUses, this.constants.BOMB_CELL_USES_DEFAULT));
    this.updateGlobalGemDisplay();
  };

  Game.prototype.loadEconomyState = function () {
    try {
      var parsed = readStorageRecord(this._getProgressStorage(), StorageSlot.ECONOMY);
      if (parsed && typeof parsed === "object") return parsed;
      var progress = readStorageRecord(this._getProgressStorage(), StorageSlot.PROGRESS);
      return extractEconomyFromProgress(progress);
    } catch (e) {
      console.warn("[Game] Failed to load economy:", e);
      return null;
    }
  };

  Game.prototype.loadAndApplyEconomyState = function () {
    var loaded = this.loadEconomyState();
    if (loaded) {
      this.applyEconomyState(loaded);
      return;
    }
    this.applyEconomyState(this.getDefaultEconomyState());
  };

  Game.prototype._applyEconomyFromCheckpoint = function (progressState) {
    var economy = extractEconomyFromProgress(progressState);
    if (economy) {
      this.applyEconomyState(economy);
      return true;
    }
    var slotEconomy = readStorageRecord(this._getProgressStorage(), StorageSlot.ECONOMY);
    if (slotEconomy) {
      this.applyEconomyState(slotEconomy);
      return true;
    }
    return false;
  };

  Game.prototype._syncEconomySlotToCheckpoint = function (progressState) {
    var economy = extractEconomyFromProgress(progressState);
    if (!economy) return;
    economy.savedAt = progressState.savedAt != null ? progressState.savedAt : Date.now();
    writeStorageRecord(this._getProgressStorage(), StorageSlot.ECONOMY, economy);
    this._relayStorageSlot(StorageSlot.ECONOMY);
  };

  Game.prototype.captureEconomySnapshot = function () {
    return {
      gemsCollected: Math.max(0, this.gemsCollected || 0),
      shakeUses: Math.max(0, normalizeUseCount(this.shakeUses, 0)),
      bombTripleUses: Math.max(0, normalizeUseCount(this.bombTripleUses, 0)),
      minimizeUses: Math.max(0, normalizeUseCount(this.minimizeUses, 0)),
      bombCellUses: Math.max(0, normalizeUseCount(this.bombCellUses, 0)),
    };
  };

  Game.prototype._relayStorageSlot = function (slot) {
    var storage = this._getProgressStorage();
    if (!storage) return;
    var key = getStorageKey(slot);
    var encoded = readStorageEncoded(storage, slot);
    if (encoded) this._notifyProgressSaved(key, encoded);
  };

  /** Persist lifetime economy only (no run checkpoint), e.g. after Play Again. */
  Game.prototype._persistLifetimeEconomy = function (savedAt) {
    if (this.testFlaskMode) return;
    try {
      var economy = this.captureEconomySnapshot();
      economy.savedAt = savedAt != null ? savedAt : Date.now();
      writeStorageRecord(this._getProgressStorage(), StorageSlot.ECONOMY, economy);
      this._relayStorageSlot(StorageSlot.ECONOMY);
    } catch (e) {
      console.warn("[Game] Failed to save economy:", e);
    }
  };

  /**
   * Atomically persist run checkpoint + economy with the same savedAt timestamp.
   * @param {Record<string, unknown>} progressPayload run fields (savedAt optional)
   * @returns {boolean}
   */
  Game.prototype._persistCheckpointPair = function (progressPayload) {
    if (this.testFlaskMode) return false;
    var storage = this._getProgressStorage();
    if (!storage) {
      reportStorageBackendUnavailable(
        storage,
        "[Game] Checkpoint save failed: no game storage (override unset and localStorage unavailable)"
      );
      return false;
    }
    var savedAt = progressPayload.savedAt != null ? progressPayload.savedAt : Date.now();
    var economy = this.captureEconomySnapshot();
    economy.savedAt = savedAt;
    var progress = Object.assign({}, progressPayload, { savedAt: savedAt });
    if (!writeCheckpointStorage(storage, progress, economy)) {
      console.warn("[Game] Failed to validate checkpoint pair");
      return false;
    }
    this._relayStorageSlot(StorageSlot.PROGRESS);
    this._relayStorageSlot(StorageSlot.ECONOMY);
    return true;
  };

  Game.prototype._hasExplicitSavedCycleIndex = function (state) {
    if (!state || typeof state !== "object") return false;
    if (!Object.prototype.hasOwnProperty.call(state, "cycleIndex")) return false;
    var n = Number(state.cycleIndex);
    return isFinite(n);
  };

  Game.prototype._restoreCycleIndexFromProgress = function (state) {
    if (!state) {
      this.currentCycleIndex = 0;
      return;
    }
    var hasExplicit = this._hasExplicitSavedCycleIndex(state);
    var cycleIndex = hasExplicit
      ? Math.max(0, Math.floor(Number(state.cycleIndex)))
      : 0;
    // Legacy saves omitted cycleIndex — infer "day 2+" when a Fall was already completed.
    if (!hasExplicit && (!!state.hasCompletedARun || state.resumeStage === "shake")) {
      cycleIndex = 1;
    }
    this.currentCycleIndex = cycleIndex;
  };

  /** Align sky clock base hour with restored cycle index before merge/fall resume. */
  Game.prototype._syncSkyClockCycleBaseFromProgress = function () {
    if (!this.skyClock) return;
    var stage = this.resumeStage === "shake" ? "shake" : "fall";
    this.skyClock.cycleBaseHour = cycleBaseHourFromCycleIndex(this.currentCycleIndex, stage);
  };

  Game.prototype._isFirstFallCycleOfRun = function () {
    return Math.max(0, Math.floor(Number(this.currentCycleIndex) || 0)) === 0;
  };

  /** Lifetime first run only (`runIndex === 0`) and first Fall of that run (`cycleIndex === 0`). */
  Game.prototype._isFirstFallOfFirstRun = function () {
    if (!this._isFirstFallCycleOfRun()) return false;
    var runIndex = this.playerStats && this.playerStats.runIndex != null
      ? Math.max(0, Math.floor(Number(this.playerStats.runIndex) || 0))
      : 0;
    return runIndex === 0;
  };

  /**
   * NEXT DAY › interstitial cadence shared by Y8 / YouTube / Playgama / FRVR.
   * Day 5, then every 4 days: `cycleIndex` before continue is 4, 8, 12, …
   * Not gated on `runIndex`. CrazyGames has no interstitials. Poki and
   * GameDistribution unlock at `cycleIndex` >= 4 and then request every NEXT DAY.
   */
  Game.prototype._isNextDayInterstitialCycle = function () {
    var cycleIndex = Math.max(0, Math.floor(Number(this.currentCycleIndex) || 0));
    return cycleIndex >= 4 && cycleIndex % 4 === 0;
  };

  Game.prototype._pokiCommercialUnlockCycle = function () {
    return Math.max(0, Math.floor(Number(this.currentCycleIndex) || 0)) >= 4;
  };

  /**
   * Poki `commercialBreak()` unlock: after cycle 4, signal every rest and let
   * Poki decide whether an ad plays. No every-N-days countdown.
   */
  Game.prototype.shouldOfferPokiCommercialBreak = function () {
    return PokiBridge.isEnabled() && this._pokiCommercialUnlockCycle();
  };

  /**
   * Y8 NEXT DAY › interstitial (`type: "next"`).
   */
  Game.prototype.shouldShowY8NextDayInterstitial = function () {
    return this._isNextDayInterstitialCycle();
  };

  /**
   * YouTube NEXT DAY › interstitial (`ytgame.ads.requestInterstitialAd`).
   * Same cycle cadence as Y8; requires a YouTube build.
   */
  Game.prototype.shouldShowYtNextDayInterstitial = function () {
    return YtBridge.isEnabled() && this._isNextDayInterstitialCycle();
  };

  /**
   * Poki NEXT DAY › `commercialBreak()` after the commercial unlock.
   */
  Game.prototype.shouldShowPokiNextDayInterstitial = function () {
    return this.shouldOfferPokiCommercialBreak();
  };

  /**
   * Poki Play Again › `commercialBreak()` after the commercial unlock.
   */
  Game.prototype.shouldShowPokiPlayAgainInterstitial = function () {
    return this.shouldOfferPokiCommercialBreak();
  };

  /**
   * GameDistribution NEXT DAY › interstitial. Same unlock as Poki: every
   * continue once `cycleIndex` >= 4 (day 5 onward). The SDK decides whether
   * an ad plays.
   */
  Game.prototype.shouldShowGdNextDayInterstitial = function () {
    var cycleIndex = Math.max(0, Math.floor(Number(this.currentCycleIndex) || 0));
    return GdBridge.isEnabled() && cycleIndex >= 4;
  };

  /**
   * Playgama NEXT DAY › interstitial. Same cycle cadence as Y8 / YouTube.
   */
  Game.prototype.shouldShowPlaygamaNextDayInterstitial = function () {
    return PlaygamaBridge.isEnabled() && this._isNextDayInterstitialCycle();
  };

  /**
   * FRVR NEXT DAY › interstitial. Same cycle cadence as Y8 / YouTube.
   */
  Game.prototype.shouldShowFrvrNextDayInterstitial = function () {
    return FrvrBridge.isEnabled() && this._isNextDayInterstitialCycle();
  };

  Game.prototype.captureFlaskBodiesSnapshot = function () {
    var sys = this.getFlaskSystem();
    if (!sys || !sys.bodies || !sys.bounds) return [];
    var b = sys.bounds;
    var w = b.right - b.left;
    var h = b.bottom - b.top;
    if (w <= 0 || h <= 0) return [];
    var result = [];
    for (var i = 0; i < sys.bodies.length; i++) {
      var m = sys.bodies[i];
      if (!m || !m.body || !m.body.position) continue;
      var physicsSizeTier = getMetaPhysicsSizeTier(m);
      result.push({
        tier: m.tier,
        minimized: !!m.minimized,
        physicsSizeTier: physicsSizeTier,
        nx: (m.body.position.x - b.left) / w,
        ny: (m.body.position.y - b.top) / h,
        vx: m.body.velocity ? (m.body.velocity.x || 0) : 0,
        vy: m.body.velocity ? (m.body.velocity.y || 0) : 0,
        angle: typeof m.body.angle === "number" ? m.body.angle : 0,
        angularVelocity: typeof m.body.angularVelocity === "number" ? m.body.angularVelocity : 0,
      });
    }
    return result;
  };

  Game.prototype._bodyOptsFromFlaskMeta = function (meta) {
    var opts = { skipOverflowImmunity: true };
    if (!meta) return opts;
    var tier = meta.tier != null ? meta.tier : 0;
    var physicsSizeTier = getMetaPhysicsSizeTier(meta);
    if (physicsSizeTier !== tier) {
      opts.physicsSizeTier = physicsSizeTier;
      opts.minimized = true;
    } else if (meta.minimized) {
      opts.minimized = true;
    }
    return opts;
  };

  Game.prototype.applyFlaskBodiesSnapshot = function (entries) {
    if (!entries || entries.length === 0) return;
    var sys = this.getFlaskSystem();
    if (!sys || typeof sys.addBodyAt !== "function" || !sys.bounds) return;
    if (typeof sys.clearBodies === "function") sys.clearBodies();
    var b = sys.bounds;
    var w = b.right - b.left;
    var h = b.bottom - b.top;
    if (w <= 0 || h <= 0) return;
    var Body = Matter.Body ? Matter.Body : null;
    for (var i = 0; i < entries.length; i++) {
      var e = entries[i];
      if (!e || e.tier == null) continue;
      var nx = Math.max(0, Math.min(1, Number(e.nx)));
      var ny = Math.max(0, Math.min(1, Number(e.ny)));
      var x = b.left + nx * w;
      var y = b.top + ny * h;
      var meta = sys.addBodyAt(e.tier, x, y, this._bodyOptsFromFlaskMeta(e));
      if (Body && meta && meta.body) {
        if (typeof Body.setAngle === "function" && typeof e.angle === "number") {
          Body.setAngle(meta.body, e.angle);
        }
        if (typeof Body.setVelocity === "function") {
          Body.setVelocity(meta.body, {
            x: typeof e.vx === "number" ? e.vx : 0,
            y: typeof e.vy === "number" ? e.vy : 0,
          });
        }
        if (typeof Body.setAngularVelocity === "function" && typeof e.angularVelocity === "number") {
          Body.setAngularVelocity(meta.body, e.angularVelocity);
        }
      }
    }
  };

  Game.prototype.saveProgressState = function (resumeStage) {
    if (this.testFlaskMode) return;
    try {
      this.resumeStage = resumeStage === "shake" ? "shake" : "fall";
      this._ensureSkyDecorSeed();
      var payload = {
        currentScore: Math.max(0, Math.floor(Number(this.currentScore) || 0)),
        flaskHp: Math.max(0, this.savedFlaskHp || 0),
        flaskBodies: this.captureFlaskBodiesSnapshot(),
        hasCompletedARun: !!this.hasCompletedARun,
        cycleIndex: Math.max(0, Math.floor(Number(this.currentCycleIndex) || 0)),
        resumeStage: this.resumeStage,
        boostBar: normalizeStashTierList(this.boostBar),
        dropMode: captureDropModeSnapshot(this),
        skyDecorSeed: Math.floor(Number(this.skyDecorSeed)),
        savedAt: Date.now(),
        victoryShownThisRun: !!this.victoryShownThisRun,
      };
      if (resumeStage === "shake" && this.skyClock) {
        var skyHour = this.getSkyHour();
        if (skyHour != null && isFinite(skyHour)) {
          payload.skyHour = Number(skyHour);
        }
      }
      if (!this._persistCheckpointPair(payload)) {
        return;
      }
      this.hasSavedProgressSnapshot = true;
      this.saveBestScore();
    } catch (e) {
      console.warn("[Game] Failed to save progress:", e);
    }
  };

  Game.prototype.loadProgressState = function () {
    try {
      var parsed = readStorageRecord(
        this._getProgressStorage(),
        StorageSlot.PROGRESS
      );
      if (!parsed || typeof parsed !== "object") return null;
      return parsed;
    } catch (e) {
      console.warn("[Game] Failed to load progress:", e);
      return null;
    }
  };

  Game.prototype.applyProgressState = function (state) {
    var s = state || this.getDefaultProgressState();
    this.currentScore = Math.max(0, Math.floor(Number(s.currentScore) || 0));
    this.bestScore = Math.max(this.bestScore || 0, this.loadBestScore(), this.currentScore);
    // Fresh run / checkpoint restore: the "NEW BEST!" celebration re-arms via addScore.
    this.newBestThisRun = false;
    var hpVal = Number(s.flaskHp);
    this.savedFlaskHp = Math.max(0, Math.min(
      this.flaskMaxHp,
      isFinite(hpVal) ? hpVal : this.flaskMaxHp
    ));
    this.hasCompletedARun = !!s.hasCompletedARun;
    // Backward-compatible with older saves that stored "run".
    this.resumeStage = s.resumeStage === "shake" ? "shake" : "fall";
    this._restoreCycleIndexFromProgress(s);
    this._syncSkyClockCycleBaseFromProgress();
    if (s.skyDecorSeed != null && isFinite(Number(s.skyDecorSeed))) {
      this.skyDecorSeed = Math.floor(Number(s.skyDecorSeed));
    }
    this._ensureSkyDecorSeed();
    if (s.skyHour != null && isFinite(Number(s.skyHour))) {
      this._restoredSkyHour = Number(s.skyHour);
    } else {
      this._restoredSkyHour = null;
    }
    this.boostBar = normalizeStashTierList(s.boostBar);
    this.victoryShownThisRun = !!s.victoryShownThisRun;
    var dropSnap = parseDropModeSnapshot(s.dropMode);
    if (dropSnap) {
      this.isDropMode = true;
      this.dropQueue = dropSnap.queue;
      this.dropCurrent = dropSnap.current;
    } else {
      this.isDropMode = false;
      this.dropQueue = [];
      this.dropCurrent = null;
    }
    this.updateGlobalGemDisplay();
    this.updateScoreDisplay();
    if (s.flaskBodies && s.flaskBodies.length > 0) {
      this.applyFlaskBodiesSnapshot(s.flaskBodies);
    } else {
      var sys = this.getFlaskSystem();
      if (sys && typeof sys.clearBodies === "function") sys.clearBodies();
      this.currentGameMaxTier = this.currentCycleIndex === 0
        ? this._getRunStartMaxTier()
        : 0;
    }
  };

  Game.prototype.restoreLastSavedState = function () {
    var state = this.loadProgressState();
    if (!state) return false;
    if (!this._applyEconomyFromCheckpoint(state)) {
      this.loadAndApplyEconomyState();
    }
    this.applyProgressState(state);
    this._syncEconomySlotToCheckpoint(state);
    this.hasSavedProgressSnapshot = true;
    return true;
  };

  /** Drop run checkpoint only — preserves score, evolution unlocks, and other runtime state. */
  Game.prototype._clearProgressCheckpoint = function () {
    try {
      removeStorageRecord(
        this._getProgressStorage(),
        StorageSlot.PROGRESS
      );
    } catch (e) {
      console.warn("[Game] Failed to clear progress checkpoint:", e);
    }
    this.pendingRestoreFromSave = false;
    this.hasSavedProgressSnapshot = false;
  };

  /**
   * @param {Object} [opts]
   * @param {boolean} [opts.preserveEvolutionUnlocks] - When true (e.g. Play Again), keep
   *   `StorageSlot.UNLOCKED_TIERS` and in-memory `unlockedTiers` so the evolution dock stays revealed.
   *   Full menu "Reset Progress" omits this flag and clears discovery state too.
   */
  Game.prototype.resetProgressState = function (opts) {
    var preserveEvo = !!(opts && opts.preserveEvolutionUnlocks);
    this._clearProgressCheckpoint();
    if (!preserveEvo) {
      clearSkyBackgroundPrefs(this._getProgressStorage());
      this._skyBackgroundPrefs = loadSkyBackgroundPrefs(
        this._getProgressStorage()
      );
      this._skyAutoProbe = null;
      this._skyCycleUpgradeProbe = null;
      this._skyPerfForcedStatic = false;
      this._skyBgBootstrapResolved = false;
      this._applySkyBackgroundPrefsToConfig();
      this._resolveSkyBackgroundBootstrap();
      this._syncSkyBackgroundEffective();
      this._syncSkyBackgroundAnalytics();
    }
    if (!preserveEvo) {
      try {
        removeStorageRecord(
          this._getProgressStorage(),
          StorageSlot.UNLOCKED_TIERS
        );
        removeStorageRecord(
          this._getProgressStorage(),
          StorageSlot.ECONOMY
        );
        removeStorageRecord(
          this._getProgressStorage(),
          StorageSlot.RETENTION
        );
        removeStorageRecord(
          this._getProgressStorage(),
          StorageSlot.SHAKE_HINT
        );
        removeStorageRecord(
          this._getProgressStorage(),
          StorageSlot.HEAL_HINT
        );
      } catch (e) { /* ignore */ }
      this._shakeHintDismissed = false;
      this._hideShakeHint();
      this._healHintDismissed = false;
      this._hideHealHint();
      this.unlockedTiers = { 0: true, 1: true, 2: true };
      this.applyEconomyState(this.getDefaultEconomyState());
    }
    if (!preserveEvo && this.retention && typeof this.retention.reset === "function") {
      this.retention.reset();
    }
    this.currentGameMaxTier = this._getRunStartMaxTier();
    this._evolutionRingSig = null;
    if (this.playerStats) {
      this.playerStats = clearVictoryFeedback(this.playerStats);
      this._savePlayerStats();
    }
    this._abortVictoryPopup(true);
    this._abortPuzzleReveal();
    var defaults = this.getDefaultProgressState();
    this.applyProgressState(defaults);
    this._persistLifetimeEconomy();
    this.populateEvolutionChrome();
  };

  /**
   * DEV ONLY — Fall speed toggle. Returns the multiplier applied to the *next* Fall's
   * RUN_DURATION. Always 1 in production builds so shipped play can never be sped up.
   * @returns {number}
   */
  Game.prototype.getDevFallSpeedMultiplier = function () {
    if (!isDevBuildEnv()) return 1;
    var steps = C.DEV_FALL_SPEED_MULTIPLIERS || [1];
    var idx = this.getDevFallSpeedIndex();
    var value = Number(steps[idx]);
    return isFinite(value) && value > 0 ? value : 1;
  };

  /** @returns {number} index into DEV_FALL_SPEED_MULTIPLIERS, restored from localStorage. */
  Game.prototype.getDevFallSpeedIndex = function () {
    if (this._devFallSpeedIndex != null) return this._devFallSpeedIndex;
    var steps = C.DEV_FALL_SPEED_MULTIPLIERS || [1];
    var stored = 0;
    try {
      if (typeof localStorage !== "undefined") {
        stored = Math.floor(Number(localStorage.getItem(C.DEV_FALL_SPEED_STORAGE_KEY)) || 0);
      }
    } catch (e) { /* ignore */ }
    if (!(stored >= 0 && stored < steps.length)) stored = 0;
    this._devFallSpeedIndex = stored;
    return stored;
  };

  /** Dev test bar: advance to the next Fall speed step and persist it across reloads. */
  Game.prototype.devCycleFallSpeed = function () {
    var steps = C.DEV_FALL_SPEED_MULTIPLIERS || [1];
    var next = (this.getDevFallSpeedIndex() + 1) % steps.length;
    this._devFallSpeedIndex = next;
    try {
      if (typeof localStorage !== "undefined") {
        localStorage.setItem(C.DEV_FALL_SPEED_STORAGE_KEY, String(next));
      }
    } catch (e) { /* ignore */ }
    this.syncDevFallSpeedBtn();
    return next;
  };

  /** Paint the dev speed button face and tooltip from the current step. */
  Game.prototype.syncDevFallSpeedBtn = function () {
    var btn = this.dom && this.dom.devTestSpeedBtn;
    if (!btn) return;
    var labels = C.DEV_FALL_SPEED_LABELS || [];
    var idx = this.getDevFallSpeedIndex();
    var mult = this.getDevFallSpeedMultiplier();
    btn.textContent = labels[idx] != null ? labels[idx] : "›";
    var pct = Math.round(mult * 100);
    var title = "Fall speed: " + pct + "% duration (applies from the next Fall)";
    btn.setAttribute("title", title);
    btn.setAttribute("aria-label", title);
  };

  /** Dev test bar: reset saved progress for testing (incl. lifetime run index for first-fall treats). */
  Game.prototype.devResetProgress = function () {
    this.resetProgressState();
    this.currentCycleIndex = 0;
    this.hasCompletedARun = false;
    if (this.playerStats) {
      this.playerStats.runIndex = 0;
      this.playerStats = clearPendingRunIndexBump(this.playerStats);
      this._savePlayerStats();
    }
    if (this.retention) {
      this.retention._sessionReturnAutoOpenAttempted = false;
      this.retention.state.dailyBonusStep = 1;
      if (typeof this.retention.save === "function") {
        this.retention.save();
      }
      if (this.retention.dom.dailyBonusGrid
        && this.retention.dom.dailyBonusGrid.dataset.built === "1") {
        this.retention._renderDailyGrid();
      }
      this.retention._updateDailyClaimButton();
      this.retention.syncHud();
    }
    if (this.dom && this.dom.gameOverOverlay) {
      this.dom.gameOverOverlay.classList.add("hidden");
    }
    this._hideLeaderboardCard();
    this._clearBridgeTransitionTimers();
    if (this.continueToFallTimeoutId != null) {
      clearTimeout(this.continueToFallTimeoutId);
      this.continueToFallTimeoutId = null;
    }
    if (this.runPhase) {
      this.runPhase.stop();
      this.runPhase = null;
    }
    this._fallSimFrozen = false;
    this._shakeSimFrozen = false;
    this._transitionSimFrozen = false;
    this._dialogPauseDepth = 0;
    this.isDialogPaused = false;
    this._clearPlayerPauseState();
    this.stateMachine = new GameStateMachine("fall");
    this.saveProgressState();
    if (this.canvas && this.layout) {
      this.startNewRun();
    }
  };

  /**
   * Warm image + SFX caches once per session; safe to call before gameplay starts.
   * @returns {Promise<void>}
   */
  Game.prototype.warmMenuAssetsIfNeeded = function () {
    if (this._menuAssetPreloadPromise) return this._menuAssetPreloadPromise;
    var self = this;
    var runPreload = typeof self.opts.menuAssetPreload === "function"
      ? function () {
        return self.opts.menuAssetPreload(self.soundManager);
      }
      : function () {
        return preloadAllGameAssets({ soundManager: self.soundManager });
      };
    this._menuAssetPreloadPromise = Promise.resolve()
      .then(runPreload)
      .catch(function (err) {
        console.warn("[Game] menu asset preload:", err);
      });
    return this._menuAssetPreloadPromise;
  };

  /**
   * @returns {Promise<void>}
   */
  Game.prototype.ensureMenuAssetsLoaded = function () {
    return this.warmMenuAssetsIfNeeded();
  };

  Game.prototype.ensureAudioUnlocked = function (fromUserGesture) {
    if (this.soundManager && typeof this.soundManager.unlock === "function") {
      return this.soundManager.unlock({ fromUserGesture: !!fromUserGesture });
    }
    return Promise.resolve(false);
  };

  /** @returns {Promise<void>} */
  Game.prototype.startFromMenu = function (opts) {
    opts = opts || {};
    var self = this;
    var autoLaunch = !!opts.autoLaunch;
    var skipShakeResume = !!opts.skipShakeResume;
    var isMenuResume = !!self.hasSavedProgressSnapshot && (self.pendingRestoreFromSave || autoLaunch);
    function runStart() {
      if (opts.fromUserGesture) {
        self.ensureAudioUnlocked(true);
      }
      if (isMenuResume) {
        if (self.playerStats) {
          self.playerStats = clearPendingRunIndexBump(self.playerStats);
          self._savePlayerStats();
        }
      } else {
        self._runPlayTimeMs = 0;
        self._maybeRecordRunStartForNewRun();
      }
      if (self.pendingRestoreFromSave) {
        var restored = self.restoreLastSavedState();
        if (restored) {
          self._recomputeCurrentGameMaxTierFromFlask();
          self.showToast("Restored last checkpoint", 2200);
        }
        self.pendingRestoreFromSave = false;
      }
      if (isMenuResume) {
        self._trackAnalytics(AnalyticsEvents.RUN_RESUMED, {
          resume_stage: self.resumeStage || "fall",
        });
        self._analyticsFallStartSource = "resume";
      } else {
        var runSource = self._analyticsRunStartSource || "menu";
        self._trackRunStart(runSource);
        self._analyticsFallStartSource = runSource;
        self._analyticsRunStartSource = null;
      }
      if (!skipShakeResume && self.hasSavedProgressSnapshot && self.resumeStage === "shake") {
        self._analyticsMergeStartSource = "resume";
        self.goToShake({ mergeStartSource: "resume" });
        return self._handleBootBgmAutoplay();
      }
      if (self.stateMachine && typeof self.stateMachine.transitionTo === "function") {
        self.stateMachine.transitionTo("fall");
      }
      self.startNewRun();
      if (self._pendingDesignFallPreviewPause) {
        self._pendingDesignFallPreviewPause = false;
        self._enterFallDesignPreviewPaused();
      }
      return self._handleBootBgmAutoplay();
    }
    return self.ensureMenuAssetsLoaded().then(runStart, runStart);
  };

  /** Skip the main menu on launch and enter gameplay (Fall or saved stage). */
  Game.prototype.shouldAutoStartFromMenu = function () {
    return true;
  };

  /** @returns {Promise<void>} */
  Game.prototype.maybeAutoStartFromMenu = function () {
    // Normal boot: Fall (or saved resumeStage). Dev/layout previews are URL-only opt-in.
    if (!this.shouldAutoStartFromMenu()) return Promise.resolve();
    if (C.isDesignFallPreviewEnabled && C.isDesignFallPreviewEnabled()) {
      return this.startDesignFallPreview();
    }
    if (C.isDesignShakePreviewEnabled && C.isDesignShakePreviewEnabled()) {
      return this.startDesignShakePreview();
    }
    if (C.isDevStartInShakeEnabled && C.isDevStartInShakeEnabled()) {
      return this.startDevShakeSession();
    }
    return this.startFromMenu({ autoLaunch: true });
  };

  /**
   * Dev preview: enter Fall immediately and pause for layout tuning (see isDesignFallPreviewEnabled).
   * @returns {Promise<void>}
   */
  Game.prototype.startDesignFallPreview = function () {
    var self = this;
    return self.ensureMenuAssetsLoaded().then(function () {
      self.ensureAudioUnlocked();
      self.pendingRestoreFromSave = false;
      self._pendingDesignFallPreviewPause = true;
      return self.startFromMenu({ autoLaunch: true, skipShakeResume: true });
    });
  };

  Game.prototype._enterFallDesignPreviewPaused = function () {
    setFallMoveHintVisible(this.dom && this.dom.fallMoveHint, false);
    this.setFallPaused(true);
    this.showToast(
      "FALL design preview (paused). Pause or Space toggles. TODO: remove when UI redesign ships.",
      10000
    );
  };

  /**
   * Dev preview: enter Shake immediately with motion frozen (see DESIGN_PREVIEW_AUTO_SHAKE_PAUSED).
   * @returns {Promise<void>}
   */
  Game.prototype.startDesignShakePreview = function () {
    var self = this;
    return self.ensureMenuAssetsLoaded().then(function () {
      self.ensureAudioUnlocked();
      self._enterShakeDesignPreviewPaused();
    });
  };

  /**
   * Dev boot: enter live Shake with seeded state for merge-stage testing.
   * @returns {Promise<void>}
   */
  Game.prototype.startDevShakeSession = function () {
    var self = this;
    return self.ensureMenuAssetsLoaded().then(function () {
      self.ensureAudioUnlocked();
      self.pendingRestoreFromSave = false;
      self.hasSavedProgressSnapshot = false;
      self.savedFlaskHp = Math.max(1, self.flaskMaxHp - 1);
      self.gemsCollected = 50;
      self.shakeUses = self.constants.SHAKE_USES_DEFAULT || 3;
      self.boostBar = [0, 1, 0, 1, 2];
      self._analyticsMergeStartSource = "dev_start";
      self.goToShake({ mergeStartSource: "dev_start" });
      var sys = self.getFlaskSystem();
      if (sys && typeof sys.getBodyCount === "function" && sys.getBodyCount() === 0
        && typeof sys.spawnTestBodies === "function") {
        // Seed only a few low tiers — a full-chain dump overflows the flask.
        sys.spawnTestBodies(3);
      }
      self.updateShakeUI();
    });
  };

  Game.prototype._enterShakeDesignPreviewPaused = function () {
    this._analyticsMergeStartSource = "design_preview";
    this.goToShake({ mergeStartSource: "design_preview" });
    // Jar starts empty — real play fills it from stash drops; the seeded test
    // bodies were only mock/example dressing.
    var now = typeof performance !== "undefined" ? performance.now() : Date.now();
    this.setShakePaused(true);
    this.showToast(
      "SHAKE design preview (paused). Pause toggles freeze. TODO: remove when UI redesign ships.",
      10000
    );
  };

  Game.prototype._getShakeDrawTime = function (currentTime) {
    if (this._isSimPaused() && this.frozenShakePreviewTime != null) {
      return this.frozenShakePreviewTime;
    }
    return this._getSkySeaDrawTime(currentTime);
  };

  Game.prototype._markLayoutDirty = function () {
    this._layoutDirty = true;
    this._shakeUiBoundsDirty = true;
    this._shakeChromeCacheKey = null;
    this._boardViewportSig = null;
  };

  /** Recompute layout only when viewport/stage geometry may have changed. */
  Game.prototype._ensureLayout = function () {
    if (!this._layoutDirty && this.layout && this.layout.runHeight != null) {
      return this.layout;
    }
    try {
      this.layout = this.getLayout();
    } catch (e) {
      this.layout = this.getDefaultLayout();
    }
    if (!this.layout || this.layout.runHeight == null) {
      this.layout = this.getDefaultLayout();
    }
    this._layoutDirty = false;
    this._shakeUiBoundsDirty = true;
    // Keep the layout_mode user property in sync.
    this._syncLayoutAnalytics();
    return this.layout;
  };

  Game.prototype._refreshLayout = function () {
    this._markLayoutDirty();
    return this._ensureLayout();
  };

  Game.prototype._syncActiveStageChromeForLayoutChange = function () {
    var sm = this.stateMachine;
    if (!sm || !sm.is) return;
    var inActiveStage = sm.is("fall") || sm.is("shake") || sm.is("transition");
    if (!inActiveStage) return;
    // Signatures may be cached for the inactive layout (e.g. after a vertical
    // strip reveal) — force a fresh render when the active chrome swaps.
    this._evolutionRingSig = null;
    this._evolutionStripSig = null;
    this.setPlayerHudVisible(true);
    this.setEvolutionDockVisible(true);
    this.populateEvolutionChrome();
    if (sm.is("fall")) {
      this.applyRunHudBounds();
    } else if (sm.is("shake")) {
      this.applyShakeUIBounds(true);
      if (this.ctx && typeof this.drawShake === "function") {
        this.drawShake();
      }
    } else if (sm.is("transition")) {
      this.applyUiScaleVars();
      this.applyStageChromeLeftBounds();
      this.applyStageChromeRightBounds();
      this.applyPlayChromeBounds();
      this.applyEvolutionRingBounds();
    }
  };

  Game.prototype.setLayoutModePreference = function (preference) {
    this.layoutModePreference = resolveLayoutMode(preference);
    this._refreshLayout();
    this._applyLayoutModeClasses();
    this.applyLayoutChromeBounds();
    this.syncDevTestBar();
    if (this.canvas && this.layout) {
      this.applyCanvasSize(this.layout.canvasWidth, this.layout.canvasHeight);
    }
    this._syncActiveStageChromeForLayoutChange();
    this._syncLayoutAnalytics();
    // Loss-dialog strip keeps its own SVG paint servers; refresh if visible after layout chrome rebuilds.
    if (this.dom && this.dom.gameOverOverlay
      && !this.dom.gameOverOverlay.classList.contains("hidden")) {
      this._populateGameOverEvolutionStrip();
    }
  };

  Game.prototype._getLayoutComputeOpts = function () {
    return {
      bottomBarHeightPx: 0,
      dockHeightPx: 0,
    };
  };

  Game.prototype._getLayoutModePreference = function () {
    if (this.layoutModePreference != null) {
      return resolveLayoutMode(this.layoutModePreference);
    }
    return readLayoutModePreference();
  };

  Game.prototype._isVerticalLayout = function () {
    return !!(this.layout && this.layout.layoutMode === "vertical");
  };

  Game.prototype._applyLayoutModeClasses = function () {
    var app = typeof document !== "undefined" ? document.getElementById("app") : null;
    if (!app || !this.layout) return;
    var isVertical = this.layout.layoutMode === "vertical";
    app.classList.toggle("layout-vertical", isVertical);
    app.classList.toggle("layout-horizontal", !isVertical);
    var vChrome = this.dom && this.dom.verticalChrome;
    if (vChrome && vChrome.classList) {
      var showVertical = isVertical
        && this.stateMachine
        && this.stateMachine.is
        && (this.stateMachine.is("fall") || this.stateMachine.is("shake") || this.stateMachine.is("transition"));
      vChrome.classList.toggle("hidden", !showVertical);
      if (typeof vChrome.setAttribute === "function") {
        vChrome.setAttribute("aria-hidden", showVertical ? "false" : "true");
      }
    }
  };

  Game.prototype._clearVerticalLayoutCssVars = function (el) {
    if (!el || !el.style || typeof el.style.removeProperty !== "function") return;
    var vars = [
      "--v-play-w-set",
      "--v-play-h-set",
      "--v-budget-h-set",
      "--v-header-h-set",
      "--v-stash-h-set",
      "--v-strip-h-set",
      "--v-content-top-set",
      "--v-play-left-set",
      "--v-play-top-set",
      "--v-stash-top-set",
      "--v-strip-top-set",
    ];
    for (var i = 0; i < vars.length; i++) {
      el.style.removeProperty(vars[i]);
    }
  };

  Game.prototype.applyVerticalChromeBounds = function (clearOnly) {
    var app = typeof document !== "undefined" ? document.getElementById("app") : null;
    if (!app) return;
    if (clearOnly || !this._isVerticalLayout()) {
      this._clearVerticalLayoutCssVars(app);
      return;
    }
    var layout = this.layout;
    var play = layout.playArea;
    var header = layout.headerBand;
    var stash = layout.stashBand;
    var strip = layout.evolutionStripBand;
    var core = layout.coreRect;
    if (!play || !header || !stash || !strip || !core) return;
    app.style.setProperty("--v-play-w-set", Math.round(play.width) + "px");
    app.style.setProperty("--v-play-h-set", Math.round(play.height) + "px");
    app.style.setProperty("--v-budget-h-set", Math.round(core.height) + "px");
    app.style.setProperty("--v-header-h-set", Math.round(header.height) + "px");
    app.style.setProperty("--v-stash-h-set", Math.round(stash.height) + "px");
    app.style.setProperty("--v-strip-h-set", Math.round(strip.height) + "px");
    app.style.setProperty("--v-content-top-set", Math.round(core.y) + "px");
    app.style.setProperty("--v-play-left-set", Math.round(play.x) + "px");
    app.style.setProperty("--v-play-top-set", Math.round(play.y) + "px");
    app.style.setProperty("--v-stash-top-set", Math.round(stash.y) + "px");
    app.style.setProperty("--v-strip-top-set", Math.round(strip.y) + "px");
  };

  Game.prototype._syncFallProgressDialMount = function () {
    if (!this.dom) return;
    var dial = this.dom.fallProgressDial;
    var logoPanel = this.dom.fallLogoPanel;
    var scorePanel = this.dom.scorePanel;
    var verticalSlot = this.dom.verticalDialSlot;
    if (!dial || !logoPanel || !verticalSlot) return;
    var useVertical = this._isVerticalLayout();
    var target = useVertical ? verticalSlot : logoPanel;
    if (dial.parentElement !== target) {
      if (useVertical) {
        verticalSlot.appendChild(dial);
      } else if (scorePanel && scorePanel.parentElement === logoPanel) {
        logoPanel.insertBefore(dial, scorePanel);
      } else {
        logoPanel.appendChild(dial);
      }
    }
    if (typeof verticalSlot.setAttribute === "function") {
      verticalSlot.setAttribute("aria-hidden", useVertical ? "false" : "true");
    }
    if (typeof dial.setAttribute === "function") {
      dial.setAttribute("aria-hidden", "false");
    }
  };

  Game.prototype._syncVerticalStageChromePanels = function () {
    if (!this.dom || !this._isVerticalLayout()) return;
    var isFall = this.stateMachine && this.stateMachine.is && this.stateMachine.is("fall");
    var isShake = this.stateMachine && this.stateMachine.is && this.stateMachine.is("shake");
    var showStage = isFall || isShake;
    if (this.dom.verticalPlayOverlays) {
      this.dom.verticalPlayOverlays.classList.toggle("hidden", !showStage);
      if (typeof this.dom.verticalPlayOverlays.setAttribute === "function") {
        this.dom.verticalPlayOverlays.setAttribute("aria-hidden", showStage ? "false" : "true");
      }
    }
    if (this.dom.verticalFallPauseBtn) {
      this.dom.verticalFallPauseBtn.classList.toggle("hidden", !showStage);
    }
    if (this.dom.verticalFallVolumeBtn) {
      this.dom.verticalFallVolumeBtn.classList.toggle("hidden", !showStage);
    }
    if (this.dom.verticalDailyBonusBtn) {
      this.dom.verticalDailyBonusBtn.classList.toggle("hidden", !showStage);
    }
    if (this.dom.verticalWheelBtn) {
      this.dom.verticalWheelBtn.classList.toggle("hidden", !showStage);
    }
    if (this.dom.verticalStashPanel) {
      var showStash = !!(this.boostBar && this.boostBar.length > 0);
      this.dom.verticalStashPanel.classList.toggle("hidden", !showStash);
      if (typeof this.dom.verticalStashPanel.setAttribute === "function") {
        this.dom.verticalStashPanel.setAttribute("aria-hidden", showStash ? "false" : "true");
      }
    }
    if (this.dom.verticalShakeActionsRow) {
      this.dom.verticalShakeActionsRow.classList.toggle("hidden", !isShake);
      if (typeof this.dom.verticalShakeActionsRow.setAttribute === "function") {
        this.dom.verticalShakeActionsRow.setAttribute("aria-hidden", isShake ? "false" : "true");
      }
    }
    this._syncFallPauseButton();
  };

  Game.prototype.applyLayoutChromeBounds = function (clearOnly) {
    this._applyLayoutModeClasses();
    this._syncFallProgressDialMount();
    if (this._isVerticalLayout()) {
      this.applyVerticalChromeBounds(clearOnly);
      if (!clearOnly) {
        this.applyUiScaleVars();
        this._syncVerticalStageChromePanels();
      }
      return;
    }
    this.applyVerticalChromeBounds(true);
  };

  Game.prototype._getVerticalLayout = function (w, h) {
    var isShake = this.stateMachine && this.stateMachine.is && this.stateMachine.is("shake");
    var vertical = computeVerticalBoard({ width: w, height: h }, this._getLayoutComputeOpts());
    var play = vertical.playArea;
    var playContent = vertical.playContentArea;
    var topPanelsHeight = playContent.height;
    var scale = getScale(playContent.width, playContent.height);
    var gridWidthPx = vertical.gridWidthPx;
    var mockupScale = computeMockupUiScale(gridWidthPx);
    var runX = playContent.x;
    var runW = playContent.width;
    var nextColW = 0;
    var fbLeft;
    var fbRight;
    var fbTop;
    var fbBot;
    var flaskOriginX = play.x;
    var flaskOriginY = playContent.y;

    if (isShake) {
      var jarMargin = Math.round(play.width * 0.06);
      var jarH = Math.round(topPanelsHeight * 0.74);
      fbLeft = jarMargin;
      fbRight = Math.max(jarMargin + 1, play.width - jarMargin);
      var jarBottomPad = Math.round(topPanelsHeight * 0.02);
      fbBot = topPanelsHeight - jarBottomPad;
      fbTop = Math.max(1, fbBot - jarH);
    } else {
      var fallMargin = Math.round(playContent.width * 0.06);
      var fallJarH = Math.round(topPanelsHeight * 0.5);
      var fallBottomPad = Math.round(topPanelsHeight * 0.02);
      fbBot = topPanelsHeight - fallBottomPad;
      fbTop = Math.max(1, fbBot - fallJarH);
      fbLeft = vertical.playSideInsetPx + fallMargin;
      fbRight = Math.max(fbLeft + 1, play.width - vertical.playSideInsetPx - fallMargin);
    }

    var core = vertical.coreRect;
    var sm = vertical.sceneryMargins;
    var layoutOut = {
      layoutMode: "vertical",
      isLandscape: false,
      runWidth: runW,
      runHeight: topPanelsHeight,
      flaskPanelWidth: Math.max(1, play.width),
      flaskPanelHeight: topPanelsHeight,
      canvasWidth: w,
      canvasHeight: vertical.boardHeight,
      scale: scale,
      runBounds: { x: runX, y: playContent.y, width: runW, height: playContent.height },
      flaskPanelOrigin: { x: flaskOriginX, y: flaskOriginY },
      flaskBounds: { left: fbLeft, right: fbRight, top: fbTop, bottom: fbBot },
      shakeNextColumnWidth: nextColW,
      evolutionDockBounds: vertical.evolutionDockBounds,
      sceneryMargins: sm,
      coreRect: core,
      playArea: play,
      playContentArea: playContent,
      headerBand: vertical.headerBand,
      stashBand: vertical.stashBand,
      evolutionStripBand: vertical.evolutionStripBand,
      leftStrip: {
        x: 0,
        y: core.y,
        width: sm.left,
        height: core.height,
      },
      rightStrip: {
        x: core.x + core.width,
        y: core.y,
        width: sm.right,
        height: core.height,
      },
      gridUnitPx: vertical.gridUnitPx,
      gridWidthPx: gridWidthPx,
      mockupScale: mockupScale,
      playSideInsetPx: vertical.playSideInsetPx,
      playSideInsetFraction: vertical.playSideInsetFraction,
      bandFractions: vertical.bandFractions,
      bandUnits: vertical.bandUnits,
    };
    return layoutOut;
  };

  Game.prototype.getLayout = function () {
    var board = this.getBoardSize();
    var w = board.width;
    var h = board.height;
    var layoutOpts = this._getLayoutComputeOpts();
    var effectiveMode = resolveEffectiveLayoutMode(
      { width: w, height: h },
      this._getLayoutModePreference(),
      layoutOpts,
    );
    if (effectiveMode === "vertical") {
      return this._getVerticalLayout(w, h);
    }
    var isShake = this.stateMachine && this.stateMachine.is && this.stateMachine.is("shake");
    var responsive = computeResponsiveBoard({ width: w, height: h }, layoutOpts);

    var play = responsive.playArea;
    var left = responsive.leftStrip;
    var right = responsive.rightStrip;
    var topPanelsHeight = responsive.coreRect.height;
    // Fall/Shake gameplay lives in the play column — scale from its size, not full viewport.
    var scale = getScale(play.width, topPanelsHeight);
    var gridWidthPx = resolveGridWidthPx(responsive);
    var mockupScale = computeMockupUiScale(gridWidthPx);
    var fallHudReserve = isShake ? 0 : getFallSidebarHudReservePx(this, gridWidthPx);
    var runX = play.x;
    var runW = play.width;
    var nextColW = 0;
    var fbLeft;
    var fbRight;
    var fbTop;
    var fbBot;
    var flaskOriginX;

    if (isShake) {
      flaskOriginX = play.x;
      var jarMargin = Math.round(play.width * 0.06);
      var jarH = Math.round(topPanelsHeight * 0.74);
      fbLeft = jarMargin;
      fbRight = Math.max(jarMargin + 1, play.width - jarMargin);
      var jarBottomPad = Math.round(topPanelsHeight * 0.02);
      fbBot = topPanelsHeight - jarBottomPad;
      fbTop = Math.max(1, fbBot - jarH);
    } else {
      nextColW = typeof window !== "undefined" && C.SHAKE_NEXT_COLUMN_WIDTH != null
        ? Math.floor(Number(C.SHAKE_NEXT_COLUMN_WIDTH))
        : 88;
      if (!isFinite(nextColW) || nextColW < 0) nextColW = 88;
      nextColW = Math.min(nextColW, 112);

      var m = scale ? scale.flaskMargin : 20;
      var topStd = scale ? scale.flaskTop : 70;
      var botStdRef = scale ? (topPanelsHeight - scale.flaskBottomOffset) : (topPanelsHeight - 35);
      var innerW2 = Math.max(1, play.width + right.width);
      var innerH2 = Math.max(1, botStdRef - topStd);
      var fallRefColumnW = right.width;
      var maxWFallRef = Math.max(1, right.width - 2 * m);
      var logoH = getFallTopHudHeightPx(this, gridWidthPx);
      var logoToScoreGap = getFallLogoToScoreGapPx(this, gridWidthPx);
      var scoreCardH = getFallScorePanelMinHeightPx(this, gridWidthPx);
      var tubeTopY = logoH + logoToScoreGap + scoreCardH + 22 + 40;
      var fbBotFall = topPanelsHeight - 34;
      var fallInnerW = maxWFallRef;
      var fallInnerH = 100;
      for (var tubeIter = 0; tubeIter < 8; tubeIter++) {
        fallInnerH = Math.max(1, fbBotFall - tubeTopY);
        fallInnerW = Math.min(maxWFallRef, (fallInnerH * innerW2) / innerH2);
        var fbTopFallProbe = fbBotFall - fallInnerH;
        var fbLeftFallProbe = (fallRefColumnW - fallInnerW) / 2;
        var cardRect = this._getFallMiniFlaskCardRect(fallRefColumnW, topPanelsHeight, {
          left: fbLeftFallProbe,
          right: fbLeftFallProbe + fallInnerW,
          top: fbTopFallProbe,
          bottom: fbBotFall,
        });
        var tubeBottomY = cardRect.y + cardRect.h - 22;
        if (Math.abs(fbBotFall - tubeBottomY) < 0.5) break;
        fbBotFall = tubeBottomY;
      }
      fallInnerH = Math.max(1, fbBotFall - tubeTopY);
      fallInnerW = Math.min(maxWFallRef, (fallInnerH * innerW2) / innerH2);

      flaskOriginX = play.x + play.width;
      fbBot = fbBotFall;
      fbTop = fbBot - fallInnerH;
      if (fbTop < tubeTopY) {
        fbTop = tubeTopY;
        fbBot = fbTop + fallInnerH;
      }
      fbLeft = (right.width - fallInnerW) / 2;
      fbRight = fbLeft + fallInnerW;
    }

    var layoutOut = {
      isLandscape: true,
      runWidth: runW,
      runHeight: topPanelsHeight,
      flaskPanelWidth: isShake ? Math.max(1, play.width) : Math.max(1, right.width),
      flaskPanelHeight: topPanelsHeight,
      canvasWidth: w,
      canvasHeight: responsive.boardHeight,
      scale: scale,
      runBounds: { x: runX, y: play.y, width: runW, height: play.height },
      flaskPanelOrigin: { x: flaskOriginX, y: responsive.coreRect.y },
      flaskBounds: { left: fbLeft, right: fbRight, top: fbTop, bottom: fbBot },
      shakeNextColumnWidth: nextColW,
      evolutionDockBounds: responsive.evolutionDockBounds,
      sceneryMargins: responsive.sceneryMargins,
      coreRect: responsive.coreRect,
      playArea: play,
      leftStrip: left,
      rightStrip: right,
      gridUnitPx: responsive.gridUnitPx,
      gridWidthPx: gridWidthPx,
      mockupScale: mockupScale,
    };
    if (!isShake) {
      layoutOut.fallSidebarBounds = {
        x: left.x,
        y: left.y,
        width: left.width,
        height: fallHudReserve,
      };
    }
    layoutOut.layoutMode = "horizontal";
    return layoutOut;
  };

  Game.prototype.getDefaultLayout = function () {
    try {
      var layout = this.getLayout();
      if (layout && layout.runHeight != null && layout.flaskBounds) return layout;
    } catch (e) { /* use minimal fallback below */ }

    var board = this.getFixedBoardSize();
    var w = board.width;
    var h = board.height;
    var scale = getScale(w, h);
    var runW = Math.floor(w * 0.68);
    var flaskW = Math.max(1, w - runW);
    var m = scale ? scale.flaskMargin : 20;
    return {
      isLandscape: true,
      runWidth: runW,
      runHeight: h,
      flaskPanelWidth: flaskW,
      flaskPanelHeight: h,
      canvasWidth: w,
      canvasHeight: h,
      scale: scale,
      runBounds: { x: 0, y: 0, width: runW, height: h },
      flaskPanelOrigin: { x: runW, y: 0 },
      flaskBounds: {
        left: m,
        right: flaskW - m,
        top: scale ? scale.flaskTop : 70,
        bottom: h - (scale ? scale.flaskBottomOffset : 35),
      },
      shakeNextColumnWidth: 0,
      evolutionDockBounds: null,
    };
  };

  Game.prototype.loadUnlockedTiers = function () {
    try {
      var arr = readStorageRecord(
        this._getProgressStorage(),
        StorageSlot.UNLOCKED_TIERS
      );
      if (Array.isArray(arr)) {
        for (var i = 0; i < arr.length; i++) this.unlockedTiers[arr[i]] = true;
      }
    } catch (e) { /* ignore */ }
    this.unlockedTiers[0] = true;
    this._applyBaselineUnlockedTiers();
  };

  Game.prototype.saveUnlockedTiers = function () {
    var arr = Object.keys(this.unlockedTiers).map(Number).filter(function (k) { return this.unlockedTiers[k]; }.bind(this));
    writeStorageRecord(
      this._getProgressStorage(),
      StorageSlot.UNLOCKED_TIERS,
      arr
    );
  };

  Game.prototype.unlockTier = function (tierIndex) {
    var chain = (typeof C.getActiveEvolutionChain === "function")
      ? C.getActiveEvolutionChain()
      : (C.EVOLUTION_CHAIN || []);
    var tierId = (chain[tierIndex] && chain[tierIndex].id != null) ? chain[tierIndex].id : tierIndex;
    if (this.unlockedTiers[tierId]) return;
    var willReveal = this._canPlayDiscoveryReveal(chain, tierIndex);
    // Hold the dial/strip DOM before analytics or any other refresh runs.
    if (willReveal) {
      this._evoRevealHold = true;
    }
    this.unlockedTiers[tierId] = true;
    this.saveUnlockedTiers();
    this._noteTierForAnalytics(tierId);
    if (willReveal) {
      if (getSecretRevealMode(chain, tierId) === "dragon") {
        this._victoryFirstUnlockPending = true;
      }
      this._queueDiscoveryReveal(chain, tierIndex, tierId);
      return;
    }
    this.populateEvolutionChrome();
  };

  Game.prototype._getEvolutionPanelMount = function () {
    if (this._isVerticalLayout()) {
      return this.dom && this.dom.verticalEvoStripMount;
    }
    return this.dom && this.dom.evoRingMount;
  };

  /**
   * First-time (lifetime) discoveries play the fly-in ceremony, but only mid
   * Merge stage with the reveal DOM available.
   */
  Game.prototype._canPlayDiscoveryReveal = function (chain, tierIndex) {
    if (!chain || !chain[tierIndex]) return false;
    if (!(this.stateMachine && this.stateMachine.is && this.stateMachine.is("shake"))) return false;
    if (!(this.dom && this.dom.secretRevealOverlay && this.dom.secretRevealPopoutMount)) return false;
    return !!this._getEvolutionPanelMount();
  };

  /**
   * @param {object} [revealOpts] mid-ceremony render overrides
   * @param {number} [revealOpts.pendingTierIndex] chain index shown as a visible empty slot while the flyer travels
   * @param {number} [revealOpts.hiddenTierIndex] chip slot kept invisible (teaser squeeze only)
   * @param {number} [revealOpts.heldTierId] hub/counter stay at this tier until the chip lands
   * @param {boolean} [revealOpts.suppressSecretChip] keep the 11th "?" chip off until the teaser squeeze
   */
  Game.prototype._populateEvolutionChromeForReveal = function (revealOpts) {
    var opts = { force: true, fromReveal: true };
    if (revealOpts) {
      if (revealOpts.pendingTierIndex != null) opts.pendingTierIndex = revealOpts.pendingTierIndex;
      if (revealOpts.hiddenTierIndex != null) opts.hiddenTierIndex = revealOpts.hiddenTierIndex;
      if (revealOpts.heldTierId != null) opts.heldTierId = revealOpts.heldTierId;
      if (revealOpts.suppressSecretChip) opts.suppressSecretChip = true;
    }
    if (this._isVerticalLayout()) {
      this.populateEvolutionStrip(opts);
    } else {
      this.populateEvolutionRing(opts);
    }
  };

  Game.prototype._queueDiscoveryReveal = function (chain, tierIndex, tierId) {
    var self = this;
    this._discoveryRevealQueue.push({ tierIndex: tierIndex, tierId: tierId });
    if (this._secretRevealActive || this._secretRevealSettleTimer) return;
    // Start on the next frame so the hold flag is visible before populate.
    this._secretRevealSettleTimer = setTimeout(function () {
      self._secretRevealSettleTimer = null;
      self._runNextDiscoveryReveal();
    }, DISCOVERY_REVEAL_TIMING.MERGE_SETTLE_MS);
  };

  Game.prototype._runNextDiscoveryReveal = function () {
    var self = this;
    var item = this._discoveryRevealQueue.shift();
    if (!item) {
      this._finishSecretRevealImmediate();
      return;
    }
    if (!(this.stateMachine && this.stateMachine.is && this.stateMachine.is("shake"))) {
      this._finishSecretRevealImmediate();
      return;
    }
    var panelMount = this._getEvolutionPanelMount();
    var overlay = this.dom && this.dom.secretRevealOverlay;
    var popoutMount = this.dom && this.dom.secretRevealPopoutMount;
    if (!panelMount || !overlay || !popoutMount) {
      this._finishSecretRevealImmediate();
      return;
    }

    var chain = (typeof C.getActiveEvolutionChain === "function")
      ? C.getActiveEvolutionChain()
      : (C.EVOLUTION_CHAIN || []);
    var tier = chain[item.tierIndex];
    if (!tier) {
      this._runNextDiscoveryReveal();
      return;
    }
    var withTeaser = getSecretRevealMode(chain, item.tierId) === "teaser";
    var prevTier = item.tierIndex > 0 ? chain[item.tierIndex - 1] : null;
    var heldTierId = prevTier ? prevTier.id : 0;
    var skipTier = function (i, c) { return self._shouldSkipEvolutionDockTier(c, i); };
    var landRingTiers = buildRingTiers(chain, self.unlockedTiers, skipTier, withTeaser);

    this._secretRevealActive = true;
    this._secretRevealHandle = runDiscoveryCeremony({
      overlay: overlay,
      popoutMount: popoutMount,
      panelMount: panelMount,
      isVertical: this._isVerticalLayout(),
      tierIndex: item.tierIndex,
      assetId: tier.assetId || "cell",
      withTeaser: withTeaser,
      secretTierIndex: chain.length - 1,
      teaserCount: { current: item.tierIndex + 1, total: chain.length },
      hubLandCountCurrent: item.tierIndex + 1,
      hubLandCountTotal: landRingTiers.length,
      hubLandRunMax: item.tierId === self.currentGameMaxTier,
      onPlayAppearSound: function () {
        if (self.soundManager && typeof self.soundManager.play === "function") {
          self.soundManager.play("gold_orb");
        }
      },
      onPlayLandSound: function () {
        if (self.soundManager && typeof self.soundManager.play === "function") {
          self.soundManager.play("shake_drop");
        }
      },
      onPlaySound: function () {
        // Teaser "?" land (gate-tier phase).
        if (self.soundManager && typeof self.soundManager.play === "function") {
          self.soundManager.play("gold_orb");
        }
      },
      onPopulateMid: function () {
        self._populateEvolutionChromeForReveal({
          pendingTierIndex: item.tierIndex,
          heldTierId: heldTierId,
          suppressSecretChip: withTeaser,
        });
      },
      // Land only mutates the target chip DOM (revealDiscoverySlot). Do not touch
      // signatures here — unlocked data is already true, and caching now would
      // make the later settle populate look like a no-op.
      onPopulateLanded: function () {},
      // After land (and teaser phase if any): force a settled ring/strip paint,
      // then cache signatures so they mean "DOM matches this unlocked state".
      onPopulateFinal: function () {
        self._populateEvolutionChromeForReveal(null);
        self._cacheEvolutionChromeSignatures();
      },
      onComplete: function () {
        self._secretRevealActive = false;
        self._secretRevealHandle = null;
        if (self._discoveryRevealQueue.length > 0) {
          self._runNextDiscoveryReveal();
        } else {
          self._releaseEvolutionRevealHold();
        }
      },
      onAbort: function () {
        self._secretRevealActive = false;
        self._secretRevealHandle = null;
        self._discoveryRevealQueue.length = 0;
        self._releaseEvolutionRevealHold();
      },
    });
  };

  Game.prototype._cacheEvolutionChromeSignatures = function () {
    var chain = (typeof C.getActiveEvolutionChain === "function")
      ? C.getActiveEvolutionChain()
      : (C.EVOLUTION_CHAIN || []);
    var self = this;
    var ringState = {
      unlockedTiers: this.unlockedTiers,
      chain: chain,
      currentTierId: this.currentGameMaxTier,
      skipTier: function (i, c) { return self._shouldSkipEvolutionDockTier(c, i); },
    };
    var stripState = {
      unlockedTiers: this.unlockedTiers,
      chain: chain,
      currentTierId: this.currentGameMaxTier,
      skipTier: function (i, c) { return self._shouldSkipEvolutionDockTier(c, i); },
    };
    if (this._isVerticalLayout()) {
      this._evolutionStripSig = buildEvolutionStripSignature(stripState);
    } else {
      this._evolutionRingSig = buildEvolutionRingSignature(ringState);
    }
  };

  Game.prototype._releaseEvolutionRevealHold = function () {
    this._evoRevealHold = false;
    this._cacheEvolutionChromeSignatures();
    this.populateEvolutionChrome();
    if (this._pendingVictoryPopup) {
      this._scheduleVictoryPopupShow();
    }
    this._schedulePuzzleRevealShow();
  };

  Game.prototype._clearVictorySettleTimer = function () {
    if (this._victorySettleTimer) {
      clearTimeout(this._victorySettleTimer);
      this._victorySettleTimer = null;
    }
  };

  Game.prototype._abortVictoryPopup = function (dropPending) {
    this._clearVictorySettleTimer();
    if (dropPending) {
      this._pendingVictoryPopup = false;
      this._victoryFirstUnlockPending = false;
    }
    if (this._victoryPopupHandle && typeof this._victoryPopupHandle.abort === "function") {
      this._victoryPopupHandle.abort();
    }
    this._victoryPopupHandle = null;
    this._victoryPopupActive = false;
  };

  /** Tear down any open/queued puzzle reveal (stage change, game over, Play Again). */
  Game.prototype._abortPuzzleReveal = function () {
    if (this._puzzleRevealTimer) {
      clearTimeout(this._puzzleRevealTimer);
      this._puzzleRevealTimer = null;
    }
    if (this._puzzleRevealActive) {
      this._puzzleRevealActive = false;
      this._hideOverlayWithFade(this.dom && this.dom.puzzleRevealOverlay);
      this._resumeFromDialog();
    }
    this._pendingPuzzlePiece = 0;
  };

  Game.prototype._onFlaskMergeVictory = function (payload) {
    payload = payload || {};
    if (payload.capped) return;
    if (payload.toTier == null) return;
    if (!(this.stateMachine && this.stateMachine.is("shake"))) return;
    if (this.testFlaskMode) return;
    if (this.victoryShownThisRun) return;
    if (!this._isDragonTier(payload.toTier)) return;
    if (!this.dom || !this.dom.victoryOverlay) return;

    this._pendingVictoryPopup = true;
    this._scheduleVictoryPopupShow();
  };

  Game.prototype._scheduleVictoryPopupShow = function () {
    var self = this;
    if (!this._pendingVictoryPopup) return;
    if (this._victoryPopupActive) return;
    if (this._isEvolutionRevealPending()) return;
    if (this._victorySettleTimer) return;

    this._victorySettleTimer = setTimeout(function () {
      self._victorySettleTimer = null;
      self._tryShowVictoryPopup();
    }, VICTORY_POPUP_TIMING.SETTLE_MS);
  };

  Game.prototype._tryShowVictoryPopup = function () {
    if (!this._pendingVictoryPopup) return;
    if (this._victoryPopupActive) return;
    if (!(this.stateMachine && this.stateMachine.is("shake"))) {
      this._pendingVictoryPopup = false;
      this._victoryFirstUnlockPending = false;
      return;
    }
    if (this._isEvolutionRevealPending()) {
      this._scheduleVictoryPopupShow();
      return;
    }
    this._pendingVictoryPopup = false;
    this._openVictoryPopup();
  };

  Game.prototype._openVictoryPopup = function () {
    var self = this;
    var dom = this.dom;
    if (!dom || !dom.victoryOverlay || !dom.victoryDragonImg || !dom.victoryHeadline
      || !dom.victoryFeedbackBlock || !dom.victoryFeedbackMount
      || !dom.victoryContinueBtn) {
      return;
    }

    var isFirstUnlock = !!this._victoryFirstUnlockPending;
    this._victoryFirstUnlockPending = false;
    var showFeedback = !(this.playerStats && this.playerStats.victoryFeedbackSubmitted);
    var devPreview = !!this._devPreviewVictory;

    this._abortVictoryPopup(false);
    this._victoryPopupActive = true;
    this._clearContinueBtnWiggleVisual();
    if (!devPreview) {
      this.victoryShownThisRun = true;
      this.saveProgressState("shake");
      this._trackAnalytics(AnalyticsEvents.VICTORY_SHOWN);
    }

    this._victoryPopupHandle = showVictoryOverlay({
      overlay: dom.victoryOverlay,
      dragonImg: dom.victoryDragonImg,
      headline: dom.victoryHeadline,
      feedbackBlock: dom.victoryFeedbackBlock,
      feedbackMount: dom.victoryFeedbackMount,
      continueBtn: dom.victoryContinueBtn,
      isFirstUnlock: isFirstUnlock,
      showFeedback: showFeedback,
      onPlaySound: function () {
        if (self.soundManager && typeof self.soundManager.play === "function") {
          self.soundManager.play("gold_orb");
        }
      },
      onContinue: function (result) {
        self._finishVictoryPopup(result, showFeedback);
      },
    });
    this._syncPlatformGameplay();
  };

  Game.prototype._finishVictoryPopup = function (result, showFeedback) {
    result = result || {};
    var devPreview = !!this._devPreviewVictory;
    this._victoryPopupHandle = null;
    this._victoryPopupActive = false;
    if (devPreview) {
      this._devPreviewVictory = false;
      this._syncPlatformGameplay();
      return;
    }

    if (showFeedback && result.moved && this.playerStats) {
      this.playerStats = recordVictoryFeedback(this.playerStats, result.value);
      this._savePlayerStats();
      this._trackAnalytics(AnalyticsEvents.VICTORY_FEEDBACK, {
        content_desire: this.playerStats.victoryFeedbackValue,
      });
    }
    this._trackAnalytics(AnalyticsEvents.VICTORY_CONTINUE);
    this._syncPlatformGameplay();
  };

  Game.prototype._shouldBlockInputForVictoryPopup = function () {
    return !!this._victoryPopupActive;
  };

  Game.prototype._victoryPopupAllowsEscDismiss = function () {
    return !!(this._victoryPopupActive && this.playerStats && this.playerStats.victoryFeedbackSubmitted);
  };

  Game.prototype._finishSecretRevealImmediate = function () {
    if (this._secretRevealSettleTimer) {
      clearTimeout(this._secretRevealSettleTimer);
      this._secretRevealSettleTimer = null;
    }
    this._discoveryRevealQueue.length = 0;
    this._abortVictoryPopup(true);
    this._abortPuzzleReveal();
    if (this._secretRevealHandle && typeof this._secretRevealHandle.abort === "function") {
      // abort() restores the panel and runs onAbort, which releases the hold.
      this._secretRevealHandle.abort();
      this._secretRevealHandle = null;
      return;
    }
    this._secretRevealActive = false;
    if (this.dom && this.dom.secretRevealOverlay) {
      this.dom.secretRevealOverlay.classList.add("hidden");
      this.dom.secretRevealOverlay.setAttribute("aria-hidden", "true");
      this.dom.secretRevealOverlay.classList.remove("secret-reveal-overlay--visible");
    }
    if (this.dom && this.dom.secretRevealPopoutMount) {
      this.dom.secretRevealPopoutMount.innerHTML = "";
    }
    if (this._evoRevealHold) {
      this._releaseEvolutionRevealHold();
    }
  };

  Game.prototype._isEvolutionRevealPending = function () {
    return !!(this._evoRevealHold || this._secretRevealActive || this._secretRevealSettleTimer
      || (this._discoveryRevealQueue && this._discoveryRevealQueue.length > 0));
  };

  Game.prototype._shouldSkipEvolutionDockTier = function (chain, tierIndex) {
    if (!chain || tierIndex !== chain.length - 1) return false;
    var gateTier = chain[tierIndex - 1];
    if (!gateTier) return false;
    return !this.unlockedTiers[gateTier.id];
  };

  Game.prototype.pickSpawnTier = function () {
    var chain = (typeof C.getActiveEvolutionChain === "function")
      ? C.getActiveEvolutionChain()
      : (C.EVOLUTION_CHAIN || []);
    var runMax = Math.max(0, Math.floor(Number(this.currentGameMaxTier) || 0));
    return pickSpawnTierFromRunMax(runMax, chain, Math.random.bind(Math));
  };

  Game.prototype.getFlaskSystem = function () {
    var bounds;
    if (!this.layout || !this.layout.flaskBounds) {
      this._ensureLayout();
    }
    if (!this.layout || !this.layout.flaskBounds) {
      this.layout = this.getDefaultLayout();
    }
    bounds = this.layout.flaskBounds;

    var onShakeStage = this.stateMachine && this.stateMachine.is && this.stateMachine.is("shake");
    var desiredPhysicsExtend = onShakeStage
      ? (C.FLASK_PHYSICS_WALL_EXTEND_UP_FRAC != null ? Number(C.FLASK_PHYSICS_WALL_EXTEND_UP_FRAC) : 0)
      : 0;
    if (!isFinite(desiredPhysicsExtend) || desiredPhysicsExtend < 0) desiredPhysicsExtend = 0;

    if (this.flaskSystem && sameBounds(this.flaskSystem.bounds, bounds)
      && Number(this.flaskSystem.physicsWallExtendUpFrac || 0) === desiredPhysicsExtend) {
      return this.flaskSystem;
    }

    var oldSys = this.flaskSystem;
    var scale = this.layout.scale;
    var activeChain = (typeof C.getActiveEvolutionChain === "function")
      ? C.getActiveEvolutionChain()
      : (C.EVOLUTION_CHAIN || []);
    var opts = {
      evolutionChain: activeChain,
      onUnlockTier: this.unlockTier.bind(this),
    };
    var w = bounds.right - bounds.left;
    var h = bounds.bottom - bounds.top;
    var paddingRatio = (typeof window !== "undefined" && C.FLASK_PADDING_RATIO != null) ? C.FLASK_PADDING_RATIO : 0.05;
    var legacyCutoff = (typeof window !== "undefined" && C.FLASK_CUTOFF_FRACTION != null) ? C.FLASK_CUTOFF_FRACTION : 0.9;
    var cutoffTopFraction = (typeof window !== "undefined" && C.FLASK_CUTOFF_TOP_FRACTION != null)
      ? C.FLASK_CUTOFF_TOP_FRACTION : legacyCutoff;
    var cutoffBottomFraction = (typeof window !== "undefined" && C.FLASK_CUTOFF_BOTTOM_FRACTION != null)
      ? C.FLASK_CUTOFF_BOTTOM_FRACTION : legacyCutoff;
    cutoffTopFraction = Math.max(0.05, Number(cutoffTopFraction) || legacyCutoff);
    cutoffBottomFraction = Math.max(0.05, Number(cutoffBottomFraction) || legacyCutoff);
    var flaskImageAspect = (typeof window !== "undefined" && C.FLASK_IMAGE_ELLIPSE_ASPECT != null)
      ? C.FLASK_IMAGE_ELLIPSE_ASPECT : 1.0;
    flaskImageAspect = Math.max(0.2, Number(flaskImageAspect) || 1.0);
    var flaskBodyRadiusRatio = (typeof window !== "undefined" && C.FLASK_IMAGE_BODY_RADIUS_RATIO != null)
      ? C.FLASK_IMAGE_BODY_RADIUS_RATIO : 1.0;
    flaskBodyRadiusRatio = Math.max(0.1, Number(flaskBodyRadiusRatio) || 1.0);
    var padding = Math.min(w, h) * paddingRatio;
    var rMaxWidth = (w - 2 * padding) / 2;
    var rMaxHeight = (h - 2 * padding) / (cutoffTopFraction + cutoffBottomFraction);
    var ryMaxByWidth = rMaxWidth / flaskImageAspect;
    var ellipseRyBase = Math.max(0, Math.min(ryMaxByWidth, rMaxHeight));
    var ellipseRy = ellipseRyBase * flaskBodyRadiusRatio;
    var ellipseRx = ellipseRy * flaskImageAspect;
    var sphereR = Math.max(0, Math.min(ellipseRx, ellipseRy));
    var effectiveDiameter = 2 * sphereR;
    var ratios = (typeof window !== "undefined" && C.FLASK_BODY_RADIUS_RATIOS) || [0.00424, 0.00568, 0.00712, 0.00856, 0.00992, 0.01136, 0.0128, 0.01424, 0.01568, 0.01704, 0.01848, 0.01992, 0.02136, 0.02272, 0.02416];
    var flaskScale = (typeof window !== "undefined" && C.FLASK_MERGE_SCALE != null) ? C.FLASK_MERGE_SCALE : 1.0;
    opts.bodyRadiusByTier = ratios.slice(0, activeChain.length).map(function (r) { return Math.max(4, effectiveDiameter * r * flaskScale); });
    opts.mergeFlashRadius = effectiveDiameter * 0.06;
    opts.physicsWallExtendUpFrac = desiredPhysicsExtend;
    var wallRatio = (typeof window !== "undefined" && C.FLASK_WALL_THICKNESS_BOUNDS_RATIO != null)
      ? Number(C.FLASK_WALL_THICKNESS_BOUNDS_RATIO)
      : 0.045;
    if (!isFinite(wallRatio) || wallRatio <= 0) wallRatio = 0.045;
    var wallThickness = Math.min(w, h) * wallRatio;
    opts.flaskWallThickness = Math.max(4, Math.min(28, wallThickness));
    this.flaskSystem = new FlaskSystem(opts, bounds);
    if (this.isRunFlaskShattered && typeof this.flaskSystem.shatterFlask === "function") {
      this.flaskSystem.shatterFlask();
    }
    if (oldSys && oldSys.bodies && oldSys.bodies.length > 0) {
      this.migrateFlaskBodies(oldSys, bounds);
    }
    return this.flaskSystem;
  };

  Game.prototype.migrateFlaskBodies = function (oldSys, newBounds) {
    if (!oldSys || !oldSys.bodies || oldSys.bodies.length === 0) return;
    var oldB = oldSys.bounds;
    var newB = newBounds;
    var oldW = oldB.right - oldB.left;
    var oldH = oldB.bottom - oldB.top;
    var newW = newB.right - newB.left;
    var newH = newB.bottom - newB.top;
    if (oldW <= 0 || oldH <= 0 || newW <= 0 || newH <= 0) return;
    var scaleX = newW / oldW;
    var scaleY = newH / oldH;
    var scaleAvg = (scaleX + scaleY) / 2;

    function clamp(v, min, max) {
      return Math.max(min, Math.min(max, v));
    }

    function clampToNewFlaskInterior(flaskSystem, radiusTier, x, y) {
      var geom = flaskSystem && flaskSystem._flaskGeometry;
      var radiusByTier = flaskSystem && flaskSystem.bodyRadiusByTier ? flaskSystem.bodyRadiusByTier : [];
      var r = radiusByTier[radiusTier] != null ? radiusByTier[radiusTier] : 0;

      if (geom) {
        var wall = geom.W != null ? geom.W : 0;
        var innerL = geom.L + wall;
        var innerR = geom.R - wall;
        var yMin = geom.T + r;
        var yMax = geom.bodyBottom - r;
        if (yMax < yMin) yMax = yMin;
        var cy = clamp(y, yMin, yMax);
        var xMin = innerL + r;
        var xMax = innerR - r;
        if (xMax < xMin) xMax = xMin;
        var cx = clamp(x, xMin, xMax);
        return { x: cx, y: cy };
      }

      // Fallback to rectangular panel clamp when geometry is unavailable.
      return {
        x: clamp(x, newB.left + r, newB.right - r),
        y: clamp(y, newB.top + r, newB.bottom - r),
      };
    }

    for (var i = 0; i < oldSys.bodies.length; i++) {
      var m = oldSys.bodies[i];
      var px = m.body.position.x;
      var py = m.body.position.y;
      var normX = (px - oldB.left) / oldW;
      var normY = (py - oldB.top) / oldH;
      var newX = newB.left + normX * newW;
      var newY = newB.top + normY * newH;
      var clampTier = getMetaPhysicsSizeTier(m);
      var clamped = clampToNewFlaskInterior(this.flaskSystem, clampTier, newX, newY);
      var newMeta = this.flaskSystem.addBodyAt(m.tier, clamped.x, clamped.y, this._bodyOptsFromFlaskMeta(m));

      // Preserve physical state across stage/layout transitions so world scales with visuals.
      var Body = Matter.Body ? Matter.Body : null;
      if (Body && newMeta && newMeta.body && m.body) {
        if (typeof m.body.angle === "number" && typeof Body.setAngle === "function") {
          Body.setAngle(newMeta.body, m.body.angle);
        }
        if (m.body.velocity && typeof Body.setVelocity === "function") {
          Body.setVelocity(newMeta.body, {
            x: (m.body.velocity.x || 0) * scaleX,
            y: (m.body.velocity.y || 0) * scaleY,
          });
        }
        if (typeof m.body.angularVelocity === "number" && typeof Body.setAngularVelocity === "function") {
          Body.setAngularVelocity(newMeta.body, m.body.angularVelocity * scaleAvg);
        }
      }
    }
  };

  /**
   * After Fall→Shake migration, zero inherited runway velocities and run sealed physics
   * so the first Merge frame does not show bodies drifting in from stale motion.
   */
  Game.prototype._stabilizeFlaskBodiesAfterHandoff = function (sys) {
    sys = sys || this.flaskSystem;
    if (!sys || !sys.bodies || sys.bodies.length === 0) return;
    var Body = Matter.Body ? Matter.Body : null;
    for (var i = 0; i < sys.bodies.length; i++) {
      var b = sys.bodies[i] && sys.bodies[i].body;
      if (!b) continue;
      if (Body && typeof Body.setVelocity === "function") {
        Body.setVelocity(b, { x: 0, y: 0 });
      }
      if (Body && typeof Body.setAngularVelocity === "function") {
        Body.setAngularVelocity(b, 0);
      }
    }
    if (typeof sys.setMouthSealed === "function") {
      sys.setMouthSealed(true);
    }
    if (typeof sys.ensureEngine === "function") {
      sys.ensureEngine();
    }
    if (typeof sys.update !== "function") return;
    var stepMs = (typeof window !== "undefined" && C.FLASK_PHYSICS_STEP_MS != null)
      ? Number(C.FLASK_PHYSICS_STEP_MS)
      : (1000 / 60);
    if (!isFinite(stepMs) || stepMs <= 0) stepMs = 1000 / 60;
    var settleMs = (typeof window !== "undefined" && C.FLASK_HANDOFF_SETTLE_MS != null)
      ? Number(C.FLASK_HANDOFF_SETTLE_MS)
      : 900;
    if (!isFinite(settleMs) || settleMs < 0) settleMs = 900;
    var steps = Math.max(1, Math.round(settleMs / stepMs));
    for (var s = 0; s < steps; s++) {
      sys.update(stepMs);
    }
  };

  Game.prototype.applyRunHudBounds = function () {
    if (!this.dom || !this.layout || !this.layout.runBounds) return;
    if (this._isVerticalLayout()) {
      this.applyLayoutChromeBounds();
      this.applyPlayChromeBounds();
      this.applyEvolutionRingBounds(true);
      this.applyStashPanelBounds(true);
      this.applyStageChromeRightBounds(true);
      this.applyStageChromeLeftBounds(true);
      var isFall = this.stateMachine && this.stateMachine.is && this.stateMachine.is("fall");
      if (isFall) {
        this.updateStashPanelDOM();
        this.populateEvolutionChrome();
      }
      return;
    }
    var score = this.dom.scorePanel;
    var logo = this.dom.fallLogoPanel;
    var hudStrip = this.dom.fallHudStrip;
    var runStrip = this.dom.fallRunStrip;
    var rightMod = this.dom.fallRightHudModule;
    if (!(this.stateMachine && this.stateMachine.is && this.stateMachine.is("fall"))) {
      if (this.dom.playerHud && this.dom.playerHud.style) {
        this.dom.playerHud.style.left = "";
        this.dom.playerHud.style.width = "";
        this.dom.playerHud.style.top = "";
        this.dom.playerHud.style.height = "";
        this.dom.playerHud.style.padding = "";
        this.dom.playerHud.classList.remove("player-hud--fall-sidebar");
        this.dom.playerHud.classList.remove("player-hud--fall-topright");
      }
      if (hudStrip) {
        hudStrip.style.left = "";
        hudStrip.style.top = "";
        hudStrip.style.width = "";
        hudStrip.style.height = "";
        hudStrip.classList.add("hidden");
      }
      if (runStrip) {
        runStrip.style.left = "";
        runStrip.style.top = "";
        runStrip.style.width = "";
        runStrip.style.height = "";
        runStrip.classList.add("hidden");
      }
      if (logo && logo.style) {
        logo.style.left = "";
        logo.style.top = "";
        logo.style.width = "";
        logo.style.height = "";
      }
      if (rightMod && rightMod.style) {
        rightMod.style.left = "";
        rightMod.style.top = "";
        rightMod.style.width = "";
        rightMod.style.height = "";
        if (rightMod.style.removeProperty) {
          rightMod.style.removeProperty("--fall-gameplay-nudge-y");
          rightMod.style.removeProperty("--fall-hud-label-extra-down-y");
        }
        rightMod.classList.remove("fall-right-hud-module--left-cluster");
        rightMod.classList.add("hidden");
      }
      if (this.dom && this.dom.fallPauseBtn) {
        this.dom.fallPauseBtn.style.left = "";
        this.dom.fallPauseBtn.style.top = "";
      }
      if (score && score.style) {
        score.style.left = "";
        score.style.top = "";
        score.style.width = "";
        score.style.height = "";
        score.style.minHeight = "";
      }
      this.applyEvolutionRingBounds(true);
      this.applyStashPanelBounds(true);
      this.applyStageChromeRightBounds(true);
      this.applyStageChromeLeftBounds(true);
      this.applyPlayChromeBounds(true);
      this.applyShakeJarChromeBounds(true);
      return;
    }
    var b = this.layout.runBounds;
    var sidebar = this.layout.fallSidebarBounds;
    var topHudH = getFallTopHudHeightPx(this);
    this.applyUiScaleVars();
    if (hudStrip) {
      hudStrip.classList.add("hidden");
      hudStrip.style.left = "";
      hudStrip.style.top = "";
      hudStrip.style.width = "";
      hudStrip.style.height = "";
    }
    var rightStrip = this.layout.rightStrip;
    if (this.dom.playerHud && this.dom.playerHud.style) {
      this.dom.playerHud.style.left = "";
      this.dom.playerHud.style.width = "";
      this.dom.playerHud.style.top = "";
      this.dom.playerHud.style.height = "";
      this.dom.playerHud.style.padding = "";
      this.dom.playerHud.classList.remove("player-hud--fall-sidebar");
      this.dom.playerHud.classList.remove("player-hud--fall-topright");
    }
    if (runStrip) {
      runStrip.classList.add("hidden");
      runStrip.style.left = "";
      runStrip.style.top = "";
      runStrip.style.width = "";
      runStrip.style.height = "";
    }
    this.applyPlayChromeBounds();
    if (this.dom && this.dom.fallPauseBtn) {
      this.dom.fallPauseBtn.style.left = "";
      this.dom.fallPauseBtn.style.top = "";
      this._syncFallPauseButton();
    }
    if (score && score.style && this.layout.flaskPanelOrigin) {
      if (rightMod && rightMod.style) {
        rightMod.classList.remove("hidden");
        rightMod.classList.remove("fall-right-hud-module--left-cluster");
        if (rightMod.style.removeProperty) {
          rightMod.style.removeProperty("--fall-gameplay-nudge-y");
          rightMod.style.removeProperty("--fall-hud-label-extra-down-y");
        }
      }
      if (logo && logo.style) {
        logo.style.left = "";
        logo.style.top = "";
        logo.style.width = "";
        logo.style.height = "";
      }
      if (score && score.style) {
        score.style.left = "";
        score.style.top = "";
        score.style.width = "100%";
        score.style.height = "";
        score.style.minHeight = "";
      }
    }
    this.applyStageChromeLeftBounds();
    this.applyStashPanelBounds();
    this.applyStageChromeRightBounds();
    this.applyPlayChromeBounds();
    this.applyEvolutionRingBounds();
    this.populateEvolutionChrome();
  };

  Game.prototype.applyStashPanelBounds = function (clearOnly) {
    var panel = this.dom && this.dom.fallStashPanel;
    if (!panel) return;
    var isFall = this.stateMachine && this.stateMachine.is && this.stateMachine.is("fall");
    if (clearOnly || !isFall) {
      panel.classList.add("hidden");
      panel.style.left = "";
      panel.style.top = "";
      panel.style.width = "";
      panel.style.height = "";
      return;
    }
    panel.classList.remove("hidden");
    this.updateStashPanelDOM();
  };

  Game.prototype.applyEvolutionRingBounds = function (clearOnly) {
    var wrap = this.dom && this.dom.evoRingWrap;
    if (!wrap || !wrap.style) return;
    if (this._isVerticalLayout()) {
      if (clearOnly) {
        wrap.style.left = "";
        wrap.style.top = "";
        wrap.style.width = "";
        wrap.style.height = "";
        wrap.classList.remove("evo-ring-wrap--shake");
      } else {
        var isShakeVertical = this.stateMachine && this.stateMachine.is && this.stateMachine.is("shake");
        var isFallVertical = this.stateMachine && this.stateMachine.is && this.stateMachine.is("fall");
        if (isFallVertical || isShakeVertical) {
          this.setEvolutionDockVisible(true);
        }
      }
      return;
    }
    if (clearOnly) {
      wrap.style.left = "";
      wrap.style.top = "";
      wrap.style.width = "";
      wrap.style.height = "";
      wrap.classList.remove("evo-ring-wrap--shake");
      return;
    }
    var isShake = this.stateMachine && this.stateMachine.is && this.stateMachine.is("shake");
    var isFall = this.stateMachine && this.stateMachine.is && this.stateMachine.is("fall");
    // Shake now uses the same 1:4:1 grid + left column flow as Fall, so the evo
    // ring sits at the column bottom (margin-top:auto). The legacy absolute
    // --shake placement collapsed it onto the logo card.
    wrap.classList.remove("evo-ring-wrap--shake");
    if (isFall || isShake) {
      wrap.classList.remove("hidden");
      wrap.style.position = "";
      wrap.style.left = "";
      wrap.style.top = "";
      wrap.style.width = "";
      wrap.style.height = "";
      wrap.style.zIndex = "";
      wrap.style.pointerEvents = "none";
    }
  };

  Game.prototype.dropIntoFlask = function (tier, screenX, fallMouthCenterX, fallMouthWidth) {
    this._noteTierForAnalytics(tier);
    var sys = this.getFlaskSystem();
    var fallPanelWidth = this.layout ? this.layout.runWidth : 600;
    sys.dropIntoFlask(tier, screenX, fallPanelWidth, fallMouthCenterX, fallMouthWidth);
  };

  Game.prototype.addToBoostBar = function (tier) {
    this._noteTierForAnalytics(tier);
    this.boostBar.push(tier);
    this.updateBoostBarUI();
    var stashMounts = [];
    if (this._isVerticalLayout()) {
      if (this.dom && this.dom.verticalStashMount) stashMounts.push(this.dom.verticalStashMount);
    } else if (this.dom && this.dom.fallStashMount) {
      stashMounts.push(this.dom.fallStashMount);
    }
    for (var si = 0; si < stashMounts.length; si++) {
      stashMounts[si].classList.remove("stash--bounce");
      void stashMounts[si].offsetWidth;
      stashMounts[si].classList.add("stash--bounce");
    }
  };

  /** @deprecated Legacy name — updates the Fall stash panel only. */
  Game.prototype.updateBoostBarUI = function () {
    this.updateStashPanelDOM();
  };

  Game.prototype._getFlaskVisualShakeOffset = function () {
    if (!this.stateMachine || !this.stateMachine.is || !this.stateMachine.is("shake")) {
      return { x: 0, y: 0 };
    }
    var nowShake = Date.now();
    if (nowShake >= this.flaskVisualShakeUntil) return { x: 0, y: 0 };
    var shakeStart = this.flaskVisualShakeStart || nowShake;
    var shakeDur = Math.max(1, this.flaskVisualShakeUntil - shakeStart);
    var shakeElapsed = nowShake - shakeStart;
    var decay = Math.max(0, (this.flaskVisualShakeUntil - nowShake) / shakeDur);
    var amp = Math.max(0, this.flaskVisualShakeStrength * decay);
    return { x: Math.sin(shakeElapsed * 0.42) * amp, y: 0 };
  };

  Game.prototype._drawShakeFlaskDynamicLayer = function (ctx, flaskHp) {
    if (!ctx || !this.layout) return;
    var sys = this.getFlaskSystem();
    var layout = this.layout;
    var W = layout.flaskPanelWidth;
    var H = layout.flaskPanelHeight;
    var b = layout.flaskBounds;
    if (!b || W <= 0 || H <= 0) return;
    var shake = this._getFlaskVisualShakeOffset();
    var jarOrigin = {
      x: layout.flaskPanelOrigin.x + shake.x,
      y: layout.flaskPanelOrigin.y + shake.y,
    };
    if (this.pixi && typeof this.pixi.setWorldClip === "function") {
      this.pixi.setWorldClip(
        layout.flaskPanelOrigin.x,
        layout.flaskPanelOrigin.y,
        W,
        H
      );
    }
    var usedPixiJar = false;
    if (sys && sys._flaskGeometry) {
      var jarLayout = layoutShakeJarChrome(
        sys._flaskGeometry,
        jarOrigin,
        this._getShakeJarCornerRadius(
          Math.max(24, sys._flaskGeometry.neckRight - sys._flaskGeometry.neckLeft),
          Math.max(24, sys._flaskGeometry.bodyBottom - sys._flaskGeometry.T)
        )
      );
      usedPixiJar = syncShakeJarChrome(this.pixi, jarLayout);
    }
    if (usedPixiJar) {
      if (sys) {
        sys.draw(ctx, {
          flaskHp: flaskHp,
          playfield: this.pixi,
          worldOrigin: jarOrigin,
        });
        this.drawOverflowWarning(ctx, sys);
      }
      if (this._showLayoutBoundsDebug && this._showLayoutBoundsDebug()) {
        ctx.save();
        ctx.translate(jarOrigin.x, jarOrigin.y);
        this.drawMergeFlaskPhysicsDebug(ctx, sys);
        ctx.restore();
      }
      return;
    }
    ctx.save();
    ctx.translate(layout.flaskPanelOrigin.x, layout.flaskPanelOrigin.y);
    ctx.beginPath();
    ctx.rect(0, 0, W, H);
    ctx.clip();
    ctx.save();
    ctx.translate(shake.x, shake.y);
    if (sys && sys._flaskGeometry) {
      this._strokeFlaskWallOutlines(ctx, sys._flaskGeometry, { fallChrome: true, openTop: true });
    }
    if (sys) {
      sys.draw(ctx, {
        flaskHp: flaskHp,
        playfield: null,
        worldOrigin: jarOrigin,
      });
      this.drawOverflowWarning(ctx, sys);
    }
    this.drawMergeFlaskPhysicsDebug(ctx, sys);
    ctx.restore();
    ctx.restore();
  };

  Game.prototype.drawFlaskPanel = function (ctx, flaskHpOverride) {
    if (!ctx || !this.layout) return;
    var sys = this.getFlaskSystem();
    var flaskHp = flaskHpOverride != null
      ? flaskHpOverride
      : (this.runPhase && this.runPhase.player ? this.runPhase.player.hp : 3);
    var layout = this.layout;
    var W = layout.flaskPanelWidth;
    var H = layout.flaskPanelHeight;
    var b = layout.flaskBounds;
    var innerW = b.right - b.left;
    var innerH = b.bottom - b.top;
    var letterboxFall = !!(layout.isLandscape && this.stateMachine && this.stateMachine.is && this.stateMachine.is("fall")
      && innerW > 0 && innerH > 0
      && (b.left > 0.5 || b.top > 0.5 || b.right < W - 0.5 || b.bottom < H - 0.5));
    var isFallStage = !!(this.stateMachine && this.stateMachine.is && this.stateMachine.is("fall"));
    if (isFallStage) {
      // Fall flask invisible + sky/sea background own right strip — skip legacy panel backdrop.
      return;
    }
    var isShake = !!(this.stateMachine && this.stateMachine.is && this.stateMachine.is("shake"));
    if (isShake) {
      // Pixi owns fill + rim as Graphics (underFx / effect). Skip the 2D chrome
      // bake so the leftover overlay canvas is not painted for the jar.
      if (!this.pixi) {
        if (this._rebuildShakeChromeCache()) {
          this._blitShakeChromeCache(ctx);
        } else {
          ctx.save();
          ctx.translate(layout.flaskPanelOrigin.x, layout.flaskPanelOrigin.y);
          ctx.beginPath();
          ctx.rect(0, 0, W, H);
          ctx.clip();
          this.drawShakeFlaskChrome(ctx, W, H, b);
          ctx.restore();
        }
      }
      this._drawShakeFlaskDynamicLayer(ctx, flaskHp);
      return;
    }
    ctx.save();
    ctx.translate(layout.flaskPanelOrigin.x, layout.flaskPanelOrigin.y);
    ctx.beginPath();
    ctx.rect(0, 0, W, H);
    ctx.clip();
    // Fall mini-flask is letterboxed inside the right panel. When the NEXT strip exists,
    // `drawFallNextColumnBackdrop` already paints one lifted surface across NEXT+flask width
    // in screen space; skip a second local-origin paint here to avoid a visible seam.
    if (letterboxFall && !(layout.shakeNextColumnWidth > 0)) {
      this._paintLiftedSurface(ctx, 0, 0, W, H, { grid: false, highlight: false });
    }
    if (!letterboxFall) {
      this.drawLiquidBackground(ctx, W, H);
    }
    ctx.fillStyle = "#DFF3FC";
    ctx.strokeStyle = "#BBDEF0";
    ctx.lineWidth = 1;
    if (!layout.isLandscape) {
      ctx.fillRect(0, -4, W, 8);
      ctx.strokeRect(0, -4, W, 8);
    }
    if (letterboxFall) {
      ctx.save();
      ctx.beginPath();
      ctx.rect(b.left, b.top, innerW, innerH);
      ctx.clip();
      ctx.translate(b.left, b.top);
      this.drawLiquidBackground(ctx, innerW, innerH);
      ctx.restore();
    }
    var shake = this._getFlaskVisualShakeOffset();
    ctx.save();
    if (letterboxFall) {
      var clipPad = (typeof window !== "undefined" && C.FLASK_MINI_VIEWPORT_CLIP_PAD_PX != null)
        ? Math.max(0, Number(C.FLASK_MINI_VIEWPORT_CLIP_PAD_PX))
        : 28;
      var cLeft = Math.max(0, b.left - clipPad);
      var cTop = Math.max(0, b.top - clipPad);
      var cRight = Math.min(W, b.left + innerW + clipPad);
      var cBottom = Math.min(H, b.top + innerH + clipPad);
      var cW = Math.max(1, cRight - cLeft);
      var cH = Math.max(1, cBottom - cTop);
      ctx.beginPath();
      ctx.rect(cLeft, cTop, cW, cH);
      ctx.clip();
    }
    ctx.translate(shake.x, shake.y);
    if (sys && sys._flaskGeometry) {
      var minimalFlaskChrome = letterboxFall;
      this._strokeFlaskWallOutlines(ctx, sys._flaskGeometry, { fallChrome: minimalFlaskChrome, openTop: false });
    }
    sys.draw(ctx, {
      flaskHp: flaskHp,
      playfield: this.pixi || null,
      worldOrigin: {
        x: layout.flaskPanelOrigin.x + shake.x,
        y: layout.flaskPanelOrigin.y + shake.y,
      },
    });
    this.drawOverflowWarning(ctx, sys);
    ctx.restore();
    ctx.restore();
  };

  Game.prototype._getOverflowWarningPulseMs = function () {
    var ms = (typeof window !== "undefined" && C.FLASK_OVERFLOW_WARN_BLINK_MS != null)
      ? Number(C.FLASK_OVERFLOW_WARN_BLINK_MS)
      : 1400;
    if (!isFinite(ms) || ms < 400) ms = 1400;
    return ms;
  };

  Game.prototype._getOverflowUiStage = function () {
    var s = this.overflow && this.overflow.state;
    if (s === "alert") return "critical";
    if (s === "warning") return "near";
    return null;
  };

  /**
   * Fall catcher flask — physics walls in run-column coordinates.
   * @param {CanvasRenderingContext2D} ctx
   */
  Game.prototype.drawFallFlaskPhysicsDebug = function (ctx) {
    if (!this._showLayoutBoundsDebug() || !ctx) return;
    var fp = this.runPhase && this.runPhase.fallPhysics;
    var geom = fp && fp.flaskGeometry;
    if (!geom) return;
    if (FlaskRenderer.drawFlaskPhysicsOutline) {
      FlaskRenderer.drawFlaskPhysicsOutline(ctx, geom);
    }
    var walls = fp.flaskWalls;
    if (walls && FlaskRenderer.drawMouthGateLine) {
      for (var i = 0; i < walls.length; i++) {
        if (walls[i] && walls[i].label === "flask_mouth_lid") {
          FlaskRenderer.drawMouthGateLine(ctx, walls[i]);
          break;
        }
      }
    }
  };

  /**
   * Merge jar — layout bounds rect, physics walls, mouth gate, overflow zone lines.
   * @param {CanvasRenderingContext2D} ctx
   * @param {import("../flask/flask-system.js").FlaskSystem|null|undefined} sys
   */
  Game.prototype.drawMergeFlaskPhysicsDebug = function (ctx, sys) {
    if (!this._showLayoutBoundsDebug() || !ctx || !sys) return;
    var bounds = sys.bounds;
    if (bounds && bounds.left != null && bounds.right != null && bounds.top != null && bounds.bottom != null) {
      ctx.save();
      ctx.strokeStyle = "rgba(132, 204, 22, 0.9)";
      ctx.lineWidth = 1.5;
      ctx.setLineDash([4, 3]);
      ctx.strokeRect(bounds.left, bounds.top, bounds.right - bounds.left, bounds.bottom - bounds.top);
      ctx.setLineDash([]);
      ctx.fillStyle = "rgba(132, 204, 22, 0.95)";
      ctx.font = "bold 10px monospace";
      ctx.textAlign = "left";
      ctx.textBaseline = "top";
      ctx.fillText("jar bounds", bounds.left + 2, bounds.top + 2);
      ctx.restore();
    }
    if (sys._flaskGeometry && FlaskRenderer.drawFlaskPhysicsOutline) {
      FlaskRenderer.drawFlaskPhysicsOutline(ctx, sys._flaskGeometry);
    }
    if (FlaskRenderer.drawMouthGateLine) {
      if (sys.mouthSealBodies && sys.mouthSealBodies.length) {
        for (var si = 0; si < sys.mouthSealBodies.length; si++) {
          FlaskRenderer.drawMouthGateLine(ctx, sys.mouthSealBodies[si]);
        }
      } else {
        FlaskRenderer.drawMouthGateLine(ctx, sys.mouthSealBody);
      }
    }
    this.drawOverflowCheckLineDebug(ctx, {
      flaskBounds: bounds,
      checkY: getOverflowAlertY(sys),
      nearFullY: getOverflowWarningY(sys),
    });
  };

  /**
   * Debug overlay: warning + alert zone lines.
   * @param {CanvasRenderingContext2D} ctx
   * @param {{ flaskBounds?: object, checkY?: number|null, nearFullY?: number|null }} area
   */
  Game.prototype.drawOverflowCheckLineDebug = function (ctx, area) {
    if (!ctx || !area) return;
    if (!C.SHOW_OVERFLOW_CHECK_LINE && !this._showLayoutBoundsDebug()) return;
    var fb = area.flaskBounds;
    if (!fb || fb.left == null || fb.right == null) return;

    var x1 = fb.left + 3;
    var x2 = fb.right - 3;
    var drawLine = function (y, color, label, dash) {
      if (y == null || !isFinite(y)) return;
      ctx.save();
      if (typeof ctx.setLineDash === "function") ctx.setLineDash(dash || []);
      ctx.strokeStyle = color;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(x1, y);
      ctx.lineTo(x2, y);
      ctx.stroke();
      if (typeof ctx.setLineDash === "function") ctx.setLineDash([]);
      ctx.fillStyle = color;
      ctx.font = "bold 10px monospace";
      ctx.textAlign = "left";
      ctx.textBaseline = "bottom";
      ctx.fillText(label, x1 + 2, y - 2);
      ctx.restore();
    };

    drawLine(area.nearFullY, "rgba(255, 193, 7, 0.95)", "warning", [5, 4]);
    drawLine(area.checkY, "rgba(255, 45, 120, 0.92)", "alert", [7, 5]);
  };

  /**
   * Rim-light the creatures poking above the warning/alert zone lines.
   * Only the part above each zone line glows. Draw after FlaskSystem.draw, in the
   * same flask-panel transform as the bodies.
   * @param {CanvasRenderingContext2D} ctx
   * @param {import("../flask/flask-system.js").FlaskSystem|null|undefined} sys
   */
  Game.prototype.drawOverflowWarning = function (ctx, sys) {
    if (!ctx || !this.overflow) return;
    var now = typeof performance !== "undefined" ? performance.now() : Date.now();
    drawOverflowOutlines(ctx, {
      system: sys,
      state: this.overflow,
      now: now,
      playfield: this.pixi || null,
      worldOrigin: this.layout && this.layout.flaskPanelOrigin
        ? {
          x: this.layout.flaskPanelOrigin.x + this._getFlaskVisualShakeOffset().x,
          y: this.layout.flaskPanelOrigin.y + this._getFlaskVisualShakeOffset().y,
        }
        : { x: 0, y: 0 },
    });
  };

  Game.prototype.drawEvolutionDockBackdrop = function (ctx, layout) {
    if (!ctx || !layout || !layout.evolutionDockBounds) return;
    var b = layout.evolutionDockBounds;
    ctx.save();
    // Match the lifted indigo + faint grid of the DOM .evolution-chain-dock so the seam disappears.
    this._paintLiftedSurface(ctx, b.x, b.y, b.width, b.height, { highlight: false });
    ctx.restore();
  };

  /**
   * Shake evolution-dock riser band; Fall bottom dock strip when present.
   * Play column has no canvas overlays (sky/sea only).
   */
  Game.prototype.drawWorkspaceStructuralAmbient = function (ctx) {
    if (!ctx || !this.layout) return;
    var layout = this.layout;
    if (!layout || layout.canvasWidth == null || layout.canvasHeight == null) return;
    var W = layout.canvasWidth;
    var H = layout.canvasHeight;
    var isShake = !!(this.stateMachine && this.stateMachine.is && this.stateMachine.is("shake"));

    ctx.save();

    /* Left strip HUD is DOM mockup chrome (#fallRightHudModule); skip legacy canvas panel. */
    if (!isShake && layout.evolutionDockBounds) {
      this.drawEvolutionDockBackdrop(ctx, layout);
    }

    // night.html has no bottom evolution-dock bridge (evo ring lives in the left
    // column); the old white bridge gradient washed out the night sea. Skip it.

    if (!isShake) {
      this.drawFallHeartsOverlay(ctx);
    }

    if (runtimeFlags.drawScorePopups !== false && this.scorePopups && this.scorePopups.length) {
      this.scorePopups = pruneScorePopups(this.scorePopups);
      drawScorePopups(ctx, this.scorePopups, undefined, Object.assign({}, this._getScorePopupDrawOpts(), {
        playfield: this.pixi || null,
      }));
    }

    ctx.restore();
  };

  Game.prototype._getCanvasNextTubeWidthPx = function (columnWidthPx) {
    var w = Math.max(0, Number(columnWidthPx) || 0);
    if (w <= 0) {
      return (typeof window !== "undefined" && C.FLASK_DROP_QUEUE_TUBE_WIDTH_MIN_PX != null)
        ? Number(C.FLASK_DROP_QUEUE_TUBE_WIDTH_MIN_PX)
        : 44;
    }
    var frac = (typeof window !== "undefined" && C.FLASK_DROP_QUEUE_TUBE_WIDTH_FRAC != null)
      ? Number(C.FLASK_DROP_QUEUE_TUBE_WIDTH_FRAC)
      : 0.72;
    var minW = (typeof window !== "undefined" && C.FLASK_DROP_QUEUE_TUBE_WIDTH_MIN_PX != null)
      ? Number(C.FLASK_DROP_QUEUE_TUBE_WIDTH_MIN_PX)
      : 44;
    var maxW = (typeof window !== "undefined" && C.FLASK_DROP_QUEUE_TUBE_WIDTH_MAX_PX != null)
      ? Number(C.FLASK_DROP_QUEUE_TUBE_WIDTH_MAX_PX)
      : 70;
    return Math.max(minW, Math.min(maxW, w * frac));
  };

  Game.prototype._getDropQueuePreviewRadiusPx = function (nextColW) {
    var tubeW = this._getCanvasNextTubeWidthPx(nextColW);
    var fill = (typeof window !== "undefined" && C.FLASK_DROP_QUEUE_PREVIEW_TUBE_FILL != null)
      ? Number(C.FLASK_DROP_QUEUE_PREVIEW_TUBE_FILL)
      : 0.76;
    var minR = (typeof window !== "undefined" && C.FLASK_DROP_QUEUE_PREVIEW_RADIUS_MIN_PX != null)
      ? Number(C.FLASK_DROP_QUEUE_PREVIEW_RADIUS_MIN_PX)
      : 16;
    var maxR = (typeof window !== "undefined" && C.FLASK_DROP_QUEUE_PREVIEW_RADIUS_MAX_PX != null)
      ? Number(C.FLASK_DROP_QUEUE_PREVIEW_RADIUS_MAX_PX)
      : 26;
    var r = Math.floor((tubeW * fill) / 2);
    return Math.max(minR, Math.min(maxR, r));
  };

  /**
   * Stage II canvas NEXT queue: how many preview icons fit between queueStartY and queueBottomY (icon centers).
   * Overflow "+N" is drawn in the bottom band — icons are not removed to make room for it.
   */
  Game.prototype._getDropQueueVisibleSlots = function (totalCount, queueStartY, queueBottomY, spacing) {
    if (totalCount <= 0) return { visible: 0, hidden: 0 };
    var maxFit = Math.max(1, Math.floor((queueBottomY - queueStartY) / spacing) + 1);
    var visible = Math.min(totalCount, maxFit);
    return { visible: visible, hidden: Math.max(0, totalCount - visible) };
  };

  Game.prototype._drawDropQueueOverflowCount = function (ctx, x, y, count) {
    if (!ctx || count <= 0) return;
    var label = "+" + count;
    var fontPx = (typeof window !== "undefined" && C.FLASK_DROP_QUEUE_OVERFLOW_FONT_PX != null)
      ? Number(C.FLASK_DROP_QUEUE_OVERFLOW_FONT_PX)
      : 15;
    if (!isFinite(fontPx) || fontPx < 10) fontPx = 15;
    ctx.save();
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.font = "700 " + fontPx + "px 'Baloo 2', 'Fredoka', sans-serif";
    var metrics = ctx.measureText(label);
    var padX = 10;
    var padY = 4;
    var pillW = metrics.width + padX * 2;
    var pillH = fontPx + padY * 2;
    var pillX = x - pillW / 2;
    var pillY = y - pillH / 2;
    ctx.fillStyle = "rgba(255, 255, 255, 0.85)";
    this._roundedRectPath(ctx, pillX, pillY, pillW, pillH, pillH / 2);
    ctx.fill();
    ctx.fillStyle = "rgba(27, 94, 125, 0.95)";
    ctx.shadowColor = "rgba(31, 118, 160, 0.3)";
    ctx.shadowBlur = 6;
    ctx.fillText(label, x, y);
    ctx.restore();
  };

  Game.prototype.drawCanvasTestTubeColumn = function (ctx, x, y, w, h, label, opts) {
    if (!ctx || w <= 0 || h <= 0) return;
    opts = opts || {};
    var cx = x + w / 2;
    var tubeW = this._getCanvasNextTubeWidthPx(w);
    var tubeTop = opts.tubeTop != null ? opts.tubeTop : (y + 34);
    var tubeBottom = opts.tubeBottom != null ? opts.tubeBottom : (y + h - 4);
    if (tubeBottom <= tubeTop + 20) {
      tubeBottom = tubeTop + (opts.tubeBottom != null ? 20 : 80);
    }
    var tubeH = tubeBottom - tubeTop;

    ctx.save();
    // Sunlit water column matches the rest of the canvas chrome (full container — not tube height).
    this._paintLiftedSurface(ctx, x, y, w, h, { highlight: false });

    var innerX = cx - tubeW / 2 + 1;
    var innerY = tubeTop + 1;
    var innerW = tubeW - 2;
    var innerH = tubeH - 2;
    ctx.save();
    this._roundedRectPath(ctx, innerX, innerY, innerW, innerH, innerW / 2);
    ctx.clip();
    var verticalFill = ctx.createLinearGradient(0, innerY, 0, innerY + innerH);
    verticalFill.addColorStop(0, "#FFFFFF");
    verticalFill.addColorStop(1, "#DFF3FC");
    ctx.fillStyle = verticalFill;
    ctx.fillRect(innerX, innerY, innerW, innerH);

    var innerShade = ctx.createLinearGradient(0, innerY, 0, innerY + Math.min(14, innerH));
    innerShade.addColorStop(0, "rgba(31, 118, 160, 0.15)");
    innerShade.addColorStop(1, "rgba(31, 118, 160, 0)");
    ctx.fillStyle = innerShade;
    ctx.fillRect(innerX, innerY, innerW, Math.min(14, innerH));
    ctx.restore();

    ctx.strokeStyle = "rgba(255, 255, 255, 0.95)";
    ctx.lineWidth = 3;
    ctx.shadowBlur = 0;
    this._roundedRectPath(ctx, cx - tubeW / 2, tubeTop, tubeW, tubeH, tubeW / 2);
    ctx.stroke();

    if (label) {
      ctx.fillStyle = "rgba(31, 168, 220, 0.95)";
      ctx.font = "800 12px 'Baloo 2', 'Fredoka', sans-serif";
      ctx.textAlign = "center";
      ctx.textBaseline = "top";
      ctx.shadowColor = "rgba(255, 255, 255, 0.9)";
      ctx.shadowBlur = 2;
      ctx.fillText(label, cx, y + 18);
      ctx.shadowBlur = 0;
    }

    ctx.strokeStyle = "rgba(255, 255, 255, 0.92)"; /* --fall-hud-rim */
    ctx.lineWidth = 2;
    ctx.shadowBlur = 0;
    ctx.strokeRect(x + 1, y + 1, Math.max(0, w - 2), Math.max(0, h - 2));
    ctx.restore();
  };

  Game.prototype._getFallMiniFlaskCardRect = function (W, H, b, opts) {
    opts = opts || {};
    var includeNudge = opts.includeFallHudNudge !== false;
    var nDown = includeNudge ? getFallGameplayRowNudgeDownPx() : 0;
    // `scoreBottom` = title band + gap + score block (matches reserved `logoH` + `logoToScoreGap` +
    // `FALL_SCORE_PANEL_MIN_HEIGHT_PX` / `min-height` in `getLayout` + `applyRunHudBounds`) before the gameplay row. The
    // score→gameplay rim is `#scorePanel` `border-bottom`, same token as `#fallLogoPanel`.
    // Symmetrical interior padding around DROP BAR + FLASK. `bottomPad` matches the DROP tube inset
    // so the card bottom lines up with `.boost-bar-panel::before` (tube bottom = card bottom − 22px
    // when panel height equals card.h — see `getLayout` fall branch). `getLayout`’s tube iteration passes
    // `{ includeFallHudNudge: false }` so convergence math stays independent of the post-layout HUD shift.
    var cardPad = 12;
    var scoreBottom = getFallTopHudHeightPx(this) + getFallLogoToScoreGapPx(this) + getFallScorePanelMinHeightPx(this);
    var scoreToModuleGap = 22;
    var titleBand = 40;
    var bottomPad = 22;
    var left = b && b.left != null ? b.left : 0;
    var right = b && b.right != null ? b.right : W;
    var top = b && b.top != null ? b.top : 0;
    var bottom = b && b.bottom != null ? b.bottom : H;
    var cardX = Math.max(8, left - cardPad);
    // Keep the DROP BAR row aligned with the hidden Fall FlaskSystem bounds used for tube-height math.
    var cardY = scoreBottom + scoreToModuleGap + nDown;
    // Lift bottom cap with nudge so `card.h` stays tied to `flaskBounds` when the panel height clamps.
    var cardBottom = Math.min(H - scoreToModuleGap + nDown, bottom + bottomPad);
    var cardW = Math.min(Math.max(1, W - cardX - 8), Math.max(1, (right - left) + cardPad * 2));
    var cardH = Math.max(1, cardBottom - cardY);
    return { x: cardX, y: cardY, w: cardW, h: cardH };
  };

  Game.prototype.drawShakeFlaskChrome = function (ctx, W, H, b) {
    if (!ctx || !b) return;
    var sys = this.getFlaskSystem();
    var geom = sys && sys._flaskGeometry;
    if (!geom) return;
    var xL = geom.neckLeft;
    var innerW = Math.max(24, geom.neckRight - xL);
    var innerH = Math.max(24, geom.bodyBottom - geom.T);
    var jarR = this._getShakeJarCornerRadius(innerW, innerH);
    ctx.save();
    this._jarUPath(ctx, xL, geom.T, innerW, innerH, jarR);
    ctx.clip();
    var grad = ctx.createLinearGradient(0, geom.T, 0, geom.bodyBottom);
    grad.addColorStop(0, "rgba(120, 190, 230, 0.18)");
    grad.addColorStop(1, "rgba(90, 165, 215, 0.36)");
    ctx.fillStyle = grad;
    ctx.fillRect(xL, geom.T, innerW, innerH);
    var glow = ctx.createLinearGradient(0, geom.bodyBottom - innerH * 0.35, 0, geom.bodyBottom);
    glow.addColorStop(0, "rgba(120, 200, 245, 0)");
    glow.addColorStop(1, "rgba(120, 200, 245, 0.40)");
    ctx.fillStyle = glow;
    ctx.fillRect(xL, geom.bodyBottom - innerH * 0.35, innerW, innerH * 0.35);
    ctx.restore();
  };

  Game.prototype._strokeShakeFlaskPanelFrame = function (ctx, W, H) {
    var panelR = 16; /* sync: --panel-radius in style.css */
    var fx = 0.5;
    var fy = 0.5;
    var fw = Math.max(1, W - 1);
    var fh = Math.max(1, H - 1);
    ctx.save();
    this._pathTopRightRoundedRect(ctx, fx, fy, fw, fh, panelR);
    ctx.strokeStyle = "rgba(255, 255, 255, 0.92)"; /* --fall-hud-rim */
    ctx.lineWidth = 2;
    ctx.shadowBlur = 0;
    ctx.stroke();
    ctx.restore();
  };

  /** Glass-tank outline in flask panel space (matches physics inner walls). */
  Game.prototype._strokeFlaskWallOutlines = function (ctx, geom, opts) {
    if (!geom) return;
    opts = opts || {};
    var xL = geom.neckLeft;
    var xR = geom.neckRight;
    var T = geom.T;
    var bodyBottom = geom.bodyBottom;
    var innerW = Math.max(24, xR - xL);
    var innerH = Math.max(24, bodyBottom - T);
    var corner = Math.max(10, Math.min(22, innerW * 0.08));

    if (opts.fallChrome) {
      if (opts.openTop) {
        // night.html glass jar: open top, rounded bottom corners (U shape).
        var jarR = this._getShakeJarCornerRadius(innerW, innerH);
        ctx.save();
        ctx.shadowColor = "rgba(120, 200, 245, 0.22)";
        ctx.shadowBlur = 12;
        ctx.strokeStyle = "rgba(170, 215, 245, 0.85)";
        ctx.lineWidth = 3;
        this._jarUPath(ctx, xL, T, innerW, innerH, jarR);
        ctx.stroke();
        // Inner glass-thickness highlight.
        ctx.shadowBlur = 0;
        ctx.strokeStyle = "rgba(220, 240, 255, 0.45)";
        ctx.lineWidth = 1;
        this._jarUPath(ctx, xL + 3, T, Math.max(1, innerW - 6), Math.max(1, innerH - 3), Math.max(2, jarR - 2));
        ctx.stroke();
        ctx.restore();
        return;
      }
      ctx.save();
      ctx.strokeStyle = "rgba(255, 255, 255, 0.92)";
      ctx.lineWidth = 2;
      ctx.shadowBlur = 0;
      this._roundedRectPath(ctx, xL, T, innerW, innerH, corner);
      ctx.stroke();
      ctx.restore();
      return;
    }

    ctx.save();
    // Outer soft shadow shell.
    ctx.shadowColor = "rgba(31, 118, 160, 0.3)";
    ctx.shadowBlur = 16;
    ctx.strokeStyle = "rgba(255, 255, 255, 0.4)";
    ctx.lineWidth = 4;
    this._roundedRectPath(ctx, xL - 5, T - 5, innerW + 10, innerH + 10, corner + 6);
    ctx.stroke();

    // Main bright white glass frame.
    ctx.shadowColor = "rgba(31, 118, 160, 0.35)";
    ctx.shadowBlur = 8;
    ctx.strokeStyle = "rgba(255, 255, 255, 0.95)";
    ctx.lineWidth = 3;
    this._roundedRectPath(ctx, xL, T, innerW, innerH, corner);
    ctx.stroke();

    // Inner highlight for the "glass thickness" look.
    ctx.shadowBlur = 0;
    ctx.strokeStyle = "rgba(146, 216, 242, 0.5)";
    ctx.lineWidth = 1;
    this._roundedRectPath(ctx, xL + 3, T + 3, Math.max(1, innerW - 6), Math.max(1, innerH - 6), Math.max(4, corner - 3));
    ctx.stroke();

    // Bottom inner light line.
    ctx.strokeStyle = "rgba(255, 255, 255, 0.7)";
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(xL + corner * 0.6, bodyBottom - 2);
    ctx.lineTo(xR - corner * 0.6, bodyBottom - 2);
    ctx.stroke();
    ctx.restore();
  };

  Game.prototype.triggerFlaskVisualShake = function (strengthPx, durationMs) {
    var now = Date.now();
    var duration = durationMs != null ? durationMs : (C.SHAKE_FLASK_GRAPHIC_SHAKE_MS || 260);
    var strength = strengthPx != null ? strengthPx : 14;
    this.flaskVisualShakeStart = now;
    this.flaskVisualShakeStrength = Math.max(this.flaskVisualShakeStrength, strength);
    this.flaskVisualShakeUntil = Math.max(this.flaskVisualShakeUntil, now + duration);
  };

  Game.prototype.updateHud = function (status) {
    if (!status) return;
    var timeRem = status.timeRemaining !== undefined ? status.timeRemaining : 0;

    if (this.dom.hudTime) {
      var timeText = "Time: " + timeRem + "s";
      if (this.dom.hudTime.textContent !== timeText) {
        this.dom.hudTime.textContent = timeText;
      }
      this.dom.hudTime.classList.toggle("hud-time-warning", timeRem <= (C.RUN_CONFIG && C.RUN_CONFIG.RUN_TIME_WARNING_AT || 5));
    }
    if (this.dom.hudGems) {
      var gemsText = "Gems: " + (status.gems !== undefined ? status.gems : this.gemsCollected);
      if (this.dom.hudGems.textContent !== gemsText) {
        this.dom.hudGems.textContent = gemsText;
      }
    }
    if (this.dom.hudHpFill) {
      var hpPct = Math.min(100, Math.max(0, status.hpPercentage !== undefined ? status.hpPercentage : 100));
      var widthStr = hpPct + "%";
      if (this.dom.hudHpFill.style.width !== widthStr) {
        this.dom.hudHpFill.style.width = widthStr;
      }
      this.updateHpUrgency(hpPct);
    }
    if (this.dom.hudFlask && status.flaskCount == null) {
      var sys = this.getFlaskSystem();
      var flaskText = "Flask: " + sys.getBodyCount();
      if (this.dom.hudFlask.textContent !== flaskText) {
        this.dom.hudFlask.textContent = flaskText;
      }
    } else if (this.dom.hudFlask && status.flaskCount != null) {
      var flaskCountText = "Flask: " + status.flaskCount;
      if (this.dom.hudFlask.textContent !== flaskCountText) {
        this.dom.hudFlask.textContent = flaskCountText;
      }
    }

    var runGems = status.gems !== undefined ? status.gems : 0;
    var displayedGems = this.gemsCollected + runGems;
    var gemStr = String(displayedGems);
    var gemCountChanged = false;
    var gemEls = this._getGemCountElements();
    for (var gi = 0; gi < gemEls.length; gi++) {
      var gemEl = gemEls[gi];
      if (gemEl.textContent !== gemStr) {
        gemEl.textContent = gemStr;
        gemCountChanged = true;
      }
    }
    if (gemCountChanged && displayedGems > this.lastRunGems) {
      this._replayClassOnGemCounts("score-pop");
    }
    this.lastRunGems = displayedGems;
    if (this.stateMachine && this.stateMachine.is("fall") && this.runPhase && this.runPhase.player) {
      var curHp = Math.max(0, Math.floor(Number(this.runPhase.player.hp) || 0));
      if (this._lastHudHp != null && curHp < this._lastHudHp) {
        this.revealHeartsTransient();
      }
      this._lastHudHp = curHp;
      this.syncFallHpRow(typeof performance !== "undefined" ? performance.now() : Date.now());
    }
    this.updateLivesHud(status);
    this._updateFallProgressDial(
      typeof performance !== "undefined" ? performance.now() : Date.now()
    );
  };

  Game.prototype._updateFallProgressDial = function (now) {
    if (!this.dom || !this.dom.fallProgressDialDisc) return;
    var t = now != null && isFinite(now)
      ? now
      : (typeof performance !== "undefined" ? performance.now() : Date.now());
    var resolved = resolveFallProgressDial({
      stateMachine: this.stateMachine,
      celestialTransition: this.celestialTransition,
      runPhase: this.runPhase,
      currentCycleIndex: this.currentCycleIndex,
      isSimPaused: this._isSimPaused(),
      bridgeCelestialPauseWallMs: this._bridgeCelestialPauseWallMs,
      buildFallSnap: this._buildSkyFallSnapshot.bind(this),
      computeShrink: computeFallBarShrink,
      runConfig: (this.runPhase && this.runPhase.runConfig) || C.RUN_CONFIG || {},
      nowMs: t,
    });
    var transform = formatDialDiscTransform(resolved.deg);
    if (this.dom.fallProgressDialDisc.style.transform !== transform) {
      this.dom.fallProgressDialDisc.style.transform = transform;
    }
    var dayStr = String(resolved.day);
    if (this.dom.fallProgressDialDay && this.dom.fallProgressDialDay.textContent !== dayStr) {
      this.dom.fallProgressDialDay.textContent = dayStr;
    }
    if (this.dom.fallProgressDialDayInk && this.dom.fallProgressDialDayInk.textContent !== dayStr) {
      this.dom.fallProgressDialDayInk.textContent = dayStr;
    }
    if (this.dom.fallProgressDial) {
      var rotVar = formatDialRotCssVar(resolved.deg);
      if (this.dom.fallProgressDial.style.getPropertyValue("--dial-rot") !== rotVar) {
        this.dom.fallProgressDial.style.setProperty("--dial-rot", rotVar);
      }
    }
  };

  Game.prototype.getFlaskFillHudState = function () {
    var state = this.overflow && this.overflow.state ? this.overflow.state : "safe";
    return {
      overflowState: state,
      overflowArmed: state === "alert",
      overflowPending: state === "warning",
    };
  };

  Game.prototype.updateLivesHud = function (status) {
    var root = this.dom && this.dom.hudLives;
    if (!root) return;
    // Fall uses `#fallHudLives` via syncFallHpRow — never rewrite the Merge pill here.
    if (this.stateMachine && this.stateMachine.is && this.stateMachine.is("fall")) return;
    // Shake + Fall→Shake bridge: paint correct hearts before/while the pill fades in
    // so a prior full-HP visit cannot flash 3 filled hearts for a damaged handoff.
    var maxHp = Math.max(1, Math.floor(Number(this.flaskMaxHp) || 3));
    var hp = Math.max(0, Math.min(maxHp, Math.floor(Number(this.savedFlaskHp) || 0)));
    if (status && status.playerHp != null) {
      hp = Math.max(0, Math.min(maxHp, Math.floor(Number(status.playerHp) || 0)));
    }
    root.setAttribute("aria-label", hp + " of " + maxHp + " lives");
    var showHeal = hp < maxHp;
    // Only rebuild heart markup when it actually changes. updateShakeUI runs on many
    // gameplay events (shake/merge/gem); replacing innerHTML detaches the node under an
    // in-progress finger press, so the browser cancels the click and tapping a heart to
    // repair HP silently fails. A stable signature keeps heart nodes alive across
    // unrelated UI refreshes.
    var sig = maxHp + "/" + hp + "/" + (showHeal ? 1 : 0);
    if (this._hudHeartSig === sig) return;
    this._hudHeartSig = sig;
    this._hudHeartUid = (this._hudHeartUid || 0) + 1;
    root.innerHTML = renderHpHeartsHtml(maxHp, hp, {
      uid: this._hudHeartUid,
      healSlot: showHeal,
    });
  };

  Game.prototype.updateGlobalGemDisplay = function () {
    var count = String(this.gemsCollected);
    if (this.dom && this.dom.gemCount) this.dom.gemCount.textContent = count;
    if (this.dom && this.dom.verticalGemCount) this.dom.verticalGemCount.textContent = count;
  };

  Game.prototype.formatScore = function (value) {
    var n = Math.max(0, Math.floor(Number(value) || 0));
    return n.toLocaleString ? n.toLocaleString("en-US") : String(n);
  };

  Game.prototype.updateScoreDisplay = function () {
    var score = this.formatScore(this.currentScore);
    var best = this.formatScore(this.bestScore);
    if (!this.dom) return;
    if (this.dom.scoreValue) this.dom.scoreValue.textContent = score;
    if (this.dom.bestScoreValue) this.dom.bestScoreValue.textContent = best;
    if (this.dom.shakeScoreValue) this.dom.shakeScoreValue.textContent = score;
    if (this.dom.shakeBestScoreValue) this.dom.shakeBestScoreValue.textContent = best;
    if (this.dom.verticalScoreValue) this.dom.verticalScoreValue.textContent = score;
    if (this.dom.verticalBestScoreValue) this.dom.verticalBestScoreValue.textContent = best;
  };

  /** Drop stale score/gem pop classes so stage chrome does not replay digit bounce. */
  Game.prototype._clearScoreDigitPop = function () {
    if (!this.dom) return;
    var els = [
      this.dom.scoreValue,
      this.dom.bestScoreValue,
      this.dom.shakeScoreValue,
      this.dom.shakeBestScoreValue,
      this.dom.verticalScoreValue,
      this.dom.verticalBestScoreValue,
    ];
    for (var i = 0; i < els.length; i++) {
      if (els[i] && els[i].classList) els[i].classList.remove("score-pop");
    }
    var gemEls = this._getGemCountElements();
    for (var g = 0; g < gemEls.length; g++) {
      if (gemEls[g] && gemEls[g].classList) {
        gemEls[g].classList.remove("score-pop");
        gemEls[g].classList.remove("gem-count-spend");
      }
    }
  };

  Game.prototype.addScore = function (points) {
    var delta = Math.max(0, Math.floor(Number(points) || 0));
    if (delta <= 0) return;
    this.currentScore = Math.max(0, Math.floor(Number(this.currentScore) || 0)) + delta;
    if (this.currentScore > (this.bestScore || 0)) {
      this.bestScore = this.currentScore;
      this.newBestThisRun = true;
    }
    this.updateScoreDisplay();
  };

  Game.prototype.getCatchScore = function (tier) {
    return this.constants.SCORE_MERGE_CATCH_BASE * (Math.max(0, Number(tier) || 0) + 1);
  };

  Game.prototype.getMergeScore = function (toTier) {
    var tier = Math.max(0, Number(toTier) || 0);
    return this.constants.SCORE_MERGE_BASE * Math.pow(2, tier);
  };

  Game.prototype._getScorePopupDrawOpts = function () {
    var cfg = C.RUN_CONFIG || {};
    return {
      peakFontPx: computeScorePopupPeakFontPx(
        this.layout && this.layout.scale,
        this.mockupScale,
        cfg
      ),
      popScaleMin: cfg.FALL_COUNTDOWN_POP_SCALE_MIN != null
        ? Number(cfg.FALL_COUNTDOWN_POP_SCALE_MIN)
        : 0.5,
    };
  };

  Game.prototype.spawnScorePopup = function (x, y, label, points) {
    if (!isFinite(x) || !isFinite(y)) return;
    if (runtimeFlags.drawScorePopups === false) return;
    var text = label ? (label + " +" + this.formatScore(points)) : ("+" + this.formatScore(points));
    this.scorePopups = pruneScorePopups(this.scorePopups || []);
    this.scorePopups.push(createScorePopup(x, y, text));
    if (this.dom && this.dom.scoreValue) {
      this.dom.scoreValue.classList.remove("score-pop");
      void this.dom.scoreValue.offsetWidth;
      this.dom.scoreValue.classList.add("score-pop");
    }
    if (this.dom && this.dom.shakeScoreValue) {
      this.dom.shakeScoreValue.classList.remove("score-pop");
      void this.dom.shakeScoreValue.offsetWidth;
      this.dom.shakeScoreValue.classList.add("score-pop");
    }
    if (this.dom && this.dom.verticalScoreValue) {
      this.dom.verticalScoreValue.classList.remove("score-pop");
      void this.dom.verticalScoreValue.offsetWidth;
      this.dom.verticalScoreValue.classList.add("score-pop");
    }
  };

  Game.prototype._getDragonTierIndex = function () {
    var chain = (typeof C.getActiveEvolutionChain === "function")
      ? C.getActiveEvolutionChain()
      : (C.EVOLUTION_CHAIN || []);
    for (var i = 0; i < chain.length; i++) {
      var entry = chain[i];
      if (entry && entry.assetId === "dragon_final") return entry.id != null ? entry.id : i;
    }
    return chain.length > 0 ? chain[chain.length - 1].id : 10;
  };

  Game.prototype._isDragonTier = function (tier) {
    return Math.floor(Number(tier) || 0) === this._getDragonTierIndex();
  };

  Game.prototype._getDragonMergeScoreBonus = function () {
    var bonus = (typeof window !== "undefined" && C.DRAGON_MERGE_SCORE_BONUS != null)
      ? Number(C.DRAGON_MERGE_SCORE_BONUS)
      : 300000;
    return isFinite(bonus) && bonus > 0 ? Math.floor(bonus) : 300000;
  };

  Game.prototype._playDragonMergeSound = function () {
    if (EventBus && typeof EventBus.emit === "function") {
      EventBus.emit("merge:revive", {});
    }
  };

  Game.prototype.handleScoreEvent = function (type, payload) {
    payload = payload || {};
    if (type === "catch") {
      this._noteTierForAnalytics(payload.tier);
      var catchPts = this.getCatchScore(payload.tier);
      this.addScore(catchPts);
      if (payload.x != null && payload.y != null) {
        this.spawnScorePopup(payload.x, payload.y, null, catchPts);
      }
    } else if (type === "merge") {
      if (payload.capped && this._isDragonTier(payload.fromTier)) {
        this._trackAnalytics(AnalyticsEvents.DRAGON_MERGE);
        var dragonPts = this._getDragonMergeScoreBonus();
        this.addScore(dragonPts);
        var dragonPos = payload.position;
        var dragonLayout = this.layout;
        if (dragonPos && dragonPos.x != null && dragonPos.y != null) {
          this._playDragonMergeSound();
          if (dragonLayout && dragonLayout.flaskPanelOrigin) {
            var dragonOrigin = dragonLayout.flaskPanelOrigin;
            this.spawnScorePopup(
              dragonOrigin.x + dragonPos.x,
              dragonOrigin.y + dragonPos.y,
              null,
              dragonPts
            );
          }
        }
        return;
      }
      if (payload.toTier == null) return;
      this._noteTierForAnalytics(payload.toTier);
      var mergePts = this.getMergeScore(payload.toTier);
      this.addScore(mergePts);
      if (this.stateMachine && this.stateMachine.is && this.stateMachine.is("shake")) {
        var pos = payload.position;
        var layout = this.layout;
        if (pos && pos.x != null && pos.y != null && layout && layout.flaskPanelOrigin) {
          var origin = layout.flaskPanelOrigin;
          this.spawnScorePopup(origin.x + pos.x, origin.y + pos.y, null, mergePts);
        }
      }
    }
  };

  Game.prototype.syncFallMovementHint = function (visible) {
    setFallMoveHintVisible(this.dom && this.dom.fallMoveHint, !!visible);
  };

  Game.prototype.syncShakeDropHint = function (visible) {
    setFallMoveHintVisible(this.dom && this.dom.shakeDropHint, !!visible);
  };

  Game.prototype._hasShakeDropStashToPlace = function () {
    if (this.boostBar && this.boostBar.length > 0) return true;
    if (!this.isDropMode) return false;
    if (this.dropCurrent) return true;
    return !!(this.dropQueue && this.dropQueue.length > 0);
  };

  Game.prototype._isShakeFlaskEmptyForDropHint = function () {
    var sys = this.getFlaskSystem();
    if (!sys || typeof sys.getBodyCount !== "function") return true;
    return sys.getBodyCount() <= 0;
  };

  Game.prototype._shouldShowShakeDropHint = function () {
    if (!this._hasShakeDropStashToPlace()) return false;
    if (!this._isShakeFlaskEmptyForDropHint()) return false;
    return true;
  };

  Game.prototype._dismissShakeDropHint = function () {
    if (!this._shakeDropHintActive) return;
    this._shakeDropHintActive = false;
    this.syncShakeDropHint(false);
  };

  Game.prototype._showShakeDropHint = function () {
    this._shakeDropHintActive = true;
    this.syncShakeDropHint(true);
  };

  Game.prototype._maybeShowShakeDropHint = function () {
    if (!this._shouldShowShakeDropHint()) return;
    this._showShakeDropHint();
  };

  Game.prototype._nowMs = function () {
    return typeof performance !== "undefined" ? performance.now() : Date.now();
  };

  Game.prototype._loadShakeHintDismissed = function () {
    try {
      var rec = readStorageRecord(this._getProgressStorage(), StorageSlot.SHAKE_HINT);
      this._shakeHintDismissed = !!(rec && rec.dismissed);
    } catch (e) {
      this._shakeHintDismissed = false;
    }
  };

  Game.prototype._persistShakeHintDismissed = function () {
    try {
      writeStorageRecord(this._getProgressStorage(), StorageSlot.SHAKE_HINT, { dismissed: true });
      this._relayStorageSlot(StorageSlot.SHAKE_HINT);
    } catch (e) { /* ignore */ }
  };

  Game.prototype._getShakeHintAnchorBtn = function () {
    if (this._isVerticalLayout() && this.dom && this.dom.verticalShakeBtn) {
      return this.dom.verticalShakeBtn;
    }
    return this.dom && this.dom.shakeBtn ? this.dom.shakeBtn : null;
  };

  Game.prototype._isShakeHintBlocked = function () {
    if (this.isPlayerPaused || this.isDialogPaused || this.isHostPaused || this.isAdPaused) return true;
    if (typeof this._isGameplaySimFrozen === "function" && this._isGameplaySimFrozen()) return true;
    return false;
  };

  Game.prototype._armShakeHintSettle = function (nowMs) {
    if (this._shakeHintVisible) return;
    var now = nowMs != null ? Number(nowMs) : this._nowMs();
    if (!isFinite(now)) now = this._nowMs();
    this._shakeHintReadyAt = now + getShakeHintSettleMs();
  };

  Game.prototype._hideShakeHint = function () {
    this._shakeHintVisible = false;
    setShakeHintVisible(this.dom && this.dom.shakeHintTooltip, false);
  };

  Game.prototype._syncShakeHintTooltip = function () {
    if (!this._shakeHintVisible) return;
    var tip = this.dom && this.dom.shakeHintTooltip;
    var anchor = this._getShakeHintAnchorBtn();
    if (!tip || !anchor) return;
    positionShakeHintTooltip(tip, anchor, this._isVerticalLayout() ? "above" : "left");
  };

  Game.prototype._showShakeHint = function () {
    this._shakeHintVisible = true;
    setShakeHintVisible(this.dom && this.dom.shakeHintTooltip, true);
    this._syncShakeHintTooltip();
  };

  Game.prototype.dismissShakeHintForever = function () {
    this._shakeHintDismissed = true;
    this._persistShakeHintDismissed();
    this._hideShakeHint();
  };

  Game.prototype._tickShakeHint = function (nowMs) {
    if (this._shakeHintDismissed) {
      if (this._shakeHintVisible) this._hideShakeHint();
      return;
    }
    var inShake = !!(this.stateMachine && this.stateMachine.is && this.stateMachine.is("shake"));
    if (!inShake) {
      if (this._shakeHintVisible) this._hideShakeHint();
      return;
    }
    var now = nowMs != null ? Number(nowMs) : this._nowMs();
    if (!isFinite(now)) now = this._nowMs();
    var show = shouldShowShakeHint({
      dismissed: this._shakeHintDismissed,
      inShake: true,
      dropHintActive: !!this._shakeDropHintActive,
      minimizeMode: !!this.isMinimizeMode,
      blocked: this._isShakeHintBlocked(),
      shakeEnabled: typeof this._isShakeBtnEnabled === "function" && this._isShakeBtnEnabled(),
      hasPair: flaskHasShakeHintPair(this.getFlaskSystem()),
      alreadyVisible: !!this._shakeHintVisible,
      now: now,
      readyAt: this._shakeHintReadyAt,
    });
    if (show) {
      if (this._shakeHintVisible) this._syncShakeHintTooltip();
      else this._showShakeHint();
    } else if (this._shakeHintVisible) {
      this._hideShakeHint();
    }
  };

  Game.prototype._loadHealHintDismissed = function () {
    try {
      var rec = readStorageRecord(this._getProgressStorage(), StorageSlot.HEAL_HINT);
      this._healHintDismissed = !!(rec && rec.dismissed);
    } catch (e) {
      this._healHintDismissed = false;
    }
  };

  Game.prototype._persistHealHintDismissed = function () {
    try {
      writeStorageRecord(this._getProgressStorage(), StorageSlot.HEAL_HINT, { dismissed: true });
      this._relayStorageSlot(StorageSlot.HEAL_HINT);
    } catch (e) { /* ignore */ }
  };

  Game.prototype._getHealHintAnchorBtn = function () {
    if (this.dom && this.dom.shakeHpPill) return this.dom.shakeHpPill;
    return this.dom && this.dom.fixBtn ? this.dom.fixBtn : null;
  };

  Game.prototype._isHealHintAnchorReady = function () {
    var row = this.dom && this.dom.shakeHpRow;
    if (!row || !row.classList || row.classList.contains("hidden")) return false;
    var anchor = this._getHealHintAnchorBtn();
    if (!anchor || typeof anchor.getBoundingClientRect !== "function") return false;
    var rect = anchor.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  };

  Game.prototype._canAffordHeal = function () {
    var cost = this.constants && this.constants.FLASK_FIX_COST;
    var price = Math.floor(Number(cost));
    if (!isFinite(price) || price < 0) price = 0;
    var gems = Math.floor(Number(this.gemsCollected) || 0);
    return gems >= price;
  };

  Game.prototype._isFlaskDamaged = function () {
    var maxHp = Math.max(1, Math.floor(Number(this.flaskMaxHp) || 1));
    var hp = Math.floor(Number(this.savedFlaskHp) || 0);
    return hp < maxHp;
  };

  Game.prototype._hideHealHint = function () {
    this._healHintVisible = false;
    setShakeHintVisible(this.dom && this.dom.healHintTooltip, false);
  };

  Game.prototype._syncHealHintTooltip = function () {
    if (!this._healHintVisible) return;
    var tip = this.dom && this.dom.healHintTooltip;
    var anchor = this._getHealHintAnchorBtn();
    if (!tip || !anchor) return;
    positionShakeHintTooltip(tip, anchor, "below");
  };

  Game.prototype._showHealHint = function () {
    this._healHintVisible = true;
    setShakeHintVisible(this.dom && this.dom.healHintTooltip, true);
    this._syncHealHintTooltip();
  };

  Game.prototype.dismissHealHintForever = function () {
    this._healHintDismissed = true;
    this._persistHealHintDismissed();
    this._hideHealHint();
  };

  Game.prototype._tickHealHint = function () {
    if (this._healHintDismissed) {
      if (this._healHintVisible) this._hideHealHint();
      return;
    }
    var inShake = !!(this.stateMachine && this.stateMachine.is && this.stateMachine.is("shake"));
    var show = shouldShowHealHint({
      dismissed: this._healHintDismissed,
      inShake: inShake,
      minimizeMode: !!this.isMinimizeMode,
      blocked: this._isShakeHintBlocked(),
      damaged: this._isFlaskDamaged(),
      canAfford: this._canAffordHeal(),
      healVisible: this._isHealHintAnchorReady(),
    });
    if (show) {
      if (this._healHintVisible) this._syncHealHintTooltip();
      else this._showHealHint();
    } else if (this._healHintVisible) {
      this._hideHealHint();
    }
  };

  Game.prototype.showToast = function (message, durationMs) {
    showStatusToast(this.dom && this.dom.statusToast, message, durationMs);
  };

  Game.prototype.updateHpUrgency = function (hpPct) {
    var app = document.getElementById("app");
    if (!app) return;
    app.classList.remove("hp-danger", "hp-critical");
    if (hpPct <= 35) app.classList.add("hp-danger");
    if (hpPct <= 18) app.classList.add("hp-critical");
  };

  Game.prototype.setStageStatus = function () {
    if (!this.dom || !this.dom.stageStatus) return;
    this.dom.stageStatus.classList.add("hidden");
  };

  Game.prototype.updateEndRunSummary = function (summary) {
    if (!this.dom || !this.dom.endRunGems || !this.dom.endRunHp || !this.dom.endRunCaught) return;
    var next = summary || this.lastRunSummary || null;
    if (!next && this.runPhase && typeof this.runPhase.getResults === "function") {
      next = this.runPhase.getResults();
    }
    if (!next) return;
    this.lastRunSummary = next;
    var runGems = Math.max(0, Number(next.gems) || 0);
    var runHp = Math.max(0, Number(next.playerHp != null ? next.playerHp : this.savedFlaskHp) || 0);
    var runCaught = Math.max(0, Number(next.growthCount) || 0);
    this.dom.endRunGems.textContent = String(runGems);
    this.dom.endRunHp.textContent = String(runHp);
    this.dom.endRunCaught.textContent = String(runCaught);
  };

  Game.prototype.getTierAssetSrc = function (tier) {
    var chain = (typeof window !== "undefined" && C.EVOLUTION_CHAIN) || [];
    var info = chain[tier] || {};
    if (!info.assetId) return "";
    return mergeTierImageUrl(info.assetId);
  };

  Game.prototype.setMockupActionBadge = function (button, isFree, usesLeft, gemCost) {
    if (!button) return;
    var badge = button.querySelector(".mockup-action__badge") || button.querySelector(".action__badge");
    if (!badge) return;
    if (isFree && usesLeft > 0) {
      badge.className = "mockup-action__badge free";
      badge.textContent = "Free x" + usesLeft;
    } else {
      badge.className = "mockup-action__badge cost";
      badge.innerHTML = "<img src=\"" + gameImageUrl("gem") + "\" alt=\"\">" + gemCost;
    }
  };

  Game.prototype.setBombButtonContent = function (button, tiers, labelText) {
    if (!button) return;
    if (button.classList && typeof button.classList.contains === "function" && button.classList.contains("mockup-action")) {
      return;
    }
    button.textContent = "Remove " + labelText;
    var html = '<span class="btn-label">Remove</span><span class="bomb-icons" aria-hidden="true">';
    for (var i = 0; i < tiers.length; i++) {
      var src = this.getTierAssetSrc(tiers[i]);
      if (src) html += '<img src="' + src + '" alt="">';
    }
    html += '</span><span class="bomb-price">' + labelText + '</span>';
    button.innerHTML = html;
  };

  Game.prototype.countFlaskBodiesInTiers = function (flask, tiers) {
    if (!flask || !flask.bodies || !tiers || tiers.length === 0) return 0;
    var count = 0;
    for (var i = 0; i < flask.bodies.length; i++) {
      if (tiers.indexOf(flask.bodies[i].tier) !== -1) count++;
    }
    return count;
  };

  Game.prototype.updateBombTierButton = function (button, flask, tiers, usesLeft, gemCost, title, emptyTitle, paidTitle) {
    if (!button) return;
    usesLeft = normalizeUseCount(usesLeft, 0);
    var isFree = usesLeft > 0;
    var labelText = isFree
      ? "(" + usesLeft + ")"
      : "(" + gemCost + " gem" + (gemCost === 1 ? "" : "s") + ")";
    if (button.classList && typeof button.classList.contains === "function" && button.classList.contains("mockup-action")) {
      this.setMockupActionBadge(button, isFree, usesLeft, gemCost);
    } else {
      this.setBombButtonContent(button, tiers, labelText);
    }
    button.classList.toggle("paid", !isFree);
    var paidKey = button.id || "bomb";
    var wasPaid = this._lastBombBtnPaid[paidKey];
    if (wasPaid != null && wasPaid !== !isFree) {
      button.classList.remove("action-badge--crossfade");
      void button.offsetWidth;
      button.classList.add("action-badge--crossfade");
    }
    this._lastBombBtnPaid[paidKey] = !isFree;
    var hasBodies = !!(flask && typeof flask.getBodyCount === "function" && flask.getBodyCount() > 0);
    var matchingCount = this.countFlaskBodiesInTiers(flask, tiers);
    var gems = Math.floor(Number(this.gemsCollected) || 0);
    var cost = Math.floor(Number(gemCost) || 0);
    if (matchingCount <= 0) {
      button.disabled = true;
      this._clearDomTitle(button);
      button.setAttribute("aria-label", hasBodies ? emptyTitle : "Nothing to remove");
      return;
    }
    // Paid mode always requires enough gems before enabling; otherwise "(N gems)" can look
    // clickable even when the player cannot afford the action.
    if (!isFree && gems < cost) {
      button.disabled = true;
      this._clearDomTitle(button);
      button.setAttribute("aria-label", "Not enough gems to remove");
      return;
    }
    button.disabled = false;
    this._clearDomTitle(button);
    button.setAttribute("aria-label", isFree ? title : paidTitle);
  };

  /** Fade-pop a free<->paid action badge only when its mode actually flips. */
  Game.prototype._animateBadgeModeChange = function (badge, stateKey) {
    if (!badge || !badge.classList) return;
    var mode = badge.classList.contains("cost") ? "cost" : "free";
    var prev = this[stateKey];
    if (prev && prev !== mode) {
      badge.classList.remove("badge-mode-swap");
      void badge.offsetWidth;
      badge.classList.add("badge-mode-swap");
    }
    this[stateKey] = mode;
  };

  Game.prototype._getGemCountElements = function () {
    if (!this.dom) return [];
    return [this.dom.gemCount, this.dom.verticalGemCount].filter(Boolean);
  };

  Game.prototype._replayClassOnGemCounts = function (className) {
    var els = this._getGemCountElements();
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      if (!el || !el.classList) continue;
      el.classList.remove(className);
      void el.offsetWidth;
      el.classList.add(className);
    }
  };

  Game.prototype.triggerGemSpendReaction = function () {
    this._replayClassOnGemCounts("gem-count-spend");
  };

  Game.prototype._clearDomTitle = function (el) {
    if (el && typeof el.removeAttribute === "function") el.removeAttribute("title");
  };

  Game.prototype.setPlayerHudVisible = function (visible) {
    if (this.dom.playerHud && this.dom.playerHud.classList) {
      if (typeof this.dom.playerHud.classList.add === "function") {
        this.dom.playerHud.classList.add("hidden");
      } else if (typeof this.dom.playerHud.classList.toggle === "function") {
        this.dom.playerHud.classList.toggle("hidden", true);
      }
    }
    var isShake = !!(this.stateMachine && this.stateMachine.is && this.stateMachine.is("shake"));
    var showStageChrome = !!visible;
    if (this._isVerticalLayout()) {
      this._applyLayoutModeClasses();
      if (this.dom.verticalChrome) {
        this.dom.verticalChrome.classList.toggle("hidden", !showStageChrome);
        if (typeof this.dom.verticalChrome.setAttribute === "function") {
          this.dom.verticalChrome.setAttribute("aria-hidden", showStageChrome ? "false" : "true");
        }
      }
      if (showStageChrome) {
        this.applyLayoutChromeBounds();
        this.applyPlayChromeBounds();
        if (isShake) {
          this.applyShakeJarChromeBounds();
          this.applyShakeHpRowBounds();
        } else {
          this.applyShakeJarChromeBounds(true);
          this.applyShakeHpRowBounds(true);
        }
      }
      if (this.dom.fallPauseBtn) this.dom.fallPauseBtn.classList.toggle("hidden", true);
      if (this.dom.fallVolumeBtn) this.dom.fallVolumeBtn.classList.toggle("hidden", true);
      if (this.dom.fallRightHudModule) {
        this.dom.fallRightHudModule.classList.add("hidden");
        if (typeof this.dom.fallRightHudModule.setAttribute === "function") {
          this.dom.fallRightHudModule.setAttribute("aria-hidden", "true");
        }
      }
      if (this.dom.stageChromeRight) {
        this.dom.stageChromeRight.classList.add("hidden");
        if (typeof this.dom.stageChromeRight.setAttribute === "function") {
          this.dom.stageChromeRight.setAttribute("aria-hidden", "true");
        }
      }
      if (this.dom.shakeActionsCol) {
        this.dom.shakeActionsCol.classList.add("hidden");
        if (typeof this.dom.shakeActionsCol.setAttribute === "function") {
          this.dom.shakeActionsCol.setAttribute("aria-hidden", "true");
        }
      }
      if (!visible || isShake) this.hideFallVolumeOverlay();
      if (this.dom.fallHudStrip) this.dom.fallHudStrip.classList.toggle("hidden", !visible || isShake);
      if (this.dom.fallRunStrip) this.dom.fallRunStrip.classList.toggle("hidden", !visible || isShake);
      if (this.dom.stageStatus && !visible) this.dom.stageStatus.classList.add("hidden");
      if (this.retention && typeof this.retention.syncStageVisibility === "function") {
        this.retention.syncStageVisibility(showStageChrome);
      }
      return;
    }
    if (this.dom.fallRightHudModule) {
      this.dom.fallRightHudModule.classList.toggle("hidden", !showStageChrome);
      if (typeof this.dom.fallRightHudModule.setAttribute === "function") {
        this.dom.fallRightHudModule.setAttribute("aria-hidden", showStageChrome ? "false" : "true");
      }
    }
    if (this.dom.stageChromeRight) {
      this.dom.stageChromeRight.classList.toggle("hidden", !showStageChrome);
      if (typeof this.dom.stageChromeRight.setAttribute === "function") {
        this.dom.stageChromeRight.setAttribute("aria-hidden", showStageChrome ? "false" : "true");
      }
    }
    if (this.dom.shakeActionsCol) {
      var showShakeActions = !!(visible && isShake);
      this.dom.shakeActionsCol.classList.toggle("hidden", !showShakeActions);
      if (typeof this.dom.shakeActionsCol.setAttribute === "function") {
        this.dom.shakeActionsCol.setAttribute("aria-hidden", showShakeActions ? "false" : "true");
      }
    }
    if (this.dom.fallPauseBtn) this.dom.fallPauseBtn.classList.toggle("hidden", !visible);
    if (this.dom.fallVolumeBtn) this.dom.fallVolumeBtn.classList.toggle("hidden", !visible);
    if (!visible || isShake) this.hideFallVolumeOverlay();
    if (this.dom.fallHudStrip) this.dom.fallHudStrip.classList.toggle("hidden", !visible || isShake);
    if (this.dom.fallRunStrip) this.dom.fallRunStrip.classList.toggle("hidden", !visible || isShake);
    if (this.dom.stageStatus && !visible) this.dom.stageStatus.classList.add("hidden");
    if (showStageChrome) {
      this.applyStageChromeLeftBounds();
      this.applyStageChromeRightBounds();
      this.applyPlayChromeBounds();
      if (isShake) {
        this.applyShakeHpRowBounds();
        this.applyShakeJarChromeBounds();
        this.applyEvolutionRingBounds();
        this.populateEvolutionChrome();
      } else {
        this.applyShakeHpRowBounds(true);
        this.applyShakeJarChromeBounds(true);
        this.applyEvolutionRingBounds();
        this.populateEvolutionChrome();
      }
    }
    if (this.retention && typeof this.retention.syncStageVisibility === "function") {
      this.retention.syncStageVisibility(showStageChrome);
    }
  };

  Game.prototype._syncFallPauseButton = function () {
    var paused = !!this.isPlayerPaused;
    var isShake = this.stateMachine && this.stateMachine.is && this.stateMachine.is("shake");
    var isTransition = this.stateMachine && this.stateMachine.is && this.stateMachine.is("transition");
    var label;
    if (isShake) {
      label = paused ? "Resume shake stage" : "Pause shake stage";
    } else if (isTransition) {
      label = paused ? "Resume transition" : "Pause transition";
    } else {
      label = paused ? "Resume fall stage" : "Pause fall stage";
    }
    var buttons = [];
    if (this.dom && this.dom.fallPauseBtn) buttons.push(this.dom.fallPauseBtn);
    if (this.dom && this.dom.verticalFallPauseBtn) buttons.push(this.dom.verticalFallPauseBtn);
    if (buttons.length === 0) return;
    for (var i = 0; i < buttons.length; i++) {
      var btn = buttons[i];
      btn.classList.toggle("is-paused", paused);
      btn.setAttribute("aria-label", label);
      this._clearDomTitle(btn);
    }
  };

  Game.prototype._isPageHidden = function () {
    if (typeof document === "undefined") return false;
    return document.visibilityState === "hidden" || document.hidden === true;
  };

  Game.prototype._isPortalLayerPaused = function () {
    return !!(this.isPlayerPaused || this.isDialogPaused || this.isEnvironmentPaused || this.isHostPaused || this.isAdPaused
      || this.isReviveOfferActive);
  };

  Game.prototype._isSimPaused = function () {
    return this._isPortalLayerPaused();
  };

  /** Physics/input freeze — portal pauses plus evolution discovery ceremony and victory popup. */
  Game.prototype._isGameplaySimFrozen = function () {
    return this._isPortalLayerPaused()
      || this._isEvolutionRevealPending()
      || this._victoryPopupActive;
  };

  Game.prototype._shouldReportPlatformGameplay = function () {
    if (!this.stateMachine || !this.stateMachine.is) return false;
    if (this._continueToRunAdPending || this._pokiUnpauseAdPending) return false;
    if (this._isPortalLayerPaused()) return false;
    if (this.stateMachine.is("start")) return false;
    if (this.stateMachine.is("fall")) {
      if (!this.runPhase) return false;
      return this.runPhase.state === "playing";
    }
    if (this.stateMachine.is("transition")) {
      // NEXT DAY › bridge (shake→fall) is non-playable until the next countdown ends.
      if (this.celestialTransition && this.celestialTransition.direction === "to_fall") {
        return false;
      }
      return true;
    }
    if (this.stateMachine.is("shake")) return true;
    return false;
  };

  Game.prototype._shouldKeepPokiFallInputLatch = function () {
    if (!this.stateMachine || !this.stateMachine.is("fall")) return false;
    if (!this.runPhase || this.runPhase.state !== "playing") return false;
    return !!(this.isPlayerPaused || this.isDialogPaused || this.isEnvironmentPaused
      || this.isHostPaused || this.isAdPaused || this.isReviveOfferActive
      || this._pokiUnpauseAdPending || this._continueToRunAdPending);
  };

  Game.prototype._shouldDeferPokiGameplayStart = function () {
    if (!isPokiPortalActive()) return false;
    if (!this.stateMachine || !this.stateMachine.is("fall")) return false;
    if (!this.runPhase || this.runPhase.state !== "playing") return false;
    return !this._platformGameplayInputLatch;
  };

  Game.prototype._beginPlatformGameplayReporting = function () {
    if (this._shouldDeferPokiGameplayStart()) {
      platformGameplayStart({ skipPoki: true });
    } else {
      platformGameplayStart();
    }
  };

  Game.prototype._onFallGameplayInput = function () {
    if (!isPokiPortalActive()) return;
    if (this._platformGameplayInputLatch) return;
    var shouldStartPoki = this._platformGameplayReported
      && this._shouldReportPlatformGameplay()
      && this.stateMachine
      && this.stateMachine.is("fall")
      && this.runPhase
      && this.runPhase.state === "playing";
    this._platformGameplayInputLatch = true;
    if (shouldStartPoki) {
      platformGameplayStartPoki();
    }
  };

  Game.prototype._stopPlatformGameplayReporting = function () {
    if (!this._platformGameplayReported) return;
    this._platformGameplayReported = false;
    this._platformGameplayInputLatch = false;
    platformGameplayStop();
  };

  Game.prototype._syncPlatformGameplay = function () {
    // Discovery ceremony and victory popup hold local physics but are not a CrazyGames/Playgama stop.
    if ((this._isEvolutionRevealPending() || this._victoryPopupActive)
      && this.stateMachine && this.stateMachine.is("shake")
      && !this._isPortalLayerPaused()) {
      if (!this._platformGameplayReported) {
        this._platformGameplayReported = true;
        this._beginPlatformGameplayReporting();
      }
      return;
    }
    var shouldReport = this._shouldReportPlatformGameplay();
    if (shouldReport && !this._platformGameplayReported) {
      this._platformGameplayReported = true;
      this._beginPlatformGameplayReporting();
    } else if (!shouldReport && this._platformGameplayReported) {
      this._platformGameplayReported = false;
      if (!this._shouldKeepPokiFallInputLatch()) {
        this._platformGameplayInputLatch = false;
      }
      platformGameplayStop();
    }
  };

  Game.prototype._shouldPauseBgm = function () {
    if (this.isEnvironmentPaused || this.isHostPaused || this.isAdPaused) return true;
    if (this.stateMachine && this.stateMachine.is) {
      if (this.stateMachine.is("fall") || this.stateMachine.is("shake")
        || this.stateMachine.is("transition")) {
        if (this.isDialogPaused && !this.isPlayerPaused && !this.isHostPaused) return false;
        return !!(this.isPlayerPaused || this.isHostPaused);
      }
    }
    return false;
  };

  Game.prototype._pauseForDialog = function () {
    if (this._dialogPauseDepth > 0) {
      this._dialogPauseDepth++;
      return;
    }
    this._dialogPauseDepth = 1;
    this._clearStaleKeyboardInput();
    this.isDialogPaused = true;
    this._reconcileFallSimPause();
    this._reconcileShakeSimPause();
    this._reconcileTransitionSimPause();
    this._syncPlatformGameplay();
  };

  Game.prototype._resumeFromDialog = function () {
    if (this._dialogPauseDepth <= 0) return;
    this._dialogPauseDepth--;
    if (this._dialogPauseDepth > 0) return;
    this.isDialogPaused = false;
    this._reconcileFallSimPause();
    this._reconcileShakeSimPause();
    this._reconcileTransitionSimPause();
    this._syncPlatformGameplay();
  };

  Game.prototype._syncBgmPauseState = function () {
    if (!this.soundManager) return;
    var sm = this.soundManager;
    if (typeof sm.setBgmPaused === "function") {
      sm.setBgmPaused(this._shouldPauseBgm());
    }
    if (typeof sm.setFocusPaused === "function") {
      sm.setFocusPaused(!!(this.isEnvironmentPaused || this.isHostPaused || this.isAdPaused));
    }
  };

  Game.prototype._clearStaleKeyboardInput = function () {
    if (this.runPhase && this.runPhase.inputManager
      && typeof this.runPhase.inputManager.clearKeyboardState === "function") {
      this.runPhase.inputManager.clearKeyboardState();
    }
    this._dropKeyLeft = false;
    this._dropKeyRight = false;
    this._clearDropAimKeyboardHold();
    this._dropTouchGestureActive = false;
    this._dropTouchStartedInFlask = false;
    this._dropTouchPointerId = null;
  };

  Game.prototype.setEnvironmentPaused = function (paused) {
    var next = !!paused;
    if (next === !!this.isEnvironmentPaused) return;
    this.isEnvironmentPaused = next;
    if (next) this._clearStaleKeyboardInput();
    this._reconcileFallSimPause();
    this._reconcileShakeSimPause();
    this._reconcileTransitionSimPause();
    this._syncBgmPauseState();
    this._syncPlatformGameplay();
  };

  Game.prototype.setHostPaused = function (paused) {
    var next = !!paused;
    if (next === !!this.isHostPaused) return;
    this.isHostPaused = next;
    if (next) {
      this._clearStaleKeyboardInput();
      // Cancel interactive aim modes so Escape/click cannot change state under the shield.
      if (this.isMinimizeMode) this._cleanupMinimizeMode();
    }
    this._reconcileFallSimPause();
    this._reconcileShakeSimPause();
    this._reconcileTransitionSimPause();
    this._syncHostFrameSuspend();
    this._syncBgmPauseState();
    this._syncPlatformGameplay();
    this._syncHostPauseOverlay();
    this._syncPlayerPauseHint();
  };

  /**
   * Full-screen input shield for YouTube host pause (Mediacube: fully non-interactive).
   * Pointer hits are swallowed by the overlay; keyboard is gated in handleGlobalKeyDown.
   */
  Game.prototype._syncHostPauseOverlay = function () {
    var el = this.dom && this.dom.hostPauseOverlay;
    if (!el || !el.classList) return;
    var show = !!this.isHostPaused;
    el.classList.toggle("hidden", !show);
    if (typeof el.setAttribute === "function") {
      el.setAttribute("aria-hidden", show ? "false" : "true");
    }
  };

  /**
   * YouTube onPause: stop rAF loops (Fall / Shake / transition) so execution truly halts.
   * Player pause keeps redrawing for the PAUSED toast; host pause must not.
   */
  Game.prototype._syncHostFrameSuspend = function () {
    if (this.isHostPaused) {
      this._cancelGameLoop();
      this._stopTransitionRedrawLoop();
      if (this.runPhase && this.runPhase.animationFrameId != null) {
        if (typeof cancelAnimationFrame === "function") {
          cancelAnimationFrame(this.runPhase.animationFrameId);
        }
        this.runPhase.animationFrameId = null;
        this._hostSuspendedFallLoop = true;
      }
      return;
    }
    if (this._hostSuspendedFallLoop && this.runPhase
      && this.runPhase.state !== "finished"
      && this.runPhase.state !== "failed"
      && this.runPhase.state !== "idle"
      && typeof requestAnimationFrame === "function"
      && this.runPhase._boundGameLoop) {
      this._hostSuspendedFallLoop = false;
      this.runPhase.animationFrameId = requestAnimationFrame(this.runPhase._boundGameLoop);
    } else {
      this._hostSuspendedFallLoop = false;
    }
    this._scheduleGameLoop();
    if (this.stateMachine && this.stateMachine.is && this.stateMachine.is("transition")) {
      this._startTransitionRedrawLoop();
    }
  };

  Game.prototype.setAdPaused = function (paused) {
    var next = !!paused;
    if (next === !!this.isAdPaused) return;
    this.isAdPaused = next;
    if (next) this._clearStaleKeyboardInput();
    this._reconcileFallSimPause();
    this._reconcileShakeSimPause();
    this._reconcileTransitionSimPause();
    this._syncBgmPauseState();
    this._syncPlatformGameplay();
  };

  Game.prototype._handlePageVisibilityChange = function () {
    // YouTube Playables: pause/resume only via ytgame.system.onPause/onResume.
    if (YtBridge.isEnabled()) return;
    var hidden = this._isPageHidden();
    this.setEnvironmentPaused(hidden);
  };

  Game.prototype._freezeFallSim = function (now) {
    if (!this.runPhase || !this.stateMachine || !this.stateMachine.is("fall")) return false;
    this.frozenFallArcProgress = this._computeFallArcProgress();
    this.frozenFallSceneTime = this._getSkySeaDrawTime(now);
    this.frozenSkyHour = this.getSkyHour(now);
    freezeSkyClock(this.skyClock, this.frozenSkyHour, now);
    if (this._fallCatcherFlaskIntroFadeStartedAt != null) {
      this._fallCatcherFlaskIntroFadePauseWallMs = now;
    }
    if (typeof this.runPhase.pause !== "function" || !this.runPhase.pause(now)) return false;
    this._endAnalyticsPlayTime();
    return true;
  };

  Game.prototype._unfreezeFallSim = function (now) {
    if (!this.runPhase || !this.stateMachine || !this.stateMachine.is("fall")) return false;
    this._shiftFallCatcherFlaskIntroForPause(this._fallCatcherFlaskIntroFadePauseWallMs, now);
    this._fallCatcherFlaskIntroFadePauseWallMs = null;
    this.frozenFallArcProgress = null;
    if (this.frozenFallSceneTime != null) {
      this.skyTimeOffsetMs = now - this.frozenFallSceneTime;
    }
    this.frozenFallSceneTime = null;
    unfreezeSkyClock(this.skyClock, now);
    this.frozenSkyHour = null;
    if (typeof this.runPhase.resume !== "function" || !this.runPhase.resume(now)) return false;
    this._beginAnalyticsPlayTime();
    return true;
  };

  Game.prototype._reconcileFallSimPause = function () {
    var inFall = !!(this.runPhase && this.stateMachine && this.stateMachine.is("fall"));
    var want = inFall && this._isSimPaused();
    if (want && !this._fallSimFrozen) {
      var now = typeof performance !== "undefined" ? performance.now() : Date.now();
      if (this._freezeFallSim(now)) this._fallSimFrozen = true;
    } else if (!want && this._fallSimFrozen) {
      var resumeNow = typeof performance !== "undefined" ? performance.now() : Date.now();
      if (this._unfreezeFallSim(resumeNow)) this._fallSimFrozen = false;
    } else if (!inFall && this._fallSimFrozen) {
      this._fallSimFrozen = false;
      this.frozenFallArcProgress = null;
      this.frozenFallSceneTime = null;
    }
  };

  Game.prototype._freezeShakeSim = function (now) {
    if (!this.stateMachine || !this.stateMachine.is("shake")) return false;
    this.frozenShakePreviewTime = this._getSkySeaDrawTime(now);
    this.frozenSkyHour = this.getSkyHour(now);
    freezeSkyClock(this.skyClock, this.frozenSkyHour, now);
    this._endAnalyticsPlayTime();
    return true;
  };

  Game.prototype._unfreezeShakeSim = function (now) {
    if (!this.stateMachine || !this.stateMachine.is("shake")) return false;
    this.frozenShakePreviewTime = null;
    unfreezeSkyClock(this.skyClock, now);
    this.frozenSkyHour = null;
    this._beginAnalyticsPlayTime();
    this.syncBackgroundMusic();
    return true;
  };

  Game.prototype._reconcileShakeSimPause = function () {
    var inShake = !!(this.stateMachine && this.stateMachine.is("shake"));
    var want = inShake && this._isSimPaused();
    if (want && !this._shakeSimFrozen) {
      var now = typeof performance !== "undefined" ? performance.now() : Date.now();
      if (this._freezeShakeSim(now)) this._shakeSimFrozen = true;
    } else if (!want && this._shakeSimFrozen) {
      var resumeNow = typeof performance !== "undefined" ? performance.now() : Date.now();
      if (this._unfreezeShakeSim(resumeNow)) this._shakeSimFrozen = false;
    } else if (!inShake && this._shakeSimFrozen) {
      this._shakeSimFrozen = false;
      this.frozenShakePreviewTime = null;
    }
  };

  Game.prototype._freezeTransitionSim = function (now) {
    if (!this.stateMachine || !this.stateMachine.is("transition")) return false;
    var sceneNow = this._getSkySeaDrawTime(now);
    var hourNow = this.getSkyHour(now);
    this._bridgeTransitionFrozenSceneTime = sceneNow;
    this.frozenSkyHour = hourNow;
    freezeSkyClock(this.skyClock, this.frozenSkyHour, now);
    if (this.celestialTransition) {
      this._bridgeCelestialFrozenAlpha = this.stageContentAlpha;
      this._bridgeCelestialPauseWallMs = now;
    }
    if (this._runwayFlaskFadeSnapshot) {
      this._runwayFlaskFadePauseWallMs = now;
    }
    this._captureAndPauseBridgeTimers(now);
    this._endAnalyticsPlayTime();
    return true;
  };

  Game.prototype._unfreezeTransitionSim = function (now) {
    if (!this.stateMachine || !this.stateMachine.is("transition")) return false;
    this._shiftBridgeCelestialForPause(this._bridgeCelestialPauseWallMs, now);
    this._shiftRunwayFlaskFadeForPause(this._runwayFlaskFadePauseWallMs, now);
    this._bridgeCelestialPauseWallMs = null;
    this._runwayFlaskFadePauseWallMs = null;
    unfreezeSkyClock(this.skyClock, now);
    this.frozenSkyHour = null;
    this._bridgeTransitionFrozenSceneTime = null;
    this._bridgeCelestialFrozenAlpha = null;
    this._resumeBridgeTimers(now);
    if (this.stateMachine.is("transition")) {
      this._startTransitionRedrawLoop();
    }
    this._beginAnalyticsPlayTime();
    this.syncBackgroundMusic();
    return true;
  };

  Game.prototype._reconcileTransitionSimPause = function () {
    var inTransition = !!(this.stateMachine && this.stateMachine.is("transition"));
    var want = inTransition && this._isSimPaused();
    if (want && !this._transitionSimFrozen) {
      var now = typeof performance !== "undefined" ? performance.now() : Date.now();
      if (this._freezeTransitionSim(now)) this._transitionSimFrozen = true;
    } else if (!want && this._transitionSimFrozen) {
      var resumeNow = typeof performance !== "undefined" ? performance.now() : Date.now();
      if (this._unfreezeTransitionSim(resumeNow)) this._transitionSimFrozen = false;
    } else if (!inTransition && this._transitionSimFrozen) {
      this._transitionSimFrozen = false;
      this._bridgeTransitionFrozenSceneTime = null;
      this._bridgeCelestialFrozenAlpha = null;
    }
  };

  Game.prototype.syncBackgroundMusic = function () {
    var sm = this.soundManager;
    if (!sm || typeof sm.setBgmScene !== "function") return;
    if (this.stateMachine && this.stateMachine.is("shake")) {
      sm.setBgmScene("shake");
    } else if (this.stateMachine && this.stateMachine.is("fall")) {
      sm.setBgmScene("fall");
    } else if (this.stateMachine && this.stateMachine.is("transition")) {
      return;
    } else {
      sm.stopBgm();
    }
    this._syncBgmPauseState();
  };

  var BGM_AUTOPLAY_BLOCKED_TOAST = "Tap anywhere for sound";
  var SFX_AUTOPLAY_BLOCKED_TOAST = "Tap anywhere for sound effects";
  var AUDIO_AUTOPLAY_BLOCKED_TOAST_MS = 5000;

  /**
   * @param {string} message
   */
  Game.prototype._notifyAudioGestureNeeded = function (message) {
    this._bgmAutoplayBlockedToastActive = true;
    this.showToast(message, AUDIO_AUTOPLAY_BLOCKED_TOAST_MS);
    this._bindBgmAutoplayBlockedToastDismiss();
  };

  Game.prototype._dismissBgmAutoplayBlockedToast = function () {
    if (!this._bgmAutoplayBlockedToastActive) return;
    this._bgmAutoplayBlockedToastActive = false;
    this.showToast(null);
  };

  Game.prototype._bindBgmAutoplayBlockedToastDismiss = function () {
    var self = this;
    function dismiss() {
      self._dismissBgmAutoplayBlockedToast();
    }
    window.addEventListener("pointerdown", dismiss, { once: true });
    window.addEventListener("keydown", dismiss, { once: true });
    window.addEventListener("touchstart", dismiss, { once: true, passive: true });
  };

  /**
   * After cold boot / checkpoint resume, probe SFX + BGM.
   * Toast when BGM is blocked, or when BGM autoplays but Web Audio SFX still need a gesture.
   */
  Game.prototype._handleBootBgmAutoplay = function () {
    var self = this;
    var sm = this.soundManager;
    if (!sm || typeof sm.ensureAudioPlayback !== "function") return Promise.resolve();
    return sm.ensureAudioPlayback().then(function () {
      var bgmBlocked = typeof sm.isAudioAutoplayBlocked === "function" && sm.isAudioAutoplayBlocked();
      if (bgmBlocked) {
        self._notifyAudioGestureNeeded(BGM_AUTOPLAY_BLOCKED_TOAST);
        return;
      }
      var sfxBlocked = typeof sm.isSfxAutoplayBlocked === "function" && sm.isSfxAutoplayBlocked();
      if (sfxBlocked) {
        self._notifyAudioGestureNeeded(SFX_AUTOPLAY_BLOCKED_TOAST);
      }
    });
  };

  Game.prototype._clearPlayerPauseState = function () {
    if (!this.isPlayerPaused) {
      this._playerPauseHintEligible = false;
      this._syncPlayerPauseHint();
      this._syncFallPauseButton();
      return;
    }
    this.isPlayerPaused = false;
    this._playerPauseHintEligible = false;
    this._reconcileFallSimPause();
    this._reconcileShakeSimPause();
    this._reconcileTransitionSimPause();
    this._syncFallPauseButton();
    this._syncBgmPauseState();
    this._syncPlatformGameplay();
    this._syncPlayerPauseHint();
  };

  Game.prototype._syncPlayerPauseHint = function () {
    var mount = this.dom && this.dom.playerPauseHint;
    if (!mount) return;
    var inStage = this.stateMachine && this.stateMachine.is && (
      (this.stateMachine.is("fall") && this.runPhase) ||
      this.stateMachine.is("shake") ||
      this.stateMachine.is("transition")
    );
    // Host pause uses `#hostPauseOverlay` instead of this play-column toast.
    var show = !!inStage && !this.isHostPaused
      && (!!this.isPlayerPaused && !!this._playerPauseHintEligible);
    var label = typeof mount.querySelector === "function"
      ? mount.querySelector(".player-pause-hint__label")
      : null;
    if (label && show) {
      label.textContent = "PAUSED";
    }
    // PAUSED is a status toast, not a keyboard/mouse control hint — show on touch too.
    setFallMoveHintVisible(mount, show, { ignoreTouchGate: true });
  };

  Game.prototype.setPlayerPaused = function (paused, opts) {
    opts = opts || {};
    var shouldPause = !!paused;
    if (!shouldPause && this._shouldBlockPlayerPauseToggle()) return;
    if (shouldPause === !!this.isPlayerPaused) return;
    this.isPlayerPaused = shouldPause;
    if (opts.source === "player") {
      this._playerPauseHintEligible = shouldPause;
    } else if (!shouldPause) {
      this._playerPauseHintEligible = false;
    }
    if (shouldPause) this._clearStaleKeyboardInput();
    this._reconcileFallSimPause();
    this._reconcileShakeSimPause();
    this._reconcileTransitionSimPause();
    this._syncFallPauseButton();
    this._syncBgmPauseState();
    this._syncPlatformGameplay();
    this._syncPlayerPauseHint();
    if (this.dom.fallPauseBtn && typeof this.dom.fallPauseBtn.blur === "function") {
      this.dom.fallPauseBtn.blur();
    }
  };

  Game.prototype.setShakePaused = function (paused) {
    if (!this.stateMachine || !this.stateMachine.is || !this.stateMachine.is("shake")) return;
    this.setPlayerPaused(paused);
  };

  Game.prototype.setTransitionPaused = function (paused) {
    if (!this.stateMachine || !this.stateMachine.is || !this.stateMachine.is("transition")) return;
    this.setPlayerPaused(paused);
  };

  Game.prototype.setFallPaused = function (paused) {
    if (!this.runPhase || (this.stateMachine && !this.stateMachine.is("fall"))) {
      this._syncFallPauseButton();
      return;
    }
    this.setPlayerPaused(paused);
  };

  Game.prototype._isOverlayOpen = function (el) {
    return !!(el && el.classList && !el.classList.contains("hidden"));
  };

  Game.prototype._shouldBlockPlayerPauseToggle = function () {
    if (this._pokiUnpauseAdPending || this._continueToRunAdPending) return true;
    if (this.isAdPaused) return true;
    if (this.isHostPaused) return true;
    if (this._shouldBlockInputForVictoryPopup()) return true;
    if (this._isEvolutionRevealPending()) return true;
    if (this.retention && typeof this.retention.isAnyPopupOpen === "function" && this.retention.isAnyPopupOpen()) {
      return true;
    }
    var dom = this.dom;
    if (!dom) return false;
    var overlayIds = [
      "fallVolumeOverlay",
      "liveReviveOverlay",
      "evolutionDockPromptOverlay",
      "gameOverOverlay",
      "hostPauseOverlay",
    ];
    for (var i = 0; i < overlayIds.length; i++) {
      if (this._isOverlayOpen(dom[overlayIds[i]])) return true;
    }
    return false;
  };

  Game.prototype._shouldOfferPokiUnpauseCommercialBreak = function () {
    return this.shouldOfferPokiCommercialBreak()
      && typeof PokiBridge.commercialBreak === "function";
  };

  Game.prototype._beginPokiUnpauseCommercialBreak = function () {
    if (this._pokiUnpauseAdPending) return;
    var self = this;
    this._pokiUnpauseAdPending = true;
    PokiBridge.commercialBreak({ game: this }).then(
      function () { self._finishPokiUnpauseCommercialBreak(); },
      function () { self._finishPokiUnpauseCommercialBreak(); }
    );
  };

  Game.prototype._finishPokiUnpauseCommercialBreak = function () {
    this._pokiUnpauseAdPending = false;
    if (!this.isPlayerPaused) return;
    this.setPlayerPaused(false, { source: "player" });
    this._trackAnalytics(AnalyticsEvents.PLAYER_RESUME);
  };

  Game.prototype.toggleFallPause = function () {
    if (!this.stateMachine || !this.stateMachine.is) return;
    if (this._shouldBlockPlayerPauseToggle()) return;
    var playable = this.stateMachine.is("shake") || this.stateMachine.is("transition")
      || (this.runPhase && this.stateMachine.is("fall"));
    if (!playable) return;
    var willPause = !this.isPlayerPaused;
    if (!willPause && this._shouldOfferPokiUnpauseCommercialBreak()) {
      this._beginPokiUnpauseCommercialBreak();
      return;
    }
    this.setPlayerPaused(willPause, { source: "player" });
    this._trackAnalytics(willPause ? AnalyticsEvents.PLAYER_PAUSE : AnalyticsEvents.PLAYER_RESUME);
  };

  Game.prototype.handleGlobalKeyDown = function (e) {
    if (!e || e.repeat) return;
    // YouTube host pause: fully non-interactive. Never preventDefault Escape (Playables Design).
    if (this.isAdPaused) {
      if (e.key !== "Escape" && e.code !== "Escape") {
        e.preventDefault();
      }
      return;
    }
    if (this.isHostPaused) {
      if (e.key !== "Escape" && e.code !== "Escape") {
        e.preventDefault();
      }
      return;
    }
    if (this._victoryPopupAllowsEscDismiss() && e.key === "Escape") {
      if (this._victoryPopupHandle && typeof this._victoryPopupHandle.close === "function") {
        this._victoryPopupHandle.close();
      }
      return;
    }
    if (this._shouldBlockInputForVictoryPopup()) {
      if (e.key === "ArrowLeft" || e.key === "ArrowRight") return;
      if (e.key !== "Escape" && e.code !== "Escape") {
        e.preventDefault();
      }
      return;
    }
    if (this._isEvolutionRevealPending()) {
      if (e.key !== "Escape" && e.code !== "Escape") {
        e.preventDefault();
      }
      return;
    }
    if (!this.stateMachine || !this.stateMachine.is) return;
    var target = e.target;
    if (target) {
      var tag = (target.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select" || target.isContentEditable) return;
    }
    var key = e.key;
    var isSpace = key === " " || key === "Spacebar" || key === "Space";
    if (isSpace) {
      var pauseStage = this.stateMachine.is("fall") || this.stateMachine.is("transition")
        || this.stateMachine.is("shake");
      if (!pauseStage) return;
      e.preventDefault();
      this.toggleFallPause();
      return;
    }
    if (key === "n" || key === "N") {
      if (!this.stateMachine.is("shake")) return;
      if (!this._isContinueBtnEnabled()) return;
      e.preventDefault();
      this.continueToRun();
      return;
    }
    if (key === "q" || key === "Q") {
      if (!this.stateMachine.is("shake")) return;
      if (!this._isShakeBtnEnabled()) return;
      e.preventDefault();
      this.doShake();
    }
  };

  Game.prototype.setEvolutionDockVisible = function (visible) {
    var show = !!visible;
    var isVertical = this._isVerticalLayout();
    if (this.dom.evoRingWrap && this.dom.evoRingWrap.classList) {
      var showRing = show && !isVertical;
      this.dom.evoRingWrap.classList.toggle("hidden", !showRing);
      if (typeof this.dom.evoRingWrap.setAttribute === "function") {
        this.dom.evoRingWrap.setAttribute("aria-hidden", showRing ? "false" : "true");
      }
      if (showRing) {
        this.populateEvolutionRing();
        this.applyEvolutionRingBounds();
      }
    }
    if (this.dom.verticalEvoStripWrap && this.dom.verticalEvoStripWrap.classList) {
      var showStrip = show && isVertical;
      this.dom.verticalEvoStripWrap.classList.toggle("hidden", !showStrip);
      if (typeof this.dom.verticalEvoStripWrap.setAttribute === "function") {
        this.dom.verticalEvoStripWrap.setAttribute("aria-hidden", showStrip ? "false" : "true");
      }
      if (this.dom.verticalEvoStripMount && typeof this.dom.verticalEvoStripMount.setAttribute === "function") {
        this.dom.verticalEvoStripMount.setAttribute("aria-hidden", showStrip ? "false" : "true");
      }
      if (showStrip) {
        this.populateEvolutionStrip();
      }
    }
  };

  Game.prototype.populateEvolutionChrome = function () {
    this.populateEvolutionRing();
    this.populateEvolutionStrip();
  };

  Game.prototype.populateEvolutionRing = function (opts) {
    opts = opts || {};
    if (this._isVerticalLayout()) return;
    // DOM is frozen while a discovery reveal is pending/running; only the
    // ceremony itself may re-render.
    if (this._evoRevealHold && !opts.fromReveal) return;
    var mount = this.dom && this.dom.evoRingMount;
    if (!mount) return;
    var chain = (typeof C.getActiveEvolutionChain === "function")
      ? C.getActiveEvolutionChain()
      : (C.EVOLUTION_CHAIN || []);
    var self = this;
    var ringState = {
      unlockedTiers: this.unlockedTiers,
      chain: chain,
      currentTierId: opts.heldTierId != null ? opts.heldTierId : this.currentGameMaxTier,
      skipTier: function (i, c) { return self._shouldSkipEvolutionDockTier(c, i); },
      suppressSecretChip: !!opts.suppressSecretChip,
      pendingTierIndex: opts.pendingTierIndex != null ? opts.pendingTierIndex : null,
      hiddenTierIndex: opts.hiddenTierIndex != null ? opts.hiddenTierIndex : null,
    };
    if (!opts.fromReveal) {
      var sig = buildEvolutionRingSignature(ringState);
      if (!opts.force && this._evolutionRingSig === sig) return;
      this._evolutionRingSig = sig;
    }
    renderEvolutionRing(mount, ringState);
    if (this.stateMachine && this.stateMachine.is && this.stateMachine.is("shake")) {
      this._shakeUiBoundsDirty = true;
    }
  };

  Game.prototype.populateEvolutionStrip = function (opts) {
    opts = opts || {};
    if (!this._isVerticalLayout()) return;
    if (this._evoRevealHold && !opts.fromReveal) return;
    var mount = this.dom && this.dom.verticalEvoStripMount;
    if (!mount) return;
    var chain = (typeof C.getActiveEvolutionChain === "function")
      ? C.getActiveEvolutionChain()
      : (C.EVOLUTION_CHAIN || []);
    var self = this;
    var stripState = {
      unlockedTiers: this.unlockedTiers,
      chain: chain,
      currentTierId: opts.heldTierId != null ? opts.heldTierId : this.currentGameMaxTier,
      skipTier: function (i, c) { return self._shouldSkipEvolutionDockTier(c, i); },
      suppressSecretChip: !!opts.suppressSecretChip,
      pendingTierIndex: opts.pendingTierIndex != null ? opts.pendingTierIndex : null,
      hiddenTierIndex: opts.hiddenTierIndex != null ? opts.hiddenTierIndex : null,
    };
    if (!opts.fromReveal) {
      var sig = buildEvolutionStripSignature(stripState);
      if (!opts.force && this._evolutionStripSig === sig) return;
      this._evolutionStripSig = sig;
    }
    renderEvolutionStrip(mount, stripState);
  };

  Game.prototype.updateStashPanelDOM = function () {
    if (!this.dom) return;
    var mounts = [];
    if (this.dom.fallStashMount) mounts.push(this.dom.fallStashMount);
    if (this._isVerticalLayout() && this.dom.verticalStashMount) {
      mounts.push(this.dom.verticalStashMount);
    }
    if (mounts.length === 0) return;
    for (var m = 0; m < mounts.length; m++) {
      var mount = mounts[m];
      var mountOpts = (this.dom.verticalStashMount && mount === this.dom.verticalStashMount)
        ? { includeLabel: false, itemsRow: true }
        : undefined;
      updateStashPanel(mount, this.boostBar || [], mountOpts);
    }
    var isFall = this.stateMachine && this.stateMachine.is && this.stateMachine.is("fall");
    if (this.dom.fallStashPanel && !this._isVerticalLayout()) {
      if (isFall) {
        this.dom.fallStashPanel.classList.remove("hidden");
        if (typeof this.dom.fallStashPanel.setAttribute === "function") {
          this.dom.fallStashPanel.setAttribute("aria-hidden", "false");
        }
      } else {
        var hasItems = this.boostBar && this.boostBar.length > 0;
        this.dom.fallStashPanel.classList.toggle("hidden", !hasItems);
      }
    }
    if (this._isVerticalLayout()) {
      this._syncVerticalStageChromePanels();
    }
  };

  Game.prototype.setDebugStripVisible = function (visible) {
    if (this.dom.hud) this.dom.hud.classList.toggle("hidden", !visible);
  };

  Game.prototype.getEvolutionRecipe = function (chain, tierIndex) {
    if (!chain || !chain[tierIndex]) {
      return { left: "?", right: "?", result: "?", leftAsset: "cell", resultAsset: "cell", isBase: false };
    }
    var current = chain[tierIndex];
    var next = chain[tierIndex + 1];
    if (!next) {
      return {
        left: current.name,
        right: current.name,
        result: null,
        leftAsset: current.assetId || "cell",
        resultAsset: null,
        isBase: false,
        isCapped: true,
      };
    }
    return {
      left: current.name,
      right: current.name,
      result: next.name,
      leftAsset: current.assetId || "cell",
      resultAsset: next.assetId || "cell",
      isBase: false,
      isCapped: false,
    };
  };

  Game.prototype._clearBridgeTransitionTimers = function () {
    if (this.continueToFallTimeoutId) {
      clearTimeout(this.continueToFallTimeoutId);
      this.continueToFallTimeoutId = null;
    }
    if (this.runToShakeTimeoutId) {
      clearTimeout(this.runToShakeTimeoutId);
      this.runToShakeTimeoutId = null;
    }
    if (this._bridgeShakeFadeInTimeoutId) {
      clearTimeout(this._bridgeShakeFadeInTimeoutId);
      this._bridgeShakeFadeInTimeoutId = null;
    }
    if (this._bridgeThemeCrossfadeTimeoutId) {
      clearTimeout(this._bridgeThemeCrossfadeTimeoutId);
      this._bridgeThemeCrossfadeTimeoutId = null;
    }
    if (this._transitionRedrawId) {
      cancelAnimationFrame(this._transitionRedrawId);
      this._transitionRedrawId = null;
    }
    this._runwayFlaskFadeSnapshot = null;
    this._runwayFlaskFadePauseWallMs = null;
  };

  Game.prototype._stopTransitionRedrawLoop = function () {
    if (this._transitionRedrawId) {
      cancelAnimationFrame(this._transitionRedrawId);
      this._transitionRedrawId = null;
    }
  };

  Game.prototype._startTransitionRedrawLoop = function () {
    var self = this;
    if (this._isSimPaused()) return;
    if (this._transitionRedrawId) {
      cancelAnimationFrame(this._transitionRedrawId);
      this._transitionRedrawId = null;
    }
    function frame(now) {
      var workStart = typeof performance !== "undefined" ? performance.now() : now;
      if (!self.stateMachine || !self.stateMachine.is("transition")) {
        self._transitionRedrawId = null;
        return;
      }
      if (self._isSimPaused()) {
        self._transitionRedrawId = null;
        return;
      }
      self._ensureLayout();
      if (self.canvas && self.layout) {
        self.applyCanvasSize(self.layout.canvasWidth, self.layout.canvasHeight, {
          skipBoardViewport: true,
        });
        self._syncBoardViewportIfNeeded();
      }
      self.updateCelestialTransition(now);
      if (self.ctx && self.layout && typeof self.ctx.clearRect === "function") {
        self._beginPlayfieldFrame();
        if (self.pixi && typeof self.pixi.clearWorldClip === "function") {
          self.pixi.clearWorldClip();
        }
        var transSkyStart = self._devNowMs();
        if (!self.pixi) {
          self.ctx.clearRect(0, 0, self.layout.canvasWidth, self.layout.canvasHeight);
        }
        self.drawSkySeaBackgroundLayer(self.ctx, self._getSkyClockNowMs(now));
        self._devTransitionSkyMs = self._devNowMs() - transSkyStart;
        var transDrawStart = self._devNowMs();
        self._drawRunwayFlaskFadeOverlay(self.ctx, now);
        self._devTransitionDrawMs = self._devNowMs() - transDrawStart;
        self._presentPlayfield();
      } else {
        self._devTransitionSkyMs = 0;
        self._devTransitionDrawMs = 0;
      }
      var transDeltaMs = 0;
      if (self._transitionPerfLastNow != null) {
        transDeltaMs = Math.max(0, Math.min(100, now - self._transitionPerfLastNow));
      }
      self._transitionPerfLastNow = now;
      var transCpuMs = self._devNowMs() - workStart;
      var transSkyMs = 0;
      var transDrawMs = 0;
      if (self._devTransitionSkyMs != null) transSkyMs = self._devTransitionSkyMs;
      if (self._devTransitionDrawMs != null) transDrawMs = self._devTransitionDrawMs;
      self._updateDevFps(now, transCpuMs, { sky: transSkyMs, draw: transDrawMs });
      self._updateFallProgressDial(now);
      self._tickPerfMonitor(now, "transition", { frameMs: transDeltaMs, bodies: 0 });
      self._transitionRedrawId = requestAnimationFrame(frame);
    }
    this._transitionPerfLastNow = null;
    this._transitionRedrawId = requestAnimationFrame(frame);
  };

  Game.prototype._stopRunForCheckpointResume = function () {
    this._endAnalyticsPlayTime();
    this._clearBridgeTransitionTimers();
    if (this.runPhase) this.runPhase.stop();
    this.runPhase = null;
    this._fallSimFrozen = false;
    this._clearPlayerPauseState();
    this.isRunFlaskShattered = false;
    this.flaskSystem = null;
    this.stateMachine = new GameStateMachine("start");
    var app = document.getElementById("app");
    if (app) app.classList.remove("stage-run", "stage-shake");
    this.resetShakeUIBounds();
    this.setDebugStripVisible(false);
    this.setPlayerHudVisible(false);
    this.setStageStatus("hidden");
    this.setEvolutionDockVisible(true);
    if (this.dom.shakeUI) this.dom.shakeUI.classList.add("hidden");
    if (this.dom.endOverlay) this.dom.endOverlay.classList.add("hidden");

    this.updateHpUrgency(100);
    this.syncBackgroundMusic();
    this._syncPlatformGameplay();
  };

  /** @returns {Promise<void>} */
  Game.prototype._resumeFromCheckpoint = function () {
    this.pendingRestoreFromSave = true;
    return this.startFromMenu({ autoLaunch: true });
  };

  Game.prototype.onRunFailed = function (data) {
    if (data && data.reason === "flask_broken") {
      this.showGameOver("flask_broken");
      return;
    }
    this._stopRunForCheckpointResume();
    return this._resumeFromCheckpoint();
  };

  /** Victory-style word-pop headline for the loss screen title. */
  Game.prototype._setGameOverHeadline = function (titleEl, text) {
    if (!titleEl) return;
    var headlineText = text || "Well done!";
    titleEl.textContent = "";
    titleEl.setAttribute("aria-label", headlineText);
    if (typeof document === "undefined" || typeof document.createElement !== "function") {
      titleEl.textContent = headlineText;
      return;
    }
    var words = headlineText.split(/\s+/).filter(Boolean);
    for (var w = 0; w < words.length; w++) {
      if (w > 0) titleEl.appendChild(document.createTextNode(" "));
      var wordSpan = document.createElement("span");
      wordSpan.className = "victory-overlay__headline-word";
      wordSpan.textContent = words[w];
      wordSpan.style.animationDelay = (0.15 + w * 0.14) + "s";
      titleEl.appendChild(wordSpan);
    }
  };

  /** Horizontal evolution strip on the loss screen (same builder as vertical layout). */
  Game.prototype._populateGameOverEvolutionStrip = function () {
    var mount = this.dom && this.dom.gameOverEvoStripMount;
    if (!mount) {
      var overlay = this.dom && this.dom.gameOverOverlay;
      mount = overlay && typeof overlay.querySelector === "function"
        ? overlay.querySelector("#gameOverEvoStripMount")
        : null;
    }
    if (!mount) return;
    var chain = (typeof C.getActiveEvolutionChain === "function")
      ? C.getActiveEvolutionChain()
      : (C.EVOLUTION_CHAIN || []);
    var self = this;
    renderEvolutionStrip(mount, {
      unlockedTiers: this.unlockedTiers,
      chain: chain,
      currentTierId: this.currentGameMaxTier,
      skipTier: function (i, c) { return self._shouldSkipEvolutionDockTier(c, i); },
    });
  };

  Game.prototype._configureGameOverOverlay = function (reason) {
    if (this.dom && this.dom.gameOverOverlay) {
      if (reason === "overflow") {
        this.dom.gameOverOverlay.classList.add("game-over-overlay--overflow");
      } else {
        this.dom.gameOverOverlay.classList.remove("game-over-overlay--overflow");
      }
      var titleEl = this.dom.gameOverOverlay.querySelector(".game-over-title");
      var subtitleEl = this.dom.gameOverOverlay.querySelector(".game-over-subtitle");
      this._setGameOverHeadline(titleEl, "Well done!");
      if (subtitleEl) {
        subtitleEl.textContent = reason === "overflow"
          ? "The jar got overflown, but look at the progress!"
          : "The flask got smashed, let's get a new one.";
      }
      this._populateGameOverEvolutionStrip();
    }
  };

  Game.prototype.showGameOver = function (reason) {
    this._abortVictoryPopup(true);
    this._abortPuzzleReveal();
    this._endAnalyticsPlayTime();
    this._configureGameOverOverlay(reason);
    this._resetOverflowState();
    this._clearBridgeTransitionTimers();
    if (this.runPhase) this.runPhase.stop();
    this.runPhase = null;
    this._fallSimFrozen = false;
    this._clearPlayerPauseState();
    this.flaskSystem = null;
    this.isRunFlaskShattered = false;
    this.boostBar = [];


    this.stateMachine = new GameStateMachine("start");
    var app = document.getElementById("app");
    if (app) app.classList.remove("stage-run", "stage-shake");
    this.resetShakeUIBounds();
    this.setDebugStripVisible(false);
    this.setPlayerHudVisible(false);
    this.setStageStatus("hidden");
    this.setEvolutionDockVisible(false);
    if (this.dom.shakeUI) this.dom.shakeUI.classList.add("hidden");
    if (this.dom.endOverlay) this.dom.endOverlay.classList.add("hidden");

    if (this.dom.gameOverOverlay) this.dom.gameOverOverlay.classList.remove("hidden");
    this._recordAnalyticsGameLoss(reason);
    if (FrvrBridge.isEnabled() && this._frvrActiveLevelId) {
      FrvrBridge.trackLevelEnd(this._frvrActiveLevelId);
      this._frvrActiveLevelId = null;
    }
    this._trackAnalytics(
      reason === "overflow" ? AnalyticsEvents.OVERFLOW : AnalyticsEvents.FLASK_BROKEN
    );
    this._pokiStage("fail");
    this._updateLossLeaderboard();
    if (this.dom.gameOverShareBtn) {
      var showShare = FrvrBridge.isEnabled() && FrvrBridge.supportsShare();
      this.dom.gameOverShareBtn.classList.toggle("hidden", !showShare);
    }
    this._clearProgressCheckpoint();
    this.updateHpUrgency(100);
    this.syncBackgroundMusic();
    this._syncPlatformGameplay();
    this.syncDevTestBar();
  };

  Game.prototype._resetLeaderboardPanelLayout = function () {
    if (this.dom.leaderboardPanel) {
      this.dom.leaderboardPanel.classList.remove("leaderboard-panel--animating");
      this.dom.leaderboardPanel.style.height = "";
      this.dom.leaderboardPanel.style.overflow = "";
    }
  };

  Game.prototype._clearLeaderboardLoadingState = function () {
    if (this.dom.leaderboardCard) {
      this.dom.leaderboardCard.classList.remove("leaderboard-card--loading");
      this.dom.leaderboardCard.removeAttribute("aria-busy");
    }
    if (this.dom.leaderboardLoading) this.dom.leaderboardLoading.classList.add("hidden");
    if (this.dom.leaderboardStatus) {
      this.dom.leaderboardStatus.classList.add("hidden");
      this.dom.leaderboardStatus.classList.remove("sr-only");
    }
  };

  /**
   * @param {number} fromHeight
   */
  Game.prototype._animateLeaderboardPanelHeight = function (fromHeight) {
    var panel = this.dom.leaderboardPanel;
    if (!panel) return;

    var reducedMotion = false;
    try {
      reducedMotion = !!(window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches);
    } catch (_) {}

    var startHeight = fromHeight > 0 ? fromHeight : panel.offsetHeight;
    var targetHeight = panel.scrollHeight;

    if (reducedMotion || Math.abs(startHeight - targetHeight) < 2) {
      this._resetLeaderboardPanelLayout();
      return;
    }

    panel.style.overflow = "hidden";
    panel.style.height = startHeight + "px";
    panel.classList.add("leaderboard-panel--animating");

    var finished = false;
    var finish = function () {
      if (finished) return;
      finished = true;
      panel.removeEventListener("transitionend", onTransitionEnd);
      panel.classList.remove("leaderboard-panel--animating");
      panel.style.height = "";
      panel.style.overflow = "";
    };
    var onTransitionEnd = function (ev) {
      if (ev.target !== panel || ev.propertyName !== "height") return;
      finish();
    };

    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        panel.addEventListener("transitionend", onTransitionEnd);
        panel.style.height = targetHeight + "px";
        setTimeout(finish, 320);
      });
    });
  };

  Game.prototype._hideLeaderboardCard = function () {
    if (this.dom.leaderboardCard) {
      this.dom.leaderboardCard.classList.add("hidden");
      this.dom.leaderboardCard.classList.remove("leaderboard-card--loading");
      this.dom.leaderboardCard.removeAttribute("aria-busy");
    }
    if (this.dom.leaderboardLoading) this.dom.leaderboardLoading.classList.add("hidden");
    this._resetLeaderboardPanelLayout();
    if (this.dom.leaderboardStatus) {
      this.dom.leaderboardStatus.classList.add("hidden");
      this.dom.leaderboardStatus.classList.remove("sr-only");
      this.dom.leaderboardStatus.textContent = "";
    }
    if (this.dom.leaderboardBody) this.dom.leaderboardBody.innerHTML = "";
    if (this.dom.leaderboardOwnTable) this.dom.leaderboardOwnTable.classList.add("hidden");
    if (this.dom.leaderboardOwnBody) this.dom.leaderboardOwnBody.innerHTML = "";
    this._setLeaderboardRankHint("");
  };

  Game.prototype._hideGameOverScoreCard = function () {
    if (!this.dom) return;
    if (this.dom.gameOverScoreCard) {
      this.dom.gameOverScoreCard.classList.add("hidden");
      this.dom.gameOverScoreCard.classList.remove("game-over-score-card--new-best");
    }
    if (this.dom.gameOverScoreTitle) this.dom.gameOverScoreTitle.classList.remove("hidden");
    if (this.dom.gameOverScoreNewBest) this.dom.gameOverScoreNewBest.classList.add("hidden");
    if (this.dom.gameOverScoreRows) this.dom.gameOverScoreRows.classList.remove("hidden");
  };

  /**
   * Local-best loss screen (YouTube / Poki): run score + high score, or a single New high score line.
   * Uses `newBestThisRun` so a tie with the prior best does not claim a new record.
   */
  Game.prototype._showLocalGameOverScoreCard = function () {
    if (!this.dom || !this.dom.gameOverScoreCard) return;
    var runScore = Math.max(0, Math.floor(Number(this.currentScore) || 0));
    var highScore = Math.max(runScore, Math.max(0, Math.floor(Number(this.bestScore) || 0)));
    var isNewBest = !!this.newBestThisRun && highScore > 0;
    var formatted = this.formatScore(isNewBest ? highScore : runScore);

    this.dom.gameOverScoreCard.classList.remove("hidden");
    this.dom.gameOverScoreCard.classList.toggle("game-over-score-card--new-best", isNewBest);
    if (this.dom.gameOverScoreTitle) {
      this.dom.gameOverScoreTitle.classList.toggle("hidden", isNewBest);
    }
    if (this.dom.gameOverScoreNewBest) {
      this.dom.gameOverScoreNewBest.classList.toggle("hidden", !isNewBest);
    }
    if (this.dom.gameOverScoreNewBestValue) {
      this.dom.gameOverScoreNewBestValue.textContent = formatted;
    }
    if (this.dom.gameOverScoreRows) {
      this.dom.gameOverScoreRows.classList.toggle("hidden", isNewBest);
    }
    if (!isNewBest) {
      if (this.dom.gameOverScoreValue) this.dom.gameOverScoreValue.textContent = this.formatScore(runScore);
      if (this.dom.gameOverBestScoreValue) {
        this.dom.gameOverBestScoreValue.textContent = this.formatScore(highScore);
      }
    }
  };

  Game.prototype._showLeaderboardLoading = function () {
    if (!this.dom.leaderboardCard) return;
    this._hideGameOverScoreCard();
    this.dom.leaderboardCard.classList.remove("hidden");
    this.dom.leaderboardCard.classList.add("leaderboard-card--loading");
    this.dom.leaderboardCard.setAttribute("aria-busy", "true");
    this._resetLeaderboardPanelLayout();
    if (this.dom.leaderboardStatus) {
      this.dom.leaderboardStatus.classList.remove("hidden");
      this.dom.leaderboardStatus.classList.add("sr-only");
      this.dom.leaderboardStatus.textContent = "Loading leaderboard…";
    }
    if (this.dom.leaderboardLoading) this.dom.leaderboardLoading.classList.remove("hidden");
    if (this.dom.leaderboardBody) this.dom.leaderboardBody.innerHTML = "";
    if (this.dom.leaderboardOwnTable) this.dom.leaderboardOwnTable.classList.add("hidden");
    if (this.dom.leaderboardOwnBody) this.dom.leaderboardOwnBody.innerHTML = "";
    this._setLeaderboardRankHint("");
  };



  /**
   * @param {{ id: string, name: string, score: number, uid: string }} row
   * @param {string | null} currentUid
   * @returns {string}
   */
  Game.prototype._formatLeaderboardPlayerName = function (row, currentUid) {
    var name = this._escapeLeaderboardText(row.name);
    if (currentUid && row.uid === currentUid) {
      name += " <span class=\"leaderboard-you-tag\">(you)</span>";
    }
    return name;
  };

  /**
   * Near-miss line under the loss-screen leaderboard ("N points to reach #K").
   * @param {Array<{ score: number, uid: string }>} rows
   * @param {{ score: number } | null} unrankedOwnRow
   * @param {string | null} currentUid
   * @returns {string}
   */
  Game.prototype._getLeaderboardRankHint = function (rows, unrankedOwnRow, currentUid) {
    if (!currentUid || !rows || !rows.length) return "";
    var ownIndex = -1;
    for (var i = 0; i < rows.length; i++) {
      if (rows[i] && rows[i].uid === currentUid) { ownIndex = i; break; }
    }
    var formatGap = function (gap, self) {
      return self.formatScore(gap) + (gap === 1 ? " point" : " points");
    };
    if (ownIndex === 0) return "You hold the top spot. Defend it!";
    if (ownIndex > 0) {
      var gapUp = Math.max(1, Math.floor(Number(rows[ownIndex - 1].score) || 0) - Math.floor(Number(rows[ownIndex].score) || 0) + 1);
      return formatGap(gapUp, this) + " to reach #" + ownIndex + "!";
    }
    if (unrankedOwnRow) {
      var lastRow = rows[rows.length - 1];
      var gapIn = Math.max(1, Math.floor(Number(lastRow.score) || 0) - Math.floor(Number(unrankedOwnRow.score) || 0) + 1);
      return formatGap(gapIn, this) + " to crack the Top 10!";
    }
    return "";
  };

  /**
   * @param {string} hint empty string hides the line
   */
  Game.prototype._setLeaderboardRankHint = function (hint) {
    var el = this.dom && this.dom.leaderboardRankHint;
    if (!el) return;
    if (hint) {
      el.textContent = hint;
      el.classList.remove("hidden");
    } else {
      el.textContent = "";
      el.classList.add("hidden");
    }
  };

  /**
   * @param {{ rows: Array<{ id: string, name: string, score: number, uid: string }>, unrankedOwnRow: { id: string, name: string, score: number, uid: string } | null }} view
   * @param {string | null} currentUid
   * @param {string | null} [currentEntryId]
   */
  Game.prototype._renderLeaderboardCard = function (view, currentUid) {
    if (!this.dom.leaderboardCard) return;
    var rows = view && Array.isArray(view.rows) ? view.rows : [];
    var unrankedOwnRow = view && view.unrankedOwnRow ? view.unrankedOwnRow : null;
    if (!rows.length && !unrankedOwnRow) {
      this._hideLeaderboardCard();
      return;
    }

    this.dom.leaderboardCard.classList.remove("hidden");
    var loadingHeight = this.dom.leaderboardPanel ? this.dom.leaderboardPanel.offsetHeight : 0;

    if (this.dom.leaderboardBody) {
      var html = "";
      for (var i = 0; i < rows.length; i++) {
        var row = rows[i];
        var rowClass = "leaderboard-row";
        if (currentUid && row.uid === currentUid) rowClass += " leaderboard-row--own";
        html += "<tr class=\"" + rowClass + "\">"
          + "<td class=\"leaderboard-rank-cell\">#" + String(i + 1) + "</td>"
          + "<td class=\"leaderboard-name-cell\">" + this._formatLeaderboardPlayerName(row, currentUid) + "</td>"
          + "<td class=\"leaderboard-score-cell\">" + this.formatScore(row.score) + "</td>"
          + "</tr>";
      }
      this.dom.leaderboardBody.innerHTML = html;
    }

    if (this.dom.leaderboardOwnTable && this.dom.leaderboardOwnBody) {
      if (unrankedOwnRow) {
        this.dom.leaderboardOwnTable.classList.remove("hidden");
        this.dom.leaderboardOwnBody.innerHTML =
          "<tr class=\"leaderboard-row leaderboard-row--own\">"
          + "<td class=\"leaderboard-rank-cell\"></td>"
          + "<td class=\"leaderboard-name-cell\">" + this._formatLeaderboardPlayerName(unrankedOwnRow, currentUid) + "</td>"
          + "<td class=\"leaderboard-score-cell\">" + this.formatScore(unrankedOwnRow.score) + "</td>"
          + "</tr>";
      } else {
        this.dom.leaderboardOwnTable.classList.add("hidden");
        this.dom.leaderboardOwnBody.innerHTML = "";
      }
    }

    this._setLeaderboardRankHint(this._getLeaderboardRankHint(rows, unrankedOwnRow, currentUid));
    this._clearLeaderboardLoadingState();
    this._animateLeaderboardPanelHeight(loadingHeight);
  };

  Game.prototype._escapeLeaderboardText = function (value) {
    var text = typeof value === "string" ? value : String(value == null ? "" : value);
    return text
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  };

  Game.prototype._updateLossLeaderboard = function () {
    var client = this.leaderboardClient;
    if (this.testFlaskMode) {
      this._hideLeaderboardCard();
      this._hideGameOverScoreCard();
      return;
    }
    // GameDistribution: tracking or third-party opt-out keeps the score on device.
    if (
      GdBridge &&
      typeof GdBridge.isEnabled === "function" &&
      GdBridge.isEnabled() &&
      typeof GdBridge.canUseLeaderboard === "function" &&
      !GdBridge.canUseLeaderboard()
    ) {
      this._hideLeaderboardCard();
      this._showLocalGameOverScoreCard();
      return;
    }
    // YouTube / Poki: no Firebase board — show local score / high score (or New high score).
    if (usesLocalBestScoreCard()) {
      this._hideLeaderboardCard();
      this._showLocalGameOverScoreCard();
      var ytRunScore = Math.max(0, Math.floor(Number(this.currentScore) || 0));
      var ytHighScore = Math.max(ytRunScore, Math.max(0, Math.floor(Number(this.bestScore) || 0)));
      if (ytHighScore > 0) {
        YtBridge.sendEngagementScore(ytHighScore).catch(function (err) {
          console.warn("[game] YT engagement sendScore:", err);
        });
      }
      return;
    }
    this._hideGameOverScoreCard();
    if (!client) {
      this._hideLeaderboardCard();
      return;
    }
    if (!this.dom.leaderboardCard) return;

    var self = this;
    this._showLeaderboardLoading();

    (async function () {
      try {
        var playerId = client.getPlayerId
          ? client.getPlayerId()
          : (client.ensureAuth ? await client.ensureAuth() : null);
        var displayName = client.getDisplayName ? client.getDisplayName() : "Scientist";
        var runIndex = self.playerStats && self.playerStats.runIndex != null
          ? Math.max(0, Math.floor(Number(self.playerStats.runIndex) || 0))
          : 0;
        var runScore = Math.max(0, Math.floor(Number(self.currentScore) || 0));
        // Board stores best-per-player; display/hint use high score, never a weaker run.
        var highScore = Math.max(runScore, Math.max(0, Math.floor(Number(self.bestScore) || 0)));
        var ownEntry = null;
        if (playerId && highScore > 0) {
          ownEntry = {
            id: playerId,
            name: displayName,
            score: highScore,
            uid: playerId,
            createdAt: Date.now(),
          };
        }
        var topRows = [];
        var haveServerRows = false;
        if (playerId && runScore > 0) {
          if (client.submitScore) {
            try {
              var submitted = await client.submitScore({
                name: displayName,
                score: runScore,
                runIndex: runIndex,
                playerId: playerId,
              });
              if (submitted && Array.isArray(submitted.rows)) {
                topRows = submitted.rows;
                haveServerRows = true;
              }
            } catch (err) {
              console.warn("[game] leaderboard submit:", err);
            }
          }
          if (Y8Bridge.isEnabled()) {
            Y8Bridge.submitLeaderboardScore({
              points: runScore,
              playerName: displayName,
            }).catch(function (err) {
              console.warn("[game] Y8 leaderboard submit:", err);
            });
          }
        }

        if (!haveServerRows && client.fetchTopScores) {
          topRows = await client.fetchTopScores(10);
        }
        var merged = client.mergeOwnEntry
          ? client.mergeOwnEntry(topRows, ownEntry, 10)
          : { rows: topRows, unrankedOwnRow: null };
        self._renderLeaderboardCard(merged, playerId);
      } catch (err) {
        console.warn("[game] leaderboard update failed:", err);
        self._hideLeaderboardCard();
      }
    })();
  };

  Game.prototype._resetOverflowState = function () {
    if (!this.overflow) this.overflow = createOverflowState();
    else resetOverflowPlayerAction(this.overflow);
  };

  Game.prototype._resetOverflowAfterPlayerAction = function () {
    this._resetOverflowState();
  };

  Game.prototype._isMergeCompletedEmpty = function () {
    if (!this._mergeStartedEmpty) return false;
    var sys = this.getFlaskSystem();
    var bodyCount = sys && typeof sys.getBodyCount === "function" ? sys.getBodyCount() : 0;
    if (bodyCount > 0) return false;
    if (this.dropCurrent) return false;
    if (this.dropQueue && this.dropQueue.length > 0) return false;
    return true;
  };

  Game.prototype._tickOverflow = function (sys, deltaMs) {
    // Suspended during Drop all: bodies land faster than their 2 s spawn immunity expires,
    // so a long pour could otherwise self-trigger alert with no way for the player to act.
    if (this._dropAllActive) return false;
    if (!this.overflow) this.overflow = createOverflowState();
    var prevOverflowState = this.overflow.state;
    var result = tickOverflowState(this.overflow, sys, deltaMs, {
      disabled: !!this.testFlaskMode,
    });
    this.overflow = result.state;
    if (prevOverflowState !== "alert" && this.overflow.state === "alert") {
      this._trackAnalytics(AnalyticsEvents.MERGE_ALERT);
    }
    this._syncContinueBtnState();
    if (result.gameOver) {
      this._clearProgressCheckpoint();
      if (this.canOfferRevive()) {
        this._enterReviveOffer("merge");
      } else {
        this.showGameOver("overflow");
      }
      return true;
    }
    return false;
  };

  Game.prototype.requestPlayAgain = function () {
    var self = this;
    if (this.retention && typeof this.retention.requestPlayAgain === "function") {
      this.retention.requestPlayAgain(function () {
        self.onPlayAgain();
      });
      return;
    }
    this.onPlayAgain();
  };

  Game.prototype.onPlayAgain = function () {
    if (this._devPreviewLoss) {
      this._devPreviewLoss = false;
      if (this.dom && this.dom.gameOverOverlay) this.dom.gameOverOverlay.classList.add("hidden");
      this._hideLeaderboardCard();
      return;
    }
    this._trackAnalytics(AnalyticsEvents.PLAY_AGAIN);
    this._maybeRecordRunStartForNewRun();
    if (this.dom.gameOverOverlay) this.dom.gameOverOverlay.classList.add("hidden");
    this._resetCurrentGameTierForAnalytics();
    this.resetProgressState({ preserveEvolutionUnlocks: true });
    this.stateMachine = new GameStateMachine("start");
    this.savedFlaskHp = this.flaskMaxHp;
    this.boostBar = [];
    this.revivesUsedThisRun = 0;
    this.isReviveOfferActive = false;
    this.reviveOfferStage = null;
    this.reviveOfferMode = null;
    this._lastReviveSource = null;
    this._reviveAdPending = false;
    this._hideLiveReviveOverlay();

    this._fallSimFrozen = false;
    this._clearPlayerPauseState();
    this._dialogPauseDepth = 0;
    this.isDialogPaused = false;
    this.setEvolutionDockVisible(true);
    this._rollSkyDecorSeed();
    this._analyticsRunStartSource = "play_again";
    this.startFromMenu();
  };

  Game.prototype.onRunFlaskShattered = function () {
    this.isRunFlaskShattered = true;
    this.savedFlaskHp = 0;
    this._clearProgressCheckpoint();
  };

  Game.prototype._isDevMode = function () {
    return typeof import.meta !== "undefined" && import.meta.env && import.meta.env.DEV;
  };

  Game.prototype._getActiveRunStageForDev = function () {
    if (!this.stateMachine || !this.stateMachine.is) return null;
    if (this.stateMachine.is("shake")) return "merge";
    if (this.stateMachine.is("fall")) return "fall";
    return null;
  };

  Game.prototype._clearDevPreviewRevive = function (stage) {
    this._devPreviewRevive = false;
    this._hideLiveReviveOverlay();
    this.isReviveOfferActive = false;
    this.reviveOfferStage = null;
    if (stage === "fall") {
      this.isRunFlaskShattered = false;
      this._reconcileFallSimPause();
    } else if (stage === "merge") {
      this._reconcileShakeSimPause();
    }
    this._syncPlatformGameplay();
  };

  Game.prototype.devPreviewReviveOverlay = function () {
    if (!this._isDevMode()) return;
    var stage = this._getActiveRunStageForDev();
    if (!stage) return;
    this._devPreviewRevive = true;
    this._enterReviveOffer(stage);
  };

  Game.prototype.devPreviewLossScreen = function () {
    if (!this._isDevMode()) return;
    var stage = this._getActiveRunStageForDev();
    if (!stage) return;
    var reason = stage === "merge" ? "overflow" : "flask_broken";
    this._devPreviewLoss = true;
    this._configureGameOverOverlay(reason);
    if (this.dom && this.dom.gameOverOverlay) this.dom.gameOverOverlay.classList.remove("hidden");
    this._updateLossLeaderboard();
  };

  Game.prototype.devPreviewVictoryPopup = function () {
    if (!this._isDevMode()) return;
    if (!(this.stateMachine && this.stateMachine.is("shake"))) return;
    if (!this.dom || !this.dom.victoryOverlay) return;
    this._devPreviewVictory = true;
    this._victoryFirstUnlockPending = !(this.playerStats && this.playerStats.victoryFeedbackSubmitted);
    this._openVictoryPopup();
  };

  /**
   * Highest unlocked evolution-chain index (for dev Discover preview).
   * @returns {number}
   */
  Game.prototype._getHighestUnlockedTierIndex = function () {
    var chain = (typeof C.getActiveEvolutionChain === "function")
      ? C.getActiveEvolutionChain()
      : (C.EVOLUTION_CHAIN || []);
    var best = -1;
    for (var i = 0; i < chain.length; i++) {
      var id = chain[i] && chain[i].id != null ? chain[i].id : i;
      if (this.unlockedTiers && this.unlockedTiers[id]) best = i;
    }
    return best;
  };

  /** Dev-only: replay discovery ceremony for the last already-unlocked tier. */
  Game.prototype.devPreviewDiscoveryReveal = function () {
    if (!this._isDevMode()) return;
    if (!(this.stateMachine && this.stateMachine.is("shake"))) return;
    if (!(this.dom && this.dom.secretRevealOverlay && this.dom.secretRevealPopoutMount)) return;

    var chain = (typeof C.getActiveEvolutionChain === "function")
      ? C.getActiveEvolutionChain()
      : (C.EVOLUTION_CHAIN || []);
    var tierIndex = this._getHighestUnlockedTierIndex();
    if (tierIndex < 0 || !chain[tierIndex]) return;
    var tierId = chain[tierIndex].id != null ? chain[tierIndex].id : tierIndex;

    if (this._secretRevealHandle && typeof this._secretRevealHandle.abort === "function") {
      this._secretRevealHandle.abort();
    }
    if (this._secretRevealSettleTimer) {
      clearTimeout(this._secretRevealSettleTimer);
      this._secretRevealSettleTimer = null;
    }
    this._discoveryRevealQueue.length = 0;
    this._secretRevealActive = false;
    this._evoRevealHold = true;
    this._queueDiscoveryReveal(chain, tierIndex, tierId);
  };

  Game.prototype._redrawAfterDevSkyToggle = function () {
    if (!this.ctx) return;
    var now = typeof performance !== "undefined" ? performance.now() : Date.now();
    if (this.stateMachine && this.stateMachine.is("shake")) {
      this.drawShake();
    } else if (this.stateMachine && this.stateMachine.is("fall") && this.runPhase
      && typeof this.runPhase._render === "function") {
      this.runPhase._render(now);
    }
  };

  Game.prototype.toggleDevSkyBackground = function () {
    if (!this._isDevMode()) return;
    runtimeFlags.drawSkySeaBackground = !runtimeFlags.drawSkySeaBackground;
    this._skyCacheKey = null;
    this.syncDevPerfDialog();
    this._redrawAfterDevSkyToggle();
  };

  Game.prototype.toggleDevSkyStars = function () {
    if (!this._isDevMode()) return;
    runtimeFlags.drawSkyStars = !runtimeFlags.drawSkyStars;
    this._skyCacheKey = null;
    this.syncDevPerfDialog();
    this._redrawAfterDevSkyToggle();
  };

  Game.prototype.toggleDevSkyDynamic = function () {
    if (!this._isDevMode()) return;
    if (!this._skyBackgroundPrefs) {
      this._skyBackgroundPrefs = loadSkyBackgroundPrefs(
        this._getProgressStorage()
      );
    }
    var pref = normalizeSkyBackgroundPreference(this._skyBackgroundPrefs.preference);
    var next = pref === C.SKY_BACKGROUND_MODES.AUTO
      ? C.SKY_BACKGROUND_MODES.DYNAMIC
      : (pref === C.SKY_BACKGROUND_MODES.DYNAMIC
        ? C.SKY_BACKGROUND_MODES.STATIC
        : C.SKY_BACKGROUND_MODES.AUTO);
    this._skyBackgroundPrefs.preference = next;
    saveSkyBackgroundPrefs(
      this._skyBackgroundPrefs,
      this._getProgressStorage()
    );
    this._applySkyBackgroundPrefsToConfig();
    this._syncSkyBackgroundEffective();
    this._syncSkyBackgroundAnalytics();
    this.syncDevPerfDialog();
    this._redrawAfterDevSkyToggle();
  };

  Game.prototype.toggleDevSounds = function () {
    if (!this._isDevMode() || !this.soundManager) return;
    this._devPerfSoundsMuted = !this._devPerfSoundsMuted;
    if (typeof this.soundManager.setMuted === "function") {
      this.soundManager.setMuted(this._devPerfSoundsMuted);
    }
    this.syncDevPerfDialog();
  };

  Game.prototype.toggleDevParticles = function () {
    if (!this._isDevMode()) return;
    runtimeFlags.drawParticles = runtimeFlags.drawParticles === false;
    this.syncDevPerfDialog();
    this._redrawAfterDevSkyToggle();
  };

  Game.prototype.toggleDevScorePopups = function () {
    if (!this._isDevMode()) return;
    runtimeFlags.drawScorePopups = runtimeFlags.drawScorePopups === false;
    if (runtimeFlags.drawScorePopups === false) {
      this.scorePopups = [];
    }
    this.syncDevPerfDialog();
    this._redrawAfterDevSkyToggle();
  };

  Game.prototype.openDevPerfDialog = function () {
    if (!this._isDevMode() || !this.dom) return;
    setDevPerfDialogOpen(this.dom.devPerfDialog, true);
    this.syncDevPerfDialog();
  };

  Game.prototype.closeDevPerfDialog = function () {
    if (!this._isDevMode() || !this.dom) return;
    setDevPerfDialogOpen(this.dom.devPerfDialog, false);
  };

  Game.prototype.syncDevPerfDialog = function () {
    if (!this._isDevMode()) return;
    syncDevPerfDialogUi(this);
  };

  Game.prototype.cycleDevLayoutPreference = function () {
    if (!this._isDevMode()) return;
    var cycle = ["auto", "vertical", "horizontal"];
    var pref = normalizeLayoutPreference(this.layoutModePreference || readLayoutModePreference());
    var idx = cycle.indexOf(pref);
    var next = cycle[(idx < 0 ? 0 : idx + 1) % cycle.length];
    try {
      writeLayoutModePreference(next, this._getProgressStorage());
    } catch (e) { /* ignore */ }
    this.setLayoutModePreference(next);
  };

  Game.prototype._applyDevLayoutBoundsClass = function () {
    if (typeof document === "undefined") return;
    var app = document.getElementById("app");
    if (!app || !app.classList) return;
    var on = this._isDevMode() && !!runtimeFlags.showLayoutSectionBorders;
    app.classList.toggle("dev-layout-bounds", on);
  };

  Game.prototype.toggleDevLayoutBounds = function () {
    if (!this._isDevMode()) return;
    runtimeFlags.showLayoutSectionBorders = !runtimeFlags.showLayoutSectionBorders;
    this._applyDevLayoutBoundsClass();
    this.syncDevPerfDialog();
    this._redrawAfterDevSkyToggle();
  };

  Game.prototype.toggleDevFallColliders = function () {
    if (!this._isDevMode()) return;
    runtimeFlags.showFallColliderDebug = !runtimeFlags.showFallColliderDebug;
    this.syncDevPerfDialog();
    this._redrawAfterDevSkyToggle();
  };

  /** Dev Bounds toggle — layout section borders + flask/jar physics + overflow lines. */
  Game.prototype._showLayoutBoundsDebug = function () {
    return !!this._isDevMode() && !!runtimeFlags.showLayoutSectionBorders;
  };

  /**
   * @param {number} now
   * @param {number} [cpuMs] main-thread time inside this frame's update + draw
   * @param {{ sky?: number, phys?: number, draw?: number }} [phases]
   */
  Game.prototype._updateDevFps = function (now, cpuMs, phases) {
    if (!this._isDevMode() || !this.dom) return;
    if (this.dom.devFpsOverlay) {
      if (!this._devFpsTracker) this._devFpsTracker = createDevFpsTracker();
      var sample = this._devFpsTracker.tick(now, cpuMs, phases);
      if (sample && this.dom.devFpsOverlay) {
        this.dom.devFpsOverlay.textContent = sample.text;
      }
    }
    this._updateDevStats(now);
  };

  Game.prototype._updateDevStats = function (now) {
    if (!this._isDevMode() || !this.dom || !this.dom.devStatsOverlay) return;
    if (!isFinite(now)) return;
    if (this._devStatsLastAt != null && now - this._devStatsLastAt < 250) return;
    this._devStatsLastAt = now;
    var runIndex = this.playerStats && this.playerStats.runIndex != null
      ? Math.max(0, Math.floor(Number(this.playerStats.runIndex) || 0))
      : 0;
    var text = formatDevStatsOverlayText({
      playTimeMs: this._getRunPlayTimeMs(now),
      runIndex: runIndex,
    });
    if (this.dom.devStatsOverlay.textContent !== text) {
      this.dom.devStatsOverlay.textContent = text;
    }
  };

  Game.prototype._devNowMs = function () {
    return typeof performance !== "undefined" ? performance.now() : Date.now();
  };

  Game.prototype.syncDevTestBar = function () {
    if (!this._isDevMode() || !this.dom) return;
    var active = !!this._getActiveRunStageForDev();
    this.syncDevPerfDialog();
    if (this.dom.devTestReviveBtn) this.dom.devTestReviveBtn.disabled = !active;
    if (this.dom.devTestLossBtn) this.dom.devTestLossBtn.disabled = !active;
    var shakeActive = !!(active && this.stateMachine && this.stateMachine.is("shake"));
    if (this.dom.devTestWinBtn) {
      this.dom.devTestWinBtn.disabled = !shakeActive;
    }
    if (this.dom.devTestDiscoverBtn) {
      this.dom.devTestDiscoverBtn.disabled = !shakeActive;
    }
    this._applyDevLayoutBoundsClass();
  };

  /** @returns {boolean} true where a rewarded revive ad can actually be shown. */
  Game.prototype._hasRewardedAdsForRevive = function () {
    return canOfferRewardedAds();
  };

  /** @returns {number} */
  Game.prototype._getFreeRevivesPerRun = function () {
    var n = (C.RUN_CONFIG && C.RUN_CONFIG.REVIVE_FREE_PER_RUN != null)
      ? Number(C.RUN_CONFIG.REVIVE_FREE_PER_RUN)
      : 1;
    return isFinite(n) && n >= 0 ? Math.floor(n) : 1;
  };

  /** @returns {number} */
  Game.prototype._getAdRevivesPerRun = function () {
    var n = (C.RUN_CONFIG && C.RUN_CONFIG.REVIVE_AD_PER_RUN != null)
      ? Number(C.RUN_CONFIG.REVIVE_AD_PER_RUN)
      : 1;
    return isFinite(n) && n >= 0 ? Math.floor(n) : 1;
  };

  /**
   * Which revive the player gets next, cheapest first: the free one, then the ad one.
   * Builds without rewarded ads only ever see the free one.
   * @returns {"free" | "ad" | null} null when the run's revive budget is spent.
   */
  Game.prototype.getReviveMode = function () {
    var used = Math.max(0, Math.floor(Number(this.revivesUsedThisRun) || 0));
    if (used < this._getFreeRevivesPerRun()) return "free";
    if (!this._hasRewardedAdsForRevive()) return null;
    if (used < this._getFreeRevivesPerRun() + this._getAdRevivesPerRun()) return "ad";
    return null;
  };

  Game.prototype.canOfferRevive = function () {
    return this.getReviveMode() !== null;
  };

  Game.prototype.canOfferFallRevive = function () {
    return this.canOfferRevive();
  };

  Game.prototype._setLiveReviveTitle = function (stage) {
    var titleEl = this.dom && this.dom.liveReviveTitle;
    if (!titleEl) return;
    titleEl.textContent = stage === "merge" ? "Overflow!" : "Flask shattered!";
  };

  Game.prototype._getOverlayPanel = function (el) {
    if (!el || typeof el.querySelector !== "function") return null;
    return el.querySelector(".retention-spotlight-overlay__panel")
      || el.querySelector(".fall-volume-overlay__panel")
      || el.querySelector(".evolution-dock-prompt__panel");
  };

  Game.prototype._isPanelBounceOverlay = function (el) {
    return !!(el && el.classList && typeof el.classList.contains === "function"
      && el.classList.contains("overlay--panel-bounce"));
  };

  Game.prototype._showOverlayWithFade = function (el) {
    if (!el || !el.classList) return;
    if (typeof el.addEventListener !== "function") {
      el.classList.remove("hidden", "transition-out", "transition-in");
      return;
    }
    var panel = this._getOverlayPanel(el);
    var panelBounce = this._isPanelBounceOverlay(el);
    el.classList.remove("hidden", "transition-out");
    if (panel) {
      panel.classList.remove("overlay-panel--transition-out");
    }
    if (typeof el.offsetWidth === "number") {
      void el.offsetWidth;
    }
    el.classList.add("transition-in");
    if (panelBounce && panel) {
      panel.classList.add("overlay-panel--transition-in");
    }
    var done = false;
    var backdropDone = !panelBounce;
    var panelDone = !panelBounce;
    var finish = function () {
      if (done) return;
      done = true;
      el.classList.remove("transition-in");
      if (panel) {
        panel.classList.remove("overlay-panel--transition-in");
      }
      if (typeof el.removeEventListener === "function") {
        el.removeEventListener("animationend", onEnd);
        if (panel && typeof panel.removeEventListener === "function") {
          panel.removeEventListener("animationend", onEnd);
        }
      }
    };
    var onEnd = function (e) {
      if (!e || !e.animationName) return;
      if (panelBounce) {
        if (e.target === el && e.animationName === "overlay-backdrop-fade-in") {
          backdropDone = true;
        }
        if (panel && e.target === panel && e.animationName === "overlay-panel-bounce-in") {
          panelDone = true;
        }
        if (backdropDone && panelDone) finish();
        return;
      }
      if (e.target !== el) return;
      if (e.animationName === "overlay-fade-in") finish();
    };
    if (typeof el.addEventListener === "function") {
      el.addEventListener("animationend", onEnd);
      if (panel && typeof panel.addEventListener === "function") {
        panel.addEventListener("animationend", onEnd);
      }
    }
    setTimeout(finish, panelBounce ? 400 : 550);
  };

  Game.prototype._hideOverlayWithFade = function (el, callback) {
    if (!el || !el.classList) {
      if (typeof callback === "function") callback();
      return;
    }
    if (typeof el.classList.contains === "function" && el.classList.contains("hidden")) {
      if (typeof callback === "function") callback();
      return;
    }
    if (typeof el.addEventListener !== "function") {
      el.classList.remove("transition-in", "transition-out");
      el.classList.add("hidden");
      if (typeof callback === "function") callback();
      return;
    }
    var panel = this._getOverlayPanel(el);
    var panelBounce = this._isPanelBounceOverlay(el);
    el.classList.remove("transition-in");
    if (panel) {
      panel.classList.remove("overlay-panel--transition-in");
    }
    if (typeof el.offsetWidth === "number") {
      void el.offsetWidth;
    }
    el.classList.add("transition-out");
    if (panelBounce && panel) {
      panel.classList.add("overlay-panel--transition-out");
    }
    var done = false;
    var backdropDone = !panelBounce;
    var panelDone = !panelBounce;
    var finish = function () {
      if (done) return;
      done = true;
      el.classList.remove("transition-out");
      if (panel) {
        panel.classList.remove("overlay-panel--transition-out");
      }
      el.classList.add("hidden");
      if (typeof el.removeEventListener === "function") {
        el.removeEventListener("animationend", onEnd);
        if (panel && typeof panel.removeEventListener === "function") {
          panel.removeEventListener("animationend", onEnd);
        }
      }
      if (typeof callback === "function") callback();
    };
    var onEnd = function (e) {
      if (!e || !e.animationName) return;
      if (panelBounce) {
        if (e.target === el && e.animationName === "overlay-backdrop-fade-out") {
          backdropDone = true;
        }
        if (panel && e.target === panel && e.animationName === "overlay-panel-bounce-out") {
          panelDone = true;
        }
        if (backdropDone && panelDone) finish();
        return;
      }
      if (e.target !== el) return;
      if (e.animationName === "overlay-fade-out") finish();
    };
    if (typeof el.addEventListener === "function") {
      el.addEventListener("animationend", onEnd);
      if (panel && typeof panel.addEventListener === "function") {
        panel.addEventListener("animationend", onEnd);
      }
    }
    setTimeout(finish, panelBounce ? 320 : 450);
  };

  /**
   * Paint the single revive button for the offer being made. Both modes read "Revive";
   * the free one is green with no ad icon (mirroring the wheel's Free spin), and the ad
   * offer keeps its existing look untouched.
   * @param {"free" | "ad" | null} mode
   */
  Game.prototype._syncLiveReviveBtnMode = function (mode) {
    var btn = this.dom && this.dom.liveReviveBtn;
    if (!btn) return;
    var isFree = mode === "free";
    if (btn.classList && btn.classList.toggle) {
      btn.classList.toggle("live-revive-overlay__revive-btn--free", isFree);
    }
    var icon = btn.querySelector && btn.querySelector(".live-revive-overlay__revive-icon");
    if (icon && icon.classList) icon.classList.toggle("hidden", isFree);
  };

  Game.prototype._showLiveReviveOverlay = function () {
    this._showOverlayWithFade(this.dom && this.dom.liveReviveOverlay);
  };

  Game.prototype._hideLiveReviveOverlay = function (callback) {
    this._hideOverlayWithFade(this.dom && this.dom.liveReviveOverlay, callback);
  };

  Game.prototype._enterReviveOffer = function (stage) {
    if (this.playerStats) {
      this.playerStats = markPendingRunIndexBump(this.playerStats);
      this._savePlayerStats();
    }
    this.isReviveOfferActive = true;
    this.reviveOfferStage = stage;
    // Lock the mode for this offer so a mid-offer state change cannot swap the button.
    this.reviveOfferMode = this.getReviveMode() || "ad";
    this._setLiveReviveTitle(stage);
    this._syncLiveReviveBtnMode(this.reviveOfferMode);
    if (stage === "fall") {
      this.isRunFlaskShattered = true;
      this._reconcileFallSimPause();
    } else if (stage === "merge") {
      this._reconcileShakeSimPause();
    }
    this._showLiveReviveOverlay();
    this._trackAnalytics(AnalyticsEvents.REVIVE_OFFERED, { revive_source: this.reviveOfferMode });
    this._syncPlatformGameplay();
  };

  Game.prototype._onFallReviveOffer = function () {
    this._enterReviveOffer("fall");
  };

  Game.prototype._onFallReviveCompleted = function () {
    if (this.playerStats) {
      this.playerStats = clearPendingRunIndexBump(this.playerStats);
      this._savePlayerStats();
    }
    this.revivesUsedThisRun += 1;
    this.isRunFlaskShattered = false;
    var now = typeof performance !== "undefined" ? performance.now() : Date.now();
    if (this.runPhase && this.runPhase.player) {
      this._lastHudHp = Math.max(0, Math.floor(Number(this.runPhase.player.hp) || 0));
      this.savedFlaskHp = Math.max(0, Math.min(this.flaskMaxHp, Number(this.runPhase.player.hp) || 0));
    }
    this.revealHeartsTransient();
    this.syncFallHpRow(now);
    this.saveProgressState("fall");
    this._trackAnalytics(AnalyticsEvents.REVIVE_COMPLETED, { revive_source: this._lastReviveSource || "ad" });
  };

  Game.prototype._onMergeReviveCompleted = function () {
    if (this.playerStats) {
      this.playerStats = clearPendingRunIndexBump(this.playerStats);
      this._savePlayerStats();
    }
    this.revivesUsedThisRun += 1;
    this._resetOverflowState();
    this._syncContinueBtnState();
    if (EventBus && typeof EventBus.emit === "function") {
      EventBus.emit("merge:revive", {});
    }
    this.saveProgressState("shake");
    this._trackAnalytics(AnalyticsEvents.REVIVE_COMPLETED, { revive_source: this._lastReviveSource || "ad" });
  };

  Game.prototype.declineRevive = function (reason) {
    if (!this.isReviveOfferActive) return;
    var stage = this.reviveOfferStage;
    if (this._devPreviewRevive) {
      this._clearDevPreviewRevive(stage);
      return;
    }
    var mode = this.reviveOfferMode || "ad";
    this._hideLiveReviveOverlay();
    this.isReviveOfferActive = false;
    this.reviveOfferStage = null;
    this.reviveOfferMode = null;
    if (stage === "fall") {
      this._reconcileFallSimPause();
      this._trackAnalytics(AnalyticsEvents.REVIVE_SKIPPED, { revive_source: mode });
      if (this.runPhase && typeof this.runPhase.declineRevive === "function") {
        var now = typeof performance !== "undefined" ? performance.now() : Date.now();
        this.runPhase.declineRevive(now);
      }
      this._syncPlatformGameplay();
      return;
    }
    if (stage === "merge") {
      this._reconcileShakeSimPause();
      this._trackAnalytics(AnalyticsEvents.REVIVE_SKIPPED, { revive_source: mode });
      this.showGameOver("overflow");
    }
  };

  Game.prototype.declineFallRevive = function (reason) {
    this.declineRevive(reason);
  };

  /** Free revive — same grant as the rewarded one, minus the ad. */
  Game.prototype._grantFreeRevive = function () {
    var stage = this.reviveOfferStage;
    this._devPreviewRevive = false;
    this.isReviveOfferActive = false;
    this.reviveOfferStage = null;
    // Completion handlers fire after this returns, so stash the source for analytics.
    this._lastReviveSource = "free";
    this.reviveOfferMode = null;
    this._hideLiveReviveOverlay();
    if (stage === "fall") {
      this._reconcileFallSimPause();
      this._syncPlatformGameplay();
      var now = typeof performance !== "undefined" ? performance.now() : Date.now();
      if (this.runPhase && typeof this.runPhase.applyRevive === "function") {
        this.runPhase.applyRevive(now);
      }
      return;
    }
    if (stage === "merge") {
      this._reconcileShakeSimPause();
      this._onMergeReviveCompleted();
      this._syncPlatformGameplay();
    }
  };

  Game.prototype.requestRevive = function () {
    if (!this.isReviveOfferActive || this._reviveAdPending) return;
    var stage = this.reviveOfferStage;
    if (stage === "fall" && !this.runPhase) return;
    // Free offer: grant straight away, never touch the ad bridge.
    if (this.reviveOfferMode === "free") {
      this._grantFreeRevive();
      return;
    }
    var self = this;
    this._reviveAdPending = true;
    requestRewardedAd({
      name: REWARD_AD_NAME_REVIVE,
      game: self,
      onStarted: function () {
        self.setAdPaused(true);
      },
      onFinished: function () {
        self.setAdPaused(false);
      },
      onError: function () {
        self.setAdPaused(false);
      },
    }).then(function (result) {
      self._reviveAdPending = false;
      if (!self.isReviveOfferActive) return;
      if (isRewardedAdGranted(result)) {
        self._devPreviewRevive = false;
        self.isReviveOfferActive = false;
        self.reviveOfferStage = null;
        self._lastReviveSource = "ad";
        self.reviveOfferMode = null;
        self._hideLiveReviveOverlay();
        if (stage === "fall") {
          self._reconcileFallSimPause();
          self._syncPlatformGameplay();
          var now = typeof performance !== "undefined" ? performance.now() : Date.now();
          if (self.runPhase && typeof self.runPhase.applyRevive === "function") {
            self.runPhase.applyRevive(now);
          }
          return;
        }
        if (stage === "merge") {
          self._reconcileShakeSimPause();
          self._onMergeReviveCompleted();
          self._syncPlatformGameplay();
        }
        return;
      }
      // Ad failed or dismissed — keep revive overlay open for retry or decline.
    });
  };

  Game.prototype.requestFallRevive = function () {
    this.requestRevive();
  };

  Game.prototype.onRunComplete = function (results) {
    this.gemsCollected += results.gems || 0;
    if (results && results.playerHp != null) {
      this.savedFlaskHp = Math.max(0, Math.min(this.flaskMaxHp, Number(results.playerHp)));
    }
    this.hasCompletedARun = true;
    this.lastRunSummary = results || null;
    var stashEmpty = !(this.boostBar && this.boostBar.length > 0);
    this.analyticsStage = "merge";
    var fallCaughtGems = Math.max(0, Math.floor(Number(results && results.gems) || 0));
    var fallCaughtCreatures = Math.max(0, Math.floor(Number(results && results.growthCount) || 0));
    var fallKeyboardParam = this._getStageUsedKeyboardParam("fall");
    this._trackAnalytics(AnalyticsEvents.FALL_COMPLETED, {
      caught_gems: fallCaughtGems,
      caught_creatures: fallCaughtCreatures,
      used_keyboard: fallKeyboardParam.used_keyboard,
    });
    if (stashEmpty) {
      this._trackAnalytics(AnalyticsEvents.FALL_COMPLETED_EMPTY, {
        used_keyboard: fallKeyboardParam.used_keyboard,
      });
    }
    this.updateShakeUI();
    if (this.runPhase) this.runPhase.stop();
    this.runPhase = null;
    this._fallSimFrozen = false;
    this._clearPlayerPauseState();
    if (this.stateMachine && this.stateMachine.is && this.stateMachine.is("transition")) {
      this._hideFallPlayChromeForBridge();
    }
    var app = document.getElementById("app");
    if (app) app.classList.remove("stage-run");
    this.applyUiScaleVars();
    this.applyStageChromeLeftBounds();
    this.applyStageChromeRightBounds();
    this.saveProgressState("shake");
    // fall:complete is emitted by FallPhase.finish(); avoid duplicate
  };

  Game.prototype.hideEndOverlay = function () {
    if (!this.dom || !this.dom.endOverlay) return;
    this.dom.endOverlay.classList.remove("transition-in", "transition-out");
    this.dom.endOverlay.classList.add("hidden");
  };

  Game.prototype.endRun = function () {
    var self = this;
    var rawNow = typeof performance !== "undefined" ? performance.now() : Date.now();
    var fallEndSkyHour = (this.stateMachine && this.stateMachine.is("fall") && this.runPhase)
      ? this.getSkyHour(rawNow)
      : null;
    this._endAnalyticsPlayTime();
    this.stateMachine.transitionTo("transition");
    this.syncBackgroundMusic();
    if (this.dom.hud) this.dom.hud.classList.add("hidden");
    this.hideEndOverlay();
    this.updateEndRunSummary();
    this.updateShakeUI();
    // Merge HP hearts before the bridge fade-in (pill stays hidden until HP < max).
    this.updateLivesHud();
    this._hideFallPlayChromeForBridge();
    this._hideJarDropGuideAim();
    this._lockShakeHpRowHiddenForBridge();

    this._clearBridgeTransitionTimers();
    this._clearScoreDigitPop();

    var cfg = C.RUN_CONFIG || {};
    var dwellMs = cfg.DAY_NIGHT_TRANSITION_MS != null
      ? cfg.DAY_NIGHT_TRANSITION_MS
      : (cfg.RUN_TO_SHAKE_TRANSITION_MS || 3000);
    this._captureRunwayFlaskFadeSnapshot(rawNow);
    this._resetSkyClockMotionForBridge(rawNow);
    var clockNow = this._getSkyClockNowMs(rawNow);
    beginTransitionToShake(this.skyClock, clockNow, fallEndSkyHour);
    this.startCelestialTransition(dwellMs, { fadeStageContent: true, direction: "to_shake" });
    // Start the theme crossfade now (not at goToShake) so the stays-put chrome
    // eases day→night across the whole transition instead of snapping at the end.
    var appThemeEl = document.getElementById("app");
    if (appThemeEl) appThemeEl.classList.add("stage-theme-night");
    var bridgeFallFadeMs = cfg.RUN_BGM_FADE_OUT_ON_BRIDGE_MS != null ? cfg.RUN_BGM_FADE_OUT_ON_BRIDGE_MS : 1000;
    var bridgeShakeFadeMs = cfg.RUN_BGM_FADE_IN_ON_BRIDGE_MS != null
      ? cfg.RUN_BGM_FADE_IN_ON_BRIDGE_MS
      : Math.max(400, dwellMs - bridgeFallFadeMs);
    var sm = this.soundManager;
    if (sm) {
      if (typeof sm.fadeOutBgmScene === "function") {
        sm.fadeOutBgmScene("fall", bridgeFallFadeMs);
      }
      if (typeof sm.fadeInBgmTo === "function") {
        this._bridgeShakeFadeInTimeoutId = setTimeout(function () {
          self._bridgeShakeFadeInTimeoutId = null;
          if (!self.stateMachine.is("transition")) return;
          if (typeof sm.stopBgmTrack === "function") sm.stopBgmTrack("fall");
          sm.fadeInBgmTo("shake", bridgeShakeFadeMs);
        }, bridgeFallFadeMs);
      }
    }
    var fadeOutMs = 400;
    this._bridgeDwellDeadlineMs = rawNow + dwellMs;
    this._bridgeShakeFadeInDeadlineMs = rawNow + bridgeFallFadeMs;
    this._syncFallPauseButton();
    this.applyUiScaleVars();
    this.applyStageChromeLeftBounds();
    this.applyStageChromeRightBounds();
    this._startTransitionRedrawLoop();
    this._reconcileTransitionSimPause();
    this._syncPlatformGameplay();
    this.runToShakeTimeoutId = setTimeout(function () {
      self.runToShakeTimeoutId = null;
      if (!self.stateMachine.is("transition")) return;
      if (self._isSimPaused()) return;
      self.goToShake({ mergeStartSource: "fall" });
    }, dwellMs);
  };

  Game.prototype.applyShakeUIBounds = function (force) {
    if (!this.dom || !this.dom.shakeUI) return;
    if (!force && !this._shakeUiBoundsDirty) return;
    if (!this.layout || !this.layout.runBounds) return;
    if (this._isVerticalLayout()) {
      this.applyLayoutChromeBounds();
      this.applyPlayChromeBounds();
      this.applyShakeJarChromeBounds();
      this.applyShakeHpRowBounds();
      this.applyEvolutionRingBounds(true);
      this._shakeUiBoundsDirty = false;
      this.updateLivesHud();
      this._syncShakeHpPillState();
      this._syncShakeHintTooltip();
      this._syncHealHintTooltip();
      return;
    }
    var ui = this.dom.shakeUI;
    ui.style.left = "0";
    ui.style.top = "0";
    ui.style.width = "0";
    ui.style.height = "0";
    ui.style.pointerEvents = "none";
    ui.classList.toggle("shake-ui-landscape", !!this.layout.isLandscape);
    ui.classList.toggle("shake-ui-portrait", !this.layout.isLandscape);
    this.applyUiScaleVars();
    this.applyStageChromeLeftBounds();
    this.applyStageChromeRightBounds();
    this.applyPlayChromeBounds();
    this.applyShakeJarChromeBounds();
    this.applyShakeHpRowBounds();
    this.applyEvolutionRingBounds();
    this._shakeUiBoundsDirty = false;
    this.updateLivesHud();
    this._syncShakeHpPillState();
    this._syncShakeHintTooltip();
    this._syncHealHintTooltip();
  };

  Game.prototype._getShakeHpHeartbeatIntervalMs = function (hp, maxHp) {
    var baseMs = C.SHAKE_HP_HEARTBEAT_MS != null ? Number(C.SHAKE_HP_HEARTBEAT_MS) : 4000;
    if (!isFinite(baseMs) || baseMs <= 0) baseMs = 4000;
    var safeMax = Math.max(1, Math.floor(Number(maxHp) || 1));
    var safeHp = Math.max(0, Math.min(safeMax, Math.floor(Number(hp) || 0)));
    var missingHp = safeMax - safeHp;
    if (missingHp === 1) {
      var mult = C.SHAKE_HP_HEARTBEAT_LOW_URGENCY_MULT != null
        ? Number(C.SHAKE_HP_HEARTBEAT_LOW_URGENCY_MULT)
        : 2;
      if (!isFinite(mult) || mult < 1) mult = 2;
      return Math.round(baseMs * mult);
    }
    return baseMs;
  };

  Game.prototype._syncShakeHpPillState = function () {
    var row = this.dom && this.dom.shakeHpRow;
    var pill = this.dom && this.dom.shakeHpPill;
    if (!row) return;
    var maxHp = Math.max(1, this.flaskMaxHp);
    var hp = Math.max(0, Math.min(maxHp, Math.floor(Number(this.savedFlaskHp) || 0)));
    var needsHeal = hp < maxHp;
    var isShake = this.stateMachine && this.stateMachine.is && this.stateMachine.is("shake");
    var visible = row.classList && typeof row.classList.contains === "function"
      ? !row.classList.contains("hidden")
      : true;
    row.classList.toggle("hp-row--full", hp >= maxHp);
    if (pill) {
      var fixCost = this.constants && this.constants.FLASK_FIX_COST;
      var costUnaffordable = needsHeal && !this.isMinimizeMode
        && this.gemsCollected < fixCost;
      pill.disabled = hp >= maxHp || !!this.isMinimizeMode || costUnaffordable;
      pill.classList.toggle("paid", true);
      pill.classList.toggle("hp-pill--cost-unaffordable", costUnaffordable);
    }
    var alpha = this.shakeHpBarAlpha != null ? this.shakeHpBarAlpha : 1;
    // Damaged merge HP must stay interactive when the DOM row is visible.
    if (isShake && visible && needsHeal && alpha < 0.01) {
      alpha = 1;
      this.shakeHpBarAlpha = 1;
    }
    var bridgeOwnsOpacity = this._bridgeFadeOwnsShakeHpOpacity();
    var displayAlpha = alpha;
    if (bridgeOwnsOpacity && row.style && row.style.opacity != null && row.style.opacity !== "") {
      var bridgeAlpha = parseFloat(row.style.opacity);
      if (isFinite(bridgeAlpha)) displayAlpha = bridgeAlpha;
    }
    if (row.style) {
      if (!bridgeOwnsOpacity) {
        row.style.opacity = String(alpha);
      }
      if (typeof row.style.setProperty === "function") {
        var beatMs = this._getShakeHpHeartbeatIntervalMs(hp, maxHp);
        row.style.setProperty("--shake-hp-heartbeat-ms", beatMs + "ms");
      }
    }
    if (row.classList && typeof row.classList.toggle === "function") {
      var shouldPulse = needsHeal && visible && displayAlpha > 0.01 && !this._shakeHpFadeStart;
      row.classList.toggle("shake-hp-row--heartbeat", shouldPulse);
      if (!needsHeal && typeof row.classList.remove === "function") {
        row.classList.remove("shake-hp-row--heartbeat");
      }
    }
  };

  Game.prototype.clearShakePlayerHudLayout = function () {
    var ph = this.dom && this.dom.playerHud;
    if (!ph || !ph.style) return;
    ph.style.left = "";
    ph.style.width = "";
    ph.style.top = "";
    ph.style.height = "";
    ph.style.padding = "";
  };

  /**
   * Stage II only: pin #playerHud above the score card, spanning `.shake-controls`
   * so lives/gems sit symmetrically inset from the button container edges.
   */
  Game.prototype.syncShakePlayerHudLayout = function () {
    if (!this.stateMachine || !this.stateMachine.is || !this.stateMachine.is("shake")) return;
    var ph = this.dom && this.dom.playerHud;
    var appEl = document.getElementById("app");
    var scoreEl = (this.dom && this.dom.shakeScorePanel) || document.getElementById("shakeScorePanel");
    var controlsEl = (this.dom && this.dom.shakeUI)
      ? this.dom.shakeUI.querySelector(".shake-controls")
      : document.querySelector("#shakeUI .shake-controls");
    if (!ph || !ph.style || !appEl || !scoreEl || !controlsEl
      || typeof scoreEl.getBoundingClientRect !== "function"
      || typeof controlsEl.getBoundingClientRect !== "function") return;
    var scoreBoard = this._clientRectToBoardSpace(appEl, scoreEl.getBoundingClientRect());
    var controlsBoard = this._clientRectToBoardSpace(appEl, controlsEl.getBoundingClientRect());
    if (!scoreBoard) return;
    if (!controlsBoard || !(controlsBoard.width > 4)) {
      controlsBoard = this._getShakeControlsBoardRect();
    }
    if (!controlsBoard || !(controlsBoard.width > 4)) return;
    var edgeOutset = 0;
    var edgeInset = 0;
    if (typeof getComputedStyle === "function") {
      var phSt = getComputedStyle(ph);
      var outsetVar = phSt.getPropertyValue("--shake-hud-edge-outset").trim();
      if (outsetVar) {
        var parsedOutset = parseFloat(outsetVar);
        if (isFinite(parsedOutset) && parsedOutset > 0) edgeOutset = parsedOutset;
      }
      var insetVar = phSt.getPropertyValue("--shake-hud-edge-inset").trim();
      if (insetVar) {
        var parsedInset = parseFloat(insetVar);
        if (isFinite(parsedInset) && parsedInset > 0) edgeInset = parsedInset;
      }
    }
    var rowGap = 10;
    var rowH = 48;
    var scoreLiftPx = 0;
    if (typeof getComputedStyle === "function") {
      var liftVar = getComputedStyle(scoreEl).getPropertyValue("--shake-score-panel-lift").trim();
      if (liftVar) scoreLiftPx = parseFloat(liftVar) || 0;
    }
    ph.style.left = Math.round(controlsBoard.left + edgeInset - edgeOutset) + "px";
    ph.style.width = Math.round(controlsBoard.width - edgeInset * 2 + edgeOutset * 2) + "px";
    ph.style.top = Math.round(scoreBoard.top - rowGap - rowH + scoreLiftPx) + "px";
    ph.style.height = rowH + "px";
    ph.style.padding = "0";
    ph.style.boxSizing = "border-box";
  };

  Game.prototype.resetShakeUIBounds = function () {
    if (!this.dom || !this.dom.shakeUI) return;
    var ui = this.dom.shakeUI;
    ui.style.left = "";
    ui.style.top = "";
    ui.style.width = "";
    ui.style.height = "";
    ui.style.pointerEvents = "";
    ui.classList.remove("shake-ui-landscape", "shake-ui-portrait");
    this.applyStageChromeLeftBounds(true);
    this.applyStageChromeRightBounds(true);
    this.applyPlayChromeBounds(true);
    this.applyShakeJarChromeBounds(true);
    this.applyShakeHpRowBounds(true);
    this.clearShakePlayerHudLayout();
  };

  /** @returns {Promise<void>} */
  Game.prototype.startTestFlask = function () {
    var self = this;
    function run() {
      self.ensureAudioUnlocked();
      self.testFlaskMode = true;
      runtimeFlags.testFlaskMode = true;
      runtimeFlags.showPhysicsBodyContour = true;
      runtimeFlags.showFlaskPhysicsOutline = true;
      if (self.dom.imageAdjustPanel) self.dom.imageAdjustPanel.classList.remove("hidden");
      self.layout = self.getLayout();
      self.goToShake();
      if (typeof TestFlaskMode !== "undefined" && TestFlaskMode.setup) {
        TestFlaskMode.setup({
          getFlaskSystem: self.getFlaskSystem.bind(self),
          isTestFlaskMode: function () { return self.testFlaskMode; }.bind(self),
        });
      }
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          if (!self.canvas || !self.stateMachine.is("shake")) return;
          var cw = Math.max(100, self.canvas.clientWidth || self.canvasCssWidth || 400);
          var ch = Math.max(100, self.canvas.clientHeight || self.canvasCssHeight || 400);
          self.applyCanvasSize(cw, ch);
          self.flaskSystem = null;
          var sys = self.getFlaskSystem();
          if (sys.getBodyCount() === 0) sys.spawnTestBodies();
        });
      });
    }
    return self.ensureMenuAssetsLoaded().then(run, run);
  };

  Game.prototype.startTestRunway = function () {
    // Test Runway mode was removed with the fall-stage rework; silently ignore.
    devInfo("[Game] Test Runway mode is not available in the fall stage.");
  };

  Game.prototype.exitTestRunway = function () {
    // No-op: Test Runway mode removed with fall-stage rework.
  };

  Game.prototype._getEvolutionDockMaxCheatShakes = function () {
    var bonus = (typeof window !== "undefined" && C.EVOLUTION_DOCK_MAX_CHEAT_SHAKES != null)
      ? Number(C.EVOLUTION_DOCK_MAX_CHEAT_SHAKES)
      : 1000;
    return isFinite(bonus) && bonus > 0 ? Math.floor(bonus) : 1000;
  };

  Game.prototype.showEvolutionDockPrompt = function () {
    if (!this.dom.evolutionDockPromptOverlay) return;
    this._trackAnalytics(AnalyticsEvents.CHEAT_DIALOG_OPEN);
    this._pauseForDialog();
    this._showOverlayWithFade(this.dom.evolutionDockPromptOverlay);
    var input = this.dom.evolutionDockPromptInput;
    if (input) {
      input.value = "";
      if (typeof input.focus === "function") input.focus();
    }
  };

  Game.prototype.hideEvolutionDockPrompt = function () {
    var self = this;
    this._hideOverlayWithFade(this.dom && this.dom.evolutionDockPromptOverlay, function () {
      self._resumeFromDialog();
    });
  };

  Game.prototype._openRewardReveal = function (grant, onClaim, onClosed) {
    if (this.rewardReveal && typeof this.rewardReveal.open === "function") {
      if (this.soundManager && typeof this.soundManager.play === "function") {
        this.soundManager.play("game_reward");
      }
      this.rewardReveal.open({ grant: grant, onClaim: onClaim, onClosed: onClosed });
      return;
    }
    if (typeof onClaim === "function") onClaim();
  };

  Game.prototype.submitEvolutionDockPrompt = function () {
    var input = this.dom.evolutionDockPromptInput;
    var value = input ? String(input.value) : "";
    if (!value) {
      this.hideEvolutionDockPrompt();
      return;
    }
    if (value === "MAX") {
      this._trackAnalytics(AnalyticsEvents.CHEAT_SUCCESS);
      var maxShakes = this._getEvolutionDockMaxCheatShakes();
      var selfMax = this;
      this._openRewardReveal({ shakes: maxShakes }, function () {
        selfMax._queueMaxShakeCheat();
        selfMax.hideEvolutionDockPrompt();
      });
      return;
    }
    if (value === "PAVLO") {
      this._trackAnalytics(AnalyticsEvents.CHEAT_SUCCESS);
      var pavloGems = this._getPavloCheatGems();
      var selfPavlo = this;
      this._openRewardReveal({ gems: pavloGems }, function () {
        selfPavlo._applyPavloGemsCheat();
        selfPavlo.hideEvolutionDockPrompt();
      });
      return;
    }
    this.hideEvolutionDockPrompt();
    if (value === "NASTYA") {
      this._trackAnalytics(AnalyticsEvents.CHEAT_SUCCESS);
      this._queueNastyaDropQueueCheat();
    } else {
      this._trackAnalytics(AnalyticsEvents.CHEAT_FAIL);
    }
  };

  Game.prototype._getPavloCheatGems = function () {
    var n = (typeof window !== "undefined" && C.EVOLUTION_DOCK_PAVLO_CHEAT_GEMS != null)
      ? Number(C.EVOLUTION_DOCK_PAVLO_CHEAT_GEMS)
      : 1000;
    return isFinite(n) && n > 0 ? Math.floor(n) : 1000;
  };

  Game.prototype._applyPavloGemsCheat = function () {
    this.gemsCollected += this._getPavloCheatGems();
    if (typeof this.updateGlobalGemDisplay === "function") this.updateGlobalGemDisplay();
    if (typeof this.updateShakeUI === "function") this.updateShakeUI();
  };

  Game.prototype._appendToDropQueue = function (tiers) {
    if (!tiers || tiers.length === 0) return;
    if (!this.dropQueue) this.dropQueue = [];
    for (var i = 0; i < tiers.length; i++) this.dropQueue.push(tiers[i]);
    this.dropQueueEverShown = true;
    this.dropQueuePanelAlpha = 1;
    this._dropQueueFadeStart = null;
    if (!this.isDropMode) {
      this.isDropMode = true;
      this._bindDropModeInputHandlers();
    }
    if (!this.dropCurrent) {
      if (this._dropSettleInterval) {
        clearInterval(this._dropSettleInterval);
        this._dropSettleInterval = null;
      }
      this._advanceDropQueue();
    }
    this._syncContinueBtnState();
  };

  Game.prototype._getPterodactylTierIndex = function () {
    var chain = (typeof C.getActiveEvolutionChain === "function")
      ? C.getActiveEvolutionChain()
      : (C.EVOLUTION_CHAIN || []);
    for (var i = 0; i < chain.length; i++) {
      var entry = chain[i];
      if (entry && entry.assetId === "pterodactyl") return entry.id != null ? entry.id : i;
    }
    return 8;
  };

  Game.prototype._getNastyaCheatTierCount = function () {
    var n = (typeof window !== "undefined" && C.EVOLUTION_DOCK_NASTYA_CHEAT_PTERODACTYLS != null)
      ? Number(C.EVOLUTION_DOCK_NASTYA_CHEAT_PTERODACTYLS)
      : 8;
    return isFinite(n) && n > 0 ? Math.floor(n) : 8;
  };

  Game.prototype._buildNastyaCheatTiers = function () {
    var tier = this._getPterodactylTierIndex();
    var count = this._getNastyaCheatTierCount();
    var tiers = [];
    for (var i = 0; i < count; i++) tiers.push(tier);
    return tiers;
  };

  Game.prototype._applyNastyaDropQueueCheat = function () {
    var tiers = this._buildNastyaCheatTiers();
    if (this.isDropMode) {
      this._appendToDropQueue(tiers);
    } else {
      this.initDropMode(tiers);
      var shakeSys = this.getFlaskSystem();
      if (shakeSys && typeof shakeSys.setMouthSealed === "function") {
        shakeSys.setMouthSealed(false);
      }
    }
    if (typeof this.updateShakeUI === "function") this.updateShakeUI();
  };

  Game.prototype._queueNastyaDropQueueCheat = function () {
    if (this.stateMachine && this.stateMachine.is && this.stateMachine.is("shake")) {
      this._applyNastyaDropQueueCheat();
      return;
    }
    this._pendingNastyaDropQueueCheat = true;
  };

  Game.prototype._consumePendingNastyaDropQueueCheat = function () {
    if (!this._pendingNastyaDropQueueCheat) return;
    this._pendingNastyaDropQueueCheat = false;
    this._applyNastyaDropQueueCheat();
  };

  Game.prototype._queueMaxShakeCheat = function () {
    if (this.stateMachine && this.stateMachine.is && this.stateMachine.is("shake")) {
      this._applyMaxShakeCheat();
      return;
    }
    this._pendingMaxShakeCheat = true;
  };

  Game.prototype._applyMaxShakeCheat = function () {
    this.shakeUses += this._getEvolutionDockMaxCheatShakes();
    if (typeof this.updateShakeUI === "function") this.updateShakeUI();
  };

  Game.prototype._consumePendingMaxShakeCheat = function () {
    if (!this._pendingMaxShakeCheat) return;
    this._pendingMaxShakeCheat = false;
    this._applyMaxShakeCheat();
  };

  /** Max gap between successive version-label taps to count as one quick burst. */
  Game.prototype._getAppVersionCheatClickGapMs = function () {
    return 450;
  };

  Game.prototype._attachAppVersionCheatPrompt = function () {
    if (!isDevBuildEnv()) return;
    var el = this.dom && this.dom.appVersionOverlay;
    if (!el || el.dataset.cheatPromptBound) return;
    el.dataset.cheatPromptBound = "1";
    var self = this;
    var clickCount = 0;
    var lastClickAt = 0;
    el.addEventListener("click", function (ev) {
      if (ev.button != null && ev.button !== 0) return;
      var now = Date.now();
      var gapMs = self._getAppVersionCheatClickGapMs();
      if (now - lastClickAt > gapMs) clickCount = 0;
      clickCount += 1;
      lastClickAt = now;
      if (clickCount < 3) return;
      clickCount = 0;
      lastClickAt = 0;
      ev.preventDefault();
      ev.stopPropagation();
      self.showEvolutionDockPrompt();
    });
  };

  Game.prototype.attachEvolutionDockPromptHandlers = function () {
    var self = this;
    this._attachAppVersionCheatPrompt();
    var okBtn = this.dom.evolutionDockPromptOk;
    if (okBtn && !okBtn.dataset.promptBound) {
      okBtn.dataset.promptBound = "1";
      okBtn.addEventListener("click", function () {
        self.submitEvolutionDockPrompt();
      });
    }
    var input = this.dom.evolutionDockPromptInput;
    if (input && !input.dataset.promptBound) {
      input.dataset.promptBound = "1";
      input.addEventListener("keydown", function (ev) {
        if (ev.key === "Enter") {
          ev.preventDefault();
          self.submitEvolutionDockPrompt();
        }
      });
    }
  };

  Game.prototype._volumeSliderToUnit = function (sliderValue) {
    var n = Number(sliderValue);
    if (!isFinite(n)) return 0;
    return Math.max(0, Math.min(1, n / 100));
  };

  Game.prototype._volumeUnitToSlider = function (unitValue) {
    var n = Number(unitValue);
    if (!isFinite(n)) return 0;
    return Math.round(Math.max(0, Math.min(1, n)) * 100);
  };

  Game.prototype._syncFallVolumeSlidersFromSound = function () {
    var sm = this.soundManager;
    if (!sm) return;
    if (this.dom.fallVolumeBgm) {
      this.dom.fallVolumeBgm.value = String(this._volumeUnitToSlider(sm.getBgmVolume()));
    }
    if (this.dom.fallVolumeSfx) {
      this.dom.fallVolumeSfx.value = String(this._volumeUnitToSlider(sm.getSfxVolume()));
    }
  };

  Game.prototype._trackVolumeSliderEdge = function (prevInt, nextInt, offEvent, onEvent) {
    var p = Math.max(0, Math.floor(Number(prevInt) || 0));
    var n = Math.max(0, Math.floor(Number(nextInt) || 0));
    if (p > 0 && n === 0) this._trackAnalytics(offEvent);
    else if (p === 0 && n > 0) this._trackAnalytics(onEvent);
  };

  Game.prototype._syncFallVolumeExternalMuteHint = function () {
    var sm = this.soundManager;
    var externallyMuted = !!(sm && typeof sm.isPlatformMuted === "function" && sm.isPlatformMuted());
    var hint = this.dom && this.dom.fallVolumePlatformMute;
    if (hint && hint.classList) {
      hint.classList.toggle("hidden", !externallyMuted);
      hint.setAttribute("aria-hidden", externallyMuted ? "false" : "true");
    }
    var sliders = [
      this.dom && this.dom.fallVolumeBgm,
      this.dom && this.dom.fallVolumeSfx,
    ];
    for (var i = 0; i < sliders.length; i++) {
      var slider = sliders[i];
      if (!slider) continue;
      slider.disabled = !!externallyMuted;
    }
    var overlay = this.dom && this.dom.fallVolumeOverlay;
    if (overlay && typeof overlay.setAttribute === "function") {
      if (externallyMuted && hint && hint.id) overlay.setAttribute("aria-describedby", hint.id);
      else if (typeof overlay.removeAttribute === "function") overlay.removeAttribute("aria-describedby");
    }
  };

  Game.prototype.showFallVolumeOverlay = function () {
    if (!this.dom.fallVolumeOverlay) return;
    this._syncFallVolumeSlidersFromSound();
    this._syncFallVolumeExternalMuteHint();
    if (this.dom.fallVolumeBgm) {
      this._lastBgmSliderInt = Math.max(0, Math.floor(Number(this.dom.fallVolumeBgm.value) || 0));
    }
    if (this.dom.fallVolumeSfx) {
      this._lastSfxSliderInt = Math.max(0, Math.floor(Number(this.dom.fallVolumeSfx.value) || 0));
    }
    this._pauseForDialog();
    this._showOverlayWithFade(this.dom.fallVolumeOverlay);
  };

  Game.prototype.hideFallVolumeOverlay = function (callback) {
    var self = this;
    this._hideOverlayWithFade(this.dom.fallVolumeOverlay, function () {
      self._resumeFromDialog();
      if (typeof callback === "function") callback();
    });
  };

  Game.prototype.applyFallVolumeSliderValues = function () {
    var sm = this.soundManager;
    if (!sm) return;
    if (this.dom.fallVolumeBgm) {
      sm.setBgmVolume(this._volumeSliderToUnit(this.dom.fallVolumeBgm.value));
    }
    if (this.dom.fallVolumeSfx) {
      sm.setSfxVolume(this._volumeSliderToUnit(this.dom.fallVolumeSfx.value));
    }
  };

  Game.prototype.submitFallVolumeOverlay = function () {
    var sm = this.soundManager;
    if (sm) {
      this.applyFallVolumeSliderValues();
      if (typeof sm.saveSettings === "function") sm.saveSettings();
      if (FrvrBridge.isEnabled() && typeof FrvrBridge.flushGameStorage === "function") {
        FrvrBridge.flushGameStorage();
      }
    }
    this.hideFallVolumeOverlay();
  };

  /**
   * Native range dragging breaks on mobile: touchmove is cancelled to lock
   * scrolling, and a CSS-scaled board mis-maps the thumb. Drive the value from
   * the finger position so the music slider actually changes loudness.
   * @param {HTMLInputElement} slider
   */
  Game.prototype._bindTouchVolumeDrag = function (slider) {
    if (!slider || slider.dataset.touchVolumeBound) return;
    slider.dataset.touchVolumeBound = "1";
    slider.addEventListener("pointerdown", function (ev) {
      if (!ev || ev.pointerType === "mouse" || ev.button > 0 || slider.disabled) return;
      if (typeof ev.preventDefault === "function") ev.preventDefault();
      if (typeof slider.setPointerCapture === "function" && ev.pointerId != null) {
        try { slider.setPointerCapture(ev.pointerId); } catch (e) { /* ignore */ }
      }
      function applyPointer(e) {
        if (!e) return;
        var rect = slider.getBoundingClientRect ? slider.getBoundingClientRect() : null;
        if (!rect || !(rect.width > 0)) return;
        var min = Number(slider.min);
        var max = Number(slider.max);
        if (!isFinite(min)) min = 0;
        if (!isFinite(max) || max <= min) max = min + 100;
        var t = (e.clientX - rect.left) / rect.width;
        if (t < 0) t = 0;
        if (t > 1) t = 1;
        var step = Number(slider.step);
        if (!isFinite(step) || step <= 0) step = 1;
        var stepped = Math.round((min + t * (max - min)) / step) * step;
        if (stepped < min) stepped = min;
        if (stepped > max) stepped = max;
        var next = String(stepped);
        if (slider.value === next) return;
        slider.value = next;
        slider.dispatchEvent(new Event("input", { bubbles: true }));
      }
      applyPointer(ev);
      function onMove(e) { applyPointer(e); }
      function onUp(e) {
        slider.removeEventListener("pointermove", onMove);
        slider.removeEventListener("pointerup", onUp);
        slider.removeEventListener("pointercancel", onUp);
        applyPointer(e);
      }
      slider.addEventListener("pointermove", onMove);
      slider.addEventListener("pointerup", onUp);
      slider.addEventListener("pointercancel", onUp);
    });
  };

  Game.prototype.attachFallVolumeHandlers = function () {
    var self = this;
    var btn = this.dom.fallVolumeBtn;
    if (btn && !btn.dataset.volumeBound) {
      btn.dataset.volumeBound = "1";
      btn.addEventListener("click", function () {
        self.showFallVolumeOverlay();
      });
    }
    var okBtn = this.dom.fallVolumeOk;
    if (okBtn && !okBtn.dataset.volumeBound) {
      okBtn.dataset.volumeBound = "1";
      okBtn.addEventListener("click", function () {
        self.submitFallVolumeOverlay();
      });
    }
    var bgmSlider = this.dom.fallVolumeBgm;
    if (bgmSlider && !bgmSlider.dataset.volumeBound) {
      bgmSlider.dataset.volumeBound = "1";
      var onBgmSlider = function () {
        var next = Math.max(0, Math.floor(Number(bgmSlider.value) || 0));
        if (self._lastBgmSliderInt != null) {
          self._trackVolumeSliderEdge(
            self._lastBgmSliderInt,
            next,
            AnalyticsEvents.MUSIC_OFF,
            AnalyticsEvents.MUSIC_ON
          );
        }
        self._lastBgmSliderInt = next;
        if (self.soundManager && typeof self.soundManager.setBgmVolume === "function") {
          self.soundManager.setBgmVolume(self._volumeSliderToUnit(bgmSlider.value));
        }
      };
      bgmSlider.addEventListener("input", onBgmSlider);
      bgmSlider.addEventListener("change", onBgmSlider);
      this._bindTouchVolumeDrag(bgmSlider);
    }
    var sfxSlider = this.dom.fallVolumeSfx;
    if (sfxSlider && !sfxSlider.dataset.volumeBound) {
      sfxSlider.dataset.volumeBound = "1";
      var onSfxSlider = function () {
        var next = Math.max(0, Math.floor(Number(sfxSlider.value) || 0));
        if (self._lastSfxSliderInt != null) {
          self._trackVolumeSliderEdge(
            self._lastSfxSliderInt,
            next,
            AnalyticsEvents.SOUND_OFF,
            AnalyticsEvents.SOUND_ON
          );
        }
        self._lastSfxSliderInt = next;
        if (self.soundManager && typeof self.soundManager.setSfxVolume === "function") {
          self.soundManager.setSfxVolume(self._volumeSliderToUnit(sfxSlider.value));
          if (next > 0 && typeof self.soundManager.play === "function") {
            self.soundManager.play("just_fall");
          }
        }
      };
      sfxSlider.addEventListener("input", onSfxSlider);
      sfxSlider.addEventListener("change", onSfxSlider);
      this._bindTouchVolumeDrag(sfxSlider);
    }
    if (!this._fallVolumePlatformMuteBound && EventBus && typeof EventBus.on === "function") {
      this._fallVolumePlatformMuteBound = true;
      EventBus.on("sound:platform_mute", function () {
        self._syncFallVolumeExternalMuteHint();
      });
    }
    this._syncFallVolumeExternalMuteHint();
  };

  Game.prototype.goToShake = function (opts) {
    if (this._isSimPaused()) return;
    this._resetStageKeyboardAnalytics();
    this.syncFallMovementHint(false);
    var rawNow = typeof performance !== "undefined" ? performance.now() : Date.now();
    this._shakeHpWasDamagedThisStage = false;
    this._resetSkyClockMotionForBridge(rawNow);
    var clockNow = this._getSkyClockNowMs(rawNow);
    var resumeShake = opts && opts.mergeStartSource === "resume";
    if (resumeShake && this._restoredSkyHour != null && isFinite(this._restoredSkyHour)) {
      restoreShakeCrawlFromHour(this.skyClock, this._restoredSkyHour, clockNow);
      this._restoredSkyHour = null;
    } else {
      completeTransitionToShake(this.skyClock, clockNow);
    }
    this.celestialTransition = null;
    this._runwayFlaskFadeSnapshot = null;
    this._runwayFlaskFadePauseWallMs = null;
    if (opts && opts.mergeStartSource === "fall") {
      this._shakeFlaskIntroFadeStartedAt = rawNow;
      this.stageContentAlpha = 0;
    } else {
      this._shakeFlaskIntroFadeStartedAt = null;
      this.stageContentAlpha = 1;
    }
    this._clearStageChromeOpacity();
    this._resetOverflowState();
    this.isPlayerPaused = false;
    this._fallSimFrozen = false;
    this._shakeSimFrozen = false;
    if (!(opts && opts.keepEndOverlay)) {
      this.hideEndOverlay();
    }
    this.stateMachine.transitionTo("shake");
    this._reconcileTransitionSimPause();
    this._reconcileFallSimPause();
    this._reconcileShakeSimPause();
    this._syncFallPauseButton();
    this._clearScoreDigitPop();
    this.lastRunGems = Math.max(0, Math.floor(Number(this.gemsCollected) || 0));
    var app = document.getElementById("app");
    if (app) {
      app.classList.add("stage-shake");
      app.classList.add("stage-theme-night");
    }
    // Fall-only UI: NEXT/drop queue is drawn on canvas in shake; hide legacy boost bar chrome.

    if (this.dom.shakeUI) this.dom.shakeUI.classList.remove("hidden");
    this.setPlayerHudVisible(true);
    this.setStageStatus("shake");
    this.setEvolutionDockVisible(true);
    this.setDebugStripVisible(!!C.SHOW_DEBUG_STRIP);
    this.updateHpUrgency((this.savedFlaskHp / this.flaskMaxHp) * 100);
    this.updateGlobalGemDisplay();
    this._consumePendingMaxShakeCheat();
    this._cleanupMinimizeMode();
    this.layout = this._refreshLayout();
    if (this.canvas) {
      this.applyCanvasSize(this.layout.canvasWidth, this.layout.canvasHeight);
    }
    this.applyShakeUIBounds(true);
    this.populateEvolutionChrome();
    this.applyEvolutionRingBounds();
    this._nextShakeReason = null;
    var oldSys = this.flaskSystem;
    this.flaskSystem = null;
    var shakeSys = this.getFlaskSystem();

    if (shakeSys && typeof shakeSys.setMouthSealed === "function") {
      shakeSys.setMouthSealed(true);
    }
    if (oldSys && oldSys.bodies && oldSys.bodies.length > 0) {
      this.migrateFlaskBodies(oldSys, this.layout.flaskBounds);
      this._stabilizeFlaskBodiesAfterHandoff(shakeSys);
    }
    if (this.ctx && typeof this.drawShake === "function") {
      this.drawShake();
    }

    if (this.boostBar && this.boostBar.length > 0) {
      if (shakeSys && typeof shakeSys.setMouthSealed === "function") {
        shakeSys.setMouthSealed(false);
      }
      this.initDropMode(this.boostBar.slice());
      this.boostBar = [];
    } else if (this._hasPendingDropModeToResume()) {
      if (shakeSys && typeof shakeSys.setMouthSealed === "function") {
        shakeSys.setMouthSealed(false);
      }
      this._resumeDropModeFromSave();
    } else {
      this._syncContinueBtnState();
    }
    var mergeBodyCount = shakeSys && typeof shakeSys.getBodyCount === "function"
      ? shakeSys.getBodyCount() : 0;
    var hasDropQueue = !!(this.isDropMode && (this.dropCurrent
      || (this.dropQueue && this.dropQueue.length > 0)));
    this._mergeStartedEmpty = mergeBodyCount <= 0 && !hasDropQueue;
    this.analyticsStage = "merge";
    this._consumePendingNastyaDropQueueCheat();
    this.updateBoostBarUI();
    this._syncShakeHpBarVisibility();
    this.updateShakeUI();
    if (this._isVerticalLayout()) {
      this._syncVerticalStageChromePanels();
    }
    this._beginAnalyticsPlayTime();
    this.syncBackgroundMusic();
    this._syncPlatformGameplay();
    var mergeSource = this._analyticsMergeStartSource
      || (opts && opts.mergeStartSource)
      || "fall";
    this._trackMergeStart(mergeSource);
    this._analyticsMergeStartSource = null;
    this._armShakeHintSettle();
    this.syncDevTestBar();
    this._scheduleGameLoop();
  };

  Game.prototype._applyShakeButtonState = function (btn, badge, badgeStateKey) {
    if (!btn) return;
    var c = this.constants;
    var sys = this.getFlaskSystem();
    var hasBodies = !!(sys && typeof sys.getBodyCount === "function" && sys.getBodyCount() > 0);
    if (this.isMinimizeMode) {
      btn.disabled = true;
    } else if (!hasBodies) {
      btn.classList.remove("paid");
      btn.disabled = true;
      this._clearDomTitle(btn);
      btn.setAttribute("aria-label", "Shake the flask — add creatures to the flask first");
      if (badge) {
        badge.className = "mockup-action__badge free";
        badge.textContent = "Free x" + Math.max(0, this.shakeUses);
      }
    } else if (this.shakeUses > 0) {
      btn.classList.remove("paid");
      btn.disabled = false;
      this._clearDomTitle(btn);
      btn.setAttribute("aria-label", "Shake the flask to mix creatures. Free shakes remaining: " + this.shakeUses);
      if (badge) {
        badge.className = "mockup-action__badge free";
        badge.textContent = "Free x" + this.shakeUses;
      }
    } else {
      btn.classList.add("paid");
      btn.disabled = this.gemsCollected < c.SHAKE_USE_COST;
      this._clearDomTitle(btn);
      btn.setAttribute(
        "aria-label",
        btn.disabled
          ? ("Shake the flask — costs " + c.SHAKE_USE_COST + " gems, not enough gems")
          : ("Shake the flask — costs " + c.SHAKE_USE_COST + " gems")
      );
      if (badge) {
        badge.className = "mockup-action__badge cost";
        badge.innerHTML = "<img src=\"" + gameImageUrl("gem") + "\" alt=\"\">" + c.SHAKE_USE_COST;
      }
    }
    this._animateBadgeModeChange(badge, badgeStateKey);
  };

  Game.prototype.updateShakeUI = function () {
    this.updateGlobalGemDisplay();
    this._updateFallProgressDial(
      typeof performance !== "undefined" ? performance.now() : Date.now()
    );
    var c = this.constants;
    var sys = this.getFlaskSystem();
    this._applyShakeButtonState(this.dom.shakeBtn, this.dom.shakeCountEl, "_lastShakeBadgeMode");
    this._applyShakeButtonState(
      this.dom.verticalShakeBtn,
      this.dom.verticalShakeCountEl,
      "_lastVerticalShakeBadgeMode",
    );
    this.updateBombTierButton(
      this.dom.bombCellBtn || this.dom.bombBtn,
      sys,
      [0],
      this.bombCellUses,
      c.BOMB_CELL_COST,
      "Remove all cells (tier 0)",
      "No cells to remove",
      "Spend " + c.BOMB_CELL_COST + " gem to clear all cells"
    );
    this.updateBombTierButton(
      this.dom.verticalBombCellBtn,
      sys,
      [0],
      this.bombCellUses,
      c.BOMB_CELL_COST,
      "Remove all cells (tier 0)",
      "No cells to remove",
      "Spend " + c.BOMB_CELL_COST + " gem to clear all cells"
    );
    this.updateBombTierButton(
      this.dom.bombTripleBtn,
      sys,
      [0, 1, 2],
      this.bombTripleUses,
      c.BOMB_THREE_TIER_COST,
      "Remove cells, starfishes, and jellyfishes (tiers 0–2)",
      "No cells, starfishes, or jellyfishes to remove",
      "Spend " + c.BOMB_THREE_TIER_COST + " gems to clear cells, starfishes, and jellyfishes"
    );
    this.updateBombTierButton(
      this.dom.verticalBombTripleBtn,
      sys,
      [0, 1, 2],
      this.bombTripleUses,
      c.BOMB_THREE_TIER_COST,
      "Remove cells, starfishes, and jellyfishes (tiers 0–2)",
      "No cells, starfishes, or jellyfishes to remove",
      "Spend " + c.BOMB_THREE_TIER_COST + " gems to clear cells, starfishes, and jellyfishes"
    );
    if (this.isMinimizeMode) {
      if (this.dom.bombCellBtn) this.dom.bombCellBtn.disabled = true;
      if (this.dom.bombTripleBtn) this.dom.bombTripleBtn.disabled = true;
      if (this.dom.verticalBombCellBtn) this.dom.verticalBombCellBtn.disabled = true;
      if (this.dom.verticalBombTripleBtn) this.dom.verticalBombTripleBtn.disabled = true;
    }
    if (this.dom.fixBtn || this.dom.shakeHpPill) {
      var healBtn = this.dom.shakeHpPill || this.dom.fixBtn;
      if (this.dom.fixBtnCostEl) {
        this.dom.fixBtnCostEl.textContent = String(c.FLASK_FIX_COST);
      }
      healBtn.classList.add("paid");
      healBtn.disabled = !!this.isMinimizeMode
        || this.savedFlaskHp >= this.flaskMaxHp
        || this.gemsCollected < c.FLASK_FIX_COST;
      this._clearDomTitle(healBtn);
      healBtn.setAttribute(
        "aria-label",
        healBtn.disabled
          ? (this.savedFlaskHp >= this.flaskMaxHp ? "Life full — cannot purchase" : "Not enough gems for life")
          : ("Get one life for " + c.FLASK_FIX_COST + " gems")
      );
    }
    this.updateMinimizeButton(sys);
    this._syncContinueBtnState();
    this.updateLivesHud();
    this._syncShakeHpPillState();
  };

  Game.prototype.doShake = function () {
    if (this.isMinimizeMode) return;
    var flask = this.getFlaskSystem();
    if (!flask || typeof flask.getBodyCount !== "function" || flask.getBodyCount() <= 0) {
      this.updateShakeUI();
      return;
    }
    var c = this.constants;
    var usedFreeStock = this.shakeUses > 0;
    if (usedFreeStock) {
      this.shakeUses--;
    } else {
      if (this.gemsCollected < c.SHAKE_USE_COST) return;
      this.gemsCollected -= c.SHAKE_USE_COST;
      this.triggerGemSpendReaction();
    }
    this.updateShakeUI();
    var graphicPx = C.SHAKE_FLASK_GRAPHIC_SHAKE_PX != null
      ? Number(C.SHAKE_FLASK_GRAPHIC_SHAKE_PX)
      : 14;
    if (!isFinite(graphicPx) || graphicPx < 0) graphicPx = 14;
    var panelScale = (flask && flask.bounds) ? getFlaskPanelScale(flask.bounds) : 1;
    if (!isFinite(panelScale) || panelScale <= 0) panelScale = 1;
    this.triggerFlaskVisualShake(graphicPx * panelScale, C.SHAKE_FLASK_GRAPHIC_SHAKE_MS || 260);
    this._resetOverflowAfterPlayerAction();
    flask.applyShake(this.constants.SHAKE_IMPULSE_STRENGTH);
    this.dismissShakeHintForever();
    this._trackAnalytics(
      usedFreeStock ? AnalyticsEvents.FREE_SHAKE : AnalyticsEvents.PAID_SHAKE
    );
  };

  Game.prototype.doBombTiers = function (tiers, stockKey, gemCost) {
    if (this.isMinimizeMode) return;
    var flask = this.getFlaskSystem();
    var canRemove = !!(flask && typeof flask.getBodyCount === "function" && flask.getBodyCount() > 0 && this.countFlaskBodiesInTiers(flask, tiers) > 0);
    if (!canRemove) {
      this.updateShakeUI();
      return;
    }
    var usedFreeStock = normalizeUseCount(this[stockKey], 0) > 0;
    if (usedFreeStock) {
      this[stockKey] = normalizeUseCount(this[stockKey], 0) - 1;
    } else {
      if (this.gemsCollected < gemCost) {
        this.updateShakeUI();
        return;
      }
      this.gemsCollected -= gemCost;
      this.triggerGemSpendReaction();
    }
    if (typeof flask.removeBodiesByTiers === "function") {
      flask.removeBodiesByTiers(tiers);
    } else if (typeof flask.removeLowestTierBodies === "function") {
      flask.removeLowestTierBodies(this.constants.BOMB_REMOVE_COUNT);
    }
    this._resetOverflowAfterPlayerAction();
    this.updateShakeUI();
    var bombEvent = null;
    if (stockKey === "bombCellUses") {
      bombEvent = usedFreeStock ? AnalyticsEvents.FREE_BOMB_CELL : AnalyticsEvents.PAID_BOMB_CELL;
    } else if (stockKey === "bombTripleUses") {
      bombEvent = usedFreeStock ? AnalyticsEvents.FREE_BOMB_TRIPLE : AnalyticsEvents.PAID_BOMB_TRIPLE;
    }
    if (bombEvent) {
      this._trackAnalytics(bombEvent);
    }
  };

  Game.prototype.doBombCells = function () {
    this.doBombTiers([0], "bombCellUses", this.constants.BOMB_CELL_COST);
  };

  Game.prototype.doBombFirstThree = function () {
    this.doBombTiers([0, 1, 2], "bombTripleUses", this.constants.BOMB_THREE_TIER_COST);
  };

  Game.prototype.doBomb = function () {
    this.doBombFirstThree();
  };

  Game.prototype.doFix = function () {
    var c = this.constants;
    if (this.isMinimizeMode) return;
    if (this.savedFlaskHp >= this.flaskMaxHp) return;
    if (this.gemsCollected < c.FLASK_FIX_COST) return;
    this.gemsCollected -= c.FLASK_FIX_COST;
    this.triggerGemSpendReaction();
    this.savedFlaskHp = Math.min(this.flaskMaxHp, this.savedFlaskHp + 1);
    if (EventBus && typeof EventBus.emit === "function") {
      EventBus.emit("shake:life_gain", {});
    }
    this._trackAnalytics(AnalyticsEvents.PAID_FIX);
    this.dismissHealHintForever();
    this.updateShakeUI();
    this._syncShakeHpBarVisibility();
  };

  Game.prototype.countMinimizableBodies = function (flask) {
    if (!flask || !flask.bodies) return 0;
    var count = 0;
    for (var i = 0; i < flask.bodies.length; i++) {
      if (isMinimizableMeta(flask.bodies[i])) count++;
    }
    return count;
  };

  Game.prototype.updateMinimizeButton = function (flask) {
    this._updateMinimizeButtonState(this.dom.minimizeBtn, this.dom.minimizeBtnCostEl, flask);
    if (this.dom.verticalMinimizeBtn) {
      this._updateMinimizeButtonState(
        this.dom.verticalMinimizeBtn,
        this.dom.verticalMinimizeBtnCostEl,
        flask,
      );
    }
  };

  Game.prototype._updateMinimizeButtonState = function (btn, costEl, flask) {
    if (!btn) return;
    var c = this.constants;
    var usesLeft = normalizeUseCount(this.minimizeUses, 0);
    var isFree = usesLeft > 0;
    var gems = Math.floor(Number(this.gemsCollected) || 0);
    var cost = Math.floor(Number(c.MINIMIZE_COST) || 0);
    var gemLabel = "(" + cost + " GEMS)";
    var hasBodies = !!(flask && typeof flask.getBodyCount === "function" && flask.getBodyCount() > 0);
    var canMinimize = this.countMinimizableBodies(flask) > 0;
    btn.classList.toggle("minimize-btn--active", !!this.isMinimizeMode);

    if (this.isMinimizeMode) {
      btn.disabled = true;
      this._clearDomTitle(btn);
      btn.setAttribute("aria-label", "Shrink aim active — select a creature in the flask");
      var freeLeftInAim = normalizeUseCount(this.minimizeUses, 0);
      var showPaidStyle = freeLeftInAim <= 0 || gems < cost;
      if (btn.classList && typeof btn.classList.contains === "function" && btn.classList.contains("mockup-action")) {
        this.setMockupActionBadge(btn, !showPaidStyle, freeLeftInAim, cost);
      } else if (costEl) {
        costEl.textContent = showPaidStyle ? gemLabel : "(" + freeLeftInAim + ")";
      }
      btn.classList.toggle("paid", showPaidStyle);
      btn.classList.toggle("minimize-btn--active", !showPaidStyle);
      return;
    }

    var disabled = false;
    if (!hasBodies) {
      disabled = true;
      this._clearDomTitle(btn);
      btn.setAttribute("aria-label", "Nothing in the flask yet");
    } else if (!isFree && gems < cost) {
      disabled = true;
      this._clearDomTitle(btn);
      btn.setAttribute("aria-label", "Not enough gems to shrink");
    } else if (!canMinimize) {
      disabled = false;
      this._clearDomTitle(btn);
      btn.setAttribute("aria-label", "Nothing left to shrink");
    } else {
      disabled = false;
      this._clearDomTitle(btn);
      btn.setAttribute(
        "aria-label",
        isFree
          ? "Shrink one creature to compact size (free)"
          : "Shrink one creature to compact size for " + cost + " gems"
      );
    }

    // Badge reflects free-stock vs paid pricing (like bomb/shake), not whether the action
    // is currently available — e.g. empty flask stays disabled but still shows "Free x1".
    if (btn.classList && typeof btn.classList.contains === "function" && btn.classList.contains("mockup-action")) {
      this.setMockupActionBadge(btn, isFree, usesLeft, cost);
    } else if (costEl) {
      costEl.textContent = isFree
        ? "(" + usesLeft + ")"
        : gemLabel;
    }
    btn.classList.toggle("paid", !isFree);
    btn.disabled = disabled;
  };

  Game.prototype._eachContinueBtn = function (fn) {
    if (!this.dom || typeof fn !== "function") return;
    if (this.dom.continueBtn) fn(this.dom.continueBtn);
    if (this.dom.verticalContinueBtn) fn(this.dom.verticalContinueBtn);
  };

  Game.prototype._isDropInputActive = function () {
    if (this._dropAllActive) return false;
    return !!this.isDropMode && !this.isMinimizeMode && !this._isGameplaySimFrozen();
  };

  Game.prototype.toggleMinimizeMode = function () {
    if (this.isMinimizeMode) {
      this._cleanupMinimizeMode();
      this.updateShakeUI();
      return;
    }
    this.startMinimizeMode();
  };

  Game.prototype.startMinimizeMode = function () {
    if (this.isHostPaused) return;
    if (this.isMinimizeMode) return;
    var c = this.constants;
    var flask = this.getFlaskSystem();
    if (!flask || typeof flask.getBodyCount !== "function" || flask.getBodyCount() <= 0) {
      this.showToast("Nothing in the flask to shrink", 2200);
      this.updateShakeUI();
      return;
    }
    var gems = Math.floor(Number(this.gemsCollected) || 0);
    var cost = Math.floor(Number(c.MINIMIZE_COST) || 0);
    var usedFreeStock = normalizeUseCount(this.minimizeUses, 0) > 0;
    if (!usedFreeStock && gems < cost) {
      this.showToast("Need " + cost + " gems to shrink", 2200);
      this.updateShakeUI();
      return;
    }
    if (this.countMinimizableBodies(flask) <= 0) {
      this.showToast("Nothing left to shrink", 2200);
      this.updateShakeUI();
      return;
    }
    this._initMinimizeMode();
    this.showToast("Choose a creature to shrink", 2200);
    this.updateShakeUI();
  };

  Game.prototype._initMinimizeMode = function () {
    this._cleanupMinimizeMode();
    this.isMinimizeMode = true;
    this._minimizeHoverMeta = null;
    this._dropKeyLeft = false;
    this._dropKeyRight = false;
    var self = this;
    var canvas = this.canvas;
    var app = document.getElementById("app");

    this._minimizeKeyHandler = function (e) {
      if (!self.isMinimizeMode || self.isHostPaused) return;
      if (e.key === "Escape" || e.code === "Escape") {
        self._cleanupMinimizeMode();
        self.updateShakeUI();
      }
    };

    this._minimizeMoveHandler = function (e) {
      if (!self.isMinimizeMode || self.isHostPaused) return;
      var clientX = e.touches ? e.touches[0].clientX : e.clientX;
      var clientY = e.touches ? e.touches[0].clientY : e.clientY;
      var pt = self._panelPointFromClient(clientX, clientY);
      var overFlask = self._isPanelPointInFlask(pt.panelX, pt.panelY);
      if (overFlask) {
        var sys = self.getFlaskSystem();
        self._minimizeHoverMeta = sys && typeof sys.pickBodyAtPanelPoint === "function"
          ? sys.pickBodyAtPanelPoint(pt.panelX, pt.panelY)
          : null;
      } else {
        self._minimizeHoverMeta = null;
      }
      if (e.touches) e.preventDefault();
    };

    this._minimizeClickHandler = function (e) {
      if (!self.isMinimizeMode || self.isHostPaused) return;
      var clientX = e.changedTouches ? e.changedTouches[0].clientX : e.clientX;
      var clientY = e.changedTouches ? e.changedTouches[0].clientY : e.clientY;
      var pt = self._panelPointFromClient(clientX, clientY);
      if (!self._isPanelPointInFlask(pt.panelX, pt.panelY)) return;
      self._applyMinimizeAtPanelPoint(pt.panelX, pt.panelY);
      e.preventDefault();
    };

    // Aim + click are tracked on `window` (not the canvas) because DOM overlays sit
    // above the canvas in the shake stage — a canvas-only listener never fires over
    // the flask interior, so aiming/clicking a creature did nothing. `_isPanelPointInFlask`
    // guards minimize handlers; drop mode uses `_isPanelPointInDropZone` (wider band above rim).
    if (typeof window !== "undefined") {
      window.addEventListener("keydown", this._minimizeKeyHandler, true);
      window.addEventListener("mousemove", this._minimizeMoveHandler);
      window.addEventListener("click", this._minimizeClickHandler);
    }
    if (canvas) {
      canvas.addEventListener("touchmove", this._minimizeMoveHandler, { passive: false });
      canvas.addEventListener("touchend", this._minimizeClickHandler, { passive: false });
    }
    if (app) app.classList.add("minimize-aim-active");
    this._setContinueBtnEnabled(false);
  };

  Game.prototype._applyMinimizeAtPanelPoint = function (panelX, panelY) {
    var sys = this.getFlaskSystem();
    if (!sys || typeof sys.pickBodyAtPanelPoint !== "function") return;
    var meta = sys.pickBodyAtPanelPoint(panelX, panelY);
    if (!meta) {
      this.showToast("Click a creature inside the flask", 1800);
      return;
    }
    var tier = meta.tier != null ? meta.tier : 0;
    if (!isMinimizableMeta(meta)) {
      if (tier <= 0 || getMetaPhysicsSizeTier(meta) <= this.constants.MINIMIZE_PHYSICS_SIZE_TIER) {
        this.showToast("Cells are already the smallest size", 1800);
      } else {
        this.showToast("That creature is already minimum size", 1800);
      }
      return;
    }
    if (typeof sys.minimizeBody !== "function" || !sys.minimizeBody(meta)) {
      this.showToast("Could not shrink that creature", 1800);
      return;
    }
    var c = this.constants;
    var cost = Math.floor(Number(c.MINIMIZE_COST) || 0);
    var usedFreeStock = normalizeUseCount(this.minimizeUses, 0) > 0;
    if (usedFreeStock) {
      this.minimizeUses = normalizeUseCount(this.minimizeUses, 0) - 1;
    } else {
      this.gemsCollected -= cost;
      this.triggerGemSpendReaction();
    }
    this._trackAnalytics(
      usedFreeStock ? AnalyticsEvents.FREE_MINIMIZE : AnalyticsEvents.PAID_MINIMIZE
    );
    this._resetOverflowAfterPlayerAction();
    if (EventBus && typeof EventBus.emit === "function") {
      EventBus.emit("shake:shrink", { tier: tier });
    }
    this._cleanupMinimizeMode();
    this.updateShakeUI();
  };

  Game.prototype._cleanupMinimizeMode = function () {
    this.isMinimizeMode = false;
    this._minimizeHoverMeta = null;
    if (typeof window !== "undefined") {
      if (this._minimizeKeyHandler) {
        window.removeEventListener("keydown", this._minimizeKeyHandler, true);
        this._minimizeKeyHandler = null;
      }
      if (this._minimizeMoveHandler) window.removeEventListener("mousemove", this._minimizeMoveHandler);
      if (this._minimizeClickHandler) window.removeEventListener("click", this._minimizeClickHandler);
    }
    var canvas = this.canvas;
    if (canvas) {
      if (this._minimizeMoveHandler) canvas.removeEventListener("touchmove", this._minimizeMoveHandler);
      if (this._minimizeClickHandler) canvas.removeEventListener("touchend", this._minimizeClickHandler);
    }
    this._minimizeMoveHandler = null;
    this._minimizeClickHandler = null;
    var app = document.getElementById("app");
    if (app) app.classList.remove("minimize-aim-active");
  };

  Game.prototype.drawMinimizeAimOverlay = function () {
    if (!this.isMinimizeMode || !this.ctx || !this.layout) return;
    var meta = this._minimizeHoverMeta;
    if (!meta || !meta.body || !meta.body.position) return;
    var layout = this.layout;
    var origin = layout.flaskPanelOrigin || { x: 0, y: 0 };
    var cx = origin.x + meta.body.position.x;
    var cy = origin.y + meta.body.position.y;
    var rx = meta.radiusX != null ? meta.radiusX : 12;
    var ry = meta.radiusY != null ? meta.radiusY : rx;
    var ctx = this.ctx;
    ctx.save();
    ctx.strokeStyle = "rgba(233, 255, 154, 0.95)";
    ctx.lineWidth = 2.5;
    ctx.setLineDash([6, 4]);
    ctx.beginPath();
    ctx.ellipse(cx, cy, rx + 4, ry + 4, meta.body.angle || 0, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
  };

  Game.prototype._hideShakeStageChrome = function () {

    if (this.dom && this.dom.shakeUI) this.dom.shakeUI.classList.add("hidden");
    if (this.dom && this.dom.shakeActionsCol) {
      this.dom.shakeActionsCol.classList.add("hidden");
      if (typeof this.dom.shakeActionsCol.setAttribute === "function") {
        this.dom.shakeActionsCol.setAttribute("aria-hidden", "true");
      }
    }
    if (this.dom && this.dom.verticalShakeActionsRow) {
      this.dom.verticalShakeActionsRow.classList.add("hidden");
      if (typeof this.dom.verticalShakeActionsRow.setAttribute === "function") {
        this.dom.verticalShakeActionsRow.setAttribute("aria-hidden", "true");
      }
    }
    if (this.dom && this.dom.shakeHpRow) this.dom.shakeHpRow.classList.add("hidden");
    if (this.dom && this.dom.shakeJarChrome) this.dom.shakeJarChrome.classList.add("hidden");
    if (this.dom && this.dom.jarDropQueue) this.dom.jarDropQueue.classList.add("hidden");
    if (this.dom && this.dom.jarDropPreview) this.dom.jarDropPreview.classList.add("hidden");
    this.applyShakeJarChromeBounds(true);
    this.applyShakeHpRowBounds(true);
    this.resetShakeUIBounds();
    this._hideShakeHint();
    this._hideHealHint();
  };

  Game.prototype._finishContinueToFall = function () {
    var rawNow = typeof performance !== "undefined" ? performance.now() : Date.now();
    this._clearBridgeTransitionTimers();
    this.celestialTransition = null;
    this._fallCatcherFlaskIntroFadeStartedAt = rawNow;
    this._fallCatcherFlaskIntroFadePauseWallMs = null;
    this.stageContentAlpha = 0;
    this._clearStageChromeOpacity();
    this._cleanupDropMode();
    this.isDropMode = false;
    this._hideShakeStageChrome();
    var app = document.getElementById("app");
    if (app) {
      this._clearScoreDigitPop();
      app.classList.remove("stage-shake", "stage-theme-night");
      app.classList.add("stage-run");
    }
    if (this.dom && this.dom.endOverlay) this.dom.endOverlay.classList.add("hidden");
    this._resetSkyClockMotionForBridge(rawNow);
    completeTransitionToFall(this.skyClock);
    this.stateMachine.transitionTo("fall");
    this._syncFallPauseButton();
    this._analyticsFallStartSource = "continue";
    this.startNewRun();
  };

  Game.prototype.continueToRun = function () {
    var self = this;
    this._abortVictoryPopup(true);
    this._abortPuzzleReveal();
    if (this._isEvolutionRevealPending()) {
      this._finishSecretRevealImmediate();
    }
    if (this.boostBar && this.boostBar.length > 0) {
      return;
    }
    if (!this._shouldContinueBeEnabled()) {
      return;
    }
    // Same button, different job: empty the queue first, NEXT DAY on a later press.
    if (this._hasPendingDropPieces()) {
      this.dropAllQueued();
      return;
    }
    this._hideShakeHint();
    this._hideHealHint();
    if (this._continueToRunAdPending) {
      return;
    }
    if (Y8Bridge.isEnabled() && this.shouldShowY8NextDayInterstitial()) {
      this._continueToRunAdPending = true;
      this._setContinueBtnEnabled(false);
      Y8Bridge.showNextInterstitial(INTERSTITIAL_AD_NAME_NEXT_DAY).then(
        function () {
          self._continueToRunAdPending = false;
          self._executeContinueToRun();
        },
        function () {
          self._continueToRunAdPending = false;
          self._executeContinueToRun();
        }
      );
      return;
    }
    if (this.shouldShowYtNextDayInterstitial()) {
      this._continueToRunAdPending = true;
      this._setContinueBtnEnabled(false);
      YtBridge.requestInterstitialAd({ game: this }).then(
        function () {
          self._continueToRunAdPending = false;
          self._executeContinueToRun();
        },
        function () {
          self._continueToRunAdPending = false;
          self._executeContinueToRun();
        }
      );
      return;
    }
    if (this.shouldShowPokiNextDayInterstitial() && typeof PokiBridge.commercialBreak === "function") {
      this._continueToRunAdPending = true;
      this._setContinueBtnEnabled(false);
      this._stopPlatformGameplayReporting();
      PokiBridge.commercialBreak({ game: this }).then(
        function () {
          self._executeContinueToRun();
          self._continueToRunAdPending = false;
          self._syncPlatformGameplay();
        },
        function () {
          self._executeContinueToRun();
          self._continueToRunAdPending = false;
          self._syncPlatformGameplay();
        }
      );
      return;
    }
    if (this.shouldShowGdNextDayInterstitial()) {
      this._continueToRunAdPending = true;
      this._setContinueBtnEnabled(false);
      GdBridge.runAfterNextGameStart(function () {
        self._continueToRunAdPending = false;
        self._executeContinueToRun();
      });
      GdBridge.requestInterstitialAd({ game: this });
      return;
    }
    if (this.shouldShowPlaygamaNextDayInterstitial() && typeof PlaygamaBridge.showInterstitial === "function") {
      this._continueToRunAdPending = true;
      this._setContinueBtnEnabled(false);
      Promise.resolve(PlaygamaBridge.showInterstitial(INTERSTITIAL_AD_NAME_NEXT_DAY)).then(
        function () {
          self._continueToRunAdPending = false;
          self._executeContinueToRun();
        },
        function () {
          self._continueToRunAdPending = false;
          self._executeContinueToRun();
        }
      );
      return;
    }
    if (this.shouldShowFrvrNextDayInterstitial() && typeof FrvrBridge.showNextInterstitial === "function") {
      this._continueToRunAdPending = true;
      this._setContinueBtnEnabled(false);
      FrvrBridge.showNextInterstitial(INTERSTITIAL_AD_NAME_NEXT_DAY, { game: this }).then(
        function () {
          self._continueToRunAdPending = false;
          self._executeContinueToRun();
        },
        function () {
          self._continueToRunAdPending = false;
          self._executeContinueToRun();
        }
      );
      return;
    }
    this._executeContinueToRun();
  };

  /** Merge → Fall bridge after any NEXT DAY interstitial has finished. */
  Game.prototype._executeContinueToRun = function () {
    var self = this;
    this._hideShakeHint();
    this._hideHealHint();
    this._setContinueBtnEnabled(false);
    if (this.isPlayerPaused) {
      this.setPlayerPaused(false);
    }
    this._cleanupMinimizeMode();
    this._stopContinueBtnWiggle();
    this._endAnalyticsPlayTime();
    var mergeKeyboardParam = this._getStageUsedKeyboardParam("merge");
    this._trackAnalytics(AnalyticsEvents.MERGE_COMPLETED, {
      used_keyboard: mergeKeyboardParam.used_keyboard,
    });
    if (this._isMergeCompletedEmpty()) {
      this._trackAnalytics(AnalyticsEvents.MERGE_COMPLETED_EMPTY, {
        used_keyboard: mergeKeyboardParam.used_keyboard,
      });
    }
    this.analyticsStage = "fall";
    this._pokiStage("complete", "night");
    this.currentCycleIndex += 1;
    this._maybeUpgradeSkyBackgroundFromCycle();
    this._clearBridgeTransitionTimers();
    this._lockShakeHpRowHiddenForBridge();

    var cfg = C.RUN_CONFIG || {};
    var dwellMs = cfg.DAY_NIGHT_TRANSITION_MS != null
      ? cfg.DAY_NIGHT_TRANSITION_MS
      : (cfg.RUN_TO_SHAKE_TRANSITION_MS || 2000);
    var themeMs = cfg.STAGE_THEME_CROSSFADE_MS != null
      ? cfg.STAGE_THEME_CROSSFADE_MS
      : 300;
    var rawNow = typeof performance !== "undefined" ? performance.now() : Date.now();
    var clockNow = this._getSkyClockNowMs(rawNow);
    var fromHour = getSkyHour(this.skyClock, {
      nowMs: clockNow,
      fall: null,
      isFirstFallOfFirstRun: this._isFirstFallOfFirstRun(),
    });
    this._resetSkyClockMotionForBridge(rawNow);
    beginTransitionToFall(this.skyClock, fromHour, clockNow);
    this.startCelestialTransition(dwellMs, { fadeStageContent: true, direction: "to_fall" });
    this.stateMachine.transitionTo("transition");
    this._hideJarDropGuideAim();
    this._cleanupDropMode();
    this.isDropMode = false;
    var appEl = document.getElementById("app");
    if (appEl) {
      var themeDelay = Math.max(0, dwellMs - themeMs);
      this._bridgeThemeCrossfadeDeadlineMs = rawNow + themeDelay;
      this._bridgeThemeCrossfadeTimeoutId = setTimeout(function () {
        self._bridgeThemeCrossfadeTimeoutId = null;
        if (!self.stateMachine || (!self.stateMachine.is("transition") && !self.stateMachine.is("fall"))) {
          return;
        }
        appEl.classList.remove("stage-theme-night");
      }, themeDelay);
    }
    if (this.dom && this.dom.playChrome) this.dom.playChrome.classList.remove("hidden");

    var bridgeShakeFadeMs = cfg.RUN_BGM_FADE_OUT_ON_BRIDGE_MS != null
      ? cfg.RUN_BGM_FADE_OUT_ON_BRIDGE_MS
      : 1000;
    var bridgeFallFadeMs = cfg.RUN_BGM_FADE_IN_ON_BRIDGE_MS != null
      ? cfg.RUN_BGM_FADE_IN_ON_BRIDGE_MS
      : 1000;
    var sm = this.soundManager;
    if (sm) {
      if (typeof sm.fadeOutBgmScene === "function") {
        sm.fadeOutBgmScene("shake", bridgeShakeFadeMs);
      }
      if (typeof sm.fadeInBgmTo === "function") {
        this._bridgeShakeFadeInTimeoutId = setTimeout(function () {
          self._bridgeShakeFadeInTimeoutId = null;
          if (!self.stateMachine.is("transition")) return;
          if (typeof sm.stopBgmTrack === "function") sm.stopBgmTrack("shake");
          sm.fadeInBgmTo("fall", bridgeFallFadeMs);
        }, bridgeShakeFadeMs);
      }
    }

    this._bridgeDwellDeadlineMs = rawNow + dwellMs;
    this._bridgeShakeFadeInDeadlineMs = rawNow + bridgeShakeFadeMs;
    this._syncFallPauseButton();
    this._startTransitionRedrawLoop();
    this._reconcileTransitionSimPause();
    this._syncPlatformGameplay();
    this.continueToFallTimeoutId = setTimeout(function () {
      self.continueToFallTimeoutId = null;
      if (!self.stateMachine.is("transition")) return;
      if (self._isSimPaused()) return;
      self._finishContinueToFall();
    }, dwellMs);
  };

  Game.prototype.startNewRun = function () {
    if (this._isFirstFallCycleOfRun()) {
      this._applyRunStartTierBaseline();
    }
    this.analyticsStage = "fall";
    this._resetStageKeyboardAnalytics();
    this._cancelGameLoop();
    this._dismissBgmAutoplayBlockedToast();
    if (this.runPhase) {
      this.runPhase.stop();
      this.runPhase = null;
    }
    this._cleanupDropMode();
    this.isDropMode = false;
    this.isRunFlaskShattered = false;
    this.isReviveOfferActive = false;
    this.reviveOfferStage = null;
    this._reviveAdPending = false;
    this._hideLiveReviveOverlay();
    this.lastRunGems = Math.max(0, Math.floor(Number(this.gemsCollected) || 0));
    this.resumeStage = "fall";
    this._fallSimFrozen = false;
    this._shakeSimFrozen = false;
    this._transitionSimFrozen = false;
    this._dialogPauseDepth = 0;
    this.isDialogPaused = false;
    this.frozenFallArcProgress = null;
    this.frozenFallSceneTime = null;
    this.frozenShakePreviewTime = null;
    this.frozenSkyHour = null;
    this.skyTimeOffsetMs = 0;
    this._clearPlayerPauseState();
    this._platformGameplayInputLatch = false;
    if (this.skyClock) beginFallSkyClock(this.skyClock);
    this._syncSkyBackgroundEffective();
    this._startSkyAutoProbeIfNeeded();
    this._cleanupMinimizeMode();
    this._refreshLayout();
    if (!this.canvas) return;
    this.applyCanvasSize(this.layout.canvasWidth, this.layout.canvasHeight);

    var app = document.getElementById("app");
    if (app) {
      this._clearScoreDigitPop();
      app.classList.remove("stage-shake", "stage-theme-night");
      app.classList.add("stage-run");
    }
    this._hideShakeStageChrome();
    this.setPlayerHudVisible(true);
    this.setStageStatus("fall");
    this.updateHpUrgency((this.savedFlaskHp / this.flaskMaxHp) * 100);
    this.setEvolutionDockVisible(true);
    this.setDebugStripVisible(!!C.SHOW_DEBUG_STRIP);
    this.applyRunHudBounds();
    this.populateEvolutionChrome();
    var fallSys = this.getFlaskSystem();
    if (fallSys && typeof fallSys.setMouthSealed === "function") {
      fallSys.setMouthSealed(true);
    }

    this.syncFallMovementHint(false);

    var RunPhaseCtor = FallPhase;
    if (!RunPhaseCtor) {
      console.error("FallPhase not loaded");
      return;
    }
    var runConfig = buildFallRunConfig({
      cycleIndex: this.currentCycleIndex,
      playerHp: this.savedFlaskHp,
      fallStartSource: this._analyticsFallStartSource || "menu",
      durationScale: this.getDevFallSpeedMultiplier(),
    });
    var self = this;
    this.boostBar = [];
    this._ensureSkyDecorSeed();
    this.updateBoostBarUI();
    if (this._isVerticalLayout()) {
      this.applyLayoutChromeBounds();
      this._syncVerticalStageChromePanels();
    }
    this.runPhase = new RunPhaseCtor(this.canvas, this.ctx, this.onRunComplete.bind(this), {
      runConfig: runConfig,
      showMovementHint: this._isFirstFallCycleOfRun(),
      getShowMovementRange: function () {
        return !self._isVerticalLayout();
      },
      runWidth: this.layout.runWidth,
      runHeight: this.layout.runHeight,
      runOriginX: (this.layout.runBounds && this.layout.runBounds.x != null) ? this.layout.runBounds.x : 0,
      runOriginY: (this.layout.runBounds && this.layout.runBounds.y != null) ? this.layout.runBounds.y : 0,
      canvasWidth: this.layout.canvasWidth,
      canvasHeight: this.layout.canvasHeight,
      scale: this.layout.scale,
      mockupScale: this.layout.mockupScale,
      onCatchToStash: function (tier) {
        self.addToBoostBar(tier);
      },
      getCssToGameScale: function () {
        var s = self.viewportScale || 1;
        return s > 0 ? 1 / s : 1;
      },
      clientXToBoardX: function (clientX) {
        return self.clientXToBoardX(clientX);
      },
      getInitialPointerClientX: function () {
        return self.getLastPointerClientX();
      },
      onFlaskShattered: this.onRunFlaskShattered.bind(this),
      onBoulderHit: function () {
        self._trackAnalytics(AnalyticsEvents.BOULDER_HIT);
      },
      onBoulderBlocked: function () {
        self._trackAnalytics(AnalyticsEvents.BOULDER_BLOCKED);
      },
      onArrowKey: function (key) {
        self._recordStageKeyboardKey(key);
      },
      canOfferRevive: this.canOfferRevive.bind(this),
      onReviveOffer: this._onFallReviveOffer.bind(this),
      onReviveCompleted: this._onFallReviveCompleted.bind(this),
      onGameplayStart: this._beginAnalyticsPlayTime.bind(this),
      onGameplayInput: this._onFallGameplayInput.bind(this),
      getFlaskCount: function () { return self.boostBar.length; },
      onUpdate: function (status, frame) {
        self.updateHud(status);
        if (frame && frame.currentTime != null) {
          self._tickSkyAutoProbe(frame.currentTime, frame.deltaMs || 0);
          self._tickPerfMonitor(frame.currentTime, "fall", {
            frameMs: frame.deltaMs || 0,
            bodies: 0,
          });
        }
      },
      onFrameWork: function (payload) {
        payload = payload || {};
        self._updateDevFps(payload.currentTime, payload.cpuMs, {
          sky: payload.skyMs,
          phys: payload.physMs,
          draw: payload.drawMs,
        });
      },
      onDrawSky: function (ctx, currentTime) {
        self.drawSkySeaBackgroundLayer(ctx, currentTime);
        self.updateCelestialTransition(currentTime);
        self._updateFallCatcherFlaskIntroAlpha(currentTime);
      },
      playfield: this.pixi || null,
      onPresent: function () {
        self._presentPlayfield();
      },
      getCatcherFlaskIntroAlpha: function () {
        return self.getCatcherFlaskIntroAlpha();
      },
      onMovementHintVisibleChange: function (visible) {
        self.syncFallMovementHint(visible);
      },
      onDrawFlask: this.drawFlaskPanel.bind(this),
      onDrawFlaskPhysicsDebug: this.drawFallFlaskPhysicsDebug.bind(this),
      onDrawStructuralAmbient: this.drawWorkspaceStructuralAmbient.bind(this),
      pickSpawnTier: this.pickSpawnTier.bind(this),
      startingHp: this.savedFlaskHp,
    });
    this.runPhase.start();
    this._reconcileFallSimPause();
    this._syncPlatformGameplay();
    this.syncBackgroundMusic();
    this.saveProgressState("fall");
    var fallSource = this._analyticsFallStartSource || "menu";
    this._trackFallStart(fallSource);
    this._pendingFallCountdownSource = fallSource;
    this._analyticsFallStartSource = null;
    // Fall uses FallPhase's RAF, not gameLoop — refresh the Loss/Revive enablement here.
    this.syncDevTestBar();
  };

  Game.prototype.drawShake = function () {
    if (!this.ctx || !this.layout) return;
    this._beginPlayfieldFrame();
    if (this.pixi) this.pixi.clearWorldClip();
    if (this._shakeUiBoundsDirty) this.applyShakeUIBounds();
    if (this.canvasCssWidth !== this.layout.canvasWidth || this.canvasCssHeight !== this.layout.canvasHeight) {
      this.applyCanvasSize(this.layout.canvasWidth, this.layout.canvasHeight);
    }
    var now = this._getShakeDrawTime(this._devNowMs());
    this.updateCelestialTransition(now);
    this._updateShakeFlaskIntroAlpha(now);
    var skyStart = this._isDevMode() ? this._devNowMs() : 0;
    this.drawSkySeaBackgroundLayer(this.ctx, now);
    if (this._isDevMode()) this._devShakeSkyMs = this._devNowMs() - skyStart;
    var drawStart = this._isDevMode() ? this._devNowMs() : 0;
    this.ctx.save();
    if (this.stageContentAlpha < 1) this.ctx.globalAlpha = this.stageContentAlpha;
    // No bottom evolution dock in night.html (evo ring is in the left column) — its
    // lifted-surface backdrop washed out the night sea, so skip it in Shake.
    // night.html shows the next drop at the jar mouth (DOM .jar-drop-queue), not a
    // separate canvas NEXT tube column. Skip the legacy tube; the reserved gap is
    // now transparent night sky.
    this.drawFlaskPanel(this.ctx, this.savedFlaskHp);
    this.drawWorkspaceStructuralAmbient(this.ctx);
    if (this._isDropInputActive()) {
      this.drawDropMode();
    }
    if (this.isMinimizeMode) {
      this.drawMinimizeAimOverlay();
    }
    this.ctx.restore();
    this.drawShakeHeartsOverlay(now);
    if (this._isDevMode()) this._devShakeDrawMs = this._devNowMs() - drawStart;
    this._presentPlayfield();
  };

  Game.prototype._clearShakeHpHideTimers = function () {
    if (this._shakeHpHideTimeout) {
      clearTimeout(this._shakeHpHideTimeout);
      this._shakeHpHideTimeout = null;
    }
    if (this._shakeHpFadeTimeout) {
      clearTimeout(this._shakeHpFadeTimeout);
      this._shakeHpFadeTimeout = null;
    }
    this._shakeHpFadeStart = null;
  };

  Game.prototype._setShakeHpRowDomVisible = function (visible) {
    var row = this.dom && this.dom.shakeHpRow;
    if (!row || !row.classList) return;
    row.classList.toggle("hidden", !visible);
    if (typeof row.setAttribute === "function") {
      row.setAttribute("aria-hidden", visible ? "false" : "true");
    }
  };

  Game.prototype._lockShakeHpRowHiddenForBridge = function () {
    this._clearShakeHpHideTimers();
    this.shakeHpBarAlpha = 0;
    this._setShakeHpRowDomVisible(false);
    var row = this.dom && this.dom.shakeHpRow;
    if (row && row.style && row.style.removeProperty) {
      row.style.removeProperty("opacity");
    } else if (row && row.style) {
      row.style.opacity = "";
    }
  };

  Game.prototype._syncShakeHpBarVisibility = function () {
    if (!this.stateMachine || !this.stateMachine.is || !this.stateMachine.is("shake")) return;
    var maxHp = Math.max(1, this.flaskMaxHp);
    var hp = Math.max(0, Math.min(maxHp, Math.floor(Number(this.savedFlaskHp) || 0)));
    if (hp < maxHp) {
      this._shakeHpWasDamagedThisStage = true;
      this._clearShakeHpHideTimers();
      this.shakeHpBarAlpha = 1;
      this._setShakeHpRowDomVisible(true);
      this._syncShakeHpPillState();
      return;
    }
    if (!this._shakeHpWasDamagedThisStage) {
      this._clearShakeHpHideTimers();
      this.shakeHpBarAlpha = 0;
      this._setShakeHpRowDomVisible(false);
      this._syncShakeHpPillState();
      return;
    }
    this._setShakeHpRowDomVisible(true);
    if (!this._shakeHpHideTimeout && !this._shakeHpFadeStart) {
      this._scheduleShakeHpHideAfterFull();
    }
    this._syncShakeHpPillState();
  };

  Game.prototype._scheduleShakeHpHideAfterFull = function () {
    if (this._shakeHpHideTimeout || this._shakeHpFadeStart) return;
    var self = this;
    var delay = (C.SHAKE_HP_HIDE_AFTER_FULL_MS != null) ? Number(C.SHAKE_HP_HIDE_AFTER_FULL_MS) : 2000;
    this._shakeHpHideTimeout = setTimeout(function () {
      self._shakeHpHideTimeout = null;
      self._fadeOutShakeHpBar();
    }, Math.max(0, delay));
  };

  Game.prototype._fadeOutShakeHpBar = function () {
    this._shakeHpFadeStart = Date.now();
    this._shakeHpFadeFrom = this.shakeHpBarAlpha != null ? this.shakeHpBarAlpha : 1;
    var self = this;
    var fadeMs = (C.SHAKE_HP_FADE_OUT_MS != null) ? Number(C.SHAKE_HP_FADE_OUT_MS) : 200;
    if (this._shakeHpFadeTimeout) clearTimeout(this._shakeHpFadeTimeout);
    this._shakeHpFadeTimeout = setTimeout(function () {
      self._shakeHpFadeTimeout = null;
      self.shakeHpBarAlpha = 0;
      self._shakeHpFadeStart = null;
      self._setShakeHpRowDomVisible(false);
    }, Math.max(50, fadeMs) + 20);
  };

  Game.prototype._updateShakeHpBarFade = function () {
    if (!this._shakeHpFadeStart) return;
    var fadeMs = (C.SHAKE_HP_FADE_OUT_MS != null) ? Number(C.SHAKE_HP_FADE_OUT_MS) : 200;
    var t = (Date.now() - this._shakeHpFadeStart) / Math.max(1, fadeMs);
    var from = this._shakeHpFadeFrom != null ? this._shakeHpFadeFrom : 1;
    this.shakeHpBarAlpha = Math.max(0, from * (1 - Math.min(1, t)));
  };

  Game.prototype._startDropQueuePanelFade = function () {
    if (this._dropQueueFadeStart) return;
    this._dropQueueFadeStart = Date.now();
    this._dropQueueFadeFrom = this.dropQueuePanelAlpha != null ? this.dropQueuePanelAlpha : 1;
  };

  Game.prototype._updateDropQueuePanelFade = function () {
    if (!this._dropQueueFadeStart) return;
    var fadeMs = (C.DROP_QUEUE_PANEL_FADE_MS != null) ? Number(C.DROP_QUEUE_PANEL_FADE_MS) : 500;
    var t = (Date.now() - this._dropQueueFadeStart) / Math.max(1, fadeMs);
    var from = this._dropQueueFadeFrom != null ? this._dropQueueFadeFrom : 1;
    this.dropQueuePanelAlpha = Math.max(0, from * (1 - Math.min(1, t)));
    if (t >= 1) {
      this._dropQueueFadeStart = null;
      this.dropQueuePanelAlpha = 0;
    }
  };

  Game.prototype.drawShakeHeartsOverlay = function (currentTime) {
    if (this.dom && this.dom.shakeHpRow && !this.dom.shakeHpRow.classList.contains("hidden")) {
      this._updateShakeHpBarFade();
      this._syncShakeHpPillState();
      return;
    }
    if (!this.ctx || !this.layout) return;
    this._updateShakeHpBarFade();
    if (this.shakeHpBarAlpha <= 0.01) return;
    var fb = this.layout.flaskBounds;
    var origin = this.layout.flaskPanelOrigin;
    if (!fb || !origin) return;
    var maxHp = Math.max(1, this.flaskMaxHp);
    var hp = Math.max(0, Math.min(maxHp, Math.floor(Number(this.savedFlaskHp) || 0)));
    if (hp >= maxHp && !this._shakeHpHideTimeout && !this._shakeHpFadeStart) return;
    var cx = origin.x + (fb.left + fb.right) / 2;
    var cy = origin.y + fb.top - 14;
    this.ctx.save();
    this.ctx.globalAlpha = this.shakeHpBarAlpha;
    drawHeartsBar(this.ctx, cx, cy, maxHp, hp, { size: 16, gap: 5 });
    this.ctx.restore();
  };

  Game.prototype.drawFallHeartsOverlay = function (ctx) {
    if (!ctx || !this.layout || !this.runPhase || !this.runPhase.player) return;
    var now = typeof performance !== "undefined" ? performance.now() : Date.now();
    this.syncFallHpRow(now);
    if (this.dom && this.dom.fallHpRow && !this.dom.fallHpRow.classList.contains("hidden")) {
      return;
    }
    if (this._fallBridgeChromeLocked) return;
    var overlayAlpha = this.getFallHpRowAlpha(now) * this._getFallHpStageChromeAlpha(now);
    if (overlayAlpha <= 0) return;
    var player = this.runPhase.player;
    var dims = this._getFallCatcherFlaskDims();
    if (!dims) return;
    // FallPhase translates the renderer by (runOriginX, 0), so run-local y maps straight to screen y.
    var ox = (this.layout.runBounds && this.layout.runBounds.x != null) ? this.layout.runBounds.x : 0;
    var cx = ox + player.x;
    var size = 14;
    var y = dims.flaskTopY + dims.flaskH + this._getFallHpBelowFlaskGapPx() + size * 0.35;
    var maxHp = Math.max(1, Math.floor(Number(this.flaskMaxHp) || 3));
    var hp = Math.max(0, Math.min(maxHp, Math.floor(Number(player.hp) || 0)));
    ctx.save();
    ctx.globalAlpha = overlayAlpha;
    drawHeartsBar(ctx, cx, y, maxHp, hp, { size: size, gap: 4 });
    ctx.restore();
  };

  Game.prototype.drawFallStashOverlay = function () {
    /* DOM stash panel replaces canvas stack (`fallStashPanel`). */
  };

  Game.prototype._setDropTouchChromeActive = function (active) {
    var app = typeof document !== "undefined" ? document.getElementById("app") : null;
    if (app) app.classList.toggle("drop-touch-aim-active", !!active);
  };

  Game.prototype._bindDropModeInputHandlers = function () {
    var self = this;
    var canvas = this.canvas;

    this._dropKeyDownHandler = function (e) {
      if (!self._isDropInputActive()) return;
      var target = e && e.target;
      if (target) {
        var tag = (target.tagName || "").toLowerCase();
        if (tag === "input" || tag === "textarea" || tag === "select" || target.isContentEditable) return;
      }
      var key = e.key;
      var code = e.code;
      if (key === "ArrowLeft" || code === "ArrowLeft") {
        self._dropKeyLeft = true;
        self._recordStageKeyboardKey("left");
        self._syncDropAimKeyHold(e);
        e.preventDefault();
      } else if (key === "ArrowRight" || code === "ArrowRight") {
        self._dropKeyRight = true;
        self._recordStageKeyboardKey("right");
        self._syncDropAimKeyHold(e);
        e.preventDefault();
      } else if ((key === "ArrowDown" || code === "ArrowDown") && !e.repeat) {
        self._recordStageKeyboardKey("down");
        if (self.dropCurrent) {
          self._dropCurrentItem();
        }
        e.preventDefault();
      }
    };

    this._dropKeyUpHandler = function (e) {
      if (!self._isDropInputActive()) return;
      var key = e.key;
      var code = e.code;
      if (key === "ArrowLeft" || code === "ArrowLeft") {
        self._dropKeyLeft = false;
        self._syncDropAimKeyHold(e);
        e.preventDefault();
      } else if (key === "ArrowRight" || code === "ArrowRight") {
        self._dropKeyRight = false;
        self._syncDropAimKeyHold(e);
        e.preventDefault();
      }
    };

    if (typeof window !== "undefined") {
      window.addEventListener("keydown", this._dropKeyDownHandler, true);
      window.addEventListener("keyup", this._dropKeyUpHandler, true);
    }

    function setDropAimFromClientX(clientX) {
      var layout = self.layout || self.getLayout();
      var panelX = self.clientXToBoardX(clientX) - layout.flaskPanelOrigin.x;
      self._setDropCurrentX(panelX);
    }

    this._dropMoveHandler = function (e) {
      if (!self._isDropInputActive() || !self.dropCurrent) return;
      if (self._dropKeyLeft || self._dropKeyRight) return;
      setDropAimFromClientX(e.clientX);
    };

    function isInteractiveDropTarget(t) {
      return !!(t && t.closest && t.closest(
        'button, a, input, select, textarea, label, [role="button"], [data-no-drop]'
      ));
    }

    function clearDropTouchGesture() {
      self._dropTouchGestureActive = false;
      self._dropTouchStartedInFlask = false;
      self._dropTouchPointerId = null;
    }

    function isActiveDropTouchPointer(e) {
      return self._dropTouchPointerId == null || e.pointerId === self._dropTouchPointerId;
    }

    function suppressClickAfterTouch(ms) {
      var dur = ms != null ? ms : 700;
      self._dropTouchClickSuppressUntil = Date.now() + Math.max(0, dur);
    }

    this._dropClickHandler = function (e) {
      if (self._dropTouchClickSuppressUntil && Date.now() < self._dropTouchClickSuppressUntil) return;
      if (!self._isDropInputActive() || !self.dropCurrent) return;
      if (isInteractiveDropTarget(e.target)) return;
      var pt = self._panelPointFromClient(e.clientX, e.clientY);
      if (!self._isPanelPointInDropZone(pt.panelX, pt.panelY)) return;
      setDropAimFromClientX(e.clientX);
      self._dropCurrentItem();
      e.preventDefault();
    };

    this._dropPointerDownHandler = function (e) {
      if (e.pointerType !== "touch") return;
      if (!self._isDropInputActive() || !self.dropCurrent) return;
      if (isInteractiveDropTarget(e.target)) return;
      var pt = self._panelPointFromClient(e.clientX, e.clientY);
      if (!self._isPanelPointInDropZone(pt.panelX, pt.panelY)) return;
      self._dropTouchGestureActive = true;
      self._dropTouchStartedInFlask = true;
      self._dropTouchPointerId = e.pointerId;
      suppressClickAfterTouch();
      setDropAimFromClientX(e.clientX);
      e.preventDefault();
    };

    this._dropPointerMoveHandler = function (e) {
      if (e.pointerType !== "touch") return;
      if (!self._dropTouchGestureActive || !isActiveDropTouchPointer(e)) return;
      if (!self._isDropInputActive() || !self.dropCurrent) {
        clearDropTouchGesture();
        return;
      }
      setDropAimFromClientX(e.clientX);
      e.preventDefault();
    };

    this._dropPointerUpHandler = function (e) {
      if (e.pointerType !== "touch") return;
      if (!self._dropTouchGestureActive || !isActiveDropTouchPointer(e)) return;
      var startedInFlask = self._dropTouchStartedInFlask;
      clearDropTouchGesture();
      if (!startedInFlask) return;
      if (!self._isDropInputActive() || !self.dropCurrent) return;
      if (isInteractiveDropTarget(e.target)) return;
      suppressClickAfterTouch();
      self._dropCurrentItem();
      e.preventDefault();
    };

    this._dropPointerCancelHandler = function (e) {
      if (e.pointerType !== "touch") return;
      if (!self._dropTouchGestureActive || !isActiveDropTouchPointer(e)) return;
      clearDropTouchGesture();
      suppressClickAfterTouch();
      e.preventDefault();
    };

    this._dropContextMenuHandler = function (e) {
      if (!self._isDropInputActive() || !self.dropCurrent) return;
      if (isInteractiveDropTarget(e.target)) return;
      e.preventDefault();
    };

    this._dropTouchStartHandler = function (e) {
      if (!self._isDropInputActive() || !self.dropCurrent) return;
      var touch = e.touches && e.touches[0];
      if (!touch) return;
      if (isInteractiveDropTarget(e.target)) return;
      var pt = self._panelPointFromClient(touch.clientX, touch.clientY);
      if (!self._isPanelPointInDropZone(pt.panelX, pt.panelY)) return;
      e.preventDefault();
    };

    if (typeof window !== "undefined") {
      var pointerOpts = { capture: true, passive: false };
      window.addEventListener("mousemove", this._dropMoveHandler);
      window.addEventListener("click", this._dropClickHandler);
      window.addEventListener("touchstart", this._dropTouchStartHandler, pointerOpts);
      window.addEventListener("pointerdown", this._dropPointerDownHandler, pointerOpts);
      window.addEventListener("pointermove", this._dropPointerMoveHandler, pointerOpts);
      window.addEventListener("pointerup", this._dropPointerUpHandler, pointerOpts);
      window.addEventListener("pointercancel", this._dropPointerCancelHandler, pointerOpts);
      window.addEventListener("contextmenu", this._dropContextMenuHandler, pointerOpts);
    }
    this._setDropTouchChromeActive(true);
  };

  Game.prototype._hasPendingDropModeToResume = function () {
    if (!this.isDropMode) return false;
    if (this.dropCurrent) return true;
    return !!(this.dropQueue && this.dropQueue.length > 0);
  };

  Game.prototype._resumeDropModeFromSave = function () {
    this.isDropMode = true;
    this.dropQueueEverShown = !!(this.dropQueue && this.dropQueue.length) || !!this.dropCurrent;
    this.dropQueuePanelAlpha = this.dropQueueEverShown ? 1 : 0;
    this._dropQueueFadeStart = null;
    this.dropHandoffUntil = 0;
    this.dropChargeUntil = 0;
    this._lastDropPanelX = this.dropCurrent ? this.dropCurrent.panelX : null;
    this._dropKeyLeft = false;
    this._dropKeyRight = false;
    this._bindDropModeInputHandlers();
    this._syncContinueBtnState();
    if (this.dropCurrent) {
      this._setDropCurrentX(this.dropCurrent.panelX);
    } else if (this.dropQueue && this.dropQueue.length > 0) {
      this._advanceDropQueue();
    }
    this._maybeShowShakeDropHint();
  };

  Game.prototype.initDropMode = function (tiers) {
    this._cleanupDropMode();
    this.isDropMode = true;
    this.dropQueue = tiers.slice();
    this.dropCurrent = null;
    this.dropQueueEverShown = this.dropQueue.length > 0;
    this.dropQueuePanelAlpha = this.dropQueueEverShown ? 1 : 0;
    this._dropQueueFadeStart = null;
    this.dropHandoffUntil = 0;
    this.dropChargeUntil = 0;
    this._lastDropPanelX = null;
    this._dropKeyLeft = false;
    this._dropKeyRight = false;
    this._bindDropModeInputHandlers();
    this._syncContinueBtnState();
    this._advanceDropQueue();
    this._maybeShowShakeDropHint();
  };

  /** Drop preview X is in flask-panel space (same coords as Matter bodies). Must match spawn/clamp so the guide line matches where the piece lands (inner wall ± radius). */
  Game.prototype._getDropModeHorizontalClamp = function (tier) {
    var layout = this.layout || this.getLayout();
    var sys = this.flaskSystem;
    var Wp = (layout && layout.flaskPanelWidth > 0) ? layout.flaskPanelWidth : 400;
    var r = (sys && sys.bodyRadiusByTier && sys.bodyRadiusByTier[tier] != null) ? sys.bodyRadiusByTier[tier] : 10;
    var b = layout && layout.flaskBounds;
    if (sys && b && b.left != null && b.right != null) {
      sys.ensureEngine();
      var Wt = sys._flaskGeometry && sys._flaskGeometry.W != null ? sys._flaskGeometry.W : 0;
      var minX = b.left + Wt + r;
      var maxX = b.right - Wt - r;
      if (maxX > minX) return { minX: minX, maxX: maxX };
    }
    var minX = r;
    var maxX = Wp - r;
    if (maxX <= minX) {
      var c = Wp / 2;
      return { minX: c, maxX: c };
    }
    return { minX: minX, maxX: maxX };
  };

  Game.prototype._setDropCurrentX = function (panelX) {
    if (!this.dropCurrent) return;
    var tier = this.dropCurrent.tier;
    var lo = this._getDropModeHorizontalClamp(tier);
    this.dropCurrent.panelX = Math.max(lo.minX, Math.min(lo.maxX, panelX));
  };

  Game.prototype._getDropAimKeyDirection = function () {
    return (this._dropKeyRight ? 1 : 0) - (this._dropKeyLeft ? 1 : 0);
  };

  Game.prototype._syncDropAimKeyHold = function (e) {
    var dir = this._getDropAimKeyDirection();
    if (dir === 0) {
      this._dropAimKeyHoldDir = 0;
      this._dropAimKeyHeldSinceMs = null;
      return;
    }
    if (dir !== this._dropAimKeyHoldDir) {
      var ts = e && e.timeStamp;
      this._dropAimKeyHeldSinceMs = ts != null && ts > 0
        ? ts
        : (typeof performance !== "undefined" ? performance.now() : 0);
      this._dropAimKeyHoldDir = dir;
    }
  };

  Game.prototype._clearDropAimKeyboardHold = function () {
    this._dropAimKeyHoldDir = 0;
    this._dropAimKeyHeldSinceMs = null;
  };

  /** Horizontal aim from held ArrowLeft / ArrowRight while a boost-queue piece is active. */
  Game.prototype._applyDropModeKeyboardAim = function (deltaMs, currentTime) {
    if (!this._isDropInputActive() || !this.dropCurrent) return;
    var dir = this._getDropAimKeyDirection();
    if (dir === 0) return;
    if (this._dropAimKeyHeldSinceMs == null) {
      this._syncDropAimKeyHold({ timeStamp: currentTime });
    }
    var baseSpeed = C.DROP_AIM_KEYBOARD_SPEED != null ? Number(C.DROP_AIM_KEYBOARD_SPEED) : 350;
    if (!isFinite(baseSpeed) || baseSpeed < 0) baseSpeed = 350;
    var flask = this.getFlaskSystem && this.getFlaskSystem();
    var aimScale = (flask && flask.bounds) ? getFlaskPanelScale(flask.bounds) : 1;
    if (!isFinite(aimScale) || aimScale <= 0) aimScale = 1;
    baseSpeed *= aimScale;
    var cfg = C.RUN_CONFIG || {};
    var minRatio = cfg.PLAYER_KEYBOARD_MOVE_SPEED_MIN_RATIO != null
      ? cfg.PLAYER_KEYBOARD_MOVE_SPEED_MIN_RATIO
      : 1;
    var maxRatio = cfg.PLAYER_KEYBOARD_MOVE_SPEED_MAX_RATIO != null
      ? cfg.PLAYER_KEYBOARD_MOVE_SPEED_MAX_RATIO
      : 2;
    var rampMs = cfg.PLAYER_KEYBOARD_MOVE_RAMP_MS != null
      ? cfg.PLAYER_KEYBOARD_MOVE_RAMP_MS
      : 1000;
    var now = currentTime != null
      ? currentTime
      : (typeof performance !== "undefined" ? performance.now() : 0);
    var speed = getKeyboardRampSpeed(
      baseSpeed * minRatio,
      baseSpeed * maxRatio,
      rampMs,
      this._dropAimKeyHeldSinceMs,
      now
    );
    var dx = speed * (deltaMs / 1000) * dir;
    this._setDropCurrentX(this.dropCurrent.panelX + dx);
  };

  Game.prototype._advanceDropQueue = function () {
    if (this._dropAdvanceTimeout) {
      clearTimeout(this._dropAdvanceTimeout);
      this._dropAdvanceTimeout = null;
    }
    if (this.dropQueue.length === 0) {
      if (this.dropQueueEverShown) this._startDropQueuePanelFade();
      this.dropCurrent = null;
      var self = this;
      var settledCount = 0;
      var maxChecks = 30;
      if (this._dropSettleInterval) clearInterval(this._dropSettleInterval);
      this._dropSettleInterval = setInterval(function () {
        maxChecks--;
        if (maxChecks <= 0) {
          clearInterval(self._dropSettleInterval);
          self._dropSettleInterval = null;
          self._onDropQueueSettled();
          return;
        }
        var sys = self.flaskSystem;
        var allStill = true;
        if (sys && sys.bodies && sys.bodies.length > 0) {
          for (var i = 0; i < sys.bodies.length; i++) {
            var b = sys.bodies[i].body;
            if (!b || !b.velocity) continue;
            var vx = b.velocity.x, vy = b.velocity.y;
            if (Math.sqrt(vx * vx + vy * vy) > 0.8) { allStill = false; break; }
          }
        }
        if (allStill) {
          settledCount++;
          if (settledCount >= 3) {
            clearInterval(self._dropSettleInterval);
            self._dropSettleInterval = null;
            self._onDropQueueSettled();
          }
        } else {
          settledCount = 0;
        }
      }, 200);
      return;
    }
    var tier = this.dropQueue.shift();
    if (this.dropQueue.length === 0 && this.dropQueueEverShown) {
      this._startDropQueuePanelFade();
    }
    var layout = this.layout;
    var lo = this._getDropModeHorizontalClamp(tier);
    var centerX = (lo.minX + lo.maxX) / 2;
    var preferredX = centerX;
    if (this._lastDropPanelX != null) {
      preferredX = this._lastDropPanelX;
    } else {
      var clientX = this.getLastPointerClientX();
      if (clientX != null) {
        var originX = layout && layout.flaskPanelOrigin ? layout.flaskPanelOrigin.x : 0;
        preferredX = this.clientXToBoardX(clientX) - originX;
      }
    }
    var clampedX = Math.max(lo.minX, Math.min(lo.maxX, preferredX));
    this.dropCurrent = { tier: tier, panelX: clampedX };
    var qm = this._getDropQueueLayoutMetrics();
    var fb = layout && layout.flaskBounds;
    this.dropHandoffFromY = qm && qm.rimPanelY != null
      ? qm.rimPanelY - (qm.previewR || 20) * 1.6
      : this._getDropSpawnPanelY() - 28;
  };

  Game.prototype._dropCurrentItem = function () {
    if (!this._isDropInputActive() || !this.dropCurrent || !this._isDropCurrentReady()) return;
    if (this._shakeDropHintActive) this._dismissShakeDropHint();
    this._armShakeHintSettle();
    var tier = this.dropCurrent.tier;
    var panelX = this.dropCurrent.panelX;
    var sys = this.getFlaskSystem();
    if (sys) {
      var spawnY = this._getDropAimSpawnPanelY(tier);
      sys.addBodyAt(tier, panelX, spawnY, { dropLanding: true });
    }
    if (typeof this.updateShakeUI === "function") this.updateShakeUI();
    if (EventBus && typeof EventBus.emit === "function") {
      EventBus.emit("shake:drop", { tier: tier, panelX: panelX });
    }
    this._lastDropPanelX = panelX;
    var now = Date.now();
    if (this.dropQueue.length === 0) {
      this.dropCurrent = null;
      this.dropChargeUntil = 0;
      this._syncContinueBtnState();
      if (this.isDropMode) this._advanceDropQueue();
    } else if (this.isDropMode) {
      this._advanceDropQueue();
      var chargeMs = this._getDropChargeMs();
      this.dropChargeUntil = now + chargeMs;
      this.dropHandoffUntil = this.dropChargeUntil;
      this.dropAppearPulseStartedAt = now;
    }
  };

  Game.prototype._onDropQueueSettled = function () {
    this._cleanupDropMode();
    this.isDropMode = false;
    this._resealFlaskMouthAfterDropMode();
    this._syncContinueBtnState();
    if (typeof this.updateShakeUI === "function") this.updateShakeUI();
  };

  /** Drop mode opens the mouth for entry; re-seal once the queue is done so shake can use headroom. */
  Game.prototype._resealFlaskMouthAfterDropMode = function () {
    if (!(this.stateMachine && this.stateMachine.is && this.stateMachine.is("shake"))) return;
    var sys = this.getFlaskSystem();
    if (sys && typeof sys.setMouthSealed === "function") {
      sys.setMouthSealed(true);
    }
  };

  /**
   * Random X for one auto-poured piece, biased away from where the last one landed so a
   * fast pour spreads across the flask instead of stacking one tower.
   * @param {number} tier
   * @returns {number} panel-space X
   */
  Game.prototype._pickDropAllPanelX = function (tier) {
    var lo = this._getDropModeHorizontalClamp(tier);
    var width = lo.maxX - lo.minX;
    if (!(width > 0)) return lo.minX;

    var sys = this.getFlaskSystem();
    var radius = (sys && sys.bodyRadiusByTier && sys.bodyRadiusByTier[tier] != null)
      ? sys.bodyRadiusByTier[tier]
      : 10;
    var prev = this._dropAllLastX;
    // Too narrow to space anything out (big tiers can collapse to a sliver) — take any X.
    if (prev == null || width < radius * 4) return lo.minX + Math.random() * width;

    var frac = C.SHAKE_DROP_ALL_MIN_SEPARATION_FRAC != null
      ? Number(C.SHAKE_DROP_ALL_MIN_SEPARATION_FRAC)
      : 0.35;
    if (!isFinite(frac) || frac <= 0) frac = 0.35;
    var minSep = width * frac;
    var samples = C.SHAKE_DROP_ALL_CANDIDATE_SAMPLES != null
      ? Math.max(1, Math.floor(Number(C.SHAKE_DROP_ALL_CANDIDATE_SAMPLES)))
      : 8;

    var best = null;
    var bestDist = -1;
    for (var i = 0; i < samples; i++) {
      var candidate = lo.minX + Math.random() * width;
      var dist = Math.abs(candidate - prev);
      if (dist >= minSep) return candidate;
      if (dist > bestDist) {
        bestDist = dist;
        best = candidate;
      }
    }
    // Nothing cleared the gap — keep the farthest sample rather than retrying forever.
    return best != null ? best : lo.minX + Math.random() * width;
  };

  /** Pour every queued piece into the flask, one per SHAKE_DROP_ALL_INTERVAL_MS. */
  Game.prototype.dropAllQueued = function () {
    if (this._dropAllActive) return;
    if (!this._hasPendingDropPieces()) return;

    var queued = (this.dropQueue ? this.dropQueue.length : 0) + (this.dropCurrent ? 1 : 0);
    this._trackAnalytics(AnalyticsEvents.DROP_ALL, { queue_size: queued });

    this._dropAllActive = true;
    this._dropAllLastX = null;
    this._dismissShakeDropHint();
    this._hideDropQueueUI();
    this._syncContinueBtnState();

    var intervalMs = C.SHAKE_DROP_ALL_INTERVAL_MS != null
      ? Number(C.SHAKE_DROP_ALL_INTERVAL_MS)
      : 200;
    if (!isFinite(intervalMs) || intervalMs < 0) intervalMs = 200;

    var self = this;
    this._dropAllStep();
    if (!this._dropAllActive) return;
    this._dropAllTimer = setInterval(function () {
      self._dropAllStep();
    }, intervalMs);
  };

  /** One poured piece; finishes the pour when the queue runs dry. */
  Game.prototype._dropAllStep = function () {
    if (!this._dropAllActive) return;

    var tier = null;
    if (this.dropCurrent && this.dropCurrent.tier != null) {
      tier = this.dropCurrent.tier;
      this.dropCurrent = null;
    } else if (this.dropQueue && this.dropQueue.length > 0) {
      tier = this.dropQueue.shift();
    }
    if (tier == null) {
      this._finishDropAll();
      return;
    }

    var panelX = this._pickDropAllPanelX(tier);
    var sys = this.getFlaskSystem();
    if (sys) sys.addBodyAt(tier, panelX, this._getDropAimSpawnPanelY(tier), { dropLanding: true });
    this._dropAllLastX = panelX;
    this._lastDropPanelX = panelX;
    if (EventBus && typeof EventBus.emit === "function") {
      EventBus.emit("shake:drop", { tier: tier, panelX: panelX });
    }
    if (typeof this.updateShakeUI === "function") this.updateShakeUI();
  };

  Game.prototype._finishDropAll = function () {
    if (this._dropAllTimer) {
      clearInterval(this._dropAllTimer);
      this._dropAllTimer = null;
    }
    this._dropAllActive = false;
    this._dropAllLastX = null;
    this.dropChargeUntil = 0;
    // Overflow was suspended for the pour; start it from a clean slate, same as any
    // other player action, so a fast pour can never hand back a flask already in alert.
    this._resetOverflowAfterPlayerAction();
    this._onDropQueueSettled();
    // _cleanupDropMode resets dropQueuePanelAlpha to 1 for the next drop mode; after a
    // Drop all the queue UI must stay gone, so re-assert the hide last.
    this._hideDropQueueUI();
  };

  /** True while pieces are still waiting to be poured or aimed into the flask. */
  Game.prototype._hasPendingDropPieces = function () {
    if (!this.isDropMode) return false;
    if (this.dropCurrent) return true;
    return !!(this.dropQueue && this.dropQueue.length > 0);
  };

  /**
   * Which job the continue button is doing right now.
   * @returns {"dropping" | "drop_all" | "next_day"}
   */
  Game.prototype._getContinueBtnMode = function () {
    if (this._dropAllActive) return "dropping";
    if (this._hasPendingDropPieces()) return "drop_all";
    return "next_day";
  };

  Game.prototype._shouldContinueBeEnabled = function () {
    if (this.isMinimizeMode) return false;
    // Overflow outranks the queue: pouring more into an alert flask only speeds the loss.
    if (isOverflowBlockingContinue(this.overflow)) return false;
    // The pour drives itself; re-pressing mid-pour would double-drain the queue.
    if (this._dropAllActive) return false;
    return true;
  };

  /** True when NEXT DAY › is enabled (keyboard N uses the same gate). */
  Game.prototype._isContinueBtnEnabled = function () {
    if (this.boostBar && this.boostBar.length > 0) return false;
    if (!this._shouldContinueBeEnabled()) return false;
    if (this.dom && this.dom.continueBtn && this.dom.continueBtn.disabled) return false;
    return true;
  };

  /** True when Shake is enabled (keyboard Q uses the same gate). */
  Game.prototype._isShakeBtnEnabled = function () {
    if (this.isMinimizeMode) return false;
    var sys = this.getFlaskSystem();
    var hasBodies = !!(sys && typeof sys.getBodyCount === "function" && sys.getBodyCount() > 0);
    if (!hasBodies) return false;
    var c = this.constants || {};
    if (this.shakeUses <= 0 && this.gemsCollected < (c.SHAKE_USE_COST != null ? c.SHAKE_USE_COST : 3)) {
      return false;
    }
    if (this.dom && this.dom.shakeBtn && this.dom.shakeBtn.disabled) return false;
    return true;
  };

  Game.prototype._syncContinueBtnOverflowAlert = function () {
    if (!this.dom) return;
    var showAlert = isOverflowAlertFlashing(this.overflow);
    var ariaLabel = showAlert
      ? "Jar overflow — next day unavailable"
      : CONTINUE_BTN_ARIA[this._getContinueBtnMode()];
    this._eachContinueBtn(function (btn) {
      if (btn.classList && btn.classList.toggle) {
        btn.classList.toggle("continue-btn--overflow-alert", showAlert);
      }
      if (typeof btn.setAttribute === "function") {
        btn.setAttribute("aria-label", ariaLabel);
      }
    });
  };

  /** Swap the button copy between NEXT DAY ›, DROP ALL and DROPPING…. */
  Game.prototype._syncContinueBtnLabel = function () {
    if (!this.dom) return;
    var mode = this._getContinueBtnMode();
    if (mode === this._continueBtnLabelMode) return;
    this._continueBtnLabelMode = mode;
    var html = CONTINUE_BTN_LABEL_HTML[mode];
    this._eachContinueBtn(function (btn) {
      var label = btn.querySelector && btn.querySelector(".continue-btn__label");
      if (label) label.innerHTML = html;
      if (btn.classList && btn.classList.toggle) {
        btn.classList.toggle("continue-btn--drop-all", mode === "drop_all");
        btn.classList.toggle("continue-btn--dropping", mode === "dropping");
      }
    });
  };

  Game.prototype._syncContinueBtnState = function () {
    this._syncContinueBtnLabel();
    this._syncContinueBtnOverflowAlert();
    this._setContinueBtnEnabled(this._shouldContinueBeEnabled());
  };

  Game.prototype._setContinueBtnEnabled = function (enabled) {
    if (!this.dom) return;
    var primaryBtn = this.dom.continueBtn || this.dom.verticalContinueBtn;
    if (!primaryBtn) return;
    var wasDisabled = !!primaryBtn.disabled;
    this._eachContinueBtn(function (btn) {
      btn.disabled = !enabled;
      if (btn.style) {
        btn.style.opacity = "";
        btn.style.pointerEvents = enabled ? "" : "none";
      }
    });
    if (!enabled) {
      this._stopContinueBtnWiggle();
      return;
    }
    var wiggleScheduled = !!(this._continueWiggleInitialTimeout || this._continueWiggleInterval);
    if (wasDisabled || !wiggleScheduled) {
      this._startContinueBtnWiggleSchedule();
    }
  };

  Game.prototype._playContinueBtnWiggle = function () {
    if (!this.dom) return;
    if (this._victoryPopupActive) return;
    var animMs = (typeof window !== "undefined" && C.SHAKE_CONTINUE_WIGGLE_ANIM_MS != null)
      ? Number(C.SHAKE_CONTINUE_WIGGLE_ANIM_MS)
      : 520;
    if (!isFinite(animMs) || animMs < 100) animMs = 520;
    var wiggleBtns = [];
    this._eachContinueBtn(function (btn) {
      if (!btn.disabled) wiggleBtns.push(btn);
    });
    if (wiggleBtns.length === 0) return;
    for (var i = 0; i < wiggleBtns.length; i++) {
      wiggleBtns[i].classList.add("continue-btn--wiggle");
    }
    if (this._continueWiggleAnimTimeout) clearTimeout(this._continueWiggleAnimTimeout);
    var self = this;
    this._continueWiggleAnimTimeout = setTimeout(function () {
      self._continueWiggleAnimTimeout = null;
      for (var j = 0; j < wiggleBtns.length; j++) {
        if (wiggleBtns[j].classList) wiggleBtns[j].classList.remove("continue-btn--wiggle");
      }
    }, animMs);
  };

  Game.prototype._startContinueBtnWiggleSchedule = function () {
    this._stopContinueBtnWiggle();
    if (!(this.stateMachine && this.stateMachine.is && this.stateMachine.is("shake"))) return;
    if (!this.dom) return;
    var primaryBtn = this.dom.continueBtn || this.dom.verticalContinueBtn;
    if (!primaryBtn || primaryBtn.disabled) return;
    var self = this;
    var initial = (typeof window !== "undefined" && C.SHAKE_CONTINUE_WIGGLE_INITIAL_DELAY_MS != null)
      ? Number(C.SHAKE_CONTINUE_WIGGLE_INITIAL_DELAY_MS)
      : 2000;
    var repeat = (typeof window !== "undefined" && C.SHAKE_CONTINUE_WIGGLE_REPEAT_MS != null)
      ? Number(C.SHAKE_CONTINUE_WIGGLE_REPEAT_MS)
      : 3000;
    if (!isFinite(initial) || initial < 0) initial = 2000;
    if (!isFinite(repeat) || repeat < 0) repeat = 3000;
    this._continueWiggleInitialTimeout = setTimeout(function () {
      self._continueWiggleInitialTimeout = null;
      var gateBtn = self.dom.continueBtn || self.dom.verticalContinueBtn;
      if (!gateBtn || gateBtn.disabled) return;
      self._playContinueBtnWiggle();
      if (repeat > 0) {
        self._continueWiggleInterval = setInterval(function () {
          var gate = self.dom.continueBtn || self.dom.verticalContinueBtn;
          if (!gate || gate.disabled) {
            self._stopContinueBtnWiggle();
            return;
          }
          self._playContinueBtnWiggle();
        }, repeat);
      }
    }, initial);
  };

  Game.prototype._clearContinueBtnWiggleVisual = function () {
    if (this._continueWiggleAnimTimeout) {
      clearTimeout(this._continueWiggleAnimTimeout);
      this._continueWiggleAnimTimeout = null;
    }
    if (this.dom) {
      this._eachContinueBtn(function (btn) {
        if (btn.classList) btn.classList.remove("continue-btn--wiggle");
      });
    }
  };

  Game.prototype._stopContinueBtnWiggle = function () {
    if (this._continueWiggleInitialTimeout) {
      clearTimeout(this._continueWiggleInitialTimeout);
      this._continueWiggleInitialTimeout = null;
    }
    if (this._continueWiggleInterval) {
      clearInterval(this._continueWiggleInterval);
      this._continueWiggleInterval = null;
    }
    this._clearContinueBtnWiggleVisual();
  };

  Game.prototype._cleanupDropMode = function () {
    this._dismissShakeDropHint();
    if (this._dropAllTimer) {
      clearInterval(this._dropAllTimer);
      this._dropAllTimer = null;
    }
    this._dropAllActive = false;
    this._dropAllLastX = null;
    if (this._dropAdvanceTimeout) {
      clearTimeout(this._dropAdvanceTimeout);
      this._dropAdvanceTimeout = null;
    }
    if (this._dropSettleInterval) {
      clearInterval(this._dropSettleInterval);
      this._dropSettleInterval = null;
    }
    if (this._dropKeyDownHandler && typeof window !== "undefined") {
      window.removeEventListener("keydown", this._dropKeyDownHandler, true);
      this._dropKeyDownHandler = null;
    }
    if (this._dropKeyUpHandler && typeof window !== "undefined") {
      window.removeEventListener("keyup", this._dropKeyUpHandler, true);
      this._dropKeyUpHandler = null;
    }
    this._dropKeyLeft = false;
    this._dropKeyRight = false;
    this._clearDropAimKeyboardHold();
    this._dropTouchGestureActive = false;
    this._dropTouchStartedInFlask = false;
    this._dropTouchPointerId = null;
    this._dropTouchClickSuppressUntil = 0;
    this._setDropTouchChromeActive(false);
    if (typeof window !== "undefined") {
      if (this._dropMoveHandler) window.removeEventListener("mousemove", this._dropMoveHandler);
      if (this._dropClickHandler) window.removeEventListener("click", this._dropClickHandler);
      if (this._dropPointerDownHandler) {
        window.removeEventListener("pointerdown", this._dropPointerDownHandler, true);
      }
      if (this._dropTouchStartHandler) {
        window.removeEventListener("touchstart", this._dropTouchStartHandler, true);
      }
      if (this._dropPointerMoveHandler) {
        window.removeEventListener("pointermove", this._dropPointerMoveHandler, true);
      }
      if (this._dropPointerUpHandler) {
        window.removeEventListener("pointerup", this._dropPointerUpHandler, true);
      }
      if (this._dropPointerCancelHandler) {
        window.removeEventListener("pointercancel", this._dropPointerCancelHandler, true);
      }
      if (this._dropContextMenuHandler) {
        window.removeEventListener("contextmenu", this._dropContextMenuHandler, true);
      }
    }
    this._dropMoveHandler = null;
    this._dropClickHandler = null;
    this._dropTouchStartHandler = null;
    this._dropPointerDownHandler = null;
    this._dropPointerMoveHandler = null;
    this._dropPointerUpHandler = null;
    this._dropPointerCancelHandler = null;
    this._dropContextMenuHandler = null;
    this.dropQueue = [];
    this.dropCurrent = null;
    this._lastDropPanelX = null;
    this.dropQueuePanelAlpha = 1;
    this.dropQueueEverShown = false;
    this._dropQueueFadeStart = null;
    this.dropHandoffUntil = 0;
    this.dropChargeUntil = 0;
    this._lastDropPreviewTier = null;
    this._dropPreviewWasCharging = false;
  };

  Game.prototype._getDropQueueLayoutMetrics = function () {
    if (!this.layout) return null;
    var layout = this.layout;
    var panelOriginY = layout.flaskPanelOrigin.y;
    var bounds = layout.flaskBounds;
    var canvasFlaskTop = panelOriginY + bounds.top;
    var nextColW = layout.shakeNextColumnWidth || 0;
    var PREVIEW_R = this._getDropQueuePreviewRadiusPx(nextColW);
    var rowGap = (C.FLASK_DROP_QUEUE_ROW_GAP_PX != null) ? Number(C.FLASK_DROP_QUEUE_ROW_GAP_PX) : 6;
    var QUEUE_SPACING = PREVIEW_R * 2 + rowGap;
    var queueColX;
    if (layout.isLandscape && nextColW > 0 && layout.runWidth != null) {
      queueColX = layout.runWidth + nextColW / 2;
    } else {
      var canvasFlaskRight = layout.flaskPanelOrigin.x + bounds.right;
      queueColX = Math.min(layout.canvasWidth - PREVIEW_R - 6, canvasFlaskRight + PREVIEW_R + 8);
    }
    var queueStartY;
    var queueBottomY;
    var rimPanelY = this._getDropSpawnPanelY();
    if (layout.isLandscape && nextColW > 0 && bounds.top != null && bounds.bottom != null) {
      queueStartY = panelOriginY + bounds.top + PREVIEW_R + 4;
      queueBottomY = panelOriginY + bounds.bottom - PREVIEW_R - 4;
    } else if (layout.isLandscape) {
      queueStartY = panelOriginY + rimPanelY;
      queueBottomY = panelOriginY + (bounds.bottom != null ? bounds.bottom : layout.runHeight) - PREVIEW_R - 4;
    } else {
      queueStartY = layout.isLandscape && nextColW > 0
        ? 54
        : Math.max(PREVIEW_R + 8, canvasFlaskTop);
      queueBottomY = layout.isLandscape && nextColW > 0
        ? layout.runHeight - 4
        : layout.canvasHeight - PREVIEW_R - 8;
    }
    return {
      queueColX: queueColX,
      queueStartY: queueStartY,
      queueBottomY: queueBottomY,
      rimPanelY: rimPanelY,
      previewR: PREVIEW_R,
      queueSpacing: QUEUE_SPACING,
    };
  };

  Game.prototype.drawDropMode = function () {
    if (!this._isDropInputActive() || !this.ctx || !this.layout) return;
    this.updateShakeJarChromeDOM();
    var useDomJarChrome = this.dom && this.dom.shakeJarChrome
      && !this.dom.shakeJarChrome.classList.contains("hidden");
    var ctx = this.ctx;
    var layout = this.layout;
    var sys = this.flaskSystem;
    var panelOriginX = layout.flaskPanelOrigin.x;
    var panelOriginY = layout.flaskPanelOrigin.y;
    var bounds = layout.flaskBounds;
    var qm = this._getDropQueueLayoutMetrics();
    if (!qm) return;
    var queueColX = qm.queueColX;
    var queueStartY = qm.queueStartY;
    var queueBottomY = qm.queueBottomY;
    var PREVIEW_R = qm.previewR;
    var QUEUE_SPACING = qm.queueSpacing;
    this._updateDropQueuePanelFade();

    var tierColors = (typeof window !== "undefined" && C.MERGE_PICKUP_COLORS) || [];
    var evolutionChain = (typeof window !== "undefined" && C.EVOLUTION_CHAIN) || [];
    var queueSlots = this._getDropQueueVisibleSlots(this.dropQueue.length, queueStartY, queueBottomY, QUEUE_SPACING);
    var visibleQueueCount = queueSlots.visible;
    var queueAlpha = this.dropQueuePanelAlpha != null ? this.dropQueuePanelAlpha : 1;

    if (!layout.isLandscape && (this.dropQueue.length > 0 || this.dropCurrent) && queueAlpha > 0.01 && !useDomJarChrome) {
      ctx.save();
      ctx.fillStyle = "rgba(255,255,255,0.55)";
      ctx.font = "bold 10px sans-serif";
      ctx.textAlign = "center";
      ctx.textBaseline = "bottom";
      ctx.fillText("NEXT", queueColX, queueStartY - PREVIEW_R - 2);
      ctx.restore();
    }

    // Draw queued items (upcoming)
    if (visibleQueueCount > 0 && queueAlpha > 0.01 && !useDomJarChrome) {
      ctx.save();
      ctx.globalAlpha = queueAlpha;
      for (var i = 0; i < visibleQueueCount; i++) {
        var qTier = this.dropQueue[i];
        var qx = queueColX;
        var qy = queueStartY + i * QUEUE_SPACING;
        var qColor = tierColors[qTier] || "#94a3b8";
        var qDrew = false;
        if (MergeAssets && MergeAssets.draw) {
          qDrew = MergeAssets.draw(ctx, qx, qy, PREVIEW_R, qTier, qColor, 0, PREVIEW_R);
        }
        if (!qDrew) {
          ctx.save();
          ctx.fillStyle = qColor;
          ctx.beginPath();
          ctx.arc(qx, qy, PREVIEW_R, 0, Math.PI * 2);
          ctx.fill();
          ctx.fillStyle = "#1e293b";
          ctx.font = "9px sans-serif";
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          var qLabel = evolutionChain[qTier] ? evolutionChain[qTier].label : "?";
          ctx.fillText(qLabel, qx, qy);
          ctx.restore();
        }
      }
      ctx.restore();
    }

    // Draw current hover item (DOM jar chrome owns guide + preview when active)
    if (this.dropCurrent && !useDomJarChrome) {
      var hCanvasX = panelOriginX + this.dropCurrent.panelX;
      var hTier = this.dropCurrent.tier;
      var rimY = this._getDropAimSpawnPanelY(hTier);
      var hRadius = (sys && sys.bodyRadiusByTier && sys.bodyRadiusByTier[hTier] != null)
        ? sys.bodyRadiusByTier[hTier]
        : 14;
      var handoffMs = (C.DROP_QUEUE_HANDOFF_MS != null) ? Number(C.DROP_QUEUE_HANDOFF_MS) : 300;
      if (this.dropHandoffUntil > Date.now()) {
        var ht = 1 - (this.dropHandoffUntil - Date.now()) / Math.max(1, handoffMs);
        var ease = 1 - Math.pow(1 - Math.min(1, ht), 2);
        rimY = this.dropHandoffFromY + (rimY - this.dropHandoffFromY) * ease;
        hRadius = PREVIEW_R * 0.55 + hRadius * 0.45 * ease;
      }
      if (this.dropAppearPulseStartedAt) {
        var appearMs = 420;
        var appearT = (Date.now() - this.dropAppearPulseStartedAt) / appearMs;
        if (appearT < 1) {
          var appearEase = 1 - Math.pow(1 - appearT, 2);
          hRadius *= 0.55 + 0.45 * appearEase;
        }
      }
      var hCanvasY = panelOriginY + rimY;
      var hColor = tierColors[hTier] || "#94a3b8";

      // Guide line from item straight down to panel floor
      var guideBottomY = panelOriginY + (bounds.bottom != null ? bounds.bottom : layout.flaskPanelHeight);
      ctx.save();
      ctx.setLineDash([7, 7]);
      ctx.strokeStyle = "rgba(255,255,255,0.22)";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(hCanvasX, hCanvasY + hRadius + 4);
      ctx.lineTo(hCanvasX, guideBottomY);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.restore();

      // Draw item sprite or fallback circle
      var hDrew = false;
      if (MergeAssets && MergeAssets.draw) {
        hDrew = MergeAssets.draw(ctx, hCanvasX, hCanvasY, hRadius, hTier, hColor, 0, hRadius);
      }
      if (!hDrew) {
        ctx.save();
        ctx.fillStyle = hColor;
        ctx.beginPath();
        ctx.arc(hCanvasX, hCanvasY, hRadius, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = "#1e293b";
        ctx.font = "10px sans-serif";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        var hLabel = evolutionChain[hTier] ? evolutionChain[hTier].label : "?";
        ctx.fillText(hLabel, hCanvasX, hCanvasY);
        ctx.restore();
      }

    }
  };

  Game.prototype._shouldRunGameLoop = function () {
    if (this.isHostPaused) return false;
    return !!(this.stateMachine && this.stateMachine.is && this.stateMachine.is("shake"));
  };

  Game.prototype._scheduleGameLoop = function () {
    if (this._gameLoopRafId != null) return;
    if (!this._shouldRunGameLoop()) return;
    if (typeof requestAnimationFrame !== "function") return;
    this._boundGameLoop = this._boundGameLoop || this.gameLoop.bind(this);
    this._gameLoopRafId = requestAnimationFrame(this._boundGameLoop);
  };

  Game.prototype._cancelGameLoop = function () {
    if (this._gameLoopRafId == null) return;
    if (typeof cancelAnimationFrame === "function") {
      cancelAnimationFrame(this._gameLoopRafId);
    }
    this._gameLoopRafId = null;
  };

  Game.prototype.gameLoop = function (frameTime) {
    this._gameLoopRafId = null;
    var workStart = typeof performance !== "undefined" ? performance.now() : Date.now();
    var now = typeof frameTime === "number"
      ? frameTime
      : (typeof performance !== "undefined" ? performance.now() : Date.now());
    if (!this.lastFrameTime) this.lastFrameTime = now;
    var rawDeltaMs = now - this.lastFrameTime;
    this.lastFrameTime = now;
    var maxFrameMs = (typeof window !== "undefined" && C.MAX_FRAME_DELTA_MS != null)
      ? C.MAX_FRAME_DELTA_MS
      : 100;
    var deltaMs = Math.max(0, Math.min(maxFrameMs, rawDeltaMs || 0));
    var mergeBodies = 0;
    var phaseSkyMs = 0;
    var phasePhysMs = 0;
    var phaseDrawMs = 0;
    if (this.ctx && this.stateMachine.is("shake")) {
      this._ensureLayout();
      if (this.layout) {
        var sys = this.getFlaskSystem();
        mergeBodies = sys ? sys.getBodyCount() : 0;
        if (!this._isGameplaySimFrozen() && sys) {
          var physStart = this._devNowMs();
          if (this.testFlaskMode && sys.getBodyCount() === 0) sys.spawnTestBodies();
          sys.update(deltaMs);
          this._applyDropModeKeyboardAim(deltaMs, now);
          if (!this._isGameplaySimFrozen()) {
            this._tickOverflow(sys, deltaMs);
          }
          phasePhysMs = this._devNowMs() - physStart;
        }
        this.drawShake();
        this._tickShakeHint(now);
        this._tickHealHint();
        if (this._devShakeSkyMs != null) phaseSkyMs = this._devShakeSkyMs;
        if (this._devShakeDrawMs != null) phaseDrawMs = this._devShakeDrawMs;
      }
    }
    var cpuMs = this._devNowMs() - workStart;
    this._updateDevFps(now, cpuMs, {
      sky: phaseSkyMs,
      phys: phasePhysMs,
      draw: phaseDrawMs,
    });
    this._tickPerfMonitor(now, "merge", { frameMs: deltaMs, bodies: mergeBodies });
    this.syncDevTestBar();
    this._scheduleGameLoop();
  };

  Game.prototype.handleResize = function () {
    if (!this.canvas) return;
    if (this._isEvolutionRevealPending()) {
      this._finishSecretRevealImmediate();
    }
    this._refreshLayout();
    this._applyLayoutModeClasses();
    this.applyLayoutChromeBounds();
    this._syncLayoutAnalytics();
    if (this.testRunwayMode && this.testRunway) {
      var cw = Math.max(100, this.canvas.clientWidth || this.canvasCssWidth || this.layout.canvasWidth || 400);
      var ch = Math.max(100, this.canvas.clientHeight || this.canvasCssHeight || this.layout.canvasHeight || 400);
      this.applyCanvasSize(cw, ch);
      this.testRunway.runWidth = cw;
      this.testRunway.runHeight = ch;
      var scale = getScale(cw, ch);
      this.testRunway.scale = scale;
      if (this.testRunway.runwayRenderer) this.testRunway.runwayRenderer.scale = scale;
      this.testRunway.render();
      return;
    }
    if (this.layout && (this.stateMachine.is("start") || this.stateMachine.is("transition") || this.stateMachine.is("shake") || this.stateMachine.is("fall"))) {
      this.applyCanvasSize(this.layout.canvasWidth, this.layout.canvasHeight);
      if (this.stateMachine.is("shake")) {
        var oldSys = this.flaskSystem;
        this.flaskSystem = null;
        var resizedShakeSys = this.getFlaskSystem();
        if (oldSys && oldSys.bodies && oldSys.bodies.length > 0) {
          this.migrateFlaskBodies(oldSys, this.layout.flaskBounds);
        }
        if (resizedShakeSys && typeof resizedShakeSys.setMouthSealed === "function") {
          resizedShakeSys.setMouthSealed(true);
        }
        if (this.testFlaskMode && resizedShakeSys && resizedShakeSys.getBodyCount() === 0) {
          resizedShakeSys.spawnTestBodies();
        }
      }
      if (this.stateMachine.is("fall") && this.runPhase) {
        this.runPhase.runWidth = this.layout.runWidth;
        this.runPhase.runHeight = this.layout.runHeight;
        this.runPhase.runOriginX = (this.layout.runBounds && this.layout.runBounds.x != null)
          ? this.layout.runBounds.x
          : 0;
        this.runPhase.runOriginY = (this.layout.runBounds && this.layout.runBounds.y != null)
          ? this.layout.runBounds.y
          : 0;
        if (this.runPhase.options) {
          this.runPhase.options.canvasWidth = this.layout.canvasWidth;
          this.runPhase.options.canvasHeight = this.layout.canvasHeight;
        }
        this.runPhase.scale = this.layout.scale;
        if (this.runPhase.fallRenderer) {
          this.runPhase.fallRenderer.scale = this.layout.scale;
          this.runPhase.fallRenderer.mockupScale = this.layout.mockupScale;
        }
        if (this.runPhase.fallPhysics) {
          this.runPhase.fallPhysics.scale = this.layout.scale;
          this.runPhase.fallPhysics.resize(
            this.layout.runWidth,
            this.layout.runHeight,
            this.runPhase.player ? this.runPhase.player.x : this.layout.runWidth / 2
          );
        }
      }
      this._syncActiveStageChromeForLayoutChange();
    } else if (this.layout) {
      this.applyBoardViewport(this.layout);
    }
  };

  Game.prototype.init = function (dom) {
    this.dom = dom || {};
    this.loadUnlockedTiers();
    this._initPlayerStats();
    this._initAnalyticsTracker();
    this._initPerfMonitor();
    this._initSkyBackgroundPrefs();
    this._trackAnalytics(AnalyticsEvents.GAME_LAUNCH);
    this.hasSavedProgressSnapshot = this.restoreLastSavedState();
    if (!this.hasSavedProgressSnapshot) {
      this.loadAndApplyEconomyState();
      this._persistLifetimeEconomy();
    }
    this._loadShakeHintDismissed();
    this._loadHealHintDismissed();
    if (this.hasSavedProgressSnapshot) {
      this._recomputeCurrentGameMaxTierFromFlask();
    }

    EventBus.on("fall:complete", this.endRun.bind(this));
    EventBus.on("fall:failed", this.onRunFailed.bind(this));
    EventBus.on("fall:countdown_finished", this.boundFallCountdownFinished);
    EventBus.on("run:complete", this.endRun.bind(this)); // legacy fallback
    EventBus.on("shake:start", this.goToShake.bind(this));
    EventBus.on("run:continue", this.continueToRun.bind(this));
    EventBus.on("fall:catch", function (payload) { this.handleScoreEvent("catch", payload); }.bind(this));
    EventBus.on("flask:merge", function (payload) { this.handleScoreEvent("merge", payload); }.bind(this));
    EventBus.on("flask:merge", function (payload) { this._onFlaskMergeVictory(payload); }.bind(this));

    window.addEventListener("resize", this.boundResize);
    if (typeof window !== "undefined" && window.visualViewport && typeof window.visualViewport.addEventListener === "function") {
      window.visualViewport.addEventListener("resize", this.boundResize);
      window.visualViewport.addEventListener("scroll", this.boundResize);
    }
    window.addEventListener("keydown", this.boundGlobalKeyDown);
    if (!YtBridge.isEnabled()
      && typeof document !== "undefined"
      && typeof document.addEventListener === "function") {
      document.addEventListener("visibilitychange", this.boundPageVisibilityChange);
      this.setEnvironmentPaused(this._isPageHidden());
    }
    this._bindPointerClientTracking();

    // Test Runway slider listeners removed with fall-stage rework.

    this._refreshLayout();
    this._applyLayoutModeClasses();
    if (this.canvas) {
      this.applyCanvasSize(this.layout.canvasWidth, this.layout.canvasHeight);
    }
    if (this._isVerticalLayout()) {
      this.applyLayoutChromeBounds();
    }

    this.setPlayerHudVisible(false);
    this.setStageStatus("hidden");
    this.updateHpUrgency(100);
    this.setEvolutionDockVisible(false);
    this.setDebugStripVisible(!!C.SHOW_DEBUG_STRIP);
    this.updateScoreDisplay();

    this.attachEvolutionDockPromptHandlers();
    this.attachFallVolumeHandlers();
    this.rewardReveal = createRewardReveal(this.dom);
    this.rewardReveal.init();
    this.retention = createRetentionManager(this, this.dom);
    this.retention.init();
    this.warmMenuAssetsIfNeeded();

    this.syncDevTestBar();
    this._scheduleGameLoop();
  };


export { Game };
